
export const translations = {
  en: {
    ".": ".",
    appName: "OneClickSell",
    trustAppName: "OneClickSell Trust",

    // Navigation
    nav: {
      features: "Features",
      pricing: "Pricing",
      faq: "FAQ",
      login: "Log In",
      startFree: "Start Free"
    },
    common: {
      ok: "OK",
      all: "All",
      cancel: "Cancel",
      confirm: "Confirm",
      close: "Close",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      clearSearch: "Clear search",
      pagination: "Pagination",
      retry: "Retry",
      saving: "Saving...",
      notice: "Notice",
      aiThinking: "AI thinking",
      ozonLogoAria: "Ozon logo",
      backToWorkspace: "Back to Workspace",
      backHome: "Back Home",
      verificationCodeDigit: "Verification code digit {index} of {total}"
    },
    highRisk: {
      subscriptionTarget: "Subscription: {name}",
      subscriptionCancelIrreversible: "The current subscription will stop renewing after confirmation.",
      notificationTarget: "Notification: {name}",
      notificationDeleteIrreversible: "This notification will be removed from your inbox.",
      avatarDeleteAction: "Delete avatar",
      avatarTarget: "Current avatar",
      avatarDeleteIrreversible: "Your current avatar will be removed.",
      collectionTarget: "Collection: {name}",
      collectionDeleteIrreversible: "This task will be deleted and cannot be restored.",
      channelTarget: "AI channel: {name}",
      channelDeleteIrreversible: "This channel will be deleted and unavailable for routing.",
      ozonPublicCredentialsTarget: "Ozon public credentials",
      ozonPublicCredentialsClearIrreversible: "Stored public credentials will be cleared.",
      imageTarget: "Image #{index}",
      candidateRejectIrreversible: "The pending generated candidate will be rejected.",
      imageGenerateCostIrreversible: "This will deduct {credits} credits for the current selected model first.",
      imageDeleteIrreversible: "The generated image will be deleted.",
      imageDeleteFinal: "The image delete operation cannot be undone."
    },
    appError: {
      title: "Unexpected error",
      subtitle: "You can refresh the page or return to Task Center to continue.",
      backTaskCenter: "Back to Task Center",
      refresh: "Refresh",
      copy: "Copy error"
    },

    theme: {
      light: "Light",
      dark: "Dark",
      switchToLight: "Switch to light mode",
      switchToDark: "Switch to dark mode"
    },

    appNav: {
      more: "More",
      loginHint: "Log in to access this feature.",
      unlockHint: "Complete an import to unlock.",
      comingSoonBadge: "Coming soon",
      extensionConnected: "Extension connected",
      extensionMissing: "Extension not detected",
      extensionOffline: "Extension offline",
      extensionUpgradeRequired: "Extension update required",
      extensionUpgradeDetail: "Current {current}, required {required}",
      extensionVersionUnknown: "Unknown",
      extensionTakeoverStatusLabel: "Automatic takeover status",
      extensionAwaitingSync: "Awaiting site takeover",
      extensionAwaitingSyncDetail: "Installed successfully. Keep this page open and the extension will sync automatically.",
      extensionStatusChecking: "Checking extension status",
      extensionStatusCheckingDetail: "The extension is installed. We are confirming takeover and backend readiness now.",
      extensionBackendUnreachable: "Backend unreachable",
      extensionBackendUnreachableDetail: "The extension is installed, but it cannot reach the current backend.",
      extensionReadyPolling: "Auto polling ready",
      extensionReadyPollingDetail: "Site takeover completed. Waiting for backend tasks to start collecting automatically.",
      extensionCollecting: "Auto collecting",
      extensionCollectingDetail: "The extension is collecting product data in the background.",
      extensionConnecting: "Connecting extension",
      extensionConnectingDetail: "Site takeover completed. Establishing extension connection now.",
      extensionDownload: "Install extension",
      extensionUpdate: "Update extension",
      extensionDownloadGuide: "Opening extension installer...",
      assetsComingSoon: "Asset Library is under development: manage generated/uploaded images, copy, and listing records with search, reuse, and cross-platform sync.",
      copyrightComingSoon: "Copyright Assets is under development: watermark/PSD fingerprinting, evidence bundles, and dispute templates to protect your creative assets."
    },
    extensionGuide: {
      badge: "Visual install guide",
      title: "Install the Chrome extension without guessing",
      subtitle: "Download it, keep one folder, load that folder in Chrome, then refresh this page once.",
      resumeHint: "We restored your last step. Continue from here.",
      progress: "Step {current} / {total}",
      sources: {
        header: "Opened from top navigation",
        workspace: "Opened from workspace",
        import: "Opened from import page"
      },
      labels: {
        expected: "You should see next",
        warning: "Do not do this"
      },
      steps: {
        intro: {
          title: "One-time setup, Chrome only",
          desc: "Follow the pictures step by step. The key rule: keep the extension folder intact and refresh this page after installation.",
          tip: "Do not install by guessing or by opening random subfolders."
        },
        download: {
          title: "Step 1: Download the ZIP installer",
          desc: "Download the ZIP file to Desktop or Downloads.",
          tip: "After this step you should have one ZIP file, not many loose files."
        },
        unzip: {
          title: "Step 2: Unzip into one folder",
          desc: "After unzipping, you should get exactly one folder named {folderName}.",
          tip: "Do not open the folder and move files around. Keep the whole folder intact."
        },
        openExtensions: {
          title: "Step 3: Open chrome://extensions/",
          desc: "Open Chrome's extension management page and keep this guide tab open.",
          tip: "If the button does not work, paste chrome://extensions/ into the address bar manually."
        },
        loadUnpacked: {
          title: "Step 4: Load the whole folder",
          desc: "Turn on Developer mode, click Load unpacked, and choose the whole {folderName} folder.",
          tip: "Do not click into child folders. Select the top-level folder only."
        },
        refreshPage: {
          title: "Step 5: Refresh this page once",
          desc: "After Chrome accepts the folder, come back here and refresh this business page once.",
          tip: "Without refresh, this tab usually cannot detect the newly installed extension."
        },
        verify: {
          title: "Step 6: Auto-check after refresh",
          desc: "Stay here after refresh. We will keep checking the extension status automatically.",
          tip: "If it still cannot be found, the usual cause is wrong folder selection or skipping refresh."
        },
        done: {
          title: "Done. The extension is active on this page",
          desc: "Installation, refresh, and detection all succeeded.",
          tip: "You can continue importing products now."
        }
      },
      expected: {
        intro: "A single Chrome-only path with visual guidance.",
        download: "One ZIP file should appear in your chosen folder.",
        unzip: "You should now see exactly one folder: {folderName}.",
        openExtensions: "Chrome should show the extension management page.",
        loadUnpacked: "You should see this extension card in Chrome's extension list.",
        refreshPage: "This page will reload and let the extension inject itself into the current tab.",
        verify: "The status here should change from checking to connected/ready.",
        done: "This page has already been refreshed and the extension is active."
      },
      warnings: {
        intro: "Do not skip the refresh step after installation.",
        download: "Do not unzip the ZIP directly into the current folder so files scatter everywhere.",
        unzip: "Wrong result: selecting the ZIP, selecting the parent folder, or scattering files.",
        openExtensions: "Do not use a search results page. The address must be chrome://extensions/.",
        loadUnpacked: "Choose the whole {folderName} folder only. Do not click into child folders.",
        refreshPage: "Without refreshing the current page, installation may be real but this tab still cannot detect it.",
        verify: "If it still says not found, the most common cause is wrong folder selection or no refresh.",
        done: "If you update the extension later, refresh this page again."
      },
      help: {
        title: "Need help?",
        show: "Show tips",
        hide: "Hide tips",
        intro1: "Follow the steps in order and do not skip the refresh step.",
        intro2: "If you close the guide, reopen it from Header, Workspace, or Import.",
        download1: "If no file downloads, check Chrome popup/download blocking.",
        download2: "Keep the ZIP file as-is until the unzip step.",
        unzip1: "The correct result is one folder named {folderName}.",
        unzip2: "Wrong result: many loose files directly in Downloads/Desktop.",
        openExtensions1: "The address must be exactly chrome://extensions/.",
        openExtensions2: "Keep this guide open in one tab and operate Chrome in another tab.",
        loadUnpacked1: "Developer mode is usually at the top right.",
        loadUnpacked2: "Choose the whole {folderName} folder, not a child folder and not the ZIP.",
        refreshPage1: "Refresh is required because the current tab needs the extension content script to be injected.",
        refreshPage2: "After reload we will reopen the guide and continue detection automatically.",
        verify1: "If detection times out, first refresh again, then reselect the folder.",
        verify2: "If backend unreachable/auth required appears, installation succeeded. That is no longer an install issue.",
        done1: "If this page stops responding later, click the header extension status to recheck.",
        done2: "You can reopen this guide any time."
      },
      actions: {
        close: "Close",
        prev: "Back",
        start: "Start now",
        next: "Next",
        downloadZip: "Download installer",
        downloadDone: "ZIP downloaded",
        confirmFolderReady: "I can see {folderName}",
        openChromeExtensions: "Open chrome://extensions/",
        backToExtensionsPage: "Back to extension page",
        confirmLoadUnpacked: "I selected the {folderName} folder",
        refreshNow: "Refresh this page now",
        retryDetect: "Retry detection",
        downloadUpdate: "Download latest package"
      },
      status: {
        ready: "Extension connected successfully",
        needUpgrade: "Current version {current}. Please update to {required} or newer.",
        unknownVersion: "unknown",
        authRequired: "Extension installed. Waiting for site auth sync.",
        backendUnreachable: "Extension installed, but backend is unreachable.",
        readyPolling: "Extension is ready and waiting for backend tasks.",
        collecting: "Extension is collecting automatically.",
        connecting: "Extension is connecting to the backend...",
        connectedWaiting: "Extension found. Finalizing checks...",
        offline: "Extension appears offline. Refresh Chrome and retry.",
        verifyTimeout: "Detection timed out. Refresh this page or re-check the folder.",
        detecting: "Checking extension status..."
      },
      verify: {
        successHint: "Everything looks good. Moving to completion.",
        upgradeHint: "The extension is installed, but this version is too old.",
        authRequiredHint: "Installation is complete. The site is now syncing auth to the extension.",
        backendUnreachableHint: "The extension is installed. Please confirm the current backend is reachable.",
        readyPollingHint: "Refresh worked. The extension is waiting for backend tasks.",
        collectingHint: "Great. The extension is already collecting in the background.",
        connectingHint: "The extension received site credentials and is establishing its connection.",
        timeoutHint: "Refresh this page first. If it still fails, reselect the {folderName} folder.",
        detectingHint: "Auto-detection in progress (attempt {attempts})..."
      },
      doneDesc: "Installed version: {version} · Minimum required: {required}. This page has been refreshed, so you can continue now.",
      doneActions: {
        workspace: "Go to import products",
        import: "Start importing now",
        header: "Continue current task"
      }
    },

    admin: {
      title: "Admin Console",
      subtitle: "Operations & Controls",
      backToWorkspace: "Back to Workspace",
      login: {
        title: "Admin Login",
        subtitle: "Use your admin credentials to continue.",
        checking: "Checking admin session...",
        username: "Username",
        usernamePlaceholder: "Enter admin username",
        password: "Password",
        passwordPlaceholder: "Enter admin password",  // pragma: allowlist secret
        action: "Log In",
        loggingIn: "Logging in...",
        required: "Username and password are required.",
        failed: "Login failed.",
        denied: "Access denied.",
        logout: "Log Out"
      },
      nav: {
        overview: "Overview",
        aiRuntime: "AI Runtime",
        aiChannels: "AI Channels",
        aiMonitor: "Channel Monitor",
        aiUsage: "AI Usage",
        marketing: "Marketing Analytics",
        billing: "Billing & Credits",
        consumption: "Credit Ledger",
        users: "Users",
        feedback: "Feedback",
        logs: "Admin Logs",
        announcements: "Announcements",
        imagePrompts: "Image Prompts",
        settings: "System Settings"
      },
      imagePrompts: {
        title: "Image Prompt Console",
        desc: "Manage image prompt templates and unified constraints. Changes take effect immediately.",
        loading: "Loading image prompt settings...",
        error: "Failed to load image prompt settings.",
        empty: "No image prompt settings found.",
        searchPlaceholder: "Search by key / category / description",
        onlyChanged: "Only changed",
        save: "Save",
        saving: "Saving...",
        saved: "Image prompt settings saved.",
        saveConfirm: "Save this image prompt setting now?",
        saveAll: "Save all changed ({count})",
        saveAllConfirm: "Save all {count} changed image prompt settings?",
        reset: "Reset",
        resetAll: "Reset all changes",
        updatedAt: "Updated at {time}",
        realtimeHint: "All saved settings are applied in real time to planning, preview, regeneration, and variant generation.",
        validationEmpty: "Prompt text cannot be empty.",
        unsavedChanges: {
          leaveTitle: "Unsaved image prompt changes",
          leaveConfirm: "You have unsaved image prompt changes. Leave this page anyway?",
          leaveNow: "Leave",
          stay: "Stay"
        },
        categories: {
          constraint: "Unified constraints",
          common: "Common templates",
          carousel: "Main/Carousel templates",
          detail: "Detail templates",
          variant: "Variant templates"
        }
      },
      announcements: {
        title: "Announcement Console",
        desc: "Publish critical sitewide updates, set active windows, and manage pin priority.",
        refresh: "Refresh",
        loading: "Loading announcements...",
        loadFailed: "Failed to load announcements.",
        createTitle: "Publish Announcement",
        createDesc: "Use this panel for major platform notices and migration communications.",
        submit: "Publish now",
        submitting: "Publishing...",
        createSuccess: "Announcement published.",
        createFailed: "Failed to publish announcement.",
        status: "Status",
        listTitle: "Announcement History",
        empty: "No announcements found under current filter.",
        actionPublish: "Republish",
        actionOffline: "Offline",
        actionPin: "Pin",
        actionUnpin: "Unpin",
        confirmPublish: "Republish this announcement now?",
        confirmOffline: "Offline this announcement now?",
        statusUpdated: "Announcement status updated.",
        statusUpdateFailed: "Failed to update announcement status.",
        pinUpdated: "Pin order updated.",
        pinUpdateFailed: "Failed to update pin order.",
        moveUp: "Move up",
        moveDown: "Move down",
        pinnedOrder: "Pinned #{order}",
        publishedAt: "Published",
        endsAt: "Expires",
        actionText: "CTA text",
        actionUrl: "CTA URL",
        filters: {
          all: "All",
          active: "Active",
          scheduled: "Scheduled",
          expired: "Expired",
          offline: "Offline"
        },
        severity: {
          info: "Notice",
          warning: "Important",
          critical: "Critical"
        },
        form: {
          title: "Title",
          titlePlaceholder: "Example: Image pricing migration notice",
          content: "Content",
          contentPlaceholder: "Describe impact, effective window, and action required by users.",
          severity: "Severity",
          pinned: "Pin this announcement",
          startsAt: "Start time",
          endsAt: "End time",
          actionText: "CTA text (optional)",
          actionTextPlaceholder: "Example: View details",
          actionUrl: "CTA URL (optional)",
          actionUrlPlaceholder: "Example: /settings/notifications or https://...",
          required: "Title and content are required.",
          invalidStartAt: "Start time format is invalid.",
          invalidEndAt: "End time format is invalid.",
          invalidWindow: "End time must be later than start time."
        }
      },
      marketing: {
        title: "Marketing Analytics",
        desc: "Track PV, engagement, CTA funnel, and UTM attribution performance.",
        loading: "Loading marketing analytics...",
        error: "Failed to load marketing analytics.",
        empty: "No marketing data for the selected filters.",
        apply: "Apply",
        reset: "Reset",
        updatedAt: "Updated at {time}",
        cards: {
          pv: "Total PV",
          visitors: "Unique Visitors",
          engagement: "Avg Engagement",
          ctaClicks: "CTA Clicks",
          conversions: "Conversions",
          conversionRate: "Conversion Rate"
        },
        filters: {
          days: "Time Range",
          utmSource: "UTM Source",
          utmMedium: "UTM Medium",
          utmCampaign: "UTM Campaign",
          experimentId: "Experiment ID",
          experimentVariant: "Experiment Variant",
          pagePath: "Page Path"
        },
        business: {
          cards: {
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            grossProfit: "Gross Profit",
            roas: "ROAS",
            roi: "ROI",
            aov: "Avg Order Value",
            cac: "Cost / Paid Order",
            cpa: "Cost / Conversion"
          }
        },
        trend: {
          title: "Daily PV/UV/Conversion Trend",
          engagementTitle: "Daily Avg Engagement (seconds)",
          series: {
            pv: "PV",
            uv: "UV",
            conversions: "Conversions",
            engagement: "Avg Engagement (s)"
          }
        },
        funnel: {
          title: "CTA Funnel",
          view: "View",
          ctaClick: "CTA Click",
          conversion: "Conversion"
        },
        utm: {
          title: "UTM Attribution"
        },
        experiment: {
          title: "Experiment Attribution",
          empty: "No experiment attribution data under current filters.",
          columns: {
            id: "Experiment",
            variant: "Variant",
            pv: "PV",
            uv: "UV",
            conversions: "Conv",
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            roas: "ROAS",
            roi: "ROI",
            cvr: "CVR"
          }
        },
        pages: {
          title: "Page Breakdown",
          empty: "No page-level data under current filters.",
          summary: {
            highPriorityPages: "High Priority Pages",
            mediumPriorityPages: "Medium Priority Pages",
            lowPriorityPages: "Low Priority Pages",
            avgScore: "Avg Optimization Score"
          },
          priority: {
            high: "High",
            medium: "Medium",
            low: "Low"
          },
          signals: {
            HIGH_BOUNCE_RATE: "High bounce rate",
            LOW_CTA_RATE: "Low CTA click-through rate",
            LOW_CTA_TO_CONVERSION: "Low conversion after CTA click",
            HIGH_ENGAGEMENT_LOW_CONVERSION: "High engagement but low conversion",
            NO_ENGAGEMENT_EVENTS: "No engagement events tracked"
          },
          actions: {
            OPTIMIZE_ABOVE_THE_FOLD: "Optimize headline/value proposition above the fold",
            IMPROVE_CTA_VISIBILITY: "Increase CTA visibility and copy clarity",
            REDUCE_FORM_FRICTION: "Reduce form and checkout friction",
            ALIGN_CONTENT_AND_OFFER: "Align page content with offer and intent",
            VERIFY_ENGAGEMENT_TRACKING: "Verify engagement tracking instrumentation",
            ENRICH_TRAFFIC_QUALITY: "Improve traffic quality and targeting",
            SCALE_WINNING_PAGE: "Scale traffic to this winning page"
          },
          columns: {
            rank: "Rank",
            priority: "Priority",
            score: "Score",
            page: "Page",
            events: "Events",
            pv: "PV",
            uv: "UV (All)",
            uvAll: "UV (All)",
            uvPageView: "UV (PV)",
            entry: "Entry",
            exit: "Exit",
            bounce: "Bounce",
            engagement: "Avg Engagement",
            engagementEvents: "Engagement Events",
            ctaClicks: "CTA Clicks",
            ctaRate: "CTA Rate",
            conversions: "Conv",
            cvr: "CVR",
            cvrPv: "CVR (PV)",
            cvrFromCta: "CVR (CTA)",
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            roas: "ROAS",
            roi: "ROI",
            aov: "AOV",
            cac: "Cost / Paid Order",
            cpa: "Cost / Conversion",
            signals: "Signals",
            actions: "Actions",
            topEvents: "Top Events"
          }
        },
        table: {
          source: "Source",
          medium: "Medium",
          campaign: "Campaign",
          pv: "PV",
          uv: "UV",
          conversions: "Conv",
          paidOrders: "Paid Orders",
          revenue: "Revenue",
          adCost: "Ad Cost",
          roas: "ROAS",
          cvr: "CVR"
        }
      },
      aiUsage: {
        title: "AI Usage Analytics",
        desc: "Track token usage and distribution by provider, call type and model",
        loading: "Loading AI usage data...",
        error: "Failed to load AI usage data.",
        empty: "No usage records found for the selected filters.",
        emptyWithLatest: "No usage records in the current window. Latest record at {latest}.",
        apply: "Apply",
        reset: "Reset",
        summary: "Period: {start} ~ {end}",
        latestRecordAt: "Latest Record: {value}",
        cards: {
          totalCalls: "Total Calls",
          totalTokens: "Total Tokens",
          totalInputTokens: "Input Tokens",
          totalOutputTokens: "Output Tokens",
          imageCalls: "Image Calls",
          nonImageCalls: "Non-Image Calls",
          avgTokens: "Avg Tokens / Call",
          quantity: "Quantity"
        },
        distribution: {
          provider: "Provider Distribution",
          supplier: "Supplier Distribution",
          callType: "Call Type Distribution",
          model: "Model Distribution"
        },
        coverage: {
          title: "Token Coverage",
          exact: "Exact split: {value}",
          estimated: "Estimated split: {value}",
          missing: "Missing split: {value}"
        },
        legend: {
          imageTokens: "Image Tokens"
        },
        trend: {
          title: "Usage Trend"
        },
        cost: {
          title: "Cost Estimation",
          description: "Adjust the unit prices below to estimate external AI costs.",
          reset: "Reset defaults",
          inputPrice: "Input price (USD / 1M tokens)",
          outputPrice: "Output price (USD / 1M tokens)",
          imageCost: "Image cost (RMB / image)",
          exchangeRate: "USD/CNY",
          totalTokenCost: "Token cost",
          imageCostTitle: "Image cost",
          totalCost: "Estimated total cost",
          avgPerTask: "Avg cost / call",
          tokenPerNonImageCall: "Avg token cost / non-image call"
        },
        filters: {
          periodStart: "Start Time",
          periodEnd: "End Time",
          interval: "Trend Interval",
          all: "All",
          image: "Image Calls Only",
          yes: "Yes",
          no: "No",
          userId: "User ID",
          supplierId: "Supplier ID",
          provider: "AI Provider",
          model: "Model",
          callType: "Call Type",
          action: "Action",
          pipelineStage: "Pipeline Stage",
          stage: "Stage",
          runId: "Run ID",
          callId: "Call ID"
        },
        intervals: {
          hour: "Hour",
          day: "Day",
          week: "Week",
          month: "Month"
        },
        table: {
          time: "Time",
          user: "User",
          supplierId: "Supplier ID",
          runId: "Run ID",
          callId: "Call ID",
          action: "Action",
          provider: "Provider",
          model: "Model",
          callType: "Call Type",
          tokens: "Tokens",
          splitMethod: "Split",
          estimatedCost: "Est. Cost",
          image: "Image",
          metadata: "Metadata"
        },
        records: "Usage Records",
        pagination: "Page {current} / {total}",
        prev: "Previous",
        next: "Next"
      },
      navGroups: {
        overview: "Overview",
        ai: "AI Console",
        operations: "Operations",
        system: "System"
      },
      access: {
        checking: "Verifying admin access...",
        loginTitle: "Please log in to continue",
        loginDesc: "Admin console requires authentication.",
        loginAction: "Log In",
        deniedTitle: "Access denied",
        deniedDesc: "Your account does not have admin permissions."
      },
      listStates: {
        loading: {
          title: "Syncing list data",
          description: "Please wait while we fetch the latest records."
        },
        error: {
          title: "Unable to load list",
          description: "Retry to get the latest list data.",
          detailPrefix: "Error detail: "
        },
        empty: {
          title: "No data to display",
          description: "Try adjusting filters or refresh and check again."
        },
        actions: {
          retry: "Retry",
          retrying: "Retrying...",
          refresh: "Refresh data",
          backToOverview: "Back to overview"
        }
      },
      overview: {
        title: "Dashboard",
        desc: "Key metrics and recent trends",
        loading: "Loading overview data...",
        error: "Failed to load overview data.",
        empty: "No data yet.",
        lastUpdated: "Last updated {time}",
        ready: "Ready",
        refreshing: "Refreshing...",
        refresh: "Refresh",
        trendLoading: "Loading trend...",
        userTrend: "New users in last 7 days",
        revenueTrend: "Revenue in last 7 days",
        cards: {
          totalUsers: "Total Users",
          activeToday: "Active Today",
          newToday: "New Today",
          totalRevenue: "Total Revenue",
          revenueMonth: "Revenue This Month",
          consumptionToday: "Consumption Today",
          totalConsumption: "Total Consumption"
        },
        units: {
          users: "Users",
          usd: "USD",
          credits: "Credits"
        }
      },
      aiChannels: {
        title: "AI Access & Routing",
        desc: "Manage channel assets, model routing and runtime health",
        routingGuide: {
          title: "Channel responsibility is separated from billing",
          desc: "This page manages API keys, base URLs, health checks, protocol capabilities, and the advanced routing strategy behind each AI mode. User-facing image prices stay in System Settings.",
          badge: "Routing assets",
          cards: {
            keys: {
              title: "1. Channel assets",
              desc: "Create official or proxy channels, set supported protocols, priority and expiry."
            },
            routing: {
              title: "2. Runtime routing",
              desc: "Open Advanced routing below to bind models, fallback order and channel strategy."
            },
            price: {
              title: "3. User billing",
              desc: "Change Normal / Official fast credits per image in System Settings, not here."
            }
          }
        },
        runtimePanel: {
          title: "Advanced routing strategy",
          hint: "Models, fallback and per-module overrides",
          notice:
            "This is an advanced area. It changes which model/channel is used, but does not change user-facing image prices."
        },
        error: "Failed to load channels.",
        testResult: {
          success: "Test passed",
          failed: "Test failed",
          successMessage: "Channel test succeeded.",
          failureUnknown: "Test failed. Please check error details below.",
          failureReason: "Failure reason",
          checkedAt: "Checked at {time}"
        },
        validationName: "Channel name is required.",
        validationKey: "API Key is required.",
        validationBaseUrl: "Base URL is required for proxy channels and non-standard official protocols.",
        validationSupportedProviders: "Please select at least one supported protocol for this proxy channel.",
        validationDefaultProvider: "Default protocol must be one of the selected supported protocols.",
        validationExpiresAtRequired: "Please set a valid expiry time or choose permanent validity.",
        validationExpiresAtInvalid: "Expiry time format is invalid.",
        validationVertexProjectLocation: "Vertex AI channels require both Project and Location.",
        validationVertexProjectLocationPair: "GCP Project and Location must both be filled or both left empty.",
        enableConfirm: "Enable channel {name}?",
        disableConfirm: "Disable channel {name}?",
        testConfirm: "Test channel {name}?",
        updated: "Channel updated.",
        created: "Channel created.",
        deleted: "Channel deleted.",
        tested: "Test completed.",
        deleteConfirm: "Delete channel {name}?",
        officialBaseUrl: "Official Base URL: {url}",
        vertexBaseUrlHint: "Vertex AI provider does not require Base URL.",
        vertexAuthHint: "Vertex AI supports API Key mode directly. Project/Location are optional and must be entered together.",
        hints: {
          neverExpires: "Current channel is permanently valid."
        },
        refresh: "Refresh",
        updatedAt: "Updated at {time}",
        edit: "Edit Channel",
        create: "Create Channel",
        newChannel: "New Channel",
        cancel: "Cancel",
        formHint: "Keep API Key empty when editing to preserve current key.",
        channelTypeHint: "Official channels are vendor-specific. Proxy channels can expose multiple protocols with one key.",
        searchPlaceholder: "Search by name / base URL",
        filters: {
          all: "All channels",
          enabled: "Enabled only",
          disabled: "Disabled only",
          official: "Official only",
          custom: "Proxy only",
          degraded: "Degraded only"
        },
        summary: {
          total: "Total Channels",
          enabled: "Enabled",
          degraded: "Degraded",
          requests: "24h Requests",
          failureRate: "24h Failure Rate"
        },
        providers: {
          developerAPI: "Gemini Developer API",
          vertexAI: "Vertex AI",
          openaiCompatible: "OpenAI-Compatible API",
          googleOfficial: "Google Official",
          customProxy: "Custom Proxy"
        },
        vendors: {
          gemini: "Google Gemini",
          qwen: "Qwen",
          openai: "OpenAI",
          claude: "Claude / Anthropic",
          custom: "Custom"
        },
        fields: {
          name: "Channel Name",
          apiKey: "API Key",  // pragma: allowlist secret
          provider: "Provider",
          channelType: "Channel Type",
          vendor: "Vendor",
          protocol: "Protocol",
          supportedProviders: "Supported Protocols",
          defaultProvider: "Default Protocol",
          baseUrl: "Base URL",
          gcpProject: "GCP Project",
          gcpLocation: "GCP Location",
          tier: "Tier",
          priority: "Priority",
          creditsPerImage: "Credits per image",
          expiresAt: "Expires At",
          maxFail: "Max Fail Count",
          recovery: "Recovery Time (s)",
          description: "Description",
          enabled: "Enabled",
          isOfficial: "Official",
          neverExpires: "Permanent"
        },
        placeholders: {
          name: "e.g. Gemini-Primary",
          apiKey: "Paste API key",  // pragma: allowlist secret
          apiKeyEdit: "Leave blank to keep current key",  // pragma: allowlist secret
          baseUrl: "https://api.example.com",
          gcpProject: "e.g. my-gcp-project",
          gcpLocation: "e.g. us-central1",
          creditsPerImage: "e.g. 5 (optional)",
          description: "Usage notes"
        },
        save: "Save",
        saving: "Saving...",
        listTitle: "Channel List",
        vendorProtocolHint: "Use channel type + protocols as the source of truth. Proxy channels can carry multiple protocols on one relay key.",
        protocolHint: "Protocol determines the actual transport for the selected official vendor.",
        supportedProvidersHint: "Proxy channels can expose multiple protocols at once for runtime switching.",
        defaultProviderHint: "Default protocol is used for testing and fallback when provider is not explicitly specified.",
        loading: "Loading channels...",
        empty: "No channels configured.",
        statusUnknown: "Unknown",
        table: {
          name: "Channel",
          status: "Status",
          channelType: "Type",
          supportedProviders: "Protocols",
          defaultProvider: "Default",
          vendor: "Vendor",
          protocol: "Protocol",
          priority: "Priority",
          creditsPerImage: "Credits/Image",
          baseUrl: "Base URL",
          requests: "24h Requests",
          failRate: "24h Failure Rate",
          updatedAt: "Updated At",
          avgLatency: "Avg Latency",
          validUntil: "Valid Until",
          permanent: "Permanent",
          expired: "Expired",
          actions: "Actions",
          scrollHint: "Scroll horizontally to view all columns."
        },
        types: {
          official: "Official",
          proxy: "Proxy / Relay"
        },
        actions: {
          edit: "Edit",
          disable: "Disable",
          enable: "Enable",
          enabling: "Enabling...",
          disabling: "Disabling...",
          test: "Test",
          testing: "Testing...",
          delete: "Delete",
          deleting: "Deleting..."
        }
      },
      aiRuntime: {
        title: "AI Runtime Configuration",
        desc: "Configure model + thinking + channel strategy per AI module",
        quickPolicyTitle: "Quick Routing Policy",
        quickPolicyDesc: "One switch to force all analysis modules to official channels. Image generation still follows user mode.",
        manageChannels: "Manage channels",
        analysisOfficialOnlyLabel: "Force official channels for analysis modules",
        analysisOfficialOnlyHintOn:
          "Enabled: all analysis modules are forced to official channels; only image generation can use Value mode.",
        analysisOfficialOnlyHintOff:
          "Disabled: analysis modules follow the global/module channel strategy configured below.",
        enabledOfficialChannels: "Enabled official channels: {count}",
        enabledThirdPartyChannels: "Enabled third-party channels: {count}",
        loading: "Loading runtime config...",
        error: "Failed to load runtime config.",
        saved: "Runtime config updated.",
        save: "Save Configuration",
        saving: "Saving...",
        refresh: "Refresh configuration",
        refreshing: "Refreshing...",
        refreshed: "Runtime config refreshed.",
        reset: "Reset unsaved changes",
        saveConfirm: "You are about to save runtime configuration and apply to current environment. Continue?",
        refreshConfirm: "Discard unsaved changes and reload the current runtime configuration?",
        summaryTitle: "Runtime Overview",
        statusDirty: "Unsaved changes",
        statusSaved: "Saved",
        global: "Global Default",
        globalHint: "Defaults are inherited by all modules unless overridden.",
        modules: "Module Overrides",
        channelsHint: "When strategy is specific channels, failover stays in selected channels.",
        emptyChannels: "No channels available. Configure channels first.",
        effectiveConfig: "Effective Runtime Policy",
        copyGlobal: "Copy global settings",
        searchResultCount: "{count} modules shown",
        groups: {
          all: "All",
          extract: "Extract",
          content: "Content",
          image: "Image"
        },
        summary: {
          total: "Total Modules",
          enabled: "Enabled",
          override: "With Override",
          specific: "Specific Channels"
        },
        fields: {
          provider: "Provider",
          model: "Model",
          thinking: "Thinking",
          strategy: "Channel Strategy",
          channels: "Channels",
          fallbackModels: "Fallback Models",
          enabled: "Enabled"
        },
        hints: {
          globalProvider: "Default provider inherited when a module does not specify its own provider.",
          globalModel: "Model inherited by modules when no module-level override is set.",
          globalThinking: "Default thinking level used as fallback for all modules.",
          globalStrategy: "Set default channel strategy when module override is empty.",
          globalChannels: "Only takes effect when channel strategy is set to specific channels.",
          moduleProvider: "Set module-level provider override; leave empty to inherit global default.",
          moduleModel: "Set module-level model override; leave empty to inherit global default.",
          moduleThinking: "Set module-level thinking level; leave empty to inherit global default.",
          moduleStrategy: "Module-specific strategy for request routing behavior.",
          moduleChannels: "When strategy is specific channels, only selected channels are used.",
          moduleEnabled: "Enable or disable this module from taking requests."
        },
        providers: {
          gemini_developer_api: "Gemini Developer API",
          vertex_ai: "Vertex AI",
          openai_compatible: "OpenAI-Compatible API"
        },
        strategy: {
          auto: "Auto",
          official_only: "Official Only",
          specific_channels: "Specific Channels"
        },
        inherit: "Inherit global",
        moduleSearch: "Search modules",
        moduleCount: "{count} modules",
        selectedModule: "Selected module"
      },
      aiMonitor: {
        title: "AI Channel Monitor",
        desc: "Runtime status and routing health",
        loading: "Loading runtime stats...",
        error: "Failed to load channel stats.",
        refreshing: "Refreshing...",
        refresh: "Refresh",
        autoRefreshOn: "Auto refresh: On",
        autoRefreshOff: "Auto refresh: Off",
        autoRefresh: "Auto Refresh",
        updatedAt: "Updated at {time}",
        current: "Current Channel",
        total: "Total Channels",
        healthy: "Healthy Channels",
        requests: "24h Requests",
        failureRate: "Failure Rate",
        imageSuccessRate: "Image Success Rate",
        imageFailureRate: "Image Failure Rate",
        officialTextSuccessRate: "Official Text Success Rate",
        officialTextFailureRate: "Official Text Failure Rate",
        avgLatency: "Avg Latency",
        requestUnit: "req",
        percentUnit: "%",
        msUnit: "ms",
        trend: "Traffic Trend",
        last24h: "Last 24h",
        noTrend: "No trend data",
        errors: "Recent Errors",
        errorsDesc: "Latest failures in last 24h",
        noErrors: "No recent errors",
        noErrorMessage: "No message",
        expand: "Expand",
        collapse: "Collapse",
        none: "None",
        empty: "No runtime data.",
        statusUnknown: "Unknown",
        yes: "Yes",
        no: "No",
        table: {
          name: "Channel",
          status: "Status",
          priority: "Priority",
          enabled: "Enabled",
          available: "Available",
          successRate: "Success Rate",
          failCount: "Fail Count",
          avgLatency: "Avg Latency"
        },
        errorsTable: {
          time: "Time",
          summary: "Summary",
          action: "Action"
        }
      },
      billing: {
        title: "Credits & Billing",
        desc: "Adjust credits and run manual recharges",
        error: "Failed to load credit users.",
        creditsBreakdown: "Sub {subscription} / Recharge {recharge} / Legacy {legacy}",
        creditUnit: "credits",
        actionTitle: "Manual credit action",
        validationUser: "User ID or phone is required.",
        defaultReason: "Admin adjustment",
        rechargeReason: "Admin recharge",
        actionSuccess: "Action completed.",
        submitting: "Submitting...",
        submit: "Submit",
        listTitle: "User Credits",
        search: "Search",
        loading: "Loading credit users...",
        empty: "No users found.",
        reset: "Reset filters",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        fields: {
          userId: "User ID / Phone",
          actionType: "Action",
          reason: "Reason",
          credits: "Credits",
          amount: "Amount",
          bonus: "Bonus"
        },
        placeholders: {
          userId: "Select from list or input user ID / phone",
          reason: "Optional reason",
          search: "Search by username / phone / email / admin notes"
        },
        actions: {
          adjust: "Adjust Credits",
          recharge: "Recharge",
          select: "Select"
        },
        chart: {
          title: "Billing order trend",
          desc: "Daily paid/pending/issue order comparison",
          loading: "Loading billing trend...",
          error: "Failed to load billing trend.",
          empty: "No billing trend data yet.",
          orderUnit: "orders",
          series: {
            paid: "Paid",
            pending: "Pending",
            issue: "Issues"
          }
        },
        table: {
          user: "User",
          plan: "Plan",
          credits: "Credits",
          trial: "Trial",
          actions: "Actions"
        }
      },
      consumption: {
        title: "Credit Ledger",
        desc: "Audit recharges, consumption, promotions, refunds, and admin adjustments",
        loading: "Loading credit ledger...",
        error: "Failed to load credit ledger.",
        empty: "No records found.",
        creditUnit: "credits",
        search: "Search",
        reset: "Reset filters",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          userId: "Filter by user ID"
        },
        chart: {
          title: "Consumption trend",
          desc: "Daily credits consumption vs recharge",
          loading: "Loading consumption trend...",
          error: "Failed to load consumption trend.",
          empty: "No consumption trend data yet.",
          series: {
            consumed: "Consumed credits",
            recharged: "Recharged credits"
          }
        },
        table: {
          user: "User",
          createdAt: "Created At"
        }
      },
      ledger: {
        placeholders: {
          search: "Search by phone / email / username / admin notes / description"
        },
        filters: {
          allTypes: "All types",
          allBuckets: "All buckets"
        },
        types: {
          recharge: "Recharge",
          consumption: "Consumption",
          subscription: "Subscription Grant",
          adjustment: "Admin Adjustment",
          promo: "Promotion",
          refund: "Refund",
          trialGrant: "Trial Grant"
        },
        buckets: {
          subscription: "Subscription",
          recharge: "Recharge",
          legacy: "Legacy"
        },
        imageBillingMeta:
          "Image billing: model {model} · unit {unit} credits/image · count {count} · phase {phase} · run {runToken}",
        table: {
          type: "Type",
          bucket: "Bucket",
          change: "Change",
          balance: "Balance",
          reference: "Reference",
          money: "Amount/Cost"
        }
      },
      users: {
        title: "User Management",
        desc: "Update status, plans, and admin flags",
        error: "Failed to load users.",
        filters: "Filters",
        search: "Search",
        filtersAllStatus: "All status",
        filtersAllAdmin: "All users",
        filtersAdmin: "Admins only",
        filtersNonAdmin: "Non-admins",
        sort: {
          createdAtDesc: "Registered time (newest first)",
          createdAtAsc: "Registered time (oldest first)",
          lastLoginDesc: "Last login (recent first)",
          lastLoginAsc: "Last login (earliest first)",
          creditsDesc: "Credits (high to low)",
          creditsAsc: "Credits (low to high)"
        },
        editorTitle: "Edit user",
        detailTitle: "User details",
        closeEditor: "Close",
        adminToggle: "Admin access",
        saved: "Changes saved.",
        saving: "Saving...",
        save: "Save changes",
        discardChanges: "Discard",
        discardSuccess: "Changes discarded.",
        listTitle: "Users",
        loading: "Loading users...",
        empty: "No users found.",
        reset: "Reset filters",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholderEmpty: "-",
        compareTitle: "Pending changes",
        noChanges: "No pending changes.",
        riskConfirmTitle: "High-risk changes detected",
        riskConfirm: "The following high-risk changes require confirmation:",
        saveError: "Save failed: {error}",
        unknownError: "Unknown error",
        adminStateEnabled: "Enabled",
        adminStateDisabled: "Not admin",
        fields: {
          status: "Status",
          plan: "Plan",
          admin: "Admin",
          notes: "Notes",
          rechargeBonusPercent: "Top-up bonus %"
        },
        detailLabels: {
          userId: "User ID",
          phone: "Phone",
          email: "Email",
          plan: "Plan",
          status: "Status",
          credits: "Credits",
          creditsLegacy: "Legacy credits",
          creditsSubscription: "Subscription credits",
          creditsRecharge: "Recharge credits balance",
          trialCredits: "Trial credits balance",
          totalRechargeCredits: "Total recharged credits",
          rechargeBonusPercent: "Admin top-up bonus %",
          rechargeTotalAmount: "Total recharge amount",
          rechargeRefundAmount: "Total refunded amount",
          rechargeNetAmount: "Net recharge amount",
          paidOrderCount: "Paid orders",
          lastPaidAt: "Last paid at",
          trial: "Trial",
          createdAt: "Created At",
          lastLogin: "Last Login"
        },
        placeholders: {
          search: "Search by name / phone / email",
          phone: "Search by phone number",
          notes: "Admin notes"
        },
        status: {
          active: "Active",
          inactive: "Inactive",
          banned: "Banned"
        },
        plan: {
          free: "Free",
          earlyBird: "Early Bird",
          standard: "Standard",
          enterprise: "Enterprise"
        },
        table: {
          user: "User",
          notes: "Notes",
          plan: "Plan",
          status: "Status",
          createdAt: "Registered At",
          lastLogin: "Last Login",
          credits: "Credits",
          rechargeTotal: "Total Recharge",
          rechargeOrders: "{count} paid orders",
          actions: "Actions"
        },
        actions: {
          manage: "Manage"
        }
      },
      feedback: {
        title: "User Feedback",
        desc: "Review submitted bug reports and feature requests, assess feasibility, and manage credit rewards.",
        stats: {
          total: "Total",
          pending: "Pending",
          accepted: "Accepted",
          inProgress: "In progress",
          completed: "Completed",
          rewarded: "Credits rewarded"
        },
        filters: {
          label: "Filters",
          typeAll: "All types",
          statusAll: "All statuses"
        },
        empty: "No feedback yet.",
        imageCount: "{count} images",
        pagination: {
          prev: "Prev",
          next: "Next"
        },
        priorityTag: "{label} priority",
        rewardBadge: "+{count} credits",
        screenshots: "Screenshots",
        meta: {
          userId: "User ID",
          submittedAt: "Submitted at",
          pageUrl: "Page URL"
        },
        form: {
          priority: "Priority",
          priorityUnset: "Not set",
          evaluation: "Assessment",
          evaluationUnset: "Not evaluated",
          reply: "Reply to user",
          replyVisibleTag: "Visible to user",
          replyPlaceholder: "Reply shown in the user's feedback list...",
          internalNote: "Internal note",
          internalOnlyTag: "Admin only",
          internalNotePlaceholder: "Internal notes...",
          reward: "Reward credits (auto-issued on completion)",
          rewardPlaceholder: "Default 10",
          rewardHint: "Awarded: {awarded} credits, extra reward up to 10.",
          statusChange: "Change status",
          saveOnly: "Save assessment only"
        },
        type: {
          bug: "Bug",
          feature: "Feature"
        },
        status: {
          pending: "Pending",
          accepted: "Accepted",
          rejected: "Rejected",
          discussing: "Discussing",
          scheduled: "Scheduled",
          in_progress: "In progress",
          completed: "Completed"
        },
        priority: {
          low: "Low",
          medium: "Medium",
          high: "High",
          urgent: "Urgent"
        },
        evaluation: {
          feasible: "Feasible",
          needs_discussion: "Needs discussion",
          not_feasible: "Not feasible",
          duplicate: "Duplicate"
        },
        imagePreviewAlt: "Preview image"
      },
      logs: {
        title: "Admin Logs",
        desc: "Audit trail of admin operations",
        loading: "Loading logs...",
        error: "Failed to load logs.",
        empty: "No logs found.",
        search: "Search",
        reset: "Reset",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          action: "Filter by action",
          admin: "Filter by admin ID",
          filter: "Filter by keyword",
          module: "Filter by module",
          startDate: "Start date",
          endDate: "End date"
        },
        labels: {
          level: "Log level",
          module: "Module",
          keyword: "Keyword",
          startDate: "Start date",
          endDate: "End date"
        },
        table: {
          status: "Status",
          level: "Level",
          action: "Action",
          target: "Target",
          admin: "Admin",
          details: "Details",
          ip: "IP",
          createdAt: "Created At"
        },
        levels: {
          all: "All",
          error: "Error",
          warn: "Warn",
          info: "Info"
        },
        copy: "Copy details",
        copying: "Copying...",
        copied: "Copied"
      },
      pagination: {
        summary: "Showing {start}-{end} of {total}",
        jumpTo: "Jump to",
        jump: "Jump",
        page: "Page"
      },
      statusBadge: {
        healthy: "Healthy",
        degraded: "Degraded",
        failed: "Failed",
        disabled: "Disabled",
        info: "Info",
        unknown: "Unknown",
        ariaLabel: "Status: {label}"
      },
      settings: {
        title: "System Settings",
        desc: "Adjust runtime configuration",
        error: "Failed to load settings.",
        loading: "Loading settings...",
        empty: "No settings available.",
        search: "Search",
        searchPlaceholder: "Search by key, hint, or group",
        onlyChanged: "Only changed",
        expandAll: "Expand all",
        collapseAll: "Collapse all",
        saved: "Setting saved.",
        saving: "Saving...",
        saveConfirm: "Saving this setting will overwrite the existing value. Continue?",
        save: "Save",
        boolean: "Enabled",
        items: "items",
        noMatch: "No settings matched your filter.",
        unsavedChanges: {
          leaveTitle: "Unsaved changes",
          leaveConfirm: "You have unsaved settings changes. Leave this page and discard them?",
          leaveNow: "Leave page",
          stay: "Stay on page"
        },
        updatedAt: "Updated at {time}",
        typeHints: {
          secret: "Sensitive credential. Keep this value hidden and avoid sharing it in screenshots.",  // pragma: allowlist secret
          number: "Numeric value. Leave blank only when supported by system fallback.",
          boolean: "Toggle the flag to enable/disable this feature.",
          text: "Text value used by current function module, please fill carefully."
        },
        groups: {
          billing: "Billing",
          pricing: "Pricing",
          ai_channel: "AI Channel",
          ai_runtime: "AI Runtime",
          image: "Image",
          platform: "Platform",
          business: "Business",
          feature: "Feature",
          other: "Other"
        },
        showMore: "Show {count} more",
        showLess: "Show less",
        imageBilling: {
          title: "Image generation billing modes",
          desc: "User-facing image prices are controlled here only. Models and fallback routes can change in Advanced routing without changing the amount charged to users.",
          badge: "Normal / Official fast",
          creditUnit: "credits",
          modes: {
            normal: "Normal mode",
            official: "Official fast mode"
          },
          fields: {
            defaultMode: "Default mode for new users",
            normalPrice: "Normal mode price",
            officialPrice: "Official fast mode price",
            officialAllowSubscription: "Official mode can use subscription credits",
            officialAllowLegacy: "Official mode can use legacy account credits"
          },
          hints: {
            defaultMode: "Normal mode is recommended as the default; Official fast mode remains opt-in.",
            normalPrice: "Credits charged per successfully requested image in Normal mode.",
            officialPrice: "Credits charged per successfully requested image in Official fast mode.",
            officialAllowSubscription: "When disabled, Official fast mode only consumes top-up credits first.",
            officialAllowLegacy: "Compatibility switch for old main-account credits."
          }
        },
        ozonPublic: {
          title: "Ozon Public Query Credentials",
          desc: "Used for public Ozon catalog queries (category/attributes). New users can complete generation without binding a store. Publishing still requires binding their own store.",
          clientId: "Client ID",
          apiKey: "API Key",  // pragma: allowlist secret
          configured: "Configured",
          notConfigured: "Not configured",
          clearTitle: "Clear Ozon public credentials",
          saved: "Ozon public credentials saved.",
          saveConfirm: "You are about to save changed Ozon public credentials. This will apply to all new user queries. Continue?",
          fillRequired: "Client ID and API Key are both required before testing.",
          clientIdHint: "Enter the Ozon public API client ID from Ozon dashboard.",
          apiKeyHint: "Enter the Ozon public API key from Ozon dashboard. This value is masked after save.",  // pragma: allowlist secret
          test: "Test Connection",
          testing: "Testing...",
          testSuccess: "Ozon connection is healthy.",
          clear: "Clear",
          clearConfirm: "Clear Ozon public query credentials? This may break category/attribute queries for new users.",
          cleared: "Ozon public credentials cleared.",
          secretHint: "API Key only shows last 4 chars. It won't be revealed after saving."
        }
      }
    },

    // Hero Section
    hero: {
      badge: "AI-Powered E-commerce",
      title: "Transform Your Products",
      titleHighlight: "Into Global Listings",
      subtitle:
        "Paste a link, let AI generate images, copy, SEO and platform-ready attributes, then publish in one click. Turn 30–60 mins of manual listing work into minutes.",
      placeholder: "Paste your 1688 or AliExpress link here...",
      button: "Start AI Optimization"
    },

    // Feature 1 - Product Showcase
    feature1: {
      title: "See AI Magic in Action",
      subtitle: "Watch how we transform raw supplier data into premium marketplace listings.",
      tab1: "Smart Watch",
      tab2: "Headphones",
      tab3: "Sneakers",
      imgBtn1: "AI Enhanced",
      imgBtn2: "Original",
      imgBtn3: "Compare",
      aiBadge: "AI Optimized",
      marketingTitle: "Marketing Assets",
      viewAll: "View All",
      listingTitleTag: "Listing Title",
      translated: "Translated",
      bulletTitle: "Key Features",
      seoTitle: "SEO Keywords",
      pushBtn: "Push to Marketplace",
      syncText: "Synced to Ozon",
      // Watch product
      prodTitle_watch: "Premium Smart Watch Pro Max",
      bullet1Title_watch: "Health Monitoring",
      bullet1Desc_watch: "24/7 heart rate, SpO2, sleep tracking",
      bullet2Title_watch: "Long Battery",
      bullet2Desc_watch: "Up to 14 days on single charge",
      key1_watch: "smartwatch",
      key2_watch: "fitness tracker",
      key3_watch: "health monitor",
      hotspot1: "AMOLED Display",
      hotspot2: "Titanium Case",
      // Headphones product
      prodTitle_headphones: "Wireless ANC Headphones Studio",
      bullet1Title_headphones: "Active Noise Cancellation",
      bullet1Desc_headphones: "Block out 99% of ambient noise",
      bullet2Title_headphones: "Hi-Res Audio",
      bullet2Desc_headphones: "40mm drivers with LDAC support",
      key1_headphones: "wireless headphones",
      key2_headphones: "noise cancelling",
      key3_headphones: "bluetooth",
      hotspot_headphones_1: "Memory Foam Pads",
      hotspot_headphones_2: "Touch Controls",
      // Sneakers product
      prodTitle_sneakers: "Ultra Boost Running Shoes X9",
      bullet1Title_sneakers: "Cloud Cushioning",
      bullet1Desc_sneakers: "Revolutionary foam technology",
      bullet2Title_sneakers: "Breathable Mesh",
      bullet2Desc_sneakers: "360° ventilation system",
      key1_sneakers: "running shoes",
      key2_sneakers: "athletic footwear",
      key3_sneakers: "sports sneakers",
      hotspot_sneakers_1: "Carbon Plate",
      hotspot_sneakers_2: "Grip Sole"
    },

    // Feature 2 - How It Works
    feature2: {
      title: "How It Works",
      subtitle: "Three steps to create marketplace listings in minutes.",
      card1Title: "Import & Analyze",
      card1Desc: "Paste any supplier link. Our AI extracts product data, images, and specifications automatically.",
      card2Title: "AI Optimization",
      card2Desc: "Generate SEO titles, translate descriptions, enhance images, and create A+ content in minutes."
    },

    // Feature 3 - Security
    feature3: {
      badge: "Enterprise Security",
      title: "Protect Your Brand Assets",
      subtitle: "Military-grade protection for your product images and listings.",
      verified: "Verified",
      watermark: "Neural Watermark",
      hash: "SHA-256 Hash",
      cert: "Certified",
      card1Title: "Invisible Watermarks",
      card1Desc: "AI-embedded markers that survive compression and cropping.",
      card2Title: "Legal Evidence Pack",
      card2Desc: "Auto-generated DMCA takedown documents with blockchain proof.",
      card3Title: "Instant Detection",
      card3Desc: "Get alerts when your images appear on unauthorized listings.",
      downloadBtn: "Download Whitepaper",
      downloadSub: "Security Architecture Overview",
      learnMore: "Learn More About Protection"
    },

    // Pricing Section
    pricing: {
      title: "Simple, Transparent Pricing",
      subtitle: "Pay only for image generation — {creditsPerImage} credits/image. Est. {imagesPerListing} images per listing (for reference).",
      roiTitle: "Do the math",
      roiDesc:
        "Manual listing creation often takes {minutesMin}–{minutesMax} minutes. Scaling usually requires dedicated staff (≈{laborMin}–{laborMax} CNY/month). Let AI handle images, copy, SEO and platform formatting—review and publish in one click.",
      timeSaveHint: "Estimated time saved: ~{hoursMin}-{hoursMax} hours (for reference).",
      timeSaveHintMonthly: "Estimated time saved: ~{hoursMin}-{hoursMax} hours/mo (for reference).",
      monthly: "Monthly",
      yearly: "Yearly",
      save: "2 months free",
      bestForGuests: "Best for Guests",
      onDemand: "Pay as you go",
      credits: "Credits",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      perMonth: "/mo",
      perYear: "/yr",
      packBasic: "Basic Pack",
      packGrowth: "Growth Pack",
      packScale: "Scale Pack",
      bestValue: "Best Value",
      featExpires: "Top-up credits never expire",
      featAi: "AI image generation",
      featPsd: "HD image exports",
      featPriority: "Priority support",
      purchase: "Purchase",
      planStarter: "Starter",
      planPro: "Pro",
      planBusiness: "Business",
      featStore: "Monthly credit bundle",
      featBatch: "Batch image generation",
      featSupport: "Priority support",
      featBatchStarter: "Basic batch generation",
      featBatchPro: "Advanced batch generation",
      featBatchBusiness: "High-volume batch generation",
      featSupportStarter: "Standard support (48h)",
      featSupportPro: "Priority support (12h)",
      featSupportBusiness: "Priority support + success manager",
      featExportsStarter: "HD image downloads",
      featExportsPro: "HD + 4K image downloads",
      featExportsBusiness: "Brand asset kit exports",
      loadFailed: "Unable to load pricing. Please retry or contact support.",
      retryAction: "Reload"
    },

    // FAQ Section
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our platform.",
      contact: "Contact Support",
      q1: "Will AI change my product information?",
      a1: "No. We optimize from your source data and you can review or edit every field before publishing.",
      q2: "Do I need to install a plugin or connect my store?",
      a2: "No. Paste a product link to try it. Store authorization is only required when publishing or syncing.",
      q3: "Is my data and images secure?",
      a3: "Enterprise-grade encryption and isolation keep your data safe. Generated images include invisible watermarks and forensic proof.",
      q4: "How does the free trial and pricing work?",
      a4: "New users get one full task with only one hero image. After that, choose a subscription or credits with transparent pricing."
    },

    // CTA Section
    cta: {
      title: "Ready to Scale",
      titleBreak: "Your E-commerce Business?",
      subtitle: "Join thousands of sellers already using AI to dominate global marketplaces.",
      button: "Get Started Free"
    },

    // Footer
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      support: "Support",
      submitFeedback: "Submit feedback",
      rights: "© {year} OneClickSell. All rights reserved."
    },

    // Trust page
    trust: {
      forensicData: "Forensic Metadata",
      badge: "Enterprise Security",
      title: "Protect Your",
      titleHighlight: "Digital Assets",
      subtitle: "Military-grade security for your product images and brand identity.",
      dmcaBadge: "DMCA Protected",
      forensicBadge: "Forensic Ready",
      encryptedBadge: "256-bit Encrypted",
      nav: {
        technology: "Technology",
        legal: "Legal Framework",
        whitepaper: "Resources"
      },
      resources: {
        copyrightTitle: "Copyright Whitepaper",
        copyrightDesc: "Full technical breakdown of our watermark technology.",
        legalTitle: "Legal Certificates",
        legalDesc: "Official templates for marketplace verification."
      },
      tech1Title: "Neural Watermarking",
      tech1Desc: "Invisible markers that survive any transformation.",
      tech2Title: "Blockchain Timestamp",
      tech2Desc: "Immutable proof of creation on distributed ledger.",
      tech3Title: "AI Detection",
      tech3Desc: "Real-time monitoring across all marketplaces.",
      compareTitle: "See the Difference",
      compareSub: "Move the slider to reveal hidden watermarks.",
      rawOutput: "Raw Output",
      status: "Status",
      ctaBtn: "Start Protection",
      protectedLayer: "Protected Layer",
      frameworkTitle: "Legal Framework",
      frameworkSub: "Compliance-ready documentation and verified evidence trails.",
      licenseTitle: "License Agreement",
      licenseDesc: "Instantly generate licensing documentation with immutable proof.",
      dmcaPackageTitle: "DMCA Package",
      dmcaPackageDesc: "Auto-build takedown evidence packages for marketplaces.",
      complianceTitle: "Compliance Ready",
      complianceDesc: "Structured evidence that meets platform enforcement requirements.",
      platformLabel: "Platform",
      priority: "Priority",
      refLabel: "Reference ID",
      evidenceLabel: "Evidence",
      evidence1: "Original listing screenshots",
      evidence2: "Watermark verification hash",
      generatorTitle: "Automated DMCA Generator",
      generatorSub: "Generate enforcement documents in minutes.",
      generatorNote: "Compatible with Amazon, Shopify, Etsy, and 30+ platforms.",
      letterAutomation: "Auto-generated DMCA Notice",
      letterBody1: "This notice confirms ownership and infringement evidence.",
      letterBody2: "All metadata and timestamps are verified on-chain.",
      letterBody3: "Supporting evidence is attached in the case file.",
      letterBody4: "Please remove the infringing content immediately.",
      letterNotice: "This notice is generated for immediate submission.",
      letterSign: "Authorized Representative",
      ctaTitle: "Ready to Protect Your Brand?",
      ctaTitleBreak: "Start Enforcement Today",
      ctaSub: "Launch compliance-grade protection in under 5 minutes.",
      downloadBtn: "Download Evidence Pack",
      verifiedHash: "Verified Hash",
      urlLabel: "Infringing URL",
      selectPlatform: "Select Platform",
      toLabel: "To",
      letterDateLabel: "Date:",
      letterSalutation: "To Whom It May Concern,",
      rights: "All rights reserved"
    },

    // Auth translations
    welcome: "Welcome Back",
    loginSubtitle: "Please enter your details to sign in to your professional account.",
    smsMethod: "SMS Code",
    passMethod: "Password",
    phoneLabel: "Phone Number",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "Mobile number",
    phoneRequired: "Please enter your phone number.",
    phoneInvalid: "Invalid phone number.",
    sendCode: "Send Verification Code",
    sendCodeFailed: "Failed to send the code. Please try again.",
    debugCodeHint: "Development code: {code}",
    verifyLabel: "Verification Code",
    resendIn: "Resend in {time}s",
    resendNow: "Resend Code",
    invalidCode: "Invalid verification code. Try 123456.",  // pragma: allowlist secret
    loginFailed: "Login failed. Please try again.",
    authErrorTermsRequired: "Please accept the Terms and Privacy Policy, then try again.",
    authErrorInvalidToken: "Your session token is invalid. Please sign in again.",
    authErrorTokenExpired: "Your session expired. Please sign in again.",
    authErrorTokenRevoked: "This session was revoked. Please sign in again.",
    authErrorUnauthorized: "You are not signed in. Please log in and retry.",
    authErrorForbidden: "Your account does not have permission for this action. Contact support if needed.",
    authErrorUserNotFound: "We couldn't find this account. Please register first.",
    authErrorUserDisabled: "This account is disabled. Contact support to restore access.",
    authErrorInvalidCredentials: "Phone number or password is incorrect. Please try again.",
    authErrorPhoneInvalid: "Phone format is invalid. Please enter an 11-digit mobile number.",
    authErrorSmsRateLimited: "Too many verification requests. Please wait 1 minute and try again.",
    authErrorSmsDailyLimit: "Today's SMS limit is reached. Please try again tomorrow.",
    authErrorSmsServiceUnavailable: "SMS service is temporarily unavailable. Please try again later.",
    passwordLabel: "Password",
    forgotPass: "Forgot Password?",
    passPlaceholder: "Enter your password",
    passwordRequired: "Please enter your password.",  // pragma: allowlist secret
    passwordTooShort: "Password must be at least 8 characters.",  // pragma: allowlist secret
    signIn: "Sign In",
    newToApp: "New to OneClickSell?",
    signUp: "Sign up",
    createAccount: "Create Your Account",
    signupSubtitle: "Join 5,000+ sellers optimizing their listings.",
    codeVerified: "Code Verified",
    setPassPlaceholder: "Set your password",
    agree: "I agree to the",
    userAgreement: "User Agreement",
    and: "and",
    privacyPolicy: "Privacy Policy",
    termsRequired: "Please read and agree to the Terms of Service and Privacy Policy first.",
    autoRegisterHint: "If your account is missing, please sign up first.",
    getStarted: "Get Started for Free",
    haveAccount: "Already have an account?",
    login: "Log in",
    resetTitle: "Reset Your Password",
    resetSubtitle: "Enter your mobile number to receive a verification code.",
    sendReset: "Send Reset Code",
    resetStep1: "Verify Phone",
    resetStep2: "Enter Code",
    resetStep3: "Set New Password",
    resetCodeSent: "Verification code sent. Please check your phone.",
    resetCodeHint: "Enter the 6-digit code",
    resetVerifyAction: "Verify & Continue",
    resetBackStep: "Back to Previous Step",
    resetMismatch: "Passwords do not match.",
    resetRequirement: "Use at least 8 characters with letters and numbers.",
    resetSuccess: "Password updated successfully.",
    resetSuccessHint: "Returning to sign in...",
    resetContextMissing: "Reset session expired. Please request a new code.",
    resetContextExpiredTitle: "Verification session expired",
    resetContextExpiredDesc: "For your account security, please go back and verify your phone number again before setting a new password.",
    resetRequestNewCode: "Request a new code",
    resetFailed: "Password reset failed. Please try again.",
    resetStrengthLabel: "Strength: {level}",
    resetStrengthWeak: "Weak",
    resetStrengthMedium: "Medium",
    resetStrengthStrong: "Strong",
    backLogin: "Back to Login",
    phoneVerified: "Phone Verified",
    phoneVerificationRequired: "Phone verification required",
    setNewPass: "Set New Password",
    newPassSubtitle: "Please create a strong password to secure your account.",
    newPassLabel: "New Password",
    newPassPlaceholder: "Create a new password",
    strength: "Strength: Strong",
    confirmPassLabel: "Confirm New Password",
    confirmPassPlaceholder: "Repeat new password",
    updatePass: "Update Password",
    backSignIn: "Back to Sign In",
    captchaLabel: "Human Verification",
    captchaHint: "Complete the verification to continue.",
    captchaLoading: "Loading verification...",
    captchaRequired: "Please complete the verification first.",
    captchaUnavailable: "Verification is unavailable. Please try again later.",
    captchaDisabledLabel: "Verification unavailable. Fallback mode enabled.",
    captchaBypassedHint: "You can continue and send the verification code directly.",
    captchaLoadFailed: "Verification failed to load. Refresh and try again.",
    captchaError: "Verification failed. Please retry.",

    // Profile Menu
    profile: {
      proPlan: "Pro Plan",
      freePlan: "Free Plan",
      guest: "Guest",
      coreNav: "Core Navigation",
      workflowNav: "Workflow",
      workspace: "Workspace",
      variantGroups: "Variant Groups",
      assetMgmt: "Asset Management",
      copyright: "Copyright Assets",
      pricing: "Pricing Strategy",
      profitProtection: "Ozon Profit Protection",
      storeAuth: "Store Authorization",
      marketIntel: "Market Intelligence",
      beta: "Beta",
      marketIntelComingSoon: "Market Intelligence is coming soon. Contact support to request early access.",
      account: "Account",
      settings: "Account Settings",
      billing: "Billing & Credits",
      help: "Help Center",
      signOut: "Sign Out"
    },

    // Settings
    settings: {
      general: "General Info",
      security: "Security",
      preferences: "Platform Preferences",
      billing: "Billing & Credits",
      notifications: "Notifications",
      feedback: "My Feedback",
      integrations: "Integrations",
      storeAuth: "Store Authorization"
    },

    // Store Binding
    storeBind: {
      title: "Store Connection",
      subtitle: "Connect your marketplace store to unlock syncing and publishing.",
      guideTitle: "Setup Guide",
      step1Title: "Log in to Ozon Seller",
      step1Desc: "Open the Ozon Seller portal and make sure you are inside the correct seller account.",
      step1Btn: "Open Ozon Seller",
      step2Title: "Open API Keys",
      step2Desc: "In the top navigation, go to Settings → API Keys.",
      step2Btn: "Open API Keys",
      step3Title: "Generate an Admin API Key",
      step3Desc: "Create an Admin key pair. Copy the Client ID and API Key shown on that same page.",
      setupBadge: "First-time guided setup",
      manageBadge: "Store management",
      firstSetupTitle: "Connect your Ozon store the foolproof way",
      firstSetupSubtitle: "You only need two values from the Ozon API page: API Client ID and API Key. Do not use the store ID.",
      addAnotherTitle: "Connect another Ozon store",
      addAnotherSubtitle: "Use the same guided flow again. Verify first, then bind.",
      manageTitle: "Manage connected Ozon stores",
      manageSubtitle: "{count} active store(s) are ready. Add a new store or manage the ones you already connected.",
      backToManage: "Back to store management",
      ozonOnly: "Currently supports Ozon only",
      progressPrepare: "Prepare the right info",
      progressFetch: "Open the Ozon API page",
      progressPaste: "Paste the two values",
      progressVerify: "Verify and bind",
      stepLabel: "Step {step}",
      prepareTitle: "Before you start, remember this one sentence",
      prepareDesc: "This page only needs the API Client ID and API Key from Ozon. It never asks for your store ID.",
      needLabel: "You need",
      needClientId: "API Client ID from the API Keys page",
      needApiKey: "API Key from the same API record",
      doNotUseLabel: "Do not use",
      doNotUseStoreId: "Store ID, seller ID, or any store profile number",
      doNotUseLoginPassword: "Your Ozon login password",  // pragma: allowlist secret
      fetchTitle: "Go copy the two values from Ozon",
      fetchDesc: "If you are not on the Ozon API page yet, open it first, then come back and paste the values here.",
      showGuide: "Can't find it? Show visual guide",
      hideGuide: "Hide visual guide",
      pasteTitle: "Paste the values exactly as shown in Ozon",
      pasteDesc: "Paste the pair from the same API key record. Don't mix a Client ID from one key with an API Key from another.",
      clientIdLabel: "API Client ID (not your store ID)",
      clientIdHelp: "This is the ID shown together with the API Key on the API Keys page. It is not your store number.",
      apiKeyLabel: "API Key",
      apiKeyHelp: "Paste the API Key from the same record as the Client ID above.",
      nicknameReady: "Verification passed. We auto-filled a store nickname. You can still edit it.",
      nicknamePending: "The nickname field stays locked until verification passes, so users do not waste time typing before the credentials are correct.",
      nicknameLabel: "Store nickname (editable after verification)",
      nicknamePlaceholder: "We will auto-fill the detected seller name",
      nicknameHelp: "This is only an internal label inside our system. It does not have to match a store ID.",
      verifyTitle: "Verify first, then bind",
      verifyDesc: "We first check whether these two values really belong together. Only verified credentials can be bound.",
      successTitle: "Store connected and ready",
      successDesc: "We detected seller {sellerName}. The store is already connected. Syncing products or inventory can be done later whenever needed.",
      bindCompleteBadge: "Binding complete",
      unknownSeller: "recognized seller",
      manageAllStores: "Manage connected stores",
      confirmBind: "Confirm and bind this store",
      singlePageGuideTitle: "Fill this page once, and we guide every field",
      singlePageGuideDesc: "Just follow the highlighted field. You can skip the guide anytime and reopen it later.",
      guideRestart: "Restart guide",
      guideSkip: "Skip guide",
      guidePrev: "Back",
      guideNext: "Next",
      guideDone: "Got it",
      guideProgress: "Guide {current}/{total}",
      guideClientIdTitle: "First: paste API Client ID",
      guideClientIdDesc: "Copy it from the Ozon API Keys page. Do not paste store ID or seller profile ID.",
      guideApiKeyTitle: "Second: paste matching API Key",
      guideApiKeyDesc: "Use the API Key from the same record as the Client ID.",
      guideTestTitle: "Then click verify",
      guideTestDesc: "Verification ensures the two values belong together before binding.",
      guideStoreNameTitle: "Check store nickname",
      guideStoreNameDesc: "After verification passes, we auto-fill nickname. You can edit it if needed.",
      guideBindTitle: "Finally bind this store",
      guideKeywordClientId: "API Client ID",
      guideKeywordApiKey: "API Key",
      guideKeywordStoreId: "Store ID",
      guideKeywordVerifyAction: "Verify",
      guideKeywordApiPair: "Matching pair",
      guideKeywordStoreName: "Store nickname",
      guideKeywordSellerName: "Seller name",
      guideKeywordBindAction: "Bind store",
      wizardPrev: "Previous step",
      wizardNext: "Next step",
      wizardNextVerify: "Go to verify and bind",
      wizardFinalStepHint: "This is the final step",
      wizardStepFocus: "Current step focus",
      commonMistakeLabel: "Most common mistake",
      commonMistakeTitle: "People keep pasting the store ID here",
      commonMistakeDesc: "If a user is confused, make them remember just this: the Client ID comes from the API Keys page, not from the store profile page.",
      compareTitle: "Quick comparison",
      compareClientId: "Correct: the API Client ID shown together with the API Key on the API Keys page.",
      compareStoreId: "Wrong: a store ID, seller account number, or any number copied from the store profile page.",
      currentDraftLabel: "Current status",
      validationStatus: "Verification",
      validationPassed: "Passed",
      validationPending: "Not verified yet",
      detectedSeller: "Detected seller",
      detectedSellerPending: "Will appear after verification",
      securityInline: "We only use encrypted API credentials. We never ask for your Ozon login password.",  // pragma: allowlist secret
      manageSummaryBadge: "Ready to manage",
      manageSummaryTitle: "{count} active store(s) connected",
      manageSummaryDesc: "Default store: {storeName}. You can switch defaults, update credentials, or add another store.",
      manageSummaryDescFallback: "Your stores are already connected. You can update settings or add another store at any time.",

      // Store Switcher
      activeSwitcher: "Active Store Switcher",
      manageAll: "Manage all stores",
      currentlyActive: "Currently Active",
      synced: "Synced",
      connected: "Connected",
      disabled: "Disabled",
      syncingShort: "Syncing",
      syncFailedShort: "Sync Failed",
      uploads7d: "7d uploads",
      companyNameLabel: "Company",
      legalNameLabel: "Legal entity",
      taxIdLabel: "Tax ID",
      regionLabel: "Region / currency",
      subscriptionTypeLabel: "Subscription",
      warehouseCountLabel: "Warehouses",
      warehouseCountSummary: "{count} warehouse(s)",
      subscriptionSummary: "{plan}",
      subscriptionPremiumSummary: "{plan} Premium",
      lastVerifiedLabel: "Last verified",
      brandLabel: "Publish brand",
      brandNone: "No brand",
      brandIdFallback: "Brand #{id}",
      manageCatalogAction: "Manage catalog",
      connectNew: "Connect new store",

      // Platform Selection
      fullApiIntegration: "Full API integration",
      readyConnect: "Ready to connect",
      comingSoon: "Coming soon",
      active: "Active",

      // Connection Form / Management
      connectOzon: "Connect Ozon Store",
      statusLabel: "Connection status",
      authorized: "Connected and ready",
      noStoresLinked: "No store connected yet",
      howToGetKey: "How to get API Key?",
      storeName: "Store nickname",
      storeNamePlaceholder: "e.g. My Ozon Store",
      clientId: "API Client ID",
      clientIdPlaceholder: "Paste the API Client ID",
      apiKey: "API Key",  // pragma: allowlist secret
      addStore: "Add Ozon Store",
      testConnection: "Verify these two values",
      testing: "Verifying...",

      // Syncing
      syncingData: "Syncing store data...",
      syncingDesc: "Importing categories, shipping methods, and warehouse info from Ozon. This only takes a moment.",
      syncComplete: "Sync complete",
      syncCompleteDesc: "Store sync is complete. Your account is ready for AI publishing.",
      success: "Success",

      // Success State
      startListing: "Start AI publishing",
      connectionSuccess: "Connection successful",
      testSuccessWithSeller: "Verified. Detected seller: {sellerName}. You can now bind this store.",
      testSuccess: "Credentials verified. You can now continue.",

      // Security
      securityTitle: "API connection security",
      securityEncryption: "Your credentials are stored using AES-256 encryption.",
      securityNoPassword: "We never ask for or store your marketplace login password.",  // pragma: allowlist secret

      // Table
      authorizedStores: "Authorized stores",
      platform: "Platform",
      actions: "Actions",
      syncSettings: "Sync settings",
      items: "items",

      // Settings Modal
      settingsTitle: "Store settings",
      settingsSubtitle: "Manage store automation and security options.",
      loading: "Loading...",
      settingActive: "Store active",
      settingActiveDesc: "Disable to pause publishing and syncing for this store.",
      settingAutoSync: "Auto sync",
      settingAutoSyncDesc: "Sync products, stocks, and orders automatically.",
      settingPsd: "PSD protection",
      settingPsdDesc: "Enable copyright embedding and evidence generation.",
      settingSyncPsd: "Sync PSD processing",
      settingSyncPsdDesc: "Embed PSD copyright before image upload.",
      settingRotateKey: "Update API Key",
      settingRotateKeyHint: "Leave empty to keep your current API Key.",
      settingLogisticsTitle: "Logistics channel pricing",
      settingLogisticsDesc: "Manage Ozon channel sync and the default provider used by pricing calculations.",
      settingLogisticsMode: "Provider mode",
      settingLogisticsModeAuto: "Auto",
      settingLogisticsModeManual: "Manual",
      settingLogisticsProvider: "Default provider",
      settingLogisticsSyncAction: "Sync channels",
      settingLogisticsSyncing: "Syncing channels...",
      settingLogisticsSaveAction: "Save logistics selection",
      settingLogisticsLastSynced: "Last synced: {date}",
      settingLogisticsNeverSynced: "No synced channel data yet. Click “Sync channels” once before pricing.",
      settingLogisticsProvidersEmpty: "No recognized provider yet. You can still save Auto mode.",
      settingLogisticsUnknownHint: "{count} channel(s) were not recognized by current provider mapping.",
      settingBrandTitle: "Publish brand",
      settingBrandDesc: "Select a fixed brand from Ozon. Publishing will always override attribute 85 with this brand.",
      settingBrandCurrent: "Current",
      settingBrandWillApply: "After saving",
      settingBrandSearchPlaceholder: "Search Ozon brands",
      settingBrandSearchAction: "Search",
      settingBrandSearching: "Searching...",
      settingBrandSearchEmpty: "No matching brands. Try another keyword.",
      settingBrandUseNone: "Use no brand",
      deleteStore: "Delete store",
      unbindStore: "Delete store",
      setDefault: "Set as default",
      openSettingsForStore: "Open settings for {storeName}",
      setDefaultConfirmTitle: "Switch default store?",
      setDefaultConfirmMessage: "Future publishing, syncing, and store operations will use “{storeName}” by default.",
      setDefaultConfirmAction: "Set as default store",
      saving: "Saving...",
      saveChanges: "Save changes",

      // Sync Modal
      syncModalTitle: "Sync store data",
      syncModalSubtitle: "Sync data for {name}.",
      syncModalDesc: "Select which data types to sync for this store.",
      syncProducts: "Products",
      syncProductsDesc: "Refresh product list from Ozon.",
      syncStocks: "Stocks",
      syncStocksDesc: "Refresh inventory levels by warehouse.",
      syncOrders: "Orders",
      syncOrdersDesc: "Refresh recent orders and statuses.",
      syncNow: "Start sync",

      // Confirm
      deleteTitle: "Delete Store",
      deleteSecondTitle: "Confirm deletion",
      confirmDelete: "Are you sure you want to delete this store?",
      confirmDeleteSecond: "Confirm delete?",
      deleteConfirm: "Delete",
      unbindConfirm: "Delete",
      storeTarget: "Connected Ozon store",
      storeUnbindImpact: "Are you sure you want to delete this store?",
      storeUnbindSecondImpact: "Confirm delete?",

      // Footer / legacy
      storageSystem: "Storage System: Localized AES Encryption",
      securityGuaranteed: "Security guaranteed",
      encryptedBridge: "Encrypted API bridge",
      noPasswordStorage: "No password storage",  // pragma: allowlist secret
      cancel: "Cancel",
      saveAuth: "Save authorization",

      // Toast
      toastTitle: "Ozon store connected",
      toastDesc: "Credentials were verified and the store is connected. Syncing can be triggered later if needed.",
      toasts: {
        defaultUpdated: "Default store updated.",
        noChanges: "No changes to save.",
        storeUpdated: "Store settings updated.",
        storeDeleted: "Store deleted.",
        storeUnbound: "Store deleted.",
        syncStarted: "Sync started. Data will refresh shortly.",
        logisticsSynced: "Logistics channels synced.",
        logisticsSaved: "Logistics provider selection saved."
      },

      continuePublish: "Continue publishing",
      noStores: "No authorized stores yet.",
      hints: {
        fillCredentialsFirst: "First paste the API Client ID and API Key.",
        validateFirst: "First click verify. Binding stays locked until verification passes.",
        waitForStoreName: "Verification passed. Review or edit the auto-filled store nickname, then bind.",
        readyToBind: "Verification passed. You can now bind this store."
      },
      errors: {
        missingCredentials: "Please paste both the API Client ID and API Key.",
        validateFirst: "Verify the API Client ID and API Key first before binding.",
        missingFields: "Please fill in store name, Client ID, and API Key.",
        testFailed: "Connection test failed. Please verify your credentials.",
        clientIdMustBeNumeric: "API Client ID must be numeric. Copy the Client ID from the Ozon Seller API Keys page; do not paste store ID, store name, or other text.",
        apiKeyMayBeWrong: "Ozon rejected the API Key. Re-copy the API Key from the same Ozon API key record as the Client ID, then verify again.",
        credentialsRejected: "Ozon rejected these credentials. Re-copy the API Client ID and API Key from the same API key record, then verify again.",
        bindFailed: "Store binding failed (status: {status}).",
        statusFailed: "Failed to load store status (status: {status}).",
        syncFailed: "Synchronization failed. You can retry from Sync Settings.",
        defaultFailed: "Failed to update default store.",
        storeDetailFailed: "Failed to load store details (status: {status}).",
        storeUpdateFailed: "Failed to update store (status: {status}).",
        logisticsLoadFailed: "Failed to load logistics channels (status: {status}).",
        logisticsSyncFailed: "Failed to sync logistics channels (status: {status}).",
        logisticsSaveFailed: "Failed to save logistics provider selection (status: {status}).",
        logisticsProviderRequired: "Manual mode requires selecting a logistics provider.",
        brandSearchFailed: "Brand search failed (status: {status}).",
        brandCreateFailed: "Failed to save selected brand (status: {status}).",
        storeNameRequired: "Please confirm or edit the store nickname before binding.",
        deleteFailed: "Failed to delete store (status: {status}).",
        unbindFailed: "Failed to delete store (status: {status}).",
        syncTypesRequired: "Please select at least one sync type.",
        syncTriggerFailed: "Failed to trigger sync (status: {status})."
      }
    },

    storeCatalog: {
      nav: "Catalog",
      title: "Store Catalog Manager",
      subtitle: "Sync store products (including external history) and run bulk updates.",
      noStores: "No stores bound yet. Please bind a store first.",
      syncButton: "Sync products",
      syncing: "Syncing...",
      syncStarted: "Sync task started.",
      syncStatusTitle: "Sync status",
      syncStatus: "Status: {status}",
      syncProgress: "Progress: {progress}% ({done}/{total})",
      syncNoJob: "No sync task yet. Click sync to fetch latest products from Ozon.",
      syncRetry: "Retry sync",
      syncFailedTitle: "Sync has failures",
      syncStoreErrorItem: "{storeName}: {message}",
      syncUnknownError: "Unknown sync error",
      syncColumnsSynced: "Synced",
      syncColumnsFailed: "Failed",
      syncNoStoreJobs: "No store rows yet.",
      statusRunning: "Running",
      statusFailed: "Failed",
      statusCompleted: "Completed",
      refresh: "Refresh",
      storeScope: "Store scope",
      keywordPlaceholder: "Search by offer ID or product name",
      sourceAll: "All sources",
      sourcePipeline: "Uploaded by system",
      sourceExternal: "External history",
      inSaleOnly: "In-sale only",
      query: "Query",
      itemsTotal: "Total: {total}",
      selectionManual: "Selected: {count}",
      selectionFiltered: "All matching current filters",
      selectionStoreAll: "All products in selected stores",
      selectPage: "Select page",
      selectFiltered: "Select filtered all",
      selectStoreAll: "Select store all",
      clearSelection: "Clear",
      allPageSelected: "All {count} items on this page selected.",
      selectAllMatching: "Select all {total} matching items",
      selectAllInStores: "Select all in stores",
      storeSelectAll: "All",
      storeDeselectAll: "Clear",
      columnsStore: "Store",
      columnsOffer: "Offer ID",
      columnsName: "Product",
      columnsBrand: "Brand",
      columnsPrice: "Price",
      columnsStock: "Stock",
      columnsSource: "Source",
      associationMatched: "Linked",
      associationUnmatched: "Unlinked",
      openStudio: "Open collection",
      openSource: "Open 1688 source",
      openPublish: "Open Ozon listing",
      noAssociation: "No link",
      columnsStatus: "Status",
      columnsError: "Error",
      loading: "Loading...",
      noItems: "No catalog items.",
      noBrand: "No brand",
      pageIndicator: "Page {page}/{totalPages}",
      prevPage: "Prev",
      nextPage: "Next",
      batchTitle: "Bulk update",
      brandOperation: "Brand update",
      brandStoreBound: "Use store bound brand",
      brandCustom: "Use searched brand",
      brandNone: "Use no brand",
      brandSearchPlaceholder: "Search Ozon brand",
      searchBrand: "Search brand",
      searching: "Searching...",
      priceOperation: "Price update",
      priceValue: "New price",
      oldPriceValue: "Old price (optional)",
      minPriceValue: "Min price (optional)",
      stockOperation: "Stock update",
      stockValue: "Stock value",
      warehouseLoading: "Loading warehouses...",
      warehouseSelectPlaceholder: "Select warehouse",
      warehouseDefaultName: "Warehouse {id}",
      warehouseEmptyForStore: "No available warehouse for this store.",
      warehouseLoadErrorForStore: "Failed to load warehouses: {message}",
      warehouseMissingHint: "{count} store(s) still need warehouse selection.",
      startBatch: "Start batch task",
      batchSubmitting: "Submitting...",
      batchStarted: "Batch task started.",
      jobTitle: "Batch task progress",
      jobStatus: "Status: {status}",
      jobProgress: "Progress: {progress}% ({done}/{total})",
      jobFailedCount: "{count} items failed",
      retryFailed: "Retry failed items",
      retryStarted: "Retry task started.",
      noJobItems: "No result rows yet.",
      errors: {
        loadStoresFailed: "Failed to load stores.",
        loadItemsFailed: "Failed to load catalog items.",
        syncFailed: "Failed to start sync task.",
        syncTaskFailed: "Sync task failed. Please retry.",
        brandSearchFailed: "Failed to search brands.",
        brandRequired: "Please select a brand first.",
        priceInvalid: "Invalid price value.",
        oldPriceInvalid: "Invalid old price value.",
        minPriceInvalid: "Invalid min price value.",
        warehouseLoadFailed: "Failed to load warehouses.",
        warehouseLoading: "Warehouses are still loading.",
        warehouseRequiredForStore: "Please select a warehouse for {storeName}.",
        stockInvalid: "Invalid stock value.",
        noOperation: "Select at least one operation.",
        selectItemsFirst: "Please select products first.",
        batchCreateFailed: "Failed to create batch task.",
        retryFailed: "Failed to retry failed items.",
      },
    },

    publish: {
      title: "Final Launch Confirmation",
      subtitle: "Currently optimized for Ozon platform publishing.",
      healthScore: "Listing Health Score",
      health: {
        excellent: "Excellent Ready",
        good: "Great Potential",
        warning: "Needs Review",
        poor: "Needs Improvement"
      },
      targetPlatforms: "1. Target Platforms",
      storeLabel: "Store",
      manageStores: "Manage stores",
      storeModeSingle: "Single",
      storeModeBatch: "Batch",
      batchPrimary: "Primary",
      batchHint: "Batch mode creates one publish task per selected store and automatically varies title, SKU, and primary image order.",
      batchSelectedCount: "{count} stores selected",
      selectAllStores: "Select all",
      clearStores: "Clear",
      creatingTasks: "Creating publish tasks ({current}/{total})...",
      modeNoticeUpdate: "Detected existing listing (offer_id: {offerId}). This action will update the existing product instead of creating a duplicate.",
      modeNoticeCreate: "No existing listing found. This action will create a new product on Ozon.",
      platformReady: "Global Seller",
      platformUnavailable: "Not Available",
      comingSoon: "Coming Soon",
      pricingConfirmation: "2. Pricing Confirmation",
      aiOptimized: "AI Optimized Prices",
      tablePlatform: "Platform",
      tableSuggested: "Suggested Price",
      tableAdjusted: "Adjusted Price",
      tableProfit: "Est. Profit",
      scheduleTitle: "3. Publishing Schedule",
      publishNow: "Publish Now",
      publishNowDesc: "Push listing to Ozon immediately.",
      scheduleLater: "Schedule for Later",
      scheduleLaterDesc: "Choose a peak traffic time to launch.",
      releaseDate: "Release Date",
      releaseTime: "Release Time",
      backToEdit: "Back to Edit",
      confirmLaunch: "Confirm & Launch",
      confirmLaunchBatch: "Confirm & Launch ({count})",
      launching: "Launching...",
      loading: "Loading...",
      progressTitle: "Ozon Publishing Progress Monitor",
      progressSubtitle: "OneClickSell • Ozon API Integration",
      publishing: "Publishing",
      batchPublishing: "Batch publishing",
      batchCompleted: "Batch completed",
      batchCompletedWithIssues: "Batch completed with issues",
      viewResult: "View result",
      estimatedRemaining: "Estimated remaining",
      progressIndeterminate: "Waiting for marketplace feedback. Progress may pause temporarily.",
      progressRefreshHint: "Status refreshes about every 20–30 seconds; you can refresh manually.",
      stepCompleted: "Completed",
      stepInProgress: "In Progress...",
      stepFailed: "Failed",
      stepPending: "Pending",
      stepLabels: {
        validate: "Validating Data",
        uploadImages: "Uploading Product Images",
        createListing: "Creating Ozon Listing",
        syncStock: "Syncing Price & Stock",
        updateListing: "Updating Listing Data",
        updateAttributes: "Syncing Product Attributes",
        updatePrices: "Syncing Product Prices",
        updateStock: "Syncing Product Stock",
        updateImages: "Updating Product Images",
        verifyUpdate: "Verifying Update Result"
      },
      doNotClose: "Please do not close this page.",
      doNotCloseDesc: "Your listing is being securely transmitted to Ozon API.",
      safeToClose: "You can close this page.",
      safeToCloseDesc: "Publishing has reached a final state. You can view the result or continue manual review.",
      publishFailedFinal: "Publishing failed.",
      publishFailedFinalDesc: "No upload is running now. Review the error details and fix the listing manually if needed.",
      successTitle: "Product Published Successfully",
      successSubtitle: "Your listing has been verified and is now live on the marketplace.",
      successWarningTitle: "Product Created (Action Needed)",
      successWarningSubtitle: "The product card was created, but it is not currently visible in Ozon backend.",
      successFixRequiredTitle: "Product Created (Fixes Required)",
      successFixRequiredSubtitle: "The product was created, but Ozon reported validation issues that must be fixed.",
      successFixRequiredHint: "Please open publish details and resolve all validation issues before creating variants.",
      liveNow: "Live Now",
      needsAction: "Action Needed",
      openInOzon: "Open in Ozon Store",
      openInOzonDisabledHint: "The publish link will appear once the listing is live.",
      detailRouteHint: "This publish result reused the current detail-image route.",
      returnDashboard: "Return to Dashboard",
      goToAssets: "Go to Asset Library",
      publishAnother: "Publish Another Product",
      resultFooter: "© {year} AI Listing Optimizer. Listing Batch ID: {ref}",
      failureTitle: "Unable to Publish to Ozon",
      failureSubtitle: "Your listing could not be processed by the marketplace API.",
      errorDetails: "Error Details",
      referenceId: "Reference ID: {ref}",
      retryPublishing: "Retry Publishing",
      retrying: "Retrying...",
      contactSupport: "Contact Support",
      batchFailuresTitle: "{count} store tasks failed to create",
      manualReview: {
        title: "Manual Review Recommended",
        desc: "Ozon may return errors while the item is still partially created. Please verify in Ozon Seller backend.",
        partialSuccessHint: "If the product already appears in Ozon Seller backend, this usually means the upload was partially successful and only specific fields need correction.",
        checkOzon: "Log in to Ozon Seller and check the exact validation errors for this product.",
        fixInOzon: "If the product card exists, you can manually correct fields and resubmit in Ozon.",
        aiNotPerfect: "AI-generated listing data improves efficiency but is not 100% accurate. Manual review is still required for some items."
      },
      recovery: {
        title: "Recovery Plan",
        loading: "Analyzing failure...",
        reasonFallback: "No detailed reason returned from Ozon. You can retry or contact support.",
        reasonCode: "Rule",
        newOfferId: "New SKU",
        newOfferIdPlaceholder: "Enter a new SKU (offer_id)",
        newOfferIdHint: "Use letters/numbers/_/- only, max length 50.",
        retryWithNewSku: "Relaunch with New SKU",
        retrySamePayload: "Retry with Same Payload",
        executing: "Submitting...",
        goBindStore: "Check Store Authorization",
        offerIdRequired: "Please enter a new SKU first.",
        loadFailed: "Failed to load recovery plan.",
        executeFailed: "Failed to relaunch. Please try again.",
        actions: {
          retry_with_new_offer_id: "Suggested: relaunch with a new SKU",
          retry_same_payload: "Suggested: retry with same payload",
          fix_content_then_retry: "Suggested: fix content and retry",
          store_auth_required: "Suggested: check store authorization",
          wait_and_poll: "Suggested: wait and refresh status",
          contact_support: "Suggested: contact support"
        }
      },
      result: {
        untitled: "Untitled Listing"
      },
      warnings: {
        nonBlockingTitle: "Optional publish warning",
        nonBlockingFields: "Some optional adapter fields are incomplete ({fields}). You can still publish; the platform will validate them during submission."
      },
      errors: {
        missingCollection: "Missing collection data. Please return and try again.",
        summaryFailed: "Failed to load publish summary (status: {status}).",
        storeStatusFailed: "Failed to load store status (status: {status}).",
        missingFields: "Required fields are missing. Please complete them before publishing.",
        noUpdateFields: "No actual changes detected. Price/image/content are unchanged, so update was not submitted.",
        richContentInvalid: "Rich content format is invalid. Please regenerate before publishing.",
        missingFieldsHint: "Missing fields: {fields}",
        storeRequired: "Please select at least one store.",
        missingSchedule: "Please select a publish date and time.",
        invalidAdjustedPrice: "Adjusted price must be a positive number greater than 0.",
        publishFailed: "Publishing failed (status: {status}).",
        statusFailed: "Failed to fetch publish status (status: {status}).",
        retryFailed: "Retry failed. Please try again later.",
        batchPartialFailed: "{count} stores failed to create tasks. Others are publishing.",
        socksProxyDependencyMissing:
          "Publishing request failed because SOCKS proxy dependency is missing. We switched to direct connection. Please retry.",
        viewTechnicalDetails: "View technical details",
        hideTechnicalDetails: "Hide technical details"
      }
    },

    // Capability Settings
    capability: {
      title: "Capability Settings",
      subtitle: "Set default 6-agent workflow once. Ozon supports full automation; other platforms export listing packages.",
      targetPlatform: "Target Platform Selection",
      supported: "Fully Supported",
      exportOnly: "Export Only",
      currentPlatform: "Current:",
      systemNotice: "System Notice",
      systemNoticeBody: "The 6-agent pipeline is platform-agnostic. Ozon supports direct publish; other platforms export listing packages for manual upload.",
      platformActive: "Full Automation",
      platformOzonDesc: "Full workflow with direct publish and compliance",
      platformAmazonDesc: "Global marketplace listing export package",
      platformShopifyDesc: "Storefront listing export package",
      platformAliDesc: "Cross-border listing export package",
      platformEbayDesc: "Marketplace listing export package",
      languageLockedHint: "Language is locked to Russian for Ozon.",
      targetMarket: "Target Market",
      russiaDefault: "Russia (Default for Ozon)",
      outputLanguage: "Output Language",
      russianDefault: "Russian (Standard Cyrillic)",
      contentStyleTitle: "Content Style",
      seoLevelTitle: "SEO Depth",
      customPromptTitle: "Custom Prompt",
      customPromptPlaceholder: "Write your custom prompt here...",
      customPromptHint: "Only used when Custom style is selected.",
      brandGuardTitle: "Brand & Compliance Guard",
      brandGuardDesc: "Apply brand protection and compliance safeguards across content and images.",
      brandGuardHint: "Advanced protection remains managed by the platform.",
      summaryTitle: "Execution Summary",
      summaryHint: "What will be applied",
      summaryImages: "Generate {count} carousel images (main + scenario + feature).",
      summaryDetailImages: "Generate {count} detail images.",
      summaryDetailDisabled: "Detail images: disabled.",
      summaryDetailCompose: "Detail images: enabled, compose from carousel ({count} images).",
      summaryDetailStandalone: "Detail images: enabled, standalone generate ({count} images).",
      summarySeo: "SEO depth: {level}.",
      summaryRich: "Rich content theme: {theme}.",
      summaryBrandOn: "Brand guard enabled.",
      summaryBrandOff: "Brand guard disabled.",
      summaryExportOnly: "{platform} currently supports listing export only.",
      imageSetStandardShort: "bundle",
      imageSetDetailOnlyShort: "detail",
      carouselImagesTitle: "Carousel Images",
      detailImagesTitle: "Detail Images",
      detailImagesHint: "Optional detail images. Fixed {count} outputs and does not affect carousel count.",
      detailImagesDisabledShort: "off",
      detailGenerationModeCompose: "Compose from carousel",
      detailGenerationModeComposeDesc: "Use generated carousel images and copy to assemble detail images.",
      detailGenerationModeComposeShort: "compose",
      detailGenerationModeStandalone: "Standalone generate",
      detailGenerationModeStandaloneDesc: "Generate 3 detail images independently.",
      detailGenerationModeStandaloneShort: "standalone",
      quickImagesSummary: "Detail path: {detail}",
      saveDefault: "Save as Default",
      useDefault: "Use Default",
      saving: "Saving...",
      saved: "Saved",
      skipAutoOpen: "Save as default and do not auto-open again",
      skipAutoOpenHint: "You can reopen from the navigation menu at any time.",
      aiContentGen: "AI Content Generation",
      aiContentDesc: "Images, titles, and localized specifications",
      active: "ACTIVE",
      directPublish: "One-Click Direct Publish",
      directPublishDesc: "Bypass manual CSV export/import",
      ozonExclusive: "OZON EXCLUSIVE",
      launchBtn: "Launch AI Optimization",
      status: "System status: Ready for Ozon high-speed synchronization",
      secure: "Secure Protocol",
      neural: "Neural Engine v4.2",
      apiSync: "API Sync Active",

      // Smart setup modes
      quickModeSubtitle: "Quick edit your existing configuration",
      expertModeSubtitle: "Full control over all settings",
      guidedModeSubtitle: "Complete the setup to get started",
      currentConfig: "Current Configuration",
      quickModeDesc: "Edit key settings or launch AI with current config",
      expertMode: "Expert Mode",
      quickMode: "Quick Mode",
      showAdvanced: "Show advanced settings",
      saveChanges: "Save Changes",
      launchAI: "Launch AI",
      perProduct: "per product",
      platformLabel: "Platform",
      marketLabel: "Market",
      styleLabel: "Style",
      imagesLabel: "Images",
      languageLabel: "Language",
      marketRuShort: "Russia",
      marketUsShort: "United States",
      marketCnShort: "China",
      marketGlobalShort: "Global",
      styleProfessionalDesc: "Business formal tone",
      styleCreativeDesc: "Emotional storytelling",
      styleConciseDesc: "Direct & punchy",
      styleCustomDesc: "Custom prompt configuration",
      stepPlatformHint: "Select one target platform for this workflow.",
      stepMarketHint: "Confirm the core market where this listing will be published.",
      stepLanguageHint: "Choose the language used for generated listing content.",
      stepStyleHint: "Pick the content tone that best matches your brand.",
      stepCustomPromptHint: "Write your custom writing instructions for the AI.",
      stepImageCountHint: "Set how many AI images to generate per product.",
      stepSkipAutoOpenHint: "Choose whether this setup should open automatically next time.",
      stepSaved: "Current step has been saved.",
      loginRequired: "Please log in to save settings",
      saveSuccess: "Settings saved successfully",
      saveFailed: "Failed to save settings",
      remoteLoadFallbackHint: "Cloud sync failed. Showing local settings, you can retry.",
      strategyTierTitle: "Strategy Tier",
      strategyTierHint: "Choose the automation depth and quality level.",
      tierEconomy: "Economy",
      tierEconomyDesc: "Lower image volume and faster output for rapid launches.",
      tierBalanced: "Balanced",
      tierBalancedDesc: "Recommended default for quality and cost balance.",
      tierPremium: "Premium",
      tierPremiumDesc: "Higher image volume and stronger model quality.",
      guidedStepLabel: "Step {current} of {total}",
      guidedBack: "Back",
      guidedNext: "Next",
      guidedSaveNext: "Save & Next",
      guidedLaunch: "Launch Optimization",
      guidedReviewLabel: "Review",
      guidedReviewHint: "Review and launch",
      guidedPlatformTitle: "Choose Your Platform",
      guidedPlatformDesc: "Where will you be selling your products?",
      guidedMarketTitle: "Target Market",
      guidedMarketDesc: "Where are your customers located?",
      guidedCountryRegion: "Country / Region",
      guidedLanguageLocked: "Locked to platform language",
      guidedStyleTitle: "Content Style",
      guidedStyleDesc: "How should your product listings sound?",
      guidedImagesTitle: "AI Image Generation",
      guidedImagesDesc: "Choose carousel image count and detail image path",
      guidedCarouselCountHint: "Choose fixed 5 / 7 / 10 carousel images",
      guidedReviewTitle: "Review & Launch",
      guidedReviewDesc: "Your AI pipeline is ready.",
      guidedDetailStandaloneCount: "{count} standalone",
      guidedDetailComposedCount: "{count} composed"
    },

    aiConfig: {
      badge: "AI Configuration",
      pageTitle: "AI Optimization Settings",
      pageSubtitle: "Review and fine-tune the AI parameters before launching your optimization task.",
      statusHint: "AI suggestions ready for review",
      targetPlatform: "Target Platform",
      platformActive: "Ozon Active",
      platformOzonDesc: "Optimized for CIS market & regional logistics",
      platformAmazonDesc: "Global marketplace optimization pipeline",
      platformShopifyDesc: "Listing prep for Shopify storefronts",
      platformAliDesc: "Cross-border listing preparation",
      comingSoon: "Coming Soon",
      targetLanguage: "Target Language",
      languageRu: "Russian",
      languageEn: "English",
      languageDe: "German",
      languageFr: "French",
      languageIt: "Italian",
      languageEs: "Spanish",
      languageJa: "Japanese",
      languagePtBr: "Portuguese (Brazil)",
      languagePtPt: "Portuguese (Portugal)",
      languageNl: "Dutch",
      languageSv: "Swedish",
      languagePl: "Polish",
      languageTr: "Turkish",
      languageAr: "Arabic",
      languageZhHans: "Chinese (Simplified)",
      languageZhHant: "Chinese (Traditional)",
      languageCs: "Czech",
      languageDa: "Danish",
      languageFi: "Finnish",
      languageKo: "Korean",
      languageNb: "Norwegian (Bokmål)",
      languageTh: "Thai",
      contentStyle: "Content Style",
      styleProfessional: "Professional",
      styleCreative: "Creative",
      styleConcise: "Concise",
      styleCustom: "Custom",
      stylePrompt: "Style Prompt Logic",
      currentStyle: "Current Style:",
      customActive: "Custom Logic Active",
      promptProfessional: "Prompt: Focus on technical specifications, durability, and business-formal tone.",
      promptCreative: "Prompt: Use evocative language, emotional storytelling, and lifestyle benefits.",
      promptConcise: "Prompt: Direct, punchy sentences focusing on key value propositions.",
      promptCustom: "Custom prompt loaded. Review advanced settings for final adjustments.",
      customPrompt: "Custom Prompt Editor",
      customPromptPlaceholder: "Write your custom prompt here...",
      customPromptHint: "Advanced variables: {product_name}, {category}, {specs}",
      customHint: "Open advanced editor to refine custom prompt",
      imageGeneration: "Enable AI Image Generation",
      imageGenerationDesc: "Removes watermarks and creates custom lifestyle backgrounds.",
      imageGenerationOnce: "Applies to this run only.",
      imageCredits: "Image generation will consume image credits per image.",
      imageCount: "Images to generate",
      imageCountHint: "Choose a fixed carousel image count: 5, 7, or 10.",
      trialImageBadge: "Trial: 1 main image only",
      trialImageHint: "Trial mode generates 1 main image. Upgrade to unlock full image sets.",
      trialUpgradeCta: "View plans",
      seoLevel: "SEO Optimization Level",
      seoStandard: "Standard",
      seoStandardDesc: "Basic keyword injection and meta-tag optimization",
      seoDeep: "Deep Analysis",
      seoDeepDesc: "Semantic clustering, competitor analysis, and LSI keyword integration",
      seoLockedHint: "Deep analysis is the default and cannot be changed.",
      review: "Review & Launch",
      recommended: "Recommended",
      launch: "Launch AI Optimization",

      modalTitle: "AI Optimization Configuration",
      modalSubtitle: "Set your final preferences before launching AI agents.",
      reuseTitle: "Reuse Previous Results?",
      reuseSubtitle: "Historical optimization outputs are available for this product.",
      reuseDesc: "You can reuse the latest downstream results or regenerate with new preferences.",
      reuseNotice: "Reuse will display results directly without regenerating.",
      reuseTimeLabel: "Last run: {time}",
      reuseStageLabel: "Reusable stages: {stages}",
      reuseSourceLabel: "Source collection: {id}",
      reuseViewHistory: "View history",
      reuseRegenerate: "Regenerate",
      reuseUse: "Reuse Results",
      reuseApplying: "Applying...",
      useDefault: "Use Default Configuration",
      advanced: "Advanced Settings",
      cancel: "Cancel",
      saveDraft: "Save Draft",
      saving: "Saving...",
      saved: "Saved",
      footerHint: "AI agents will follow your selected strategy and credits plan."
    },

    // AI Workspace / Review
    ai: {
      workspaceTitle: "Active Listing Optimization",
      syncProgress: "Sync Progress",
      sourceRef: "Source Reference",
      rawMedia: "Raw Media Assets",
      brandProt: "Brand Protection",
      agentOrch: "Agent Orchestration",
      neuralCenter: "Neural Insight Center",

      // Modules
      sourceAnalysis: "Source Analysis & Structure",
      byImageAnalyzer: "By Image Analyzer",
      dataStructurer: "Data Structurer",
      rawImages: "Raw Supplier Images",
      scanned: "Scanned",
      unstructured: "Unstructured Text → Structured Attributes",
      processed: "Processed",

      seoArch: "SEO Keyword Graph",
      bySeo: "By SEO Architect",
      optTitle: "Optimized Title",
      score: "Score",
      bullets: "Bullet Points",
      keywordStrat: "Keyword Strategy",
      core: "Core",
      hot: "Hot",
      longTail: "Long-Tail",

      visualPipeline: "Visual Asset Pipeline",
      byImgSel: "By Image Selector",
      visualPlanner: "Visual Planner",
      refSel: "Reference Selection",
      sourcesLocked: "Sources Locked",
      visualPlan: "Visual Plan (Generative Prompts)",
      readyArtist: "Ready for Artist",

      optGallery: "Listing Writer",
      byArtist: "By Visual Artist",

      marketInt: "Platform Adapter",
      byRichBuilder: "By Rich Content Builder",
      contextMaster: "Context Master",
      aplusPreview: "Rich Content Preview",
      fieldMap: "Marketplace Field Mapping",

      motionProd: "Export & Delivery",
      byVideoCreator: "By Video Creator",
      rendering: "VEO 3.1 Rendering Engine",
      scriptGen: "Script Generated",

      // Agents
      analyzer: "Product Understanding",
      structurer: "Product Understanding",
      architect: "SEO Keyword Agent",
      planner: "Image Slots",
      selector: "Reference Selector",
      richContent: "Platform Adapter",
      videoCreator: "Export Agent",
      visualArtist: "Listing Writer",

      // Status
      complete: "COMPLETE",
      thinking: "THINKING",
      queued: "WAITING",
      waiting: "WAITING",
      idle: "IDLE",
      active: "ACTIVE (RENDERING)",
      enableVideo: "Enable Video Generation",

      // Footer
      reviewChanges: "Review Changes",
      reviewAfterGenerate: "Review after generation",
      oneClick: "One-Click Publish",
      neuralActive: "Neural Insight Center active. Pipeline targeting {platform} {market} ({language})."
    },

    markets: {
      ru: "Russia",
      us: "United States",
      cn: "China",
      global: "Global"
    },

    studio: {
      nav: "Studio",
      control: {
        title: "Generation & Debug Control",
        simpleTitle: "Generation Control",
        collection: "Collection:",
        collectionEmpty: "Not selected",
        setupRequiredTitle: "Complete AI settings first",
        setupRequiredDesc: "Please confirm target market/language and image settings before starting generation.",
        setupRequiredAction: "Go to AI settings",
        store: "Store",
        manageStores: "Manage stores",
        storeLockedHint: "This task is currently bound to {store}. To switch stores, go back to Task Center and reopen or create a task under the target store.",
        skipImages: "Skip images",
        onceOnly: "One-time only",
        costHint: "Estimated cost: {count} images · {credits} credits",
        confirmCostTitle: "Confirm deduction",
        confirmCostMessage: "This run will generate {count} images and deduct {credits} credits.\nContinue?",
        confirmCostConfirm: "Continue",
        start: "Start generation",
        regenerate: "Regenerate",
        resume: "Resume",
        starting: "Starting...",
        cancelGenerate: "Cancel",
        cancelling: "Cancelling...",
        runningStages: "Running stages",
        parallelRunningHint: "Parallel execution in progress",
        stageDebug: "Single-stage debug",
        runStage: "Run stage",
        executing: "Running...",
        infoStructureHint: "Running product understanding only updates this stage."
      },
      credits: {
        balanceHint: "Balance: {credits} credits",
        totalBalanceHint: "Total: {credits} credits",
        bucketSubscription: "Subscription credits",
        bucketRecharge: "Top-up credits",
        bucketLegacy: "Legacy credits",
        availableTooltipPrefix: "Available credits = ",
        availableTooltipSuffixThirdParty: " (Normal mode).",
        availableTooltipSuffixOfficial: " (Official fast mode; usable buckets follow admin settings).",
        channelPlanLabel: "Image mode",
        channelPlanThirdParty: "Normal mode",
        channelPlanOfficial: "Official fast mode",
        perImageHint: "Current mode {credits} credits/image",
        settlementShortHint: "Model routing is automatic; price follows the selected mode",
        settlementConfirmHint:
          "The platform may route to different image models internally, but your charge follows the selected mode. Failed images are refunded automatically.",
        channelPlanUnavailable: "This image mode is temporarily unavailable. Please switch mode or contact the admin.",
        channelPlanUnavailableInline: "Unavailable",
        channelPlanCompareHint: "Normal {thirdParty}/image · Official fast {official}/image",
        channelSwitchChecking: "Checking task retry status...",
        channelSwitchBlockedDefault: "You can switch mode only after this task exhausts all automatic retries.",
        switchOfficialTitle: "Switch to official fast mode?",
        switchOfficialConfirm: "Official fast mode uses the official-priority channel price. Continue switching?",
        switchOfficialConfirmWithDiff:
          "Official fast mode is {official} credits/image (current {current}/image, +{diff}/image). Continue?",
        switchOfficialConfirmLabel: "Switch",
        switchOfficialCancelLabel: "Cancel",
        insufficientTitle: "Insufficient credits",
        insufficientMessage: "Need {needed} credits, you currently have {current} credits.\nTop up to continue.",
        insufficientMessageGeneric: "Insufficient credits. Top up to continue.",
        goRecharge: "Top up",
        later: "Later",
        skipImages: "Skip images",
        stageHint: "Tip: You can skip images to finish copy/mapping now, then top up and generate images later.",
        generateHint: "This run estimates {count} images. You can also skip images to proceed first.",
        affordableHint: "With your balance, you can generate up to {count} images. You can adjust the image count in AI settings.",
        singleHint: "Image generation costs credits per image. Please top up and try again."
      },
      errors: {
        missingCollection: "Missing collection ID for Studio.",
        previewFailed: "Failed to load preview data.",
        stageStatusFailed: "Failed to load stage status.",
        generateFailed: "Failed to start generation.",
        stageExecuteFailed: "Failed to run stage.",
        agentLogFailed: "Failed to load agent logs.",
        previewRequestFailed: "Preview request failed (status {status}).",
        imageSlotRequestFailed: "Image slot request failed (status {status}).",
        imageGenerationStatusRequestFailed: "Image generation status request failed (status {status}).",
        stageStatusRequestFailed: "Stage status request failed (status {status}).",
        generateRequestFailed: "Generate request failed (status {status}).",
        stageExecuteRequestFailed: "Stage execution request failed (status {status}).",
        agentLogRequestFailed: "Agent logs request failed (status {status}).",
        promptUpdateFailed: "Failed to update prompt.",
        imageGenerateFailed: "Failed to generate image.",
        imageRegenerateFailed: "Failed to regenerate image.",
        imageRegenerateNotAccepted: "Regeneration request was not accepted by backend. Please retry.",
        imageRegenerateSubmittedSyncDelayed: "Regeneration was submitted, but status refresh is delayed. Please wait a moment.",
        imageDecisionFailed: "Failed to apply image decision.",
        imageDeleteFailed: "Failed to delete image.",
        updateChannelFailed: "Failed to update image mode.",
        cancelGenerateFailed: "Failed to cancel generation.",
        queueBusy: "Queue is busy. Please try again shortly.",
        internalStageFailed: "The image task hit an internal service error. Please refresh and retry.",
        refundHint: "Credits refunded.",
        publishBlocked: "Cannot publish yet. Please resolve the failed stage ({stage}) and regenerate.",
        publishNotReady: "Not ready to publish. Please complete generation first.",
        publishImageOpsPending: "Images are still being updated. Please wait before publishing.",
        publishMissingImages: "No images to upload. Please generate or add images before publishing.",
        publishPartialImagesWarning: "Only {generated}/{required} required images are ready (missing {missing}). You can publish now, or add the remaining images first.",
        publishMissingFields: "Required attributes are missing. Please finish platform mapping before publishing.",
        publishAttributesUnavailable: "Attribute payload is empty. Please rerun platform adapter before publishing.",
        stageFailed: "Stage failed: {stage}"
      },
      publishGuide: {
        title: "Pre-publish checklist",
        subtitle: "Fix the items below before publishing.",
        close: "Got it",
        itemImages: "Missing images",
        itemPendingImages: "Images are processing",
        itemCategory: "Missing category / type",
        itemTitle: "Missing title",
        itemAttributes: "Missing required attributes",
        itemPrice: "Missing price",
        itemAdapter: "Platform mapping incomplete",
        itemStageFailed: "Stage failed: {stage}",
        itemStageIncomplete: "Stage incomplete: {stage}",
        goFixImages: "Add images",
        goToStage: "Go to {stage}",
        partialDecisionTitle: "Missing required images",
        generateMissingOnly: "Generate missing images",
        continuePublish: "Continue publishing"
      },
      action: {
        generateStarted: "Generation started.",
        generateAdjusted: "Generation started (auto adjusted to {count} images).",
        generateSkipImagesAdjusted: "Insufficient credits. Auto disabled image generation; you can top up and generate images later.",
        generateQueued: "The same generation task is already running. Progress has been restored.",
        cancelRequested: "Cancel request submitted.",
        stageTriggered: "Stage triggered: {stage}",
        stageQueued: "The same stage task is already running: {stage}",
        promptUpdated: "Prompt updated for image {index}.",
        imageGenerateTriggered: "Image generation triggered: {index}",
        imageGenerateQueued: "Image generation already queued: {index}",
        missingImagesGenerateTriggered: "Missing image generation started for {count} plans.",
        missingImagesGenerateQueued: "Missing image generation is already queued for {count} plans.",
        trackImageGenerateTriggered: "{track} generation triggered for {count} plans.",
        trackImageGenerateQueued: "{track} generation is already queued for {count} plans.",
        trackImageGenerateNothingPending: "{track} has no remaining images to generate.",
        imageRegenerateTriggered: "Image regeneration triggered: {index}",
        imageRegenerateQueued: "Image regeneration already queued: {index}",
        imageCandidateAccepted: "Applied new image for plan {index}.",
        imageCandidateRejected: "Kept old image for plan {index}.",
        imageDeleted: "Image deleted: {index}",
        queueRetrying: "Queue busy, retrying ({attempt}/{max})..."
      },
      imageGeneration: {
        title: "Image generation task",
        statusQueued: "Waiting for resources",
        statusRunning: "Running",
        statusRunningBackground: "Background running",
        statusCompleted: "Completed",
        statusFailed: "Failed",
        statusCancelled: "Cancelled",
        progress: "{done}/{total} completed",
        progressWithFailed: "{done}/{total} completed · {failed} failed",
        queuePosition: "Queue position: #{position}",
        estimatedWait: "Estimated start in about {duration}",
        backgroundEstimatedWait: "Estimated sync in about {duration}",
        nextAttemptAt: "Next retry: {time}",
        defaultQueued: "Task submitted. Waiting for resources.",
        defaultRunning: "Image generation is in progress.",
        defaultRunningBackground: "Task is still running in backend. Results will sync automatically.",
        defaultCompleted: "Image generation completed and results are synced.",
        defaultFailed: "Image generation failed. Credits will be refunded automatically.",
        defaultCancelled: "Image generation task has been cancelled.",
        backgroundHint: "You can continue other operations. This task is still running in background.",
        lastUpdated: "Last update: {time}"
      },
      module: {
        expand: "Expand",
        collapse: "Collapse",
        collapsedHint: "Module collapsed. Click Expand to view details.",
        hiddenQueueTitle: "Pending modules",
        hiddenQueueDesc: "Modules without active work are hidden. They will appear automatically when their stage starts.",
        hiddenQueueAction: "Show"
      },
      stage: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Graph",
        listing: "Listing Writer",
        selector: "Reference Selector",
        visual: "Image Generation",
        imageGeneration: "Image Generation",
        normalized: "Normalized Payload",
        category: "Category Match",
        adapter: "Platform Adapter",
        export: "Export & Delivery"
      },
      source: {
        title: "Product Understanding",
        verified: "Data Verified",
        pending: "Awaiting Data",
        visualDiagnostics: "Asset Diagnostics",
        analyzing: "Analyzing",
        ready: "Ready",
        summary: "Summary",
        expand: "Expand details",
        collapse: "Collapse details",
        thumbnailAlt: "Thumbnail",
        mainImageAlt: "Source Image",
        emptyImages: "No source images",
        emptyTitle: "Waiting for source images",
        emptyDesc: "Connect the extension or import a product to start understanding.",
        imageCount: "Images: {count}",
        noImages: "No images",
        scanning: "ANALYZING...",
        sectionsTitle: "Captured Sections",
        sectionsEmpty: "No section data yet."
      },
      rawText: {
        title: "Source Text",
        length: "{count} chars",
        emptyTitle: "No raw text yet",
        emptyDesc: "Run product understanding to parse source text."
      },
      productCore: {
        title: "Product Core",
        materials: "Materials",
        specs: "Specs",
        useCases: "Use cases",
        audience: "Audience",
        facts: "Facts",
        emptyTitle: "Product core pending",
        emptyDesc: "Run product understanding to generate core facts."
      },
      productSchema: {
        title: "Product Data Schema",
        identity: "Identity",
        titleLabel: "Title",
        brandLabel: "Brand",
        categoryLabel: "Category",
        attributes: "Key Attributes",
        categoryKeywords: "Category Keywords",
        noCategoryKeywords: "No category keywords yet.",
        empty: "No attribute data yet.",
        emptyTitle: "Attributes pending",
        emptyDesc: "Complete structuring to generate product attributes.",
        logistics: "Logistics",
        price: "Price (CNY)",
        weight: "Weight",
        dimensions: "Dimensions",
        emptyValue: "—",
        sku: "SKU Overview",
        skuCount: "Variants",
        skuUnit: "Items",
        noColors: "No color data",
        sizes: "Sizes",
        noSizes: "No size data",
        skuReadOnlyHint:
          "Set the SKU and preferred reference image in Product Preview. Studio only shows the final reference result used for generation.",
        skuReadOnlyAction: "Go to Product Preview settings",
        stockZeroWarning: "The selected SKU currently has 0 stock. We will keep this SKU selected, but please confirm inventory before publishing."
      },
      metrics: {
        images: "Images",
        skus: "SKUs",
        attributes: "Attributes",
        sections: "Sections",
        rawText: "Raw Text"
      },
      imageText: {
        title: "Image Text Extraction",
        count: "Images {count}",
        empty: "No image text extracted yet.",
        emptyTitle: "No text detected yet",
        emptyDesc: "Run image text extraction to populate results here."
      },
      seo: {
        strength: "Search Strength",
        failed: "SEO generation failed",
        failedDesc: "No SEO result available.",
        optimizedKeywords: "Optimized Keywords",
        emptyKeywords: "No keywords generated yet.",
        emptyTitle: "SEO keywords pending",
        emptyDesc: "Run SEO stage to generate keyword graph.",
        searchVolume: "Search Volume",
        competition: "Intent",
        unknown: "Unknown",
        clusterLabel: "Cluster",
        volumeTier: "Volume",
        volumeHigh: "High",
        volumeMedium: "Medium",
        volumeLow: "Low",
        volumeUnknown: "Unknown",
        searchPipeline: "Keyword Graph Summary",
        pipelinePending: "Generating keyword graph...",
        graphTitle: "Keyword Graph Visualization",
        graphEmpty: "No graph nodes yet.",
        meaningLabel: "Chinese meaning",
        coverageTitle: "Coverage Report",
        missingAspects: "Missing Aspects",
        duplicates: "Duplicate Keywords",
        languageIssues: "Language Issues",
        coverageEmpty: "No issues detected"
      },
      keyword: {
        primary: "Primary",
        secondary: "Secondary",
        longTail: "Long-Tail",
        conversion: "Conversion",
        transactional: "Transactional",
        information: "Information",
        other: "Other"
      },
      agent: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Agent",
        listing: "Listing Writer",
        visual: "Image Generation",
        images: "Image Generation",
        selector: "Reference Selector",
        normalized: "Normalized Payload",
        category: "Category Match",
        adapter: "Platform Adapter",
        export: "Export & Delivery",
        error: "Stage failed. Check logs."
      },
      visual: {
        title: "Image Assets",
        subtitle: "Image slots • {count} sequences",
        collapseAll: "Collapse all",
        regeneratePrompts: "Regenerate prompts",
        regeneratePromptsTitle: "Regenerate image prompts",
        regeneratePromptsDesc:
          "Regenerate prompts from the current track setup. Auto-generate currently queues carousel {carouselCount} + detail {detailCount} = {totalCount} plans.",
        regeneratePromptsRunning: "Running...",
        regeneratePromptsOnly: "Prompt only",
        regeneratePromptsAndGenerate: "Prompt + generate",
        regeneratePromptsAutoTitle: "Regenerate prompts and images",
        regeneratePromptsAutoAction: "Confirm",
        regeneratePromptsAutoConfirm:
          "This will regenerate all prompts and auto-render the first {count} images. Estimated cost follows image generation pricing ({credits} credits/image). Continue?",
        regeneratePromptsSuccess: "Prompt regeneration completed ({count} plans). Free retries left: {remaining}.",
        regeneratePromptsCharged: "Charged {credits} credits for prompt regeneration.",
        regeneratePromptsAutoQueued: "Auto image rendering queued for {count} plans.",
        regeneratePromptsAutoFailed: "Prompts regenerated, but auto image rendering was not queued.",
        empty: "No image slots yet. Run the images stage first.",
        emptyTitle: "Image stage ready to start",
        emptyDesc: "Run the images stage to generate image slots. We'll show editable prompts and outputs here.",
        emptyStep1: "Step 1: Run images stage",
        emptyStep2: "Step 2: Review prompts",
        emptyStep3: "Step 3: Generate images",
        constraintsTitle: "Constraints",
        constraintsEmpty: "No constraints",
        keywordsTitle: "Keyword Focus",
        keywordsEmpty: "No keywords yet",
        referenceTitle: "Reference Images",
        referenceEmpty: "No reference images yet",
        referencePreviewTitle: "Reference Image #{index}",
        referenceSwitchTitle: "Switch Reference",
        referenceSwitchHint: "Use thumbnails or arrows to switch references quickly.",
        referenceSource: {
          sku: "SKU image",
          main: "main image",
          detail: "detail image",
          collection: "collected image",
          extracted: "extracted result",
          legacy: "historical cache",
          manual: "chosen by you",
          auto_sku_match: "auto matched inside current SKU",
          sku_image_url: "SKU image",
          selected_sku_image_indices: "images inside your locked SKU scope",
          selected_sku: "the SKU you selected",
          selected_anchor: "your preferred image choice",
          none: "None",
          unknown: "Unknown"
        },
        anchorModeImage: "Preferred-image mode",
        anchorModeText: "Text matching · threshold {threshold}%",
        anchorSourceLabel: "Current default image comes from: {source}",
        selectorSelectedCount: "Selected {count}",
        referenceControlTitle: "Reference control",
        referenceControlScopeTitle: "Reference scope",
        referenceControlScopeUserSku: "Locked to your selected SKU",
        referenceControlScopeUserSkuCount: "{count} candidate images are inside your SKU scope.",
        referenceControlScopeUserSkuNoImage:
          "Your SKU has no directly matched image. AI will fall back to text and full-image matching.",
        referenceControlScopeAiAuto: "Reference scope is still decided automatically by AI.",
        referenceControlSelectionTitle: "Current selection",
        referenceControlSelectionManualLocked: "You manually locked the final reference image. AI reference selection is skipped.",
        referenceControlSelectionAiWithinUserSku:
          "SKU is locked. The system will auto-pick one final reference image inside this SKU scope.",
        referenceControlSelectionAiAuto: "The system will auto-pick the final reference image.",
        referenceControlSelectionPendingUserSku:
          "SKU is locked. If you do not choose a preferred image, the system will pick one inside this SKU scope.",
        referenceControlSelectionPendingAi:
          "The system will determine both reference scope and final reference image automatically.",
        referenceControlPreferredTitle: "Preferred reference",
        referenceControlPreferredManual: "You chose preferred image #{index}.",
        referenceControlPreferredAuto: "System currently prefers image #{index}.",
        referenceControlPreferredNone: "No preferred image is fixed yet. The system will automatically choose one inside the current scope.",
        referenceOwnershipManual: "Chosen by you",
        referenceOwnershipAiWithinUserSku: "AI selected (inside your SKU scope)",
        referenceOwnershipAiAuto: "AI selected",
        referencePreferredManualBadge: "Your preferred image",
        referencePreferredAutoBadge: "System preferred image",
        referenceLockedTitle: "Reference locked",
        referenceLockedSubtitle: "Your chosen image will be used directly",
        referenceLockedBannerTitle: "You already chose the reference image.",
        referenceLockedBannerBody:
          "The next image generation will use this single image directly and will not run AI reference selection.",
        referenceLockedPendingHint: "This image is already locked and will be used directly in the next generate run.",
        referenceLockedCardTitle: "Locked reference image",
        referenceLockedChangeAction: "Change preferred image",
        selectorSummaryTitle: "Selection Summary",
        selectorSummaryEmpty: "No selection summary yet.",
        selectorRationaleEmpty: "No rationale details yet.",
        selectorPromptTitle: "Selector Prompt",
        selectorPromptEmpty: "Prompt snapshot not available yet.",
        selectorDecisionManualLocked: "Manual lock",
        selectorDecisionPrimary: "Primary group",
        selectorDecisionSupplement: "Confidence supplement",
        selectorDecisionFallback: "Fallback fill",
        selectorReasonTitle: "Selection Reason",
        selectorReasonEmpty: "No explicit reason provided.",
        selectorConfidence: "Confidence {value}%",
        selectorSkuConfidence: "SKU confidence {value}%",
        singleSkuDecisionTitle: "Single SKU Decision",
        singleSkuDecisionEmpty: "No single SKU decision output yet.",
        singleSkuUnknown: "Unknown SKU",
        singleSkuBadge: "SKU · {sku}",
        singleSkuReasonsTitle: "Why this SKU",
        singleSkuReasonsEmpty: "No explicit SKU rationale.",
        singleSkuExcludedTitle: "Excluded SKU Notes",
        singleSkuExcludedEmpty: "No excluded SKU notes.",
        singleSkuRiskPrefix: "Risk: {risk}",
        singleSkuCandidatesTitle: "SKU Candidate Scores",
        singleSkuCandidatesEmpty: "No SKU candidate scoring data.",
        singleSkuCandidateScore: "Score {value}",
        singleSkuCandidateImageCount: "Images {count}",
        singleSkuCandidateReasonEmpty: "No candidate reason provided.",
        manualAnchorAction: "Go to Product Preview and choose a preferred reference image",
        referenceSkuTitle: "Mapped SKU",
        referenceSkuEmpty: "No SKU mapping",
        referenceSkuReasonTitle: "SKU Mapping Reason",
        referenceSkuReasonEmpty: "No SKU mapping reason.",
        overlayLabel: "Overlay Text",
        promptCopied: "Prompt copied",
        sceneLabel: "Scene",
        placeholderMainTitle: "Hero shot",
        placeholderMainDesc: "Primary listing image prompt",
        placeholderSceneTitle: "Scenario shot",
        placeholderSceneDesc: "Lifestyle and usage context",
        placeholderDetailTitle: "Detail shot",
        placeholderDetailDesc: "Material and feature close-ups",
        detailRouteLabel: "Detail Route",
        detailRouteComposeTitle: "Carousel compose",
        detailRouteComposeDesc: "Reuse generated carousel images and copy to assemble the detail page.",
        detailRouteStandaloneTitle: "AI standalone",
        detailRouteStandaloneDesc: "Generate 3 dedicated detail images in a separate continuity series.",
        detailRouteChanged: "Detail route switched to {route}.",
        detailRouteChangeFailed: "Failed to switch detail route.",
        detailRouteComposeEmpty: "This route reuses carousel results as detail-page source material. Generate the carousel images above first.",
        trackSecondaryEmpty: "Standalone detail images are separated from carousel plans. No dedicated detail plans are available yet.",
        morePlansHint: "{count} additional plans are ready with prompts.",
        expandExtraPlans: "Show {count} more plans",
        collapseExtraPlans: "Hide extra plans",
        viewMore: "View {count} more",
        statusWaiting: "Status: Waiting",
        statusGenerated: "Status: {current}/{total} Generated",
        statusPrompted: "Status: {count} Planned",
        statusQueueing: "Status: {count} Waiting",
        statusRendering: "Status: Rendering {count}",
        galleryTitle: "Carousel Image Preview",
        detailGalleryTitle: "Detail Image Preview",
        editorCarouselSectionTitle: "Main / Carousel generation plans",
        editorDetailSectionTitle: "Detail generation plans",
        detailReuseFromCarouselHint:
          "Current detail route is carousel compose. Detail images are assembled from main/carousel outputs and do not support standalone regenerate/delete here.",
        detailSectionIntroAdvantagesTitle: "Part 1 · Introduction & key advantages",
        detailSectionIntroAdvantagesGoal: "Open the detail series with product identity and 2-4 core benefits.",
        detailSectionUsageEvidenceTitle: "Part 2 · Usage scenes & proof",
        detailSectionUsageEvidenceGoal: "Show real usage context and visible structural proof for the key claims.",
        detailSectionSpecsGuaranteeTitle: "Part 3 · Specs, options & assurance",
        detailSectionSpecsGuaranteeGoal: "Close with specs, package/variant summary, and reassurance information.",
        detailSectionFallbackTitle: "Part {order}",
        planSectionLabel: "Detail Section",
        planGoalLabel: "Section Goal",
        generated: "Generated",
        openImage: "Open",
        selectingReference: "Selecting reference images...",
        savePrompt: "Save Prompt",
        savePromptSaving: "Saving...",
        generateImage: "Generate Image",
        generating: "Generating...",
        regenerate: "Regenerate",
        regenerating: "Regenerating...",
        regenerateModalTitle: "Regenerate image slot #{index}",
        regenerateModalDesc: "Choose whether to use the original prompt, or append additional prompt constraints.",
        regenerateModeOriginal: "Use original prompt",
        regenerateModeAppend: "Append constraints",
        regenerateBasePromptLabel: "Full prompt preview",
        regenerateBasePromptEmpty: "No original prompt available yet.",
        regeneratePromptPreviewLoading: "Loading full prompt preview...",
        regeneratePromptPreviewEmpty: "Full prompt preview is empty, fallback to seed prompt.",
        regenerateConstraintLabel: "Additional constraints",
        regenerateConstraintPlaceholder: "Describe why this image should be regenerated, and what needs to change.",
        regenerateConstraintRequired: "Please enter additional constraints before confirming.",
        regenerateModalCancel: "Cancel",
        regenerateModalConfirm: "Start regenerate",
        pendingCandidateLabel: "New image pending review",
        pendingCandidateAt: "Generated at",
        pendingCandidateExists: "A pending new image already exists. Please choose keep old or use new first.",
        compareOpen: "Compare",
        compareUseNew: "Use new",
        compareKeepOld: "Keep old",
        compareKeepOldTitle: "Keep current image",
        compareKeepOldConfirm: "Keep the current image for plan #{index} and discard the new candidate?",
        compareOld: "Current image",
        compareNew: "New image",
        compareModeSlider: "Slider compare",
        compareModeSideBySide: "Side by side",
        compareModalTitle: "Compare image slot #{index}",
        compareModalDesc: "Review both versions and choose the one to keep.",
        promptSlotLabel: "Slot {slot}:",
        promptGenerating: "Generating...",
        promptWaiting: "Waiting for context...",
        deleteImage: "Delete image",
        deleteConfirmTitle: "Delete generated image",
        deleteConfirmMessage: "Delete the generated image for plan #{index}? You can regenerate later.",
        deleteConfirm: "Delete",
        costHint: "Estimated: {count} image · {credits} credits",
        planBadge: "{index} {type}",
        planDefaultType: "MAIN",
        planTypeTitle: "{type} Plan",
        planTitle: "Image Plan {index}",
        planAspect: "Aspect {ratio}",
        planSubtitle: "Prompt plan",
        historyTitle: "Version History",
        historyEmpty: "No history yet",
        historyAccepted: "Accepted",
        historyRejected: "Rejected",
        historyViewImage: "View",
        historyRestore: "Switch",
        historyRestored: "Switched to selected version",
        historyReplaced: "Replaced",
        historyCompare: "Compare",
        historyAt: "at {time}",
        stylePreset: {
          dramatic: "Dramatic",
          minimal: "Minimal",
          lifestyle: "Lifestyle",
          tech: "Tech",
          warm: "Warm",
          industrial: "Industrial",
          nature: "Nature",
          luxury: "Luxury"
        }
      },
      listing: {
        title: "Listing Writer",
        writing: "Writing...",
        keywordInput: "SEO input keywords",
        keywordEmpty: "No keyword hints yet.",
        emptyTitle: "Listing pending",
        emptyDesc: "Run listing stage to generate the listing output.",
        fieldTitle: "Title",
        shortDesc: "Short description",
        longDesc: "Long description",
        bullets: "Bullet points",
        emptyBullets: "No bullet points yet.",
        faq: "FAQ",
        emptyFaq: "No FAQ yet."
      },
      normalized: {
        title: "Normalized Payload",
        platformUnknown: "Platform",
        summary: "Normalized Summary",
        platform: "Platform",
        market: "Market",
        language: "Language",
        keywordCount: "Keywords",
        imageCount: "Images",
        skuCount: "SKUs",
        factCount: "Facts",
        payload: "Normalized Output",
        emptyPayload: "No normalized payload.",
        emptyTitle: "Normalized payload pending",
        emptyDesc: "Run normalized stage to generate."
      },
      adapter: {
        title: "Platform Adapter",
        rerun: "Rerun adapter",
        platformUnknown: "Platform",
        summary: "Adapter Summary",
        platform: "Platform",
        format: "Format",
        fieldCount: "Fields",
        category: "Category",
        typeId: "Type ID",
        price: "Price (CNY)",
        images: "Images",
        attributesCount: "Attributes",
        richContent: "Rich content",
        richContentMissingHint: "Rich content missing; rerun adapter stage to regenerate.",
        missingFields: "Missing fields",
        missingFieldsEmpty: "No missing fields",
        payload: "Adapter Output",
        emptyPayload: "No adapter output.",
        fieldMap: "Field Mapping",
        emptyMappingTitle: "No mapped fields yet",
        emptyMappingDesc: "Run adapter stage to generate platform payload.",
        mappingField: "Field",
        mappingSource: "Normalized source",
        mappingStatus: "Status",
        mappingSuggestion: "Suggestion",
        mappingStatusMapped: "Mapped",
        mappingStatusInferred: "Can infer",
        mappingStatusMissing: "Missing",
        mappingSuggestUseSource: "Fill from normalized payload",
        mappingSuggestInfer: "Need inference / manual fill",
        mappingSuggestCategory: "Needs category match",
        mappingPlatformHint: "Current output is normalized; run Ozon adapter for field mapping.",
        schemaTitle: "Upload Parameters",
        schemaFetchedAt: "Schema fetched at {time}",
        schemaEmpty: "No schema data yet.",
        schemaField: "Field",
        schemaFieldId: "ID",
        schemaRequired: "Required",
        schemaType: "Type",
        schemaValue: "Value",
        schemaDictionary: "Dictionary values ({count})",
        schemaStatus: "Status",
        schemaStatusFilled: "Filled",
        schemaStatusMissing: "Missing",
        schemaRequiredYes: "Required",
        schemaRequiredNo: "Optional",
        overviewTitle: "Publish Readiness",
        pendingTitle: "Items requiring action",
        pendingDesc: "Resolve missing fields first to avoid publish rejection.",
        viewMissingAction: "View missing",
        blockingTitle: "Required attributes missing",
        blockingDesc: "{count} required attributes need completion before publish.",
        coreTitle: "Core commerce attributes",
        coreDesc: "{count} key attributes for conversion and compliance",
        contentTitle: "Content attributes",
        contentDesc: "Description / tags / rich content: {count} items",
        optionalTitle: "Other optional attributes",
        optionalDesc: "{count} items after filtering",
        groupOverflowHint: "+{count} more hidden",
        keyAttributesTitle: "Key attributes",
        keyAttributesDesc: "{count} items shown by priority",
        showAllAttributes: "Show all attributes",
        hideAllAttributes: "Hide all attributes",
        allAttributesTitle: "All attributes",
        allAttributesDesc: "{count} items after filtering",
        searchPlaceholder: "Search by field name / ID / value",
        filterAll: "All",
        filterMissing: "Missing",
        filterRequired: "Required",
        filterFilled: "Filled",
        expandValueAction: "Expand",
        collapseValueAction: "Collapse",
        emptyFilterResult: "No attributes match the current filter.",
        pageIndicator: "Page {current}/{total}",
        pagePrev: "Prev",
        pageNext: "Next",
        fieldIdFallback: "Attribute ID {id}",
        payloadAttributeCount: "{count} payload attributes",
        mappingHiddenWithoutSchema: "Readable field names are unavailable now. IDs are shown temporarily."
      },
      export: {
        title: "Export & Delivery",
        exporting: "Exporting...",
        open: "Export details",
        reexport: "Re-export",
        platform: "Platform",
        category: "Category",
        columns: "Columns",
        rows: "Rows",
        templateKey: "Template key",
        format: "Format",
        version: "Template Version",
        validation: "Validation",
        validationPass: "Passed",
        validationPending: "Pending / Issues",
        fileName: "File Name",
        delivery: "Delivery",
        ready: "Download ready.",
        pending: "Waiting for export data.",
        download: "Download",
        waiting: "No download link yet.",
        validationErrors: "Validation errors"
      },
      preview: {
        title: "Generated Preview",
        back: "Back",
        retry: "Retry",
        images: "Preview Images",
        adapter: "Adapter Payload",
        upload: "Upload Payload",
        export: "Export Payload",
        noImages: "No preview images.",
        imageCountSuffix: "images",
        mainImageAlt: "Main preview image",
        listingTitle: "Listing Title",
        shortDesc: "Short Description",
        bulletPoints: "Key Benefits",
        longDesc: "Long Description",
        faq: "FAQ",
        attributes: "Product Attributes",
        highlights: "Key Highlights",
        seoKeywords: "SEO Keywords",
        metricsTitle: "Content Overview",
        metricImages: "Images",
        metricSkus: "SKUs",
        metricAttributes: "Attributes",
        metricSections: "Sections",
        metricRawText: "Raw Text",
        priceSummary: "Product Summary",
        price: "Price (CNY)",
        purchaseCostCny: "Purchase cost (CNY)",
        skuCount: "SKU Count",
        weight: "Weight (g)",
        size: "Size (mm)",
        empty: "No data available.",
        statusReady: "Ready for review",
        statusUploaded: "Uploaded, ready to update",
        reviewBtn: "Review changes",
        publishBtn: "Publish now",
        updateBtn: "Update on Ozon",
        updateUnavailable: "This task is not uploaded yet, cannot update.",
        fixRequiredTitle: "Fix-required notice from Ozon",
        fixRequiredDesc: "You can continue fixing fields on this page, or jump to Ozon backend for direct remediation.",
        fixRequiredFallback: "The listing is created but still has fix-required issues. Please complete remediation and refresh status.",
        fixRequiredOpenOzon: "Open Ozon remediation page",
        fixRequiredOpenOzonDisabled: "Cannot build Ozon remediation link right now.",
        updateTriggered: "Update request submitted.",
        saveDraft: "Save draft",
        savingDraft: "Saving...",
        draftSaved: "Draft saved.",
        draftNoChanges: "No draft changes to save.",
        draftSaveFailed: "Failed to save draft.",
        draftUnsaved: "Draft changed, not saved",
        openPricingEngine: "Open Pricing Engine",
        setPrimaryImage: "Set as primary image",
        primaryImageCurrent: "Current primary image",
        imageOrderHint: "Drag thumbnails to adjust upload order. Carousel and detail tracks are isolated and cannot be mixed.",
        imageOrderCarouselSectionTitle: "Main / Carousel",
        imageOrderDetailSectionTitle: "Detail Images",
        imageOrderSectionCount: "{count} images",
        imageOrderCarouselEmpty: "No main/carousel images yet.",
        imageOrderTitle: "Image order {index}",
        imageOrderCarouselBadge: "Carousel",
        imageOrderDetailBadge: "Detail",
        imageOrderCrossGroupBlocked: "Reordering is only allowed within the same group (carousel/detail).",
        detailRouteHint: "The rich-content preview and export output will follow this detail route.",
        readonlyTitle: "Read-only fields",
        tagsHint: "Enter tags separated by spaces or commas",
        oldPrice: "Old price (CNY)",
        invalidPrice: "Price must be a positive number.",
        invalidOldPrice: "Old price must be a positive number.",
        invalidPriceRelation: "Old price cannot be lower than price.",
        invalidWidth: "Width must be a positive number.",
        invalidHeight: "Height must be a positive number.",
        invalidDepth: "Depth must be a positive number.",
        invalidWeight: "Weight must be a positive number.",
        invalidImages: "Keep at least one image.",
        invalidPrimaryImage: "Primary image must exist in image list.",
        ozonOnly: "Only Ozon preview is supported right now.",
        ozonPayloadMissing: "No usable Ozon preview payload found. Complete the adapter and listing fields first.",
        ozonListing: "Ozon Listing",
        targetStoreLabel: "Target store",
        targetStoreBinding: "Binding...",
        targetStoreMissing: "Select a target store before generating images or publishing.",
        targetStoreMissingShort: "No store selected",
        targetStoreBindFailed: "Failed to bind the target store.",
        ozonOfferId: "Ozon SKU",
        ozonCurrency: "Currency",
        ozonSpecs: "Specifications (mm)",
        selectedSkuTitle: "Selected source SKU",
        selectedSkuLocked: "Locked",
        selectedSkuId: "SKU ID",
        selectedSkuPrice: "Source price",
        selectedSkuSource: "Selection source",
        selectedSkuSourceUser: "Manual",
        selectedSkuSourceAuto: "Auto",
        selectedSkuNoDetail: "No additional spec snapshot",
        selectedSkuDetailShape: "Type",
        selectedSkuDetailDimensions: "Dimensions",
        selectedSkuDetailPackaging: "Packaging",
        selectedSkuDetailSize: "Size",
        selectedSkuDetailColor: "Color",
        selectedSkuDetailStock: "Stock",
        dimensionReviewTitle: "Dimension review",
        dimensionReviewManualHint: "Manual review recommended",
        dimensionReviewOriginal: "Original",
        dimensionReviewResolved: "Resolved",
        packagingReserveBadge: "Estimated reserve value",
        packagingReserveDimensions: "Current {current}; original {original}; reserve uplift {percent}.",
        packagingReserveWeight: "Current {current}; original {original}; reserve uplift {increase} / {percent}.",
        packagingReserveLength: "L",
        packagingReserveWidth: "W",
        packagingReserveHeight: "H",
        dimensionReviewStatus: {
          passed: "Passed",
          corrected: "AI corrected",
          needs_manual_review: "Needs review",
          failed: "Review failed"
        },
        ozonDescription: "Ozon Description",
        ozonTags: "Ozon Tags",
        ozonAttributes: "Ozon Attributes",
        pricingFinalized: "FINALIZED PRICING",
        pricingEngineTitle: "Pricing Engine Logic",
        pricingPurchaseCost: "Purchase Cost",
        pricingTotalCost: "Upfront Investment",
        pricingPostSaleDeductions: "Post-sale Deductions",
        pricingProfitPerOrder: "Net Profit / Order",
        pricingRoi: "ROI",
        pricingProfitability: "Profitability",
        pricingMargin: "Margin",
        pricingFinalPrice: "Final Listing Price (CNY)",
        pricingBuyerPriceLabel: "Buyer Pays",
        pricingSnapshotOutdatedHint: "Current listing price has changed. Profit and cost metrics still come from the latest pricing snapshot. Open Pricing Engine to recalculate.",
        pricingLogisticsWarningTitle: "Logistics compatibility warning",
        pricingLogisticsWarningHint: "Current pricing did not fully match an available logistics channel (provider={provider}). You can continue, but please verify logistics settings before publishing.",
        pricingViewBreakdown: "View Breakdown",
        pricingHideBreakdown: "Hide Breakdown",
        pricingDiscountExplainTitle: "Fixed 40% discount policy",
        pricingDiscountExplainLine: "Listing price is intentionally higher. Discount {discount}, pay factor {pay_factor}. Fill {listing_price} in Ozon backend, buyer pays about {target_price}.",
        pricingBreakdownMissing: "Pricing breakdown is not available.",
        pricingBreakdown: {
          purchaseCost: "Purchase Cost",
          upfrontInvestment: "Upfront Investment",
          postSaleLogistics: "Post-sale Int'l Logistics",
          postSalePlatform: "Post-sale Platform Fees",
          postSaleOther: "Post-sale Other Deductions"
        }
      },
      gallery: {
        main: "MAIN",
        empty: "No generated images",
        slot: "SLOT {index}",
        diffusing: "DIFFUSING...",
        loading: "Loading...",
        mainPlaceholder: "Hero slot",
        emptySlot: "Slot {index}",
        emptyHint: "Awaiting render",
        waiting: "Generating..."
      },
      marketplace: {
        emptyRich: "No rich content assets yet",
        widgets: "Widgets {count}",
        images: "Images {count}",
        theme: "Theme {theme}",
        emptyMapping: "No mapped attributes yet."
      },
      richContent: {
        title: "Rich Content Structure",
        summary: "{count} widgets",
        empty: "No rich content widgets yet.",
        emptyTitle: "Rich content is ready to render",
        emptyDesc: "Once the rich content stage finishes, widgets and images will appear here.",
        emptyPreview: "No rich content preview yet.",
        images: "Rich content images",
        emptyImages: "No rich content images available.",
        version: "Version",
        generatedAt: "Generated",
        blocks: "{count} blocks",
        rows: "{count} rows",
        columns: "{count} columns",
        untitled: "Untitled widget",
        noDescription: "No text content yet.",
        moreWidgets: "More widgets: {count}",
        emptyMappingTitle: "No field mapping yet",
        emptyMappingDesc: "Finish attribute generation to map fields automatically.",
        detailHint: "Structure ready",
        viewDetail: "View details",
        collapse: "Collapse",
        detailTitle: "Widget details",
        detailDesc: "Structured output from the rich content agent.",
        jsonTitle: "Widget JSON",
        blocksTitle: "Block preview",
        emptyBlocks: "No blocks generated yet.",
        galleryTitle: "Rich content image stream",
        emptyGallery: "No rich content images yet.",
        emptyGalleryDesc: "Generate rich content to populate the image stream.",
        widget: {
          showcase: "Showcase",
          text: "Text Block",
          table: "Table",
          video: "Video",
          list: "List",
          unknown: "Widget"
        }
      },
      video: {
        empty: "Video generation is not enabled yet.",
        hint: "Enable the video stage to produce motion assets.",
        recording: "RECORDING..."
      },
      status: {
        complete: "COMPLETE",
        thinking: "THINKING...",
        processing: "PROCESSING...",
        backgroundProcessing: "BACKGROUND...",
        error: "ERROR",
        waiting: "WAITING"
      },
      agentLogs: {
        title: "Agent Live Feed",
        subtitle: "Agent Stream Console",
        copyDiagnostics: "Copy diagnostics",
        copied: "Copied",
        downloadLogs: "Download logs",
        chainTitle: "AGENT LOG STREAM",
        streaming: "Streaming",
        emptyTitle: "No agent logs yet",
        emptyDesc: "Run a stage to see live agent events here.",
        logCount: "{count} logs",
        lastUpdated: "Last updated {time}",
        duration: "Duration {duration}",
        expandLog: "Expand",
        collapseLog: "Collapse",
        status: {
          running: "RUNNING",
          completed: "DONE",
          failed: "FAILED",
          idle: "IDLE"
        },
        event: {
          start: "START",
          thinking: "THINK",
          result: "RESULT",
          error: "ERROR",
          cacheHit: "CACHE",
          skip: "SKIP"
        }
      }
    },

    // Pricing Engine
    pricingEngine: {
      title: "OneClickSell Dynamic Pricing Engine",
      subtitle: "Multi-platform profit calculator with platform-specific fee logic for global marketplaces.",
      returnToPreview: "Back to preview",
      calcMode: "Calculation Mode",
      manualEntry: "Manual Entry",
      myListings: "My Listings",
      destPlatform: "Destination Platform",
      globalChannels: "{count} Global Platforms Available",
      ready: "Ready",
      soon: "Soon",
      listingsOzonOnly: "Listings sync is currently available for Ozon only.",
      inputMode: "Input Mode",
      compactMode: "Compact",
      fullMode: "Advanced",
      singlePlatformHint: "Most sellers operate on a single platform.",
      rateSource: "Live FX source",
      rateLoading: "Updating...",
      calculationLoadingTitle: "Recalculating pricing...",
      calculationLoadingDesc: "Latest Ozon fee and profit simulation is running. Please wait before retrying.",
      calculationFailed: "Pricing calculation failed",
      retryCalculation: "Retry calculation",
      rateUpdated: "Updated {date}",
      rateError: "Rate error",
      rateErrorDesc: "Failed to fetch the latest exchange rate. Please retry.",
      networkErrorDesc: "Network connection failed. Please retry.",
      requestTimeoutDesc: "Request timed out. Please retry.",
      autoRateOn: "Auto Rate",
      autoRateOff: "Manual",
      refreshRate: "Refresh Rate",
      autoRateHint: "Auto rate enabled (Frankfurter API).",
      manualRateHint: "Manual override enabled.",
      tiktokRegion: "Selling Region",
      regionUs: "United States",
      regionUk: "United Kingdom",
      regionSea: "Southeast Asia",
      tiktokRegionHint: "Switching region will update commission and transaction presets. Edit any field if your actual rate differs.",
      selectProduct: "Select Product from Workspace",
      viewDetails: "View Details",
      dataSynced: "Data Synced",
      pricingStrategy: "Pricing Strategy",
      targetMargin: "Target Margin (%)",
      targetProfit: "Target Profit (¥)",
      tieredProfit: "Tiered Profit",
      tieredProfitHint: "Configure profit by purchase-cost bands. Effective target = max(Fixed Profit, Cost × Profit Rate).",
      currentTier: "Active Tier",
      tierFixedProfit: "Fixed Profit",
      tierProfitRate: "Profit Rate",
      tieredTargetHint: "Current tier target profit: {value}",
      tierLabel: "Tier",
      targetValue: "Target Value",
      quickAdjustTag: "Live Tuning",
      quickAdjustTitle: "Adjust strategy right here",
      quickAdjustHint: "Drag slider or type value",
      quickAdjustClickHint: "Click to switch to margin editing",
      autoCalculated: "Auto-calculated from current inputs",
      breakEven: "Break-even",
      suggestedPrice: "Suggested Listing Price",
      ozonListingPrice: "Suggested Listing Price (Ozon Backend)",
      ozonListingPriceShort: "Ozon backend listing",
      ozonTargetPriceLabel: "Buyer Pays",
      rate: "Rate: 1 CNY = {rate} {currency} (Input)",
      breakEvenPrice: "Break-even Price: {price}",
      optimizedMsg: "Optimized for {value}% Margin",
      optimizedProfit: "Optimized for {value} Profit",
      netProfit: "Net Profit",
      profitMargin: "Profit Margin",
      targetMet: "Target Met",
      targetNotReached: "Target not reached (cap applied)",
      roi: "ROI",
      highMargin: "High Margin",
      updateListing: "Update Price in Listing",
      syncDesc: "Instantly sync this price to your Ozon listing in the workspace.",
      syncSuccess: "Price Sync Complete",
      syncSuccessDesc: "Successfully sent {price} to {platform} listing.",
      feeBreakdown: "Platform Fee Breakdown",
      catLogic: "Category Logic Applied",
      commission: "Marketplace Commission",
      lastMile: "Last-mile Delivery",
      handling: "Handling & Warehousing",
      totalOpCost: "Total Operational Cost",
      costBreakdown: "Cost Breakdown",
      coreCalculationFailed: "Pricing calculation failed. Please check inputs and retry.",
      costBreakdownTotalHintOzon: "Total cost includes upfront costs, international shipping, platform deductions, withdrawal, ads, and return reserve.",
      exportReport: "Export Report",
      shareImage: "Generate Share Image",
      shareReady: "Share Image Ready",
      shareReadyDesc: "Share image for {platform} saved to your device.",
      shareError: "Share Image Failed",
      shareErrorDesc: "Unable to render share image. Please retry.",
      saveOzonConfig: "Save Ozon Strategy",
      savingConfig: "Saving...",
      applyOzonConfig: "Apply to Ozon Flow",
      saveAndApplyOzonConfig: "Save, Apply & Sync Preview",
      applyingConfig: "Applying...",
      saveConfigSuccess: "Ozon strategy saved",
      saveConfigSuccessDesc: "Your custom Ozon pricing strategy has been saved.",
      saveConfig: "Save Config",
      saveConfigFailed: "Save failed",
      saveConfigFailedDesc: "Unable to save Ozon pricing strategy. Please retry.",
      applyConfig: "Apply Config",
      heroBadge: "Pricing cockpit",
      platformMatrixHint: "Click a chart card to switch platform views, including margin, ROI, and platform fee rate.",
      strategyWorkbenchTitle: "Strategy workbench",
      strategyWorkbenchDesc: "Simple mode focuses on key inputs, while full mode exposes all parameters and the complete Ozon strategy loop.",
      delete: "Delete",
      minShort: "Min",
      maxShort: "Max",
      noLimitHint: "0 = no limit",
      targetProgress: "Target progress",
      summaryBadge: "Strategy cockpit",
      profitCurveTitle: "Profit sensitivity curve",
      costWaterfallTitle: "Cost waterfall",
      profitSignals: {
        strong: {
          label: "Strong profit signal",
          desc: "You can keep the current strategy and scale traffic."
        },
        stable: {
          label: "Stable profit",
          desc: "Keep watching category fee changes."
        },
        pressure: {
          label: "Profit under pressure",
          desc: "Raise the target margin before scaling traffic."
        }
      },
      executionSignals: {
        localOnly: "Non-Ozon flow: local strategy simulation only",
        loginRequired: "Not logged in: cannot save and apply Ozon configuration",
        contextReady: "Collection context connected: supports closed-loop preview sync",
        contextMissing: "No collection context yet: choose a product in Quick Source first"
      },
      ozonPanel: {
        core: "Core strategy",
        cost: "Fulfillment cost",
        risk: "Risk rates"
      },
      ozonPanelHint: "Dense parameters are split into grouped panels to reduce configuration complexity at once.",
      applyConfigSuccess: "Strategy applied",
      applyConfigSuccessDesc: "The strategy is now active for subsequent Ozon generation.",
      applyConfigTriggeredDesc: "Applied successfully and started backfilling pricing for the current product.",
      applyConfigNoCollectionDesc: "Applied successfully for subsequent generation. Open Studio product to backfill history.",
      applyConfigSnapshotSavedDesc: "Saved current pricing snapshot with backend canonical calculation.",
      applyConfigConflictDesc: "A locked price already exists and conflicts with current strategy.",
      applyConfigConflictConfirm: "Do you want to overwrite locked price with current strategy?",
      applyConfigAutoSyncSuccessDesc: "Applied successfully and synced to Studio preview draft (40% discount logic included).",
      applyConfigAutoSyncFailedDesc: "Applied successfully, but syncing to Studio preview failed. Click Retry preview sync to try again.",
      nextStepTitle: "Next Actions",
      nextStepSubtitle: "Apply the latest pricing, export a handoff snapshot, or continue to publish.",
      nextStepApplyCurrent: "Apply to Current Product",
      nextStepApplyCurrentDesc: "Push this pricing strategy to the selected product context.",
      nextStepExportPlan: "Export Plan",
      nextStepExportPlanDesc: "Download current pricing inputs and calculated results as JSON.",
      nextStepGoPublish: "Go to Publish",
      nextStepGoPublishDesc: "Continue with publishing workflow for the selected product.",
      nextStepDisabledCalculating: "Pricing is still recalculating. Please wait a moment.",
      nextStepDisabledInvalidPrice: "Suggested price is invalid. Adjust inputs before continuing.",
      nextStepApplyDisabledPlatform: "Apply to current product is only available for Ozon.",
      nextStepApplyDisabledAuth: "Sign in first to apply pricing to the current product.",
      nextStepApplyDisabledContext: "Select a product context before applying.",
      nextStepApplyDisabledApplying: "Applying strategy in progress. Please wait.",
      nextStepApplyDisabledSaving: "Saving strategy in progress. Please wait.",
      nextStepGoPublishDisabledAuth: "Sign in first to continue to publish.",
      nextStepGoPublishDisabledContext: "Select a product context before entering publish.",
      nextStepExportSuccess: "Pricing plan exported successfully.",
      nextStepExportFailed: "Failed to export pricing plan. Please retry.",
      applyConfigFailed: "Apply failed",
      applyConfigFailedDesc: "Unable to apply Ozon strategy. Please retry.",
      loadConfigFailed: "Load failed",
      loadConfigFailedDesc: "Unable to load your saved Ozon strategy.",
      loginRequiredTitle: "Login required",
      loginRequiredDesc: "Please sign in first to save and apply Ozon strategy.",
      loadingOzonConfig: "Loading your Ozon strategy...",
      updatedAt: "Updated: {date}",
      ozonOnlyHint: "Ozon strategy apply is available on Ozon only.",
      ozonFlowStrategyTitle: "Ozon flow strategy",
      ozonFlowStrategyDesc: "Apply current strategy to Ozon flow and auto-sync Studio preview in one click.",
      collectionContextTitle: "Current product context",
      contextLoading: "Loading current product context...",
      contextEmpty: "No collection context found. You can pick one from the quick list below.",
      contextPriceHint: "Current suggested CNY price: {price}",
      contextLoadFailedDesc: "Failed to load pricing context for this product.",
      dimensionReviewTitle: "Dimension review",
      dimensionReviewManualHint: "Manual review recommended",
      dimensionReviewOriginal: "Original",
      dimensionReviewResolved: "Resolved",
      dimensionReviewStatus: {
        passed: "Passed",
        corrected: "AI corrected",
        needs_manual_review: "Needs review",
        failed: "Review failed"
      },
      collectionUnnamed: "Unnamed product",
      quickSourceTitle: "Quick product picker",
      sourceAssets: "Asset Library",
      sourceTasks: "Task List",
      sourceSearchPlaceholder: "Search by SKU ID / 1688 ID / title / link",
      sourceSearchHint: "Supports SKU ID, 1688 ID, collection/task ID, title and link fuzzy search.",
      sourcesLoading: "Loading products...",
      sourcesEmpty: "No matching products found.",
      sourcesLoadFailedDesc: "Failed to load quick picker list.",
      sourceUploaded: "Uploaded",
      sourcePending: "Not uploaded",
      inputConfirm: "Confirm input",
      inputConfirmShort: "Confirm",
      sourceNotReadyTitle: "Unavailable",
      sourceNotReadyDesc: "This task has no collection yet, and cannot be used for pricing.",
      syncToPreview: "Sync price to preview",
      retrySyncToPreview: "Retry preview sync",
      syncingPreview: "Syncing...",
      syncPreviewHint: "After applying, price is auto-synced to Studio preview draft: listing price = buyer price ÷ 0.6 (fixed 40% discount).",
      syncPreviewMissingCollection: "Missing collection context. Open from Studio preview or pick one above first.",
      syncPreviewInvalidRate: "Exchange rate invalid, unable to convert RUB to CNY.",
      syncPreviewInvalidPrice: "Suggested price invalid, unable to sync.",
      syncPreviewFailed: "Sync failed",
      syncPreviewFailedDesc: "Unable to sync latest pricing details to Studio preview.",
      syncPreviewSuccess: "Synced to preview",
      syncPreviewSuccessDesc: "Latest pricing details have been written to Studio preview draft.",
      logisticsWarningNonBlocking: "Logistics is currently unmatched. You can still sync and save. Please review dimensions/weight or switch provider, then recalculate.",
      logisticsWarningBackendFlag: "Backend marked this result as logistics risk. You can continue, but fixing logistics first is recommended.",
      ozonMinProfitSyncHint: "Current Ozon minimum profit: ¥{value}",
      ozonTieredProfitSyncHint: "Current Ozon tier target: ¥{value} ({range})",
      ozonStrategyTitle: "Ozon Generation Strategy",
      ozonStrategyDesc: "These settings are persisted and used by Ozon generation price stage.",
      ozonProfitRate: "Target Profit Rate",
      ozonMinProfit: "Minimum Profit",
      ozonPromotionReserve: "Promotion Reserve",
      ozonOperationMode: "Operation Mode",
      ozonAdvertisingStrategy: "Advertising Strategy",
      ozonAdvertisingNone: "None",
      ozonAdvertisingCpoCpc: "CPO / CPC",
      ozonAdvertisingAggressive: "Aggressive",
      ozonCrossBorder: "Seller Type",
      ozonCrossBorderOn: "Cross-border",
      ozonCrossBorderOff: "Domestic",
      ozonDefaultCategory: "Default Category Code",
      ozonExchangeRate: "Custom FX (optional)",
      ozonDomesticShipping: "Domestic Shipping",
      ozonLabelingFee: "Labeling Fee",
      ozonReturnRate: "Return Rate",
      ozonWithdrawalRate: "Withdrawal Rate",
      ozonOrderProcessingRate: "Order Processing Rate",
      ozonAcquiringRate: "Acquiring Rate",
      ozonAgentRate: "Ozon Agent Rate",
      ozonPartnerDeliveryRate: "Partner Delivery Rate",
      logisticsProvider: "Logistics Provider",
      logisticsProviderMode: "Provider Selection",
      logisticsProviderModeAuto: "Auto (Recommended)",
      logisticsProviderModeManual: "Manual",
      logisticsProviderUni: "UNI (Legacy)",
      logisticsProviderUral: "Ural (2026-03 Quote)",
      autoModeLabel: "Auto",
      logisticsManagedByStore: "Logistics channels are managed in Store Settings.",
      logisticsManagedByStoreDesc: "Current pricing uses store-level logistics selection. Store ID: {storeId}",
      logisticsManageInStoreSettings: "Open Store Settings",
      logisticsSyncChannelsNow: "Sync channels now",
      logisticsSyncingChannels: "Syncing channels...",
      logisticsSyncChannelsSuccess: "Logistics channels synced.",
      logisticsSyncChannelsFailed: "Failed to sync logistics channels.",
      ozonDiscountExplainTitle: "Fixed 40% discount policy",
      ozonDiscountExplainLine: "Buyer price is calculated first, then converted to listing price. Discount: {discount}, pay factor: {pay_factor}. Buyer pays {target_rub}, set listing price to {listing_rub}.",
      ozonDiscountExplainFormula: "Formula: buyer pays = listing × 0.6. So listing = {target_cny} ÷ 0.6 = {listing_cny}.",
      saveScenario: "Save Scenario",
      prodMetadata: "Product Metadata (Locked)",
      prodCategory: "Product Category",
      purchaseCost: "Purchase Cost (CNY)",
      weight: "Weight (g)",
      dimensions: "Dimensions (L*W*H mm)",
      dimensionLengthMm: "Length (mm)",
      dimensionWidthMm: "Width (mm)",
      dimensionHeightMm: "Height (mm)",
      dimensionDepthMm: "Depth (mm)",
      logistics: "Logistics",
      air: "Air",
      sea: "Sea",
      saveWorkspace: "Save this calculation to your workspace",
      saveWorkspaceDesc: "Sync directly to Ozon and track your margin history by creating a free account.",
      createAccount: "Create Free Account",
      basicCosts: "Base Costs",
      costCurrency: "Cost currency: CNY → {currency}",
      purchaseCostLabel: "Purchase Cost",
      shippingCostLabel: "Shipping Cost",
      shippingEst: "Estimated International Shipping",
      estimatedShipping: "Estimated Shipping",
      estimatedShippingHint: "Estimated from current weight, dimensions, and shipping mode.",
      packagingCostLabel: "Packaging / Prep Cost",
      otherCostLabel: "Other Cost",
      estimatedTag: "Estimated",
      shippingCharged: "Shipping Charged to Buyer",
      exchangeRateLabel: "Exchange Rate (1 CNY → {currency})",
      inputGuardrailInvalid: "Please enter a valid number ({range}). Auto-corrected to {value}.",
      inputGuardrailAdjusted: "Accepted range: {range}. Auto-corrected to {value}.",
      inputRangeBetween: "{min} - {max}",
      inputRangeMinimum: "≥ {min}",
      platformFees: "Platform Fee Inputs",
      marginCapHint: "Current practical margin cap: {value}%",
      categoryLabel: "Category / Segment",
      referralFeeLabel: "Referral Fee",
      referralRateLabel: "Referral / Commission Rate",
      ozonCommissionTierLow: "Low-price tier",
      ozonCommissionTierMid: "Mid-price tier",
      ozonCommissionTierHigh: "High-price tier",
      ozonCommissionTierActive: "Active",
      ozonCommissionTierRangeLow: "≤ {upper} RUB",
      ozonCommissionTierRangeMid: "{lower} - {upper} RUB",
      ozonCommissionTierRangeHigh: "> {lower} RUB",
      ozonCommissionTierHint: "Commission is tiered by target price: ≤{low} RUB uses low tier, ≤{mid} RUB uses mid tier, and >{mid} RUB uses high tier.",
      ozonCommissionCurrentHint: "Current target price {price} falls into {tier}, effective commission {rate}% ({mode_adjust}).",
      ozonCommissionModeAdjustFbo: "FBO adjustment {delta}%",
      ozonCommissionModeAdjustDefault: "default rate does not apply mode adjustment",
      ozonCommissionModeAdjustNone: "no mode adjustment",
      ozonEffectiveCommissionLabel: "Effective Commission (Tiered)",
      ozonEffectiveCommissionFeeLabel: "Marketplace Commission (effective: {tier}, {rate}%)",
      ozonDefaultCommissionFeeLabel: "Marketplace Commission (default: {rate}%, unmatched category)",
      ozonUnmatchedCategoryTag: "Default 15% (Unmatched)",
      ozonUnmatchedCategoryOptionRate: "default {rate}%",
      ozonUnmatchedCategoryHint: "No matched category found. Using default commission {rate}% (not tied to any category, no tiering).",
      commissionRate: "Commission Rate",
      referralMinFeeLabel: "Minimum Referral Fee",
      sellingPlan: "Selling Plan",
      sellingPlanIndividual: "Individual",
      sellingPlanProfessional: "Professional",
      sellingPlanFee: "Per-item Selling Fee",
      closingFee: "Closing Fee",
      closingFeeLabel: "Closing Fee",
      fulfillmentMode: "Fulfillment Mode",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "Fulfillment Fee",
      storageFee: "Storage Fee",
      inboundShippingFee: "Inbound Shipping Fee",
      fbmHandlingFee: "FBM Handling / Pick Fee",
      platformDiscount: "Platform Discount",
      tax: "Tax / VAT",
      sellerDiscount: "Seller Discount",
      affiliateRate: "Affiliate Fee Rate",
      affiliatePartnerRate: "Affiliate Partner Rate",
      adsRate: "Ads / Promotion Rate",
      marketingCost: "Marketing Cost",
      shippingType: "Shipping Option",
      shippingPlatform: "Platform Shipping",
      shippingSeller: "Seller Shipping",
      shippingFbt: "FBT (Fulfilled by TikTok)",
      fbtFulfillmentFee: "FBT Fulfillment Fee",
      fbtStorageFee: "FBT Storage Fee",
      transactionFeeRate: "Transaction Fee Rate",
      transactionFeeFixed: "Transaction Fixed Fee",
      serviceFeeRate: "Service Fee Rate",
      serviceFeeFixed: "Service Fixed Fee",
      orderServiceFee: "Order Service Fee",
      platformSupportFee: "Platform Support Fee",
      platformSupportFeeHint: "Some markets charge a fixed support fee per order (e.g., MY).",
      shippingSubsidy: "Shipping Subsidy / Program Cost",
      ozonPlacementFee: "Placement / Service Fee",
      ebayFinalValueRate: "Final Value Fee Rate",
      ebayPerOrderFee: "Per-order Fixed Fee",
      ebayInsertionFee: "Insertion Fee",
      ebayInternationalRate: "International Fee Rate",
      etsyListingFee: "Listing Fee",
      etsyTransactionFee: "Transaction Fee Rate",
      etsyPaymentRate: "Payment Processing Rate",
      paymentFeeRate: "Payment Fee Rate",
      etsyPaymentFixed: "Payment Fixed Fee",
      etsyOffsiteAdsRate: "Offsite Ads Rate",
      shippingSupportRate: "Shipping Support Rate",
      costItemTransactionFee: "Transaction Fee",
      costItemServiceFee: "Service Fee",
      costItemAds: "Ads",
      costItemAffiliate: "Affiliate",
      costItemFinalValueFee: "Final Value Fee",
      costItemOrderFee: "Order Fee",
      costItemInternationalFee: "International Fee",
      costItemPaymentProcessing: "Payment Processing",
      costItemOffsiteAds: "Offsite Ads",
      totalCost: "Total Cost",
      totalBaseCost: "Total Base Cost",
      platformFeeTotal: "Platform Fees",
      baseCostTotal: "Base Cost",
      platformInsights: "Platform Insights",
      marketingCosts: "Marketing & Creator Costs",
      temuProgram: "Temu Incentives",
      aliexpressProgram: "AliExpress Campaigns",
      shopeeProgram: "Shopee Programs",
      temuHeroTitle: "Temu Low Commission Stack",
      temuHeroDesc: "Commission is often 0%. Track service fees, fulfillment, and subsidies separately.",
      temuHeroTag1: "Zero Commission",
      temuHeroTag2: "Service Fee Adjustable",
      aliexpressHeroTitle: "AliExpress Category Commission",
      aliexpressHeroDesc: "Commission varies by category. Use this panel to model payment fees and campaigns.",
      aliexpressHeroTag1: "5-8% Commission",
      aliexpressHeroTag2: "No Annual Fee",
      shopeeHeroTitle: "Shopee Fee Stack",
      shopeeHeroDesc: "Combine commission, transaction, and optional program fees.",
      shopeeHeroTag1: "Multi-fee",
      shopeeHeroTag2: "Program Optional",
      shopeeHeroTag3: "Support Fee",
      amazonHighlightTitle: "Amazon Plan & Fulfillment Snapshot",
      amazonHighlightDesc: "Model referral fees, plan charges, and FBA/FBM costs together to find the right floor price.",
      ebayHighlightTitle: "eBay Final Value Fee Focus",
      ebayHighlightDesc: "Final value fees apply to item + shipping + tax; add per-order and international fees if applicable.",
      walmartHighlightTitle: "Walmart Referral Fee Focus",
      walmartHighlightDesc: "Referral fee is category-based; WFS and other fulfillment costs can be added separately.",
      etsyHighlightTitle: "Etsy Listing Cycle Snapshot",
      etsyHighlightDesc: "Listing fee, transaction fee, payment processing, and offsite ads can be toggled for each item.",
      platformNotesAmazon: "Includes referral fee (category-based), per-item fee (Individual plan), and fulfillment fees (FBA/FBM).",
      platformNotesTikTok: "Fees may include commission (category-based), transaction fee, affiliate commission, and optional FBT fulfillment costs.",
      platformNotesOzon: "Commission depends on category/mode; shipping, withdrawal and return reserve are estimated items.",
      platformNotesEbay: "Final value fees apply to item + shipping + tax; per-order fee is added separately.",
      platformNotesWalmart: "Referral fee varies by category; other fulfillment costs are excluded in this quick calculator.",
      platformNotesEtsy: "Includes listing fee, transaction fee, payment processing fee, and optional offsite ads.",
      platformNotesTemu: "Temu commission can be 0%, while a small service fee or fulfillment cost may apply depending on program.",
      platformNotesAliExpress: "AliExpress commission varies by category; payment or order service fees may apply by region.",
      platformNotesShopee: "Shopee fees typically include commission, transaction fee, and optional program/support fees.",

      // Strategy presets (new)
      selectStrategy: "Select Pricing Strategy",
      strategyHint: "Choose based on your business goals",
      strategyVolume: "Quick Volume",
      strategyVolumeDesc: "Lower margin (15-20%) to quickly gain market share",
      strategyBalanced: "Balanced Growth",
      strategyBalancedDesc: "Moderate margin (25%) balancing sales and profit",
      strategyProfit: "Profit First",
      strategyProfitDesc: "Higher margin (35%) maximizing per-unit profit",
      strategyCustom: "Custom Strategy",
      strategyCustomDesc: "Full manual control over all parameters",
      strategyScenarioLabel: "Use Case",
      strategyRiskLabel: "Risk",
      strategyVolumeScenario: "Suitable for new launches or campaigns needing fast order momentum.",
      strategyVolumeRisk: "Lower unit margin leaves less room for ad volatility and returns.",
      strategyBalancedScenario: "Suitable for steady growth phases that need both conversion and margin.",
      strategyBalancedRisk: "Needs disciplined discount control to avoid gradual margin erosion.",
      strategyProfitScenario: "Suitable for differentiated products with stable demand and pricing power.",
      strategyProfitRisk: "Higher list price may reduce conversion if competitive pressure rises.",
      strategyCustomScenario: "Suitable when you already have validated cost and channel benchmarks.",
      strategyCustomRisk: "Manual tuning requires continuous monitoring to avoid out-of-range targets.",
      strategyImpactTitle: "Strategy Impact",
      strategyImpactSubtitle: "Key effects after switching this preset",
      strategyVolumeImpact1: "Suggested price baseline drops to improve click-through and traffic intake.",
      strategyVolumeImpact2: "Inventory turnover speed increases, but each order contributes less net profit.",
      strategyVolumeImpact3: "Ad budget should be capped tightly to prevent margin compression.",
      strategyBalancedImpact1: "Keeps margin and conversion in a middle band for stable weekly performance.",
      strategyBalancedImpact2: "Supports iterative testing without large swings in listing price.",
      strategyBalancedImpact3: "Provides enough profit buffer for moderate promotions.",
      strategyProfitImpact1: "Raises unit contribution margin to prioritize net profit per sale.",
      strategyProfitImpact2: "Requires stronger differentiation in content, reviews, and fulfillment promise.",
      strategyProfitImpact3: "Best paired with controlled traffic quality instead of broad traffic buys.",
      strategyCustomImpact1: "Current target inputs become the main pricing driver.",
      strategyCustomImpact2: "You can iterate quickly for special campaigns or category experiments.",
      strategyCustomImpact3: "Please verify range limits frequently to keep calculation outputs stable.",
      recommended: "Recommended",
      estimatedProfit: "Est. Profit",
      estimatedSales: "Est. Monthly Sales",
      profitTarget: "Profit Target",
      profitTargetHint: "Choose margin, fixed profit, or tiered profit mode.",
      enabled: "Enabled",

      // Slider
      adjusting: "Adjusting...",

      // Actions
      saving: "Saving...",
      applying: "Applying...",

      // Cost breakdown
      clickForDetails: "Click segments for details",

      // Logistics
      logisticsHint: "Estimate shipping impact based on weight and dimensions.",

      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    // Copyright
    copyright: {
      nav: "Copyright Assets",
      title: "Copyright & Legal Assets",
      subtitle: "One-stop evidence bundles for instant IP enforcement and copyright protection.",
      statusActive: "Legal Security Status: Active",
      protected: "Protected Listings",
      certified: "Certified Bundles",
      onChain: "On-Chain Proofs",
      ready: "Enforcement Ready",
      filter: "Filter",
      searchPlaceholder: "Search by product name or SKU...",
      table: {
        preview: "Asset Preview",
        product: "Product / Platform",
        status: "Certification Status",
        bundle: "Legal Evidence Bundle",
        actions: "Actions",
        download: "Download Full Legal Bundle",
        previewBtn: "Preview",
        synced: "Synced: ",
        certified: "Certified",
        docs: "High-Res Docs"
      },
      bundleNav: {
        back: "Back",
        details: "Bundle Details",
        downloadCurrent: "Download Current",
        downloadAll: "Download Full Evidence Pack (.zip)",
        contents: "Bundle Contents",
        letter: "Official Authorization Letter",
        psd: "Forensic PSD Source",
        watermark: "Neural Watermark Analysis",
        blockchain: "Blockchain Proof-of-Creation",
        dispute: "IP Dispute Template",
        securityHash: "Security Hash"
      },
      tabs: {
        docView: "DOCUMENT VIEW",
        metaDash: "METADATA DASHBOARD",
        xray: "X-RAY ANALYSIS"
      },
      letter: {
        title: "Platform Authorization Letter",
        seal: "OFFICIAL SEAL APPLIED",
        certTitle: "Certificate of Authenticity",
        certSub: "Official Legal Evidence Record",
        docId: "Document ID",
        date: "Date of Issuance",
        subject: "Subject Matter Identification",
        prodName: "Product Name",
        sku: "Asset SKU",
        jurisdiction: "Platform Jurisdiction",
        grade: "Evidence Grade",
        gradeValue: "Forensic Certified",
        body1: "This document constitutes formal legal evidence that the asset described above has been verified and registered within the OneClickSell Intellectual Property Protection Vault.",
        body2: "Verification includes forensic analysis of source PSD files, embedding of neural watermarks persistent through transformation, and timestamping on the public blockchain ledger.",
        officer: "Legal Compliance Officer",
        signature: "Digitally signed: ",
        footer1: "Secure Forensic Chain: #991-AUTH-Z",
        footer2: "Proprietary Evidence Format v4.2"
      },
      psd: {
        reportTitle: "Forensic Metadata Report",
        verified: "Metadata Verified",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 Layers",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "Technical Property",
        colValue: "Forensic Value",
        created: "Creation Timestamp",
        createdDesc: "File system origin date",
        fingerprint: "Neural Fingerprint (SHA-256)",
        fingerprintDesc: "Unique mathematical identity",
        author: "Author ID",
        authorDesc: "Verified creator signature",
        software: "Software Signature",
        softwareDesc: "Engine and build version",
        seed: "Embedded Watermark Seed",
        seedDesc: "Persistent neural tracer",
        engine: "Analysis Engine",
        integrity: "Integrity Check",
        secure: "100% SECURE"
      },
      watermark: {
        title: "Neural Watermark X-Ray",
        scanning: "Scanning Neural Layers...",
        status: "Status",
        detected: "NEURAL WATERMARK DETECTED",
        score: "Resilience Score",
        stable: "CRYPTO-STABLE",
        encodingTitle: "Neural Encoding",
        encodingDesc: "This watermark is embedded at the neural level during generative diffusion. Unlike traditional pixel overlays, this signature is part of the image's weights, making it resistant to AI-based erasure, cropping, or noise injection.",
        latentSeed: "Latent Seed",
        engineSig: "Engine Signature",
        inference: "Inference Time",
        confidence: "Scan Confidence"
      },
      blockchain: {
        title: "Blockchain Verification Certificate",
        subtitle: "Immutable Proof of Asset Origination",
        verified: "Verified On-Chain",
        network: "Network:",
        networkVal: "Arbitrum One / Ethereum Mainnet",
        txHash: "Transaction Hash (TxID)",
        signature: "Digital Signature (EdDSA)",
        block: "Block Height",
        timestamp: "Timestamp (UTC)",
        legalNoteTitle: "Legal Admissibility Note",
        legalNoteDesc: "This certificate provides a cryptographically secure timestamp and authorship record. The record is permanently anchored to the Arbitrum L2 network, inheriting the full security of the Ethereum settlement layer.",
        scan: "Scan to verify",
        verifStatus: "Verification Status",
        protocol: "OneClick-ChainLink Oracle v2",
        confirmed: "CONFIRMED",
        confirmations: "Node Confirmations",
        integrity: "Data Integrity",
        secure: "100% SECURE"
      },
      dispute: {
        preview: "PREVIEW DOCUMENT",
        settings: "TEMPLATE SETTINGS",
        language: "Language:",
        title: "IP Infringement Takedown Notice",
        subtitle: "Automated Legal Compliance Document",
        to: "To: Legal Compliance Department",
        ref: "Ref:",
        date: "Date:",
        s1Title: "1. Statement of Ownership",
        s1Body: "I, the undersigned, hereby declare under penalty of perjury that I am the authorized representative of the copyright owner for the intellectual property described herein. OneClickSell Inc. maintains exclusive rights to the proprietary digital assets associated with this claim.",
        s2Title: "2. Description of Infringement",
        s2Body: "We have identified unauthorized use of our copyrighted visual assets at the following location:",
        s2Url: "URL:",
        s2Desc: "This listing utilizes proprietary product imagery and creative layouts that are identical or substantially similar to our original works.",
        s3Title: "3. Evidence of Originality",
        s3Body: "The originality and creation date of the disputed assets are verified through the following cryptographic methods included in the attached Evidence Pack:",
        s3Item1: "Neural Watermark:",
        s3Item1Desc: " Invisible forensic markers embedded within the image data matching our master files.",
        s3Item2: "Blockchain Proof:",
        s3Item2Desc: " Immutable proof-of-creation recorded on the Arbitrum Mainnet.",
        s4Title: "4. Legal Declaration",
        s4Body: "I have a good faith belief that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law. The information in this notification is accurate. We request the immediate removal of the infringing material.",
        signature: "Authorized Signature",
        division: "IP Protection Division, OneClickSell Inc.",

        sidebar: {
          verifStatus: "Verification Status",
          link: "Infringing Link",
          linkDesc: "URL provided",
          psd: "Forensic PSD",
          psdDesc: "Layer data verified",
          evidence: "Additional Evidence",
          missing: "2 modules missing",
          prob: "Enforcement Probability",
          lowConf: "Low Confidence",
          guide: "Evidence Completion Guide",
          scan: "Neural Watermark Scan",
          boost30: "Missing: +30% boost",
          complete: "Complete Now",
          proof: "Blockchain Proof",
          boost22: "Missing: +22% boost",
          autoFiling: "Automated Filing",
          filingNote: "Filing not recommended until confidence is above 80%.",
          autoSubmit: "Auto-Submit Claim",
          processing: "Processing...",
          submitted: "Claim Submitted"
        }
      }
    },

    // Workspace
    workspace: {
      nav: "Workspace",
      newUserDesc: "Import your first product to unlock the full workflow.",
      heroTitle: "Create Your First AI Listing",
      heroSub: "Paste a link → AI Optimization → One-click Publishing. Transform product data in seconds.",
      returningLabel: "Welcome back",
      returningTitle: "Pick up where you left off",
      returningSub: "Your latest imports are ready. Continue polishing listings or jump into the task center.",
      startBtn: "Start Importing Products",
      taskCenterEntry: "Open Task Center",
      pasteLink: "Paste Link",
      pasteDesc: "Import from AliExpress, 1688, Amazon and more.",
      uploadMedia: "Upload Media",
      uploadDesc: "Drag & drop images, videos or ZIP files.",
      batchImport: "Batch Import",
      batchDesc: "Import products in bulk via CSV or multiple URLs.",
      recentImports: "Recent Imports",
      recentImportsDesc: "Your latest imports and their current status",
      viewHistory: "View History",
      extRequired: "Chrome Extension Required",
      extNotFound: "Not Found",
      extOutdated: "Outdated",
      extDesc: "Required for secure data collection from major platforms.",
      extDownload: "Download Extension",
      extUpdate: "Update Extension",
      extRecheck: "I've Updated",
      extUpgradeRequired: "Extension update required",
      extUpgradeDesc: "Current version {current}. Minimum required is {required}. Please update before importing.",
      extVersionUnknown: "Unknown",
      watchTutorial: "Watch Video Tutorial",
      readGuide: "Read User Guide",
      status: {
        processing: "Processing",
        ready: "Ready to Publish",
        uploaded: "Uploaded",
        failed: "Failed"
      },
      loadFailed: "Failed to load recent imports.",
      loadHint: "Please retry to fetch your latest tasks.",
      retry: "Retry",
      emptyTitle: "No imports yet",
      emptyDesc: "Start by importing a product link to see progress here.",
      loginTitle: "Log in to view your imports",
      loginDesc: "Sign in to sync your import history and workflow status.",
      loginAction: "Log In",
      unknownSource: "Unknown Source",
      totalImports: "Total Imports",
      completed: "Completed",
      processing: "Processing"
    },

    // Task Center
    taskCenter: {
      nav: "Workbench",
      breadcrumb: {
        workspace: "Workspace",
        taskCenter: "Task Center"
      },
      title: "Unified Workbench",
      subtitle: "Manage tasks and completed assets in one page with less context switching.",
      topBarSearchPlaceholder: "Search 1688 link / product name / SKU / Ozon ID / asset ID",
      target: {
        label: "Target:",
        platform: "Amazon",
        region: "United States",
        language: "English"
      },
      sync: {
        label: "Sync Progress",
        value: "85%"
      },
      reviewChanges: "Review Changes",
      noReviewableTasks: "No changes to review yet.",
      createTask: "Create New Task",
      dropdown: {
        singleTitle: "Single Product Task",
        singleDesc: "Import via a single URL",
        batchTitle: "Batch Import Task",
        batchDesc: "Import via multiple URLs or CSV"
      },
      table: {
        source: "Source",
        task: "Task Name / ID",
        status: "Status",
        progress: "AI Progress",
        createdAt: "Created At",
        action: "Action"
      },
      sourceUnavailable: "No source link available",
      dailyGoal: {
        title: "Today's listing goal",
        subtitle: "Track successful publishes in real time",
        currentStore: "Current store: {store}",
        currentStoreShort: "Current store",
        workingStoreLabel: "Working store",
        switchStore: "Switch store",
        quickEdit: "Quick edit",
        progressLabel: "Progress",
        openSettings: "Settings",
        badges: {
          completed: "Completed",
          behind: "Behind",
          error: "Load issue",
          disabled: "Off",
          active: "In progress"
        },
        summary: {
          disabled: "Daily goal display is off. You can enable it anytime.",
          completed: "You have completed {count} publishes today and reached the goal.",
          behind: "Progress is a bit behind. {remaining} more to reach the goal.",
          start: "You have not started today yet. Goal: {target}.",
          progress: "Completed {completed}/{target} today, {remaining} remaining."
        },
        disabledValue: "Off",
        targetLabel: "Target",
        remainingLabel: "Remaining",
        updatedLabel: "Updated",
        todayCount: "{count} published today",
        quickEnableHint: "Enable it to show real-time progress in Task Center.",
        updatedAt: "Updated {time}",
        collapseToIcon: "Minimize widget",
        restoreCard: "Expand today's goal",
        hideCard: "Turn off in settings",
        displaySwitchLabel: "Show goal",
        enableCard: "Enable goal",
        quickEditHint: "Adjust today's target and display preference here.",
        targetInputLabel: "Today's target",
        saveAction: "Save"
      },
      filters: {
        failed: "Failed"
      },
      status: {
        active: "Active",
        completed: "Completed",
        waiting: "Waiting",
        idle: "Idle",
        collect_pending: "Waiting for capture",
        assigned: "Capture assigned",
        collecting: "Collecting",
        collect_uploading: "Uploading",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        pending_generate: "Waiting to generate",
        generating: "Generating",
        publishing: "Publishing",
        publish_reviewing: "Publish reviewing",
        publish_fix_required: "Needs fixes",
        ready: "Ready to publish",
        uploaded: "Sellable",
        publish_failed: "Publish failed",
        generation_failed: "Generation failed",
        variant_pending: "Waiting for execution",
        variant_running: "Variant running",
        variant_completed: "Variant completed",
        variant_preview_ready: "Preview ready",
        variant_partial: "Partially publishable",
        variant_blocked: "Variant blocked",
        variant_failed: "Variant failed",
        variant_cancelled: "Variant cancelled"
      },
      batchLabel: "Batch",
      itemsLabel: "Items",
      progress: {
        processing: "Processing",
        copywriting: "AI Copywriting",
        inQueue: "Waiting",
        summary: {
          queueing: "Task submitted. Waiting for execution resources.",
          collecting: "Collecting product data and syncing captured assets.",
          prepareGenerate: "Capture is complete. Waiting for manual generation start.",
          generating: "AI is generating content stage by stage.",
          publishing: "Publishing to store and waiting for platform feedback.",
          variantRunning: "Variant generation is running.",
          variantPreviewReady: "Variant preview is ready. Select items to publish next.",
          completed: "Task completed successfully.",
          failed: "Task failed. You can retry after checking the error.",
          stuckRecoverable: "No recent progress detected. You can refresh or retry.",
          stuckUnrecoverable: "Task state is abnormal and cannot recover automatically."
        },
        detail: {
          currentStageAndLastUpdated: "{detail}"
        }
      },
      stage: {
        collect_pending: "Waiting for capture",
        collect_assigned: "Plugin assigned",
        collect_preparing: "Preparing capture",
        collect_scrolling: "Scrolling page",
        collect_collecting: "Collecting data",
        collect_saving: "Saving data",
        collect_uploading: "Uploading assets",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        generate_pending: "Waiting to generate",
        generate_info_structure: "Product understanding",
        generate_seo: "SEO keyword graph",
        generate_listing: "Listing writing",
        generate_visual: "Image generation",
        generate_adapter: "Platform adapter",
        generate_export: "Export delivery",
        publish_pending: "Preparing publish",
        publish_processing: "Publishing",
        publish_reviewing: "Under review",
        publish_scheduled: "Scheduled",
        publish_failed: "Publish failed",
        publish_completed: "Published",
        generate_ready: "Ready to publish",
        generate_uploaded: "Published",
        generate_failed: "Generation failed"
      },
      actions: {
        workspace: "Workspace",
        preview: "Preview",
        generate: "Start AI",
        publish: "Publish",
        viewPublish: "View Publish",
        goEdit: "Go Edit",
        goOzonBackend: "Open Ozon Backend",
        fixChoiceTitle: "Choose a fix path",
        fixChoiceDesc: "The listing is already created but has fix-required issues. Choose where to continue.",
        fixChoiceOzonHint: "Open publish details first, then jump to Ozon backend to edit and refresh status.",
        refreshPublishStatus: "Refresh Status",
        refreshPublishStatusSuccess: "Publish status refreshed.",
        refreshPublishStatusFailed: "Failed to refresh publish status.",
        createVariantPending: "Awaiting approval",
        createVariantPendingHint: "Variant creation is available after master SKU review is approved.",
        createVariantFixRequired: "Fix required",
        createVariantFixRequiredHint: "Resolve publish issues first, then refresh status to unlock variant creation.",
        update: "Update Listing",
        batchOverview: "Batch Overview",
        assets: "Open Asset Surface",
        viewProgress: "View Progress",
        retry: "Retry",
        retryTask: "Retry Task",
        retryCollect: "Retry Capture",
        retryGenerate: "Retry Generate",
        createVariant: "Create Variants",
        viewVariantProgress: "Variant Progress",
        viewVariantResult: "Variant Result",
        reviewAndPublishVariant: "Review & Publish",
        continueVariantTask: "Continue Variants",
        handleVariantException: "Handle Variant Issue",
        viewVariantBlockReason: "View Block Reason",
        viewVariantRecord: "View Variant Record",
        retryVariant: "Retry Variants",
        cancelCollect: "Cancel Local Collect",
        forceStopRetry: "Force Stop & Retry",
        pending: "Pending",
        managementDisabledFallback: "Action unavailable right now. Please try again later.",
        managementMissingTarget: "Task metadata is incomplete. This action is temporarily unavailable.",
        archive: "Archive",
        delete: "Delete"
      },
      tooltips: {
        previewLocked: "Preview is available after collection completes",
        previewUnavailable: "Preview requires a completed capture task",
        previewCollecting: "Capture in progress. Preview will be available soon.",
        previewFailed: "Capture failed. No preview data available.",
        expand: "Expand batch",
        collapse: "Collapse batch",
        errorFallback: "Task failed. Please retry or contact support.",
        publishFixRequiredFallback: "Published but requires fixes. Open publish details to resolve issues.",
        workspaceLocked: "Workspace is available once capture and AI processing are complete",
        actionLocked: "Task is processing. Try again later."
      },
      confirmArchive: "Archive this task?",
      confirmDelete: "Delete this task? This action cannot be undone.",
      actionFailed: "Operation failed. Please try again.",
      generateNotReady: "Collection data is still being prepared. We will update this card automatically.",
      retryUnavailable: "This task does not have a retryable capture ID. Please import again to start a new task.",
      errorSummaryDefault: "Task failed. Please retry.",
      manualReview: {
        shortHint: "Check Ozon backend. The item may have been partially created.",
        detailHint:
          "Ozon can return failed status while some data is already created. Open Ozon Seller backend to review exact errors and manually fix fields if needed. AI-generated data is not always 100% accurate."
      },
      pagination: {
        showing: "Showing {count} of {total} tasks",
        showingRange: "Showing {start}-{end} / {total} (Page {page}/{totalPages})",
        first: "First",
        last: "Last",
        perPage: "Per page"
      },
      stats: {
        total: "Total",
        active: "Active",
        completed: "Completed",
        failed: "Failed",
        waiting: "Waiting",
        successRate: "Success Rate",
        avgTime: "Avg. Completion Time",
        performance: "Performance"
      },
      bulk: {
        selected: "{count} selected",
        selectedAllFiltered: "All {count} matching tasks selected",
        retry: "Retry",
        refreshStatus: "Refresh Status",
        archive: "Archive",
        delete: "Delete",
        deleting: "Deleting",
        archiving: "Archiving...",
        retrying: "Retrying...",
        refreshingStatus: "Refreshing status...",
        deleteImpactCalculating: "Calculating delete impact...",
        deleteWaitingBackend: "Waiting for backend delete confirmation...",
        deleteImpactFailed: "Failed to calculate delete impact. Stop and retry before deleting.",
        deleteRefreshing: "Refreshing task cards...",
        selectAll: "Select All",
        selectCurrentPage: "Select current page",
        selectAllFiltered: "Select all matching ({count})",
        deselectAll: "Deselect All",
        confirmArchive: "Archive {count} selected tasks?",
        confirmDelete: "Delete {count} selected tasks? This cannot be undone.",
        confirmDeleteTitle1: "Confirm delete",
        confirmDeleteTitle2: "Confirm delete again",
        confirmDeleteTitle3: "Final confirm delete",
        confirmDeleteStep1: "Delete {count} selected tasks?",
        confirmDeleteStep2: "Confirm delete {count} tasks?",
        confirmDeleteStep3: "Final confirm: delete {count} tasks?"
      },
      view: {
        cards: "Cards",
        list: "List",
        compact: "Compact"
      },
      batch: "Batch",
      refresh: "Refresh",
      autoRefresh: {
        activeTitle: "Auto refresh enabled",
        pausedTitle: "Auto refresh paused",
        runningSummary: "Running tasks: {count}",
        waitingSummary: "Waiting tasks: {count}",
        noAutoRefreshTasks: "No tasks need automatic refresh right now.",
        activeCadence: "Running tasks refresh every {seconds} seconds",
        waitingCadence: "Waiting tasks refresh every {seconds} seconds",
        manualOnlyCadence: "Auto updates are temporarily unavailable",
        lastRefresh: "Last refreshed: {time}",
        neverRefreshed: "Not refreshed yet",
        backgroundHint: "Auto refresh pauses when this page is in the background.",
        pausedDetail: "This page is currently in the background, so auto refresh is paused. It will resume when you return.",
        refreshCadenceLabel: "Every {seconds}s",
        pausedBadge: "Paused in background"
      },
      exportCSV: "Export CSV",
      clearFilters: "Clear Filters",
      storeFilter: {
        label: "Store",
        allStores: "All stores",
        defaultTag: "Default",
        inactiveTag: "Inactive",
        unboundTag: "Unbound",
        unboundStore: "Unbound store #{id}"
      },
      importComposerToggleExpand: "Show Quick Import",
      importComposerToggleCollapse: "Hide Quick Import",
      surfaceTasks: "Task Queue",
      surfaceAssets: "Asset Results",
      focusTitle: "This view is for scheduling and recovery",
      focusHint: "Use this queue for running or failed jobs. Completed listings belong to the Asset Results surface.",
      focusShowAll: "Show All Tasks",
      focusGoAssets: "Open Asset Surface",
      assetsFocusTitle: "This view is for completed asset operations",
      assetsFocusHint: "Review ready/published results here. For retries and failures, switch back to Task Queue.",
      assetsFilterAll: "All Results",
      assetsFilterReady: "Ready to Publish",
      assetsFilterUploaded: "Published",
      assetsFilterFailed: "Needs Fix",
      filterPanelExpand: "Show Filters",
      filterPanelCollapse: "Hide Filters",
      shortcuts: {
        title: "Keyboard Shortcuts",
        refresh: "Refresh tasks",
        selectAll: "Select all",
        bulkArchive: "Bulk archive",
        bulkDelete: "Bulk delete",
        bulkRetry: "Bulk retry",
        gridView: "Grid view",
        listView: "List view",
        compactView: "Compact view",
        search: "Search",
        newTask: "New task"
      },
      banner: {
        template: "{pipeline}. {brand}. Targeting {target}. Workspace access available upon completion.",
        pipelineMulti: "Multi-agent pipeline active",
        pipelineLite: "Lightweight pipeline active",
        brandOn: "Brand Protection enabled",
        brandOff: "Brand Protection disabled"
      },
      loading: "Loading tasks...",
      emptyTitle: "No tasks yet",
      emptyHint: "Import a product or start a batch to see progress here.",
      emptyAction: "Create Task",
      emptyImportAction: "Import Products",
      emptySampleAction: "View Sample",
      emptyHelpAction: "Help Docs",
      emptyNoActiveTitle: "No active tasks right now",
      emptyNoActiveHint: "Review historical tasks or switch to Asset Results for completed listings.",
      emptyNoActivePrimaryAction: "View All Tasks",
      emptyNoActiveSecondaryAction: "Open Asset Results",
      assetsEmptyTitle: "No completed assets yet",
      assetsEmptyHint: "Create or run tasks first. Completed listings will appear in this asset surface automatically.",
      assetsEmptyPrimaryAction: "Create Task",
      assetsEmptySecondaryAction: "Back to Task Queue",
      loginHint: "Log in to unlock Task Center tracking",
      loginDesc: "Sign in to review collection, AI generation, and upload progress.",
      loginAction: "Log In",
      loadFailed: "Failed to load tasks",
      retryHint: "Please refresh or try again later.",
      retryAction: "Reload",
      modal: {
        title: "Batch Import Task",
        subtitle: "Add multiple products to your catalog simultaneously.",
        productUrls: "Product URLs",
        totalLinks: "Total Unique Links:",
        onePerLine: "One URL per line",
        runModeTitle: "Run Mode",
        runMode: {
          collect: {
            title: "Capture only",
            desc: "Only capture product data. No AI generation or publishing."
          },
          generate: {
            title: "Capture + AI",
            desc: "Capture and run AI generation. Stop at \"Ready to publish\"."
          },
          publish: {
            title: "One-click publish",
            desc: "Capture + AI + automatically publish to selected stores."
          }
        },
        recommended: "Recommended",
        storeTitle: "Publish Stores",
        storeNotBoundTitle: "No Ozon store connected",
        storeNotBoundDesc: "Connect your Ozon API credentials first to enable publishing.",
        storeBindAction: "Connect",
        storeSelected: "{count} selected",
        storeSelectAll: "Select all",
        storeClear: "Clear",
        defaultStore: "Default",
        scheduleTitle: "Publish Time",
        scheduleNow: "Publish now",
        scheduleLater: "Schedule",
        scheduleHint: "Time is based on the system timezone.",
        advancedTitle: "Advanced",
        skipImagesTitle: "Skip image generation",
        skipImagesDesc: "Faster, text-first generation. You can add images later.",
        useCacheTitle: "Reuse cache",
        useCacheDesc: "Reuse previous AI results when available to save time and credits.",
        emptyWarning: "Please add at least one product URL.",
        storeNotBound: "You have not connected an Ozon store yet. Go connect now?",
        storeEmptyWarning: "Please select at least one store to publish.",
        scheduleEmptyWarning: "Please choose the scheduled date and time.",
        fileEmptyWarning: "No valid URLs found in the file.",
        duplicateWarning: "{count} duplicate links detected and automatically removed.",
        uploadTitle: "Upload TXT or CSV file",
        uploadSubtitle: "Max 500 links per batch. One URL per line.",
        uploadHint: "Need a template? Download the example file",
        browseFiles: "Browse Files",
        cancel: "Cancel",
        startBatch: "Start Batch Import",
        startCollect: "Start Capture",
        startGenerate: "Start Capture + AI",
        startPublish: "Start One-click Publish",
        batchConfirmTitle: "Confirm batch import",
        batchConfirmMessage: "This will create {count} collection task(s). Please confirm these are real product links, not sample links.",
        batchConfirmAction: "Confirm import",
        batchPartial: "Batch import partially succeeded",
        batchSuccess: "Batch import submitted."
      },
      batchOverview: {
        title: "Batch Overview",
        subtitle: "See publishing results for every product across stores.",
        back: "Back",
        batchIdLabel: "Batch ID:",
        publishBatch: "Publish to stores",
        publishMore: "Publish to more stores",
        manageSchedule: "Manage schedule",
        refresh: "Refresh",
        exportFailedCsv: "Export failed CSV",
        exportingCsv: "Exporting...",
        exportFailedCsvMessage: "Failed to export. Please try again.",
        loading: "Loading batch overview...",
        loadFailed: "Failed to load batch overview.",
        retryLoad: "Reload",
        empty: "No tasks found in this batch yet.",
        hint: "Tip: Click a store status to open detailed publish progress.",
        searchPlaceholder: "Search title / ID / link...",
        search: "Search",
        clear: "Clear",
        viewLabel: "View",
        view: {
          all: "All",
          failed: "Failed",
          publishing: "Publishing",
          completed: "Completed",
          scheduled: "Scheduled"
        },
        storeColumns: "Store columns",
        storeColumnsHint: "{shown}/{total} shown",
        storeColumnsManage: "Manage",
        pagination: "Showing {start}-{end} of {total}",
        prev: "Prev",
        next: "Next",
        retryAllFailed: "Retry all failed",
        retryStoreFailed: "Retry {count} failed",
        retryItemFailed: "Retry failures",
        retrying: "Retrying...",
        retrySubmitted: "Retry submitted.",
        retryFailed: "Retry failed. Please try again.",
        retryConfirm: "Retry failed publishes in this batch?",
        retryStoreConfirm: "Retry failed publishes for this store?",
        relaunchModal: {
          title: "Smart Recovery Relaunch",
          subtitle: "Scope: {scope}. We analyzed each failed publish and prepared recommended actions.",
          scopeBatch: "Whole batch",
          scopeStore: "Current store",
          scopeSelection: "Selected failures",
          planFailed: "Failed to build recovery plan.",
          noActionable: "No auto-relaunchable failures found in this scope.",
          reasonFallback: "No detailed reason returned.",
          originalSku: "Original SKU",
          newSkuLabel: "New SKU",
          newSkuPlaceholder: "Enter a new SKU",
          newSkuHint: "Only letters, numbers, _ and - are allowed. Max length 50.",
          manualHint: "This failure needs manual handling first (store auth / content fix / support).",
          skippedTitle: "Skipped items ({count})",
          selectedCount: "{count} item(s) selected for relaunch",
          selectAtLeastOne: "Select at least one actionable item.",
          offerIdRequired: "Please complete new SKU before submitting.",
          confirmExecute: "Submit relaunch for {count} item(s)?",
          submit: "Submit Relaunch",
          submitting: "Submitting...",
          executeFailed: "Batch relaunch failed. Please try again.",
          executeSuccess: "Relaunch submitted: {created} created, {skipped} skipped.",
          actions: {
            retry_with_new_offer_id: "New SKU relaunch",
            retry_same_payload: "Retry same payload",
            fix_content_then_retry: "Fix content first",
            store_auth_required: "Store auth required",
            wait_and_poll: "Wait and poll",
            contact_support: "Contact support"
          },
          summary: {
            total: "Analyzed",
            newSku: "New SKU",
            retry: "Same payload",
            manual: "Manual"
          },
          skipped: {
            NOT_IN_BATCH: "Not in this batch",
            NOT_FOUND: "Publish record not found",
            NOT_RETRYABLE_STATUS: "Status is not retryable",
            STORE_FILTERED: "Filtered by store",
            COLLECTION_ID_MISSING: "Missing collection reference",
            STORE_NOT_BOUND: "Store not bound or inactive",
            STRATEGY_NOT_ALLOWED: "Selected strategy does not match failure type",
            INVALID_STRATEGY: "Invalid strategy",
            EMPTY_OFFER_ID: "New SKU is empty",
            INVALID_OFFER_ID_FORMAT: "New SKU format is invalid",
            DUPLICATE_IN_REQUEST: "New SKU duplicated in this request",
            EXISTS_IN_LOCAL: "New SKU already exists in this system",
            EXISTS_IN_OZON: "New SKU already exists in Ozon store",
            CHECK_OZON_FAILED: "Failed to check SKU availability on Ozon",
            OFFER_ID_UNAVAILABLE: "New SKU is unavailable",
            RECOVERY_PLAN_BUILD_FAILED: "Failed to prepare SKU suggestion",
            DUPLICATE_PUBLISH_ID: "Duplicated publish id in request",
            UNKNOWN: "Unknown reason"
          }
        },
        missingBatch: "Missing batch ID.",
        notStarted: "Not started",
        notSelected: "Not selected",
        storeInactive: "Inactive",
        openLink: "Open listing",
        failedCount: "{count} failed",
        storeFilter: {
          title: "Store columns",
          subtitle: "Choose which stores to display in the table.",
          selected: "{count} selected",
          selectAll: "Select all",
          activeOnly: "Active only",
          clear: "Clear",
          save: "Save",
          empty: "Please select at least one store column."
        },
        publishModal: {
          title: "Publish batch",
          subtitle: "Select stores and schedule the publish time.",
          storeTitle: "Stores",
          selectAll: "Select all",
          clear: "Clear",
          noActiveStores: "No active stores available. Please connect or enable a store first.",
          storeEmpty: "Please select at least one store to publish.",
          scheduleTitle: "Publish time",
          scheduleNow: "Publish now",
          scheduleLater: "Schedule",
          scheduleHint: "Time is based on the system timezone.",
          scheduleEmpty: "Please choose the scheduled date and time.",
          scheduleInvalid: "Invalid scheduled time.",
          submit: "Submit",
          submitting: "Submitting...",
          submitted: "Publish submitted.",
          failed: "Failed to submit publish."
        },
        scheduleModal: {
          title: "Manage schedule",
          subtitle: "Cancel or reschedule scheduled publishes for a store.",
          storeTitle: "Store",
          storePlaceholder: "Select a store",
          storeEmpty: "Please select a store.",
          actionTitle: "Action",
          cancel: "Cancel schedule",
          reschedule: "Reschedule",
          timeTitle: "New time",
          scheduleHint: "Time is based on the system timezone.",
          scheduleEmpty: "Please choose the scheduled date and time.",
          scheduleInvalid: "Invalid scheduled time.",
          submit: "Submit",
          submitting: "Submitting...",
          submitted: "Schedule updated.",
          failed: "Failed to update schedule."
        },
        summary: {
          items: "Products",
          completed: "Published",
          failed: "Failed",
          processing: "Publishing",
          scheduled: "Scheduled"
        },
        table: {
          product: "Product",
          actions: "Actions"
        }
      }
    },

    orders: {
      nav: "Order Center",
      title: "Order & Procurement Center",
      subtitle: "Review Ozon orders, track internal fulfillment, and jump back to the original 1688 source for procurement.",
      refresh: "Refresh orders",
      retry: "Retry",
      loading: "Loading orders...",
      loadFailed: "Failed to load orders",
      loadFailedDetail: "We could not load the latest order list right now.",
      detailLoadFailed: "Failed to load order details",
      detailLoadFailedDetail: "We could not load the latest order detail right now.",
      loginTitle: "Log in to review your orders",
      loginDesc: "Sign in first to load Ozon orders and update fulfillment progress.",
      loginAction: "Log In",
      emptyTitle: "No orders yet",
      emptyDesc: "Once real Ozon orders are synced, you can review procurement and fulfillment here.",
      filteredEmptyTitle: "No orders match the current filters",
      filteredEmptyDesc: "Clear one or more filters to review other orders.",
      detailEmptyTitle: "Select an order to continue",
      detailEmptyDesc: "Open one order from the list to view procurement links and fulfillment actions.",
      save: "Save fulfillment",
      saving: "Saving...",
      saveSuccess: "Fulfillment details saved.",
      saveFailed: "Failed to save fulfillment details.",
      orderSaveConfirm: "Confirm save procurement info",
      orderSaveSuccess: "Procurement info saved.",
      orderSaveFailed: "Failed to save procurement info.",
      itemSave: "Save item progress",
      itemSaving: "Saving item...",
      itemSaveSuccess: "Item fulfillment updated.",
      itemSaveFailed: "Failed to update item fulfillment.",
      syncSuccess: "Synced {count} orders from Ozon.",
      syncFailed: "Failed to sync orders from Ozon.",
      shipToOzon: "Sync shipment to Ozon",
      shippingToOzon: "Syncing shipment...",
      shipToOzonSuccess: "Tracking and shipment synced to Ozon.",
      shipToOzonFailed: "Failed to sync shipment to Ozon.",
      prepareToOzon: "Prepare on Ozon",
      preparingToOzon: "Preparing...",
      prepareToOzonSuccess: "Prepared on Ozon. You can now download posting-label.",
      prepareToOzonFailed: "Failed to prepare on Ozon.",
      batchPreparing: "Batch preparing...",
      batchPrepareSuccess: "Batch prepare completed: {count} orders succeeded.",
      batchPrepareFailed: "Batch prepare failed.",
      batchPreparePartial: "Batch prepare finished: {succeeded} succeeded, {failed} failed.",
      batchPrepareSelectRequired: "Select at least one posting before batch prepare.",
      batchPrepareFilteredEmpty: "No orders in current filters to batch prepare.",
      batchPrepareConfirmSelected: "Batch prepare selected {count} postings now?",
      batchPrepareConfirmFiltered: "Batch prepare all {count} postings under current filters now?",
      prepareSplitConfirm: "This posting has {productCount} product lines / {quantity} units. Split packages on Ozon and continue prepare?",
      batchPrepareSplitConfirmSelected: "{count} selected postings may need Ozon package splitting. Continue with split package prepare?",
      batchPrepareSplitConfirmFiltered: "Some filtered postings may need Ozon package splitting. Continue with split package prepare?",
      prepareTrackingRequired: "Ozon prepare does not require a domestic tracking number.",
      prepareTrackingRequiredHint: "Ozon prepare sends package products only; domestic tracking is used later for warehouse export.",
      prepareRequiresPurchased: "Fill domestic order number or procurement info first, then prepare on Ozon.",
      prepareRequiresPurchasedHint: "Anti-mistake hint: fill domestic order number first so status becomes purchased.",
      prepareNotAvailableByOzonStatus: "Current Ozon status is not awaiting packaging, so prepare is disabled.",
      prepareBlockedByBuyerCancel: "Buyer has cancelled this order. Preparing on Ozon is disabled.",
      buyerCancelledTag: "Buyer cancelled",
      buyerCancelledNote: "Buyer cancelled this order. Do not prepare shipment.",
      qualityInspection: {
        badge: "Quality inspection order",
        noticeTitle: "Quality inspection order",
        noticeDescription: "This Ozon order starts with 0213. Please handle it with extra attention.",
        groupNotice: "{count} quality inspection orders in this group. Please handle them with extra attention."
      },
      shipMissingStore: "The store context for this order is missing.",
      productIdCopied: "1688 product ID copied.",
      productIdCopyFailed: "Failed to copy product ID.",
      labelDownloading: "Downloading label...",
      labelDownloadSuccess: "Posting label downloaded.",
      labelDownloadFailed: "Failed to download posting label.",
      exportSuccess: "Forwarder sheet exported.",
      exportPartialWithSkipped:
        "Forwarder sheet exported: {exported} postings exported, {skipped} skipped because they are already in delivery.",
      exportFailed: "Failed to export forwarder sheet.",
      exportingForwarderSheet: "Exporting...",
      exportSelectRequired: "Select at least one posting before exporting.",
      exportIncomplete: "Some required shipment fields are still missing.",
      exportIncompleteMore: "and {count} more records.",
      exportIncompleteItemFallback: "Please complete required fields.",
      matchedSource: "Source matched",
      unmatchedSource: "Source not matched",
      unknown: "Unknown",
      creatorBadge: "Created by {creator}",
      lastEditedBadge: "Last edited by {editor}",
      results: "Showing {visible} / {total} orders",
      syncBackgroundStarted: "Background incremental sync started: {count} stores are updating.",
      syncBackgroundPending: "Background sync status is updating. Please wait...",
      syncBackgroundRunning: "Background sync is running: {count} stores are still syncing.",
      syncBackgroundCompleted: "Background sync completed and orders are up to date.",
      syncBackgroundFinishedWithFailures: "Background sync finished, but {count} stores failed. Check store status.",
      hasMoreHint: "More orders are available on the next page.",
      itemsTitle: "Order items",
      itemsHint: "Review the item snapshot and matched offer/SKU for this order.",
      itemsCount: "{count} items",
      moreItems: "+{count} more items",
      noItems: "No item snapshot returned yet.",
      quickActionsTitle: "Quick procurement actions",
      quickActionsHint: "Open the original 1688 link, copy the supplier product ID, or jump to the published Ozon page.",
      workbench: {
        sidebarTitle: "Order center",
        sidebarSubtitle: "Fulfillment workspace",
        skuHandoffDesk: "SKU fulfillment handoff desk",
        skuOverview: "SKU overview",
        skuOverviewDescription: "All active and historical SKU groups",
        skuOverviewFocus: "Pick a SKU group, then complete the first missing fulfillment step.",
        skuListByStatus: "SKU list by status",
        issueBlocked: "Blocked issues",
        issueBlockedDescription: "Orders requiring manual attention",
        issueBlockedFocus: "Resolve missing fields, abnormal logistics, or Ozon-side blockers first.",
        historyCompleted: "Historical completed orders",
        relatedOrdersByMissing: "Related orders by missing field",
        missingPurchaseInfo: "Missing purchase info",
        missingDomesticOrder: "Missing domestic order no.",
        missingDomesticTracking: "Missing domestic tracking",
        ozonActionNeeded: "Ozon action needed",
        forwarderReady: "Ready for forwarder export",
        inProgressOrders: "Other in-progress orders",
        stepPurchaseInfo: "Purchase info",
        stepPurchaseInfoHint: "Record supplier platform, total batch purchase cost, SKU, and domestic order no.",
        stepDomesticLogistics: "Domestic logistics",
        stepOzonPrepare: "Ozon preparation",
        stepForwarderExport: "Forwarder export",
        goodMorningTitle: "Good morning, {count} shipments need purchasing today",
        dataUpdatedAt: "Data updated at {time}",
        neverSynced: "not synced yet",
        recentSync: "Last sync {minutes} min ago",
        recentSyncUnknown: "No sync record yet",
        syncNow: "Sync now",
        metricPendingPurchase: "To purchase",
        metricReadyPrepare: "Ready for Ozon prep",
        metricReadyExport: "Ready to export",
        metricIssue: "Issues",
        metricShippingToday: "Shipping today",
        metricAwaitingAction: "Needs action",
        metricReadyAction: "Ready",
        metricAttentionNeeded: "Check now",
        metricRealtime: "Live",
        allOrders: "All orders",
        selectedShipment: "Selected shipment",
        detailTab: "Details",
        ordersTab: "Orders ({count})",
        timelineTab: "Timeline",
        markOrdered: "Mark ordered",
        badge: "Card fulfillment flow",
        cardFlowTitle: "Card-based fulfillment flow",
        cardFlowSubtitle: "Orders move through five clear cards: purchase, supplier order, domestic transit, Ozon preparation, and forwarder export. The next action stays visible without table noise.",
        commandCenter: "Flow command center",
        loadedLines: "Loaded order lines",
        issueLines: "Issue lines",
        flowGroups: "Flow cards",
        totalQuantity: "Total units",
        searchPlaceholder: "Search posting, SKU, 1688 ID, purchase note, or product name",
        advancedFilters: "Advanced filters",
        queueMeta: "{groups} cards · {lines} order lines · {quantity} units",
        emptyTitle: "No cards in the current flow",
        emptyDesc: "Clear filters or refresh orders to continue processing procurement and fulfillment.",
        lineItems: "Order lines ({count})",
        contextDrawer: "Action drawer",
        flowRail: "Fulfillment flow",
        cardFlow: "Card flow board",
        linesUnit: "lines",
        stageEmpty: "No cards",
        hasIssue: "Issue",
        quantity: "Qty",
        orderCount: "Orders",
        nextStep: "Next action",
        exportChecklist: "Export readiness",
        decisionBadge: "Single-item visual decision desk",
        decisionTitle: "Procurement Decision Center",
        decisionSubtitle: "Use one large product image, restrained SKU facts, store filters, and previous purchase history to help operators place orders faster without pretending to know other marketplace prices.",
        currentTask: "Current task",
        storeSnapshot: "Store context",
        allStoreSnapshot: "All stores",
        readyActions: "Ready",
        activeFilters: "Active filters",
        noActiveFilters: "No extra filters",
        batchMode: "Batch mode",
        batchBasket: "Merged purchase basket",
        filteredQueueHint: "Based on the current filtered results",
        batchGroupSummary: "{orders} orders / {quantity} units",
        expectedOnePurchase: "1 supplier order expected",
        batchPrepareReady: "Ozon prep scope",
        batchExportReady: "Export-ready orders",
        sameSkuOrders: "{orders} orders / {quantity} units",
        storesCovered: "{count} stores",
        groupOrderList: "Group orders",
        groupOrderListHint: "This purchase record applies to {orders} orders and {quantity} units in the selected group.",
        groupOrderListMore: "+{count} more order lines in this group",
        batchApplyScope: "Apply to {count} orders",
        batchProcurementRecord: "Batch procurement record",
        batchProcurementHint: "One purchase result will be written back to {count} grouped orders.",
        batchOperationQueue: "Batch action queue",
        batchSelection: "Batch selection",
        batchReady: "Ready",
        batchMissing: "Missing",
        batchReadyCount: "{count} ready",
        batchReadyChecklist: "Batch readiness",
        domesticTrackingOzonNumberIgnored: "Ozon shipment numbers are not domestic tracking numbers. Fill the supplier-side domestic tracking number here when it is available.",
        batchSaveAndSync: "Apply to {count} orders",
        batchRecordPurchaseResult: "Batch record purchase",
        batchPrepareToOzon: "Batch Ozon prep",
        batchPrepareToOzonWithCount: "Batch Ozon prep ({count})",
        batchExportForwarder: "Export forwarder sheet",
        batchExportForwarderWithCount: "Export forwarder sheet ({count})",
        currentBatchProgress: "Current group {current} / {total} · {orders} orders · {quantity} units",
        selectCurrentBatch: "Select current group orders",
        selectedCurrentBatch: "Current group selected",
        groupSelectionCount: "Selected {selected}/{total}",
        productQueue: "Purchase queue",
        phoneScanHint: "Use a phone app to scan the screen image",
        viewAllImages: "View large images",
        noImageHint: "No product image",
        dataFromSnapshot: "From snapshot",
        expandMoreInfo: "Expand more info",
        platformStatus: "Platform status",
        nextAction: "Next action",
        procurementDecision: "Procurement decision",
        procurementDecisionHint: "Decide where to buy, then record the result.",
        recommendedMethod: "Recommended method",
        recommendReuseHistory: "Reuse previous {channel} purchase",
        recommendUse1688: "Start from the original 1688 link",
        recommendIdentifyFirst: "Identify the product with the large image first",
        historyHelpsDecision: "A previous purchase record exists, so keep the operator on the shortest path.",
        noHistoryDecisionHint: "No Taobao/Pinduoduo data is assumed here. The operator can scan the screen image with a phone.",
        purchaseMethodSelection: "Purchase method",
        open1688Source: "Open original 1688 source",
        noSourceUrl: "No source link",
        taobaoScreenSearch: "Search the screen image in Taobao app",
        pddScreenSearch: "Search the screen image in Pinduoduo app",
        usePhoneSearchOnly: "Use the phone app camera / image search directly on the large product image. No platform price is shown here.",
        procurementRecord: "Procurement record",
        readyChecklist: "Action checklist",
        checkSourceImage: "Source image and product identity checked",
        checkPurchaseMethod: "Purchase method or reference recorded",
        recordPurchaseResult: "Record purchase result",
        selectCurrentProduct: "Select current product orders",
        selectedCurrentProduct: "Current product selected",
        currentProductProgress: "Current product {current} / {total}",
        moreExport: "More export",
        nextPurchaseFirst: "Complete purchasing first",
        nextExportForwarder: "Export forwarder sheet",
        nextPrepareOzon: "Prepare on Ozon",
        nextCompleteProcurement: "Complete the next fulfillment step",
        tabs: {
          purchase: { title: "To purchase", description: "Open source links and place supplier orders." },
          ordered: { title: "Ordered", description: "Fill purchase cost and domestic order no." },
          domestic: { title: "Domestic transit", description: "Follow supplier-side tracking before Ozon preparation." },
          ozon: { title: "Ozon prep", description: "Prepare shipments and download labels." },
          warehouse: { title: "Forwarder export", description: "Export ready packages and posting labels." }
        },
        focus: {
          purchase: "Open the source link, confirm SKU and quantity, then save procurement details.",
          ordered: "Fill domestic order number, cost, shipping fee, and purchase reference.",
          domestic: "Confirm the real domestic tracking number and watch the package until it is ready for Ozon.",
          ozon: "Prepare on Ozon, download posting-labels, and keep shipment data aligned.",
          warehouse: "Check export readiness before handing files and labels to the forwarder."
        }
      },
      purchaseReimbursement: {
        badge: "Daily reimbursement",
        title: "Purchase reimbursement daily report",
        hint: "Grouped by procurement save time and operator. Merged purchases count their batch total once, not once per order line.",
        date: "Date",
        totalCost: "Total cost",
        unreimbursedCost: "Unreimbursed",
        reimbursedCost: "Reimbursed",
        orderCount: "Orders",
        recordCount: "Records",
        itemUnit: "items",
        loadFailed: "Failed to load reimbursement report.",
        recordsLoadFailed: "Failed to load purchase records.",
        saveFailed: "Failed to update reimbursement status.",
        empty: "No purchase records for this day.",
        emptyHint: "After staff save procurement details, the daily reimbursement report appears here automatically.",
        recordsEmpty: "No purchase record details.",
        markReimbursed: "Mark reimbursed",
        markUnreimbursed: "Mark unpaid",
        markRecordReimbursedSuccess: "Purchase record marked reimbursed.",
        markRecordUnreimbursedSuccess: "Purchase record marked unpaid.",
        markDayReimbursedSuccess: "The employee's daily records were marked reimbursed.",
        markDayUnreimbursedSuccess: "The employee's daily records were marked unpaid.",
        mergedRecord: "Merged purchase",
        singleRecord: "Single purchase",
        mergedCostHint: "Merged purchase is counted once by batch total.",
        status: {
          unreimbursed: "Unreimbursed",
          partial: "Partially reimbursed",
          reimbursed: "Reimbursed"
        }
      },
      preview: {
        domesticTitle: "Domestic fulfillment overview",
        ozonTitle: "Ozon status overview",
        scopeCurrentPage: "Scope: current page data",
        scopeStoreLevel: "Scope: current store range, independent of status, search, and pagination"
      },
      procurementTitle: "Procurement information",
      logisticsTitle: "Domestic logistics & forwarder",
      mergeInfoTitle: "Merge & package mapping",
      statusCodesTitle: "Ozon status codes",
      fulfillmentPanelTitle: "Internal fulfillment",
      fulfillmentPanelHint: "Manage procurement and shipping progress inside the workspace without leaving this page.",
      timelineTitle: "Fulfillment timeline",
      detailTitle: "Order detail",
      trackingPlaceholder: "Enter an internal tracking number. This value will also be synced to Ozon when you confirm shipment.",
      itemNotePlaceholder: "Add item-level procurement notes or exceptions",
      purchaseSkuPlaceholder: "For example: red / size L / 220V",
      purchaseReferencePlaceholder: "Paste a link, purchase token, order remark, or mobile order note",
      purchaseReferenceCopied: "Purchase reference copied.",
      purchaseReferenceCopyFailed: "Failed to copy purchase reference.",
      sourceUrlPlaceholder: "Paste a 1688 link or enter a 1688 product ID. Leave empty to clear the source.",
      sourceUrlHint: "You can add a source for unmatched items or replace the current one with a new 1688 link/ID.",
      notePlaceholder: "Add procurement notes, supplier feedback, or exceptions here",
      mergeSuggestionsTitle: "Smart merge suggestions",
      mergeSuggestionsHint: "{count} order lines · {quantity} units",
      mergeApplyAction: "Apply merge",
      mergeApplySuccess: "Merge suggestion applied.",
      mergeApplyFailed: "Failed to apply merge suggestion.",
      mergeSaveConfirm: "Confirm group save",
      mergeSaveSuccess: "Merged procurement info saved.",
      mergeSaveFailed: "Failed to save merged procurement info.",
      mergeMissingGroupKey: "Please enter a merge group key.",
      mergeGroupsHint: "{lines} order lines · {quantity} units · {orders} orders",
      mergeFieldConflictHint: "These fields have inconsistent values in merged lines. Please confirm before saving: {fields}.",
      singleProcurementHint: "{count} lines are marked as single procurement.",
      singleProcurementEnabled: "Switched to single procurement handling.",
      singleProcurementDisabled: "Restored to merged procurement handling.",
      mergeToggleFailed: "Failed to change merge status.",
      mergedSectionTitle: "Merged procurement ({count})",
      mergedSectionEmpty: "No merged-procurement lines in this group.",
      singleSectionTitle: "Single procurement handling ({count})",
      singleSectionEmpty: "No single-procurement lines in this group.",
      procurementMode: {
        merged: "Merged procurement",
        single: "Single procurement handling"
      },
      freeShippingStatus: {
        unset: "Unset",
        free: "Free shipping",
        paid: "Paid shipping"
      },
      filters: {
        store: "Store",
        allStores: "All stores",
        ozonStatus: "Ozon status",
        allStatuses: "All Ozon statuses",
        fulfillmentStatus: "Fulfillment status",
        allFulfillmentStatuses: "All fulfillment statuses",
        domesticTrackingStatus: "Domestic tracking",
        allDomesticTrackingStatuses: "All domestic tracking",
        warehouseExportStatus: "Warehouse export",
        allWarehouseExportStatuses: "All export statuses",
        warehouseExportEligible: "Export suggestion",
        allWarehouseExportEligibility: "All suggestions",
        search: "Search",
        searchPlaceholder: "Search posting number, offer ID, 1688 ID, SKU, or item name",
        showPanel: "Show filters",
        hidePanel: "Hide filters",
        clear: "Clear filters"
      },
      actions: {
        open1688: "Open 1688 source",
        copyProductId: "Copy 1688 ID",
        viewPublish: "Open Ozon page",
        openPurchase: "Open purchase link",
        copyPurchaseReference: "Copy purchase reference",
        useOriginal1688Link: "Use original 1688 link",
        queryDomesticTracking: "Track domestic shipment",
        openDomesticTracking: "External tracking",
        refreshDomesticTracking: "Refresh status",
        downloadLabel: "Download posting label",
        openOrderCenter: "View orders",
        exportForwarderCsv: "Export forwarder sheet",
        selectPageVisible: "Select current page",
        clearPageSelection: "Clear current page",
        selectAllFiltered: "Select all filtered (cross-page)",
        selectCurrentQueue: "Select current queue",
        clearCurrentQueue: "Clear current queue",
        clearSelection: "Clear selection",
        forceExportForwarder: "Force export (allow empty date/remark)",
        selectGroup: "Select group",
        goPurchase: "Go purchase on 1688",
        batchPrepareSelected: "Batch prepare selected",
        batchPrepareFiltered: "Batch prepare filtered",
        moveToSingleProcurement: "Single procurement handling",
        restoreMergedProcurement: "Restore merged procurement",
        previousImage: "Previous image",
        nextImage: "Next image"
      },
      selection: {
        count: "{count} postings selected",
        queueCount: "{selected} / {total} postings selected",
        filteredAllActive: "Exporting all filtered postings"
      },
      unsavedChanges: {
        leaveTitle: "Unsaved procurement changes",
        leaveConfirm: "You have unsaved procurement changes. Leave this order anyway?",
        refreshConfirm: "You have unsaved procurement changes. Refresh and discard them?"
      },
      labels: {
        postingNumber: "Posting",
        orderNumber: "Order no.",
        store: "Store",
        totalPrice: "Total price",
        inProcessAt: "Created",
        shipmentDate: "Ship by",
        substatus: "Substatus",
        noSubstatus: "No substatus",
        offerId: "Offer ID",
        noOfferId: "No offer ID matched",
        productId: "1688 ID",
        noProductId: "No 1688 ID matched",
        collectionId: "Collection ID",
        matchSource: "Match result",
        sourceUrl: "Source link / 1688 ID",
        platformStatus: "Platform status",
        domesticStatus: "Domestic status",
        warehouseExportStatus: "Warehouse export",
        warehouseExportEligible: "Export suggestion",
        purchaseChannel: "Purchase channel",
        selectPurchaseChannel: "Select a channel",
        purchaseReference: "Purchase reference",
        purchaseUrl: "Purchase link",
        purchaseSkuInfo: "Purchase SKU details",
        purchaseCostCny: "Purchase cost (CNY)",
        batchPurchaseTotalCostCny: "Batch purchase total (CNY)",
        purchaseTotalCostCny: "Total purchase cost (CNY)",
        purchaseOperator: "Purchase operator",
        purchaseRecordedAt: "Purchase saved at",
        reimbursementStatus: "Reimbursement",
        shippingFeeCny: "Shipping fee (CNY)",
        isFreeShipping: "Free shipping",
        domesticOrderNumber: "Domestic order no.",
        domesticLogisticsChannel: "Domestic logistics",
        domesticTrackingNumber: "Domestic tracking no.",
        domesticTrackingPhoneTail: "Phone last 4 digits",
        domesticTrackingLatestTime: "Latest trace time",
        domesticTrackingSignedAt: "Signed at",
        forwarderName: "Forwarder",
        forwarderWarehouse: "Forwarder warehouse",
        labelingFeeCny: "Labeling fee (CNY)",
        dimensions: "Dimensions",
        weight: "Weight",
        russianTitle: "Generated Russian title",
        chineseTitle: "Collected Chinese title",
        lockedSkuKey: "Locked SKU key",
        lockedSkuSource: "SKU source",
        lockedSkuState: "Locked SKU JSON",
        mergeGroupKey: "Merge batch",
        packageCode: "Package code",
        procurementMode: "Procurement mode",
        fulfillmentStatus: "Fulfillment status",
        ozonTrackingNumber: "Ozon tracking",
        ozonLogistics: "Ozon logistics",
        deliveryMethod: "Delivery method",
        trackingNumber: "Internal tracking",
        trackingNumberUnit: "tracking nos.",
        note: "Notes"
      },
      warehouseExport: {
        status: {
          not_exported: "Not exported",
          exported: "Exported",
          needs_reexport: "Needs re-export"
        },
        eligibility: {
          eligible: "Suggested",
          ineligible: "Not suggested"
        },
        blockReasons: {
          ALREADY_EXPORTED: "Already exported. Re-export only when key info changes.",
          INVALID_OZON_STATUS: "Current Ozon status does not require warehouse export.",
          BUYER_CANCELLED_OR_TERMINAL: "Order is cancelled or already terminal.",
          FULFILLMENT_NOT_SHIPPED: "Domestic fulfillment is not shipped yet.",
          MISSING_STORE_ID: "Missing store context for export.",
          MISSING_SHIPPED_AT: "Missing shipped time.",
          MISSING_PURCHASE_RECORD: "Missing purchase method or proof.",
          MISSING_DOMESTIC_ORDER_NUMBER: "Missing domestic order number.",
          MISSING_DOMESTIC_TRACKING_NUMBER: "Missing domestic tracking number."
        },
        lastExportedAt: "Last exported",
        exportCount: "{count} exports"
      },
      orderIssue: {
        quickTitle: "Problem orders",
        filterLabel: "Problem",
        warningPrefix: "Warning: ",
        overduePrefix: "Overdue: ",
        responsibility: "Responsibility",
        referenceAt: "Since",
        filters: {
          problem_orders: "All problem orders",
          domestic_signed_ozon_pending: "Signed but still awaiting dispatch"
        },
        types: {
          not_exported_after_domestic_signed: "User has not exported to forwarder",
          needs_reexport_after_domestic_signed: "Needs forwarder sheet re-export",
          warehouse_not_processed_after_export: "Forwarder warehouse not processed",
          domestic_tracking_verification_blocked: "Domestic tracking needs verification"
        },
        descriptions: {
          not_exported_after_domestic_signed: "Domestic tracking is signed at the partner warehouse, but the forwarder sheet has not been exported.",
          needs_reexport_after_domestic_signed: "Domestic tracking is signed, but key information changed after export and the forwarder sheet must be exported again.",
          warehouse_not_processed_after_export: "Domestic tracking is signed and the forwarder sheet was exported, but Ozon is still awaiting dispatch.",
          domestic_tracking_verification_blocked: "The domestic tracking provider cannot confirm the shipment status. Complete the phone verification data and refresh tracking."
        },
        responsibilities: {
          user: "User action needed",
          forwarder_warehouse: "Forwarder / warehouse follow-up"
        }
      },
      domesticTrackingStatus: {
        pending: "Pending subscription",
        subscribed: "Subscribed",
        in_transit: "In transit",
        warehouse_signed: "Domestic first-leg warehouse signed",
        exception: "Exception",
        needs_courier: "Needs courier"
      },
      domesticTrackingNoTrace: "No tracking event yet.",
      domesticTrackingRequiresPhoneTail: "Phone last 4 digits required",
      domesticTrackingRequiresPhoneTailHint: "This carrier requires the sender or receiver phone last 4 digits. Fill it in and save, then refresh tracking.",
      domesticTrackingRefreshSuccess: "Domestic tracking status refreshed.",
      domesticTrackingRefreshFailed: "Failed to refresh domestic tracking status.",
      ozonStatusLabels: {
        awaiting_packaging: "Awaiting packaging",
        awaiting_deliver: "Awaiting delivery",
        delivering: "Delivering",
        delivered: "Delivered",
        cancelled: "Cancelled",
        acceptance_in_progress: "Acceptance in progress",
        sent_by_seller: "Shipped by seller",
        not_accepted: "Not accepted",
        returned: "Returned"
      },
      ozonSubstatusLabels: {
        posting_created: "Posting created",
        awaiting_registration: "Awaiting registration",
        acceptance_in_progress: "Acceptance in progress",
        ready_to_ship: "Ready to ship",
        shipped: "Shipped",
        delivered: "Delivered",
        cancelled: "Cancelled",
        product_cancel: "Product cancelled",
        product_cancel_awaiting_resolve: "Cancellation pending",
        package_created: "Package created"
      },
      ozonActionLabels: {
        ship: "Ship",
        ship_async: "Ship async",
        ship_async_retry: "Retry async ship",
        ship_with_additional_info: "Ship with additional info",
        cancel: "Cancel order",
        product_cancel: "Cancel product",
        has_barcode_for_printing: "Barcode ready",
        label_download: "Download label",
        label_download_big: "Download A4 label",
        label_download_small: "Download thermal label",
        set_cutoff: "Set cutoff",
        tracking_number_set: "Set tracking number"
      },
      matchSourceLabels: {
        manualSource: "Manually updated source",
        offerId: "Matched via Offer ID"
      },
      purchaseChannels: {
        1688: "1688 original link",
        taobao: "Taobao",
        pinduoduo: "Pinduoduo",
        other: "Other"
      },
      timeline: {
        purchasedAt: "Purchased at",
        receivedAt: "Received at",
        readyToShipAt: "Ready to ship at",
        shippedAt: "Marked shipped at",
        ozonShipmentDate: "Ozon ship deadline"
      },
      fulfillmentStatus: {
        pending_purchase: "Pending purchase",
        purchased: "Purchased",
        received: "Received",
        ready_to_ship: "Ready to ship",
        shipped: "Shipped",
        issue: "Issue"
      }
    },

    // Import
    import: {
      nav: "Import Product",
      title: "Import Product Source",
      subtitle: "Paste your supplier link to begin AI optimization.",
      extActive: "Browser Extension Active",
      connected: "Connected",
      extActiveDesc: "The companion is active. You can import directly from suppliers.",
      extSettings: "Extension Settings",
      extMissing: "Extension Required",
      extMissingDesc: "Please install the companion to scrape product data locally.",
      extUpgradeRequiredDesc: "Current extension version {current}. Please update to {required} or later.",
      extVersionUnknown: "unknown",
      extUpdateBtn: "Update Extension",
      extRecheckBtn: "I've Updated",
      extRecheckGuide: "Rechecking extension status...",
      downloadBtn: "Download Extension",
      downloadGuide: "Opening extension download...",
      urlLabel: "Product URL",
      urlSub: "Supports AliExpress, 1688, Amazon, Shopify, and more.",
      urlPlaceholder: "Paste your product link here...",
      mainProductAlt: "Main product image",
      urlRequired: "Please enter a product URL.",
      authRequired: "Please log in to start collection.",
      guestFailed: "Unable to start guest session. Please try again.",
      consent: {
        title: "Before you continue",
        subtitle: "To start a guest session, please read and agree to the Terms of Service and Privacy Policy.",
        note: "You can continue as a guest. We will only use your data to provide the import and AI optimization services.",
        cancel: "Cancel",
        continue: "Agree & Continue"
      },
      importBtn: "Import",
      submitting: "Submitting...",
      collectFailed: "Failed to submit collection task.",
      uploadTitle: "Upload Media Assets",
      uploadSub: "Drag and drop images, videos or ZIP folders here",
      chooseFiles: "Choose Files",
      alternative: "Alternative",
      targetGlobal: "Target: Global Market",
      taskCenterEntry: "Task Center",
      taskCenterHint: "Log in to track progress in Task Center.",
      recent: {
        title: "Recent Task",
        subtitle: "Latest import: {status}",
        taskId: "Task ID",
        source: "Source URL",
        createdAt: "Created",
        statusHint: "We will notify you when the collection finishes.",
        continue: "Continue",
        hint: {
          processing: "Capture is running. Keep this page open or track progress in Task Center.",
          queued: "Task submitted. Waiting for plugin/resource availability.",
          completed: "Capture complete. Continue to preview and configure AI settings.",
          failed: "Capture failed. Check Task Center for details or retry.",
          generating: "AI is generating. Track progress in Task Center.",
          publishing: "Publishing in progress. View publish progress.",
          publishFailed: "Publishing failed. Review result details and follow manual review guidance.",
          ready: "Generation complete. Continue to publish.",
          uploaded: "Published. View it in Assets."
        },
        viewTaskCenter: "View Task Center",
        loginToView: "Log in to View",
        status: {
          processing: "Processing",
          queued: "Waiting",
          completed: "Completed",
          failed: "Failed"
        }
      },
      success: {
        title: "Task Created",
        subtitle: "We started collecting your product data. Continue to preview or track it in Task Center.",
        taskId: "Task ID",
        source: "Source URL",
        status: "Status",
        statusProcessing: "Processing",
        statusHint: "Collection is running in the background.",
        continue: "Continue Import",
        goTaskCenter: "Go to Task Center",
        loginTaskCenter: "Log in to View Tasks",
        loginHint: "Log in to unlock Task Center tracking."
      }
    },

    // General Info Settings
    generalInfo: {
      title: "General Information",
      subtitle: "Manage your account details and profile information.",
      proPlan: "Pro Plan",
      upgradePlan: "Upgrade Plan",
      uploadNew: "Upload New",
      remove: "Remove",
      photoHint: "At least 400x400 px recommended. JPG or PNG is allowed.",
      fullName: "Full Name",
      fullNamePlaceholder: "e.g. Alex Chen",
      professionalEmail: "Professional Email",
      emailPlaceholder: "name@company.com",
      emailReadonlyHint: "Email is currently read-only.",
      companyName: "Company Name",
      companyPlaceholder: "Enter your company name",
      cancel: "Cancel",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      saveFailed: "Failed to save changes.",
      avatarUploadFailed: "Avatar upload failed.",
      avatarUpdated: "Avatar updated.",
      avatarRemoved: "Avatar removed.",
      footer: "Platform Settings"
    },

    // Billing & Credits
    billing: {
      title: "Billing & Credits",
      subtitle: "Track credit usage, image billing rules, and your full payment history.",
      currentBalance: "Current Balance",
      credits: "Credits",
      balanceHint: "Subscription credits reset each billing cycle; top-up credits never expire.",
      topUp: "Top Up",
      subscriptionPlan: "Subscription Plan",
      proPlanPrice: "Pro Plan · 560 credits/mo ($49)",
      nextRenewal: "Next renewal:",
      noPlan: "No active plan",
      noPlanHint: "No active subscription",
      loading: "Loading billing data...",
      loadFailed: "Failed to load billing data.",
      emptyHistory: "No billing history yet.",
      paymentSuccess: "Subscription payment completed.",
      paymentCanceled: "Subscription payment canceled.",
      rechargeSuccess: "Top-up completed.",
      rechargeCanceled: "Top-up canceled.",
      refresh: "Refresh",
      syncing: "Syncing...",
      summaryTotal: "Total Credits",
      summarySubscription: "Subscription Credits",
      summaryRecharge: "Top-up Credits",
      summaryTrial: "Trial Credits",
      statusActive: "Active",
      statusInactive: "Inactive",
      badgeSubscription: "Subscription",
      badgeRecharge: "Top-up",
      badgeAdjustment: "Adjustment",
      badgeRefund: "Refund",
      badgePromo: "Promo",
      badgeUsage: "Usage",
      badgeOther: "Other",
      usageImageGenerate: "Image generation · {count} images",
      usageImageRefund: "Image refund · {count} images",
      usageImageGenerateDetailed: "Image generation · {count} images · {credits} credits/image · {model}",
      usageImageRefundDetailed: "Image refund · +{credits} credits · {model}",
      usageImageAdjustmentDetailed: "Historical image charge adjustment · -{credits} credits · {model}",
      managePlan: "Manage Plan",
      billingHistory: "Billing History",
      date: "Date",
      description: "Description",
      amount: "Amount",
      invoice: "Invoice",
      proSubscription: "Pro Plan Subscription (560 Credits)",
      creditTopUp: "Credit Top-up (300 Credits)",
      downloadAll: "Export billing history",
      exportRow: "Export row",
      exportSuccess: "Billing history exported.",
      exporting: "Exporting...",
      footer: "All prices are in USD. Subscription credits reset each billing cycle; top-up credits never expire.",
      footerCopyright: "Financial Operations",
      pricingTitle: "Pricing Rules",
      ruleImagePrice: "Image generation costs {creditsPerImage} credits per image (${usdPerImage}).",
      ruleImageSettlement:
        "Image generation deducts the current selected model price first, then settles by the actual successful model after generation; any difference is refunded or additionally deducted.",
      ruleExchange: "1 credit = ${usdPerCredit}.",
      ruleRollover: "Subscription credits reset each billing cycle; top-up credits never expire.",
      ruleGift: "New users get {registerGiftCredits} credits; guests get {guestTrialCredits} credits.",
      imageUsageTitle: "Image usage by mode (Last {days} days)",
      imageUsageHint: "Counts billed image-generation records only; refunds are shown in the history list.",
      imageUsageTotal: "Total",
      imageUsageUnknownHint: "Other: {images} images · {credits} credits.",
      faqTitle: "FAQ",
      faq: {
        q1: "Why only charge for image generation?",
        a1: "Images are the primary cost driver; text and other modules are free to keep pricing simple.",
        q2: "Do subscription credits roll over?",
        a2: "No. Subscription credits reset each billing cycle. Top-up credits are permanent.",
        q3: "What happens if generation fails?",
        a3: "Failed images are refunded automatically based on the audit record.",
        q4: "Can I top up while subscribed?",
        a4: "Yes. Top-up credits stack on top of your subscription credits.",
      }
    },

    purchaseCredits: {
      title: "Purchase Credits",
      subtitle: "Choose a credit pack for image generation ({plan} · {creditsPerImage} credits per image).",
      priceHint: "1 credit = ${usdPerCredit}. Top-up credits never expire.",
      planSectionTitle: "Image Generation Mode",
      planThirdParty: "Value",
      planThirdPartyDesc: "Partner channels · best for everyday batch generation with fewer credits per image.",
      planOfficial: "Official",
      planOfficialDesc: "More stable for critical tasks, costs more credits per image.",
      planRecommended: "Recommended",
      planUnavailable: "This mode is temporarily unavailable. Please contact the admin.",
      planUnavailableTag: "Unavailable",
      noChannelsHint: "No available AI channels are configured. Please contact the admin to enable them.",
      creditsUnitPerImage: "credits/image",
      planEstimateHint: "Estimates assume {imagesPerListing} images per listing (main + detail + lifestyle). Actual may vary.",
      bucketSubscription: "Subscription credits",
      bucketRecharge: "Top-up credits",
      bucketLegacy: "Legacy credits",
      creditsScopePrefix: "Credits used: ",
      creditsScopeSuffixThirdParty: " (deduct order: subscription → top-up → legacy).",
      creditsScopeSuffixOfficial: " (deduct order: top-up first; subscription/legacy depends on settings).",
      summaryPlanLine: "Mode: {plan} · {creditsPerImage} credits/image",
      adminBonusLine: "Admin bonus {percent}%: +{credits} credits",
      creditBreakdownLine:
        "Base {baseCredits} credits + admin bonus {adminPercent}% ({adminCredits}) = {totalCredits} credits",
      listingsLabel: "Listings",
      listingsUnit: "listings",
      timeSaveHint: "Estimated time saved: ~{hoursMin}-{hoursMax} hours (for reference).",
      laborHint: "Manual listing creation typically takes 30–60 minutes; scaling often needs dedicated staff (≈4k–8k RMB/month).",
      planCompareHint:
        "Same credits: Value ≈ {thirdPartyImages} images / {thirdPartyListings} listings; Official ≈ {officialImages} images / {officialListings} listings (based on {imagesPerListing} images per listing).",
      planUpdateFailed: "Failed to update image mode.",
      stepPack: "Pick Pack",
      stepPay: "Choose Payment",
      stepConfirm: "Complete",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      imagesUnit: "images",
      topUpLabel: "Top up",
      bestValue: "Best Value",
      saveLabel: "Save",
      summaryTitle: "Selected Pack",
      summaryPay: "Payment Method",
      bonusLabel: "Bonus",
      featureNeverExpire: "Top-up credits never expire",
      featureBatch: "Batch generation",
      featureNoBatch: "No batch generation",
      featurePriority: "Priority support",
      paymentTitle: "Payment Method",
      purchaseButton: "Complete Purchase",
      buttonProcessing: "Processing...",
      processing: "Processing payment...",
      redirecting: "Redirecting to payment...",
      success: "Purchase successful! Credits added.",
      paymentFailed: "Failed to start payment.",
      payHint: "Top-ups support WeChat Pay and Alipay. Credits arrive instantly after payment.",
      acceptedMethods: "Accepted Methods",
      visaLabel: "Visa ending in 4242",
      visaExpire: "Expires 12/26",
      wechat: "WeChat Pay",
      wechatDesc: "Fast and secure mobile payment",
      wechatPendingBadge: "Pending",
      wechatPendingTitle: "WeChat Pay pending approval",
      wechatPendingDesc: "WeChat Pay is still under review in Stripe Live mode and is temporarily unavailable. Please use Alipay for now.",
      wechatPendingToast: "WeChat Pay is temporarily unavailable. Please use Alipay.",
      alipay: "Alipay",
      alipayDesc: "Global payment solution",
      addNew: "Add New Payment Method",
      addNewDesc: "Credit Card, WeChat Pay, or Alipay",
      security: "Secured by Stripe SSL Encryption",
      total: "Total to Pay",
      payHintAlipayOnly: "Top-ups currently support Alipay. WeChat Pay is pending Stripe approval.",
      footer: "© {year} OneClickSell Financial Operations"
    },

    subscription: {
      title: "Manage Subscription Plan",
      subtitle: "Select the plan that matches your image generation volume.",
      currentPlanLabel: "Current Plan",
      currentPlanBadge: "Current Plan",
      activeTag: "Active",
      inactiveTag: "Inactive",
      freeTag: "Free",
      noPlan: "No active plan",
      noPlanHint: "No active subscription",
      freePlanBillingHint: "Free access · no recurring billing",
      nextBilling: "Next billing date:",
      nextBillingDate: "Updating...",
      uploadQuotaTitle: "Image upload quota",
      uploadQuotaSubtitle: "Track today's raw image upload allowance and total storage before starting another task.",
      uploadQuotaBadge: "Today",
      uploadQuotaRemaining: "{used}/{total} MB used",
      uploadQuotaDailyLimit: "Daily upload limit",
      uploadQuotaDailyLimitHint: "Raw image volume refreshed every day.",
      uploadQuotaUsedToday: "Uploaded today",
      uploadQuotaUsedHint: "Includes confirmed raw image uploads.",
      uploadQuotaRemainingLabel: "Remaining today",
      uploadQuotaRemainingHint: "Shown before the next upload request.",
      uploadQuotaStorageLabel: "Storage used",
      uploadQuotaMaxImageHint: "Max {size}MB per image.",
      uploadQuotaFootnote: "This quota applies to original image upload size only. It is separate from credits used for AI generation.",
      uploadQuotaDynamicHint: "Raw daily cap = min(plan cap {planCap}MB, credit cap {creditCap}MB).",
      uploadQuotaBlockedHint: "Raw upload requires at least {minCredits} credits. Current available: {availableCredits}.",
      uploadQuotaLoadFailed: "Failed to load upload quota.",
      activeCycle: "Active Cycle",
      monthly: "Monthly",
      yearly: "Yearly",
      yearlySave: "2 months free",
      billedMonthly: "Billed monthly",
      billedYearly: "Billed yearly (10 months)",
      rolloverHint: "Subscription credits reset each billing cycle; top-up credits never expire.",
      creditsScopeHint: "Current image mode: {plan} · {creditsPerImage} credits/image. Credits used: {scope}.",
      officialTopUpOnly: "top-up credits only",
      perMonth: "/mo",
      perYear: "/yr",
      plansEmpty: "No subscription plans available.",
      planImagesPerMonth: "{count} images per month",
      planListingsPerMonth: "{count} listings per month",
      planCreditsPerMonth: "{count} credits per month",
      planUnlimited: "Unlimited",
      planStarterDesc: "Ideal for individuals getting started.",
      planProDesc: "Best for growing teams.",
      planBusinessDesc: "Designed for high-volume sellers.",
      featureStarter1: "Basic batch generation",
      featureStarter2: "HD image downloads",
      featureStarter3: "Standard support (48h)",
      featurePro1: "Advanced batch generation",
      featurePro2: "HD + 4K image downloads",
      featurePro3: "Priority support (12h)",
      featurePro4: "Dedicated growth manager",
      featureBusiness1: "High-volume batch generation",
      featureBusiness2: "Brand asset kit exports",
      featureBusiness3: "Priority support + success manager",
      featureBusiness4: "Workflow automation guidance",
      downgrade: "Downgrade",
      downgradeSuccess: "Plan downgraded.",
      upgrade: "Upgrade",
      manageInStripe: "Manage in Stripe",
      activePlan: "Active Plan",
      subscribe: "Subscribe",
      processing: "Redirecting to Stripe checkout...",
      paymentFailed: "Payment initialization failed.",
      loading: "Syncing subscription status...",
      buttonProcessing: "Processing...",
      cardHint: "Subscriptions are charged via credit/debit card (Stripe).",
      contactSales: "Contact Sales",
      customTitle: "Need a custom plan?",
      customDesc: "Our team can tailor a plan for your volume and workflow.",
      consultSales: "Consult with Sales",
      cancel: "Cancel Subscription",
      cancelConfirm: "Are you sure you want to cancel?",
      footer: "© {year} OneClickSell Financial Operations"
    },

    // Subscription Settings
    subscriptionSettings: {
      title: "Manage Subscription Plan",
      subtitle: "Select the best plan for your business needs.",
      currentPlanLabel: "Current Plan:",
      currentPlan: "Pro Plan",
      active: "Active",
      nextBillingLabel: "Next billing date:",
      nextBillingDate: "Updating...",
      activeCycleLabel: "Active Cycle:",
      monthly: "Monthly",
      yearly: "Yearly",
      save20: "Save 20%",
      billedAnnually: "Billed annually for maximum savings",
      billed: "billed {cycle}",
      perMonth: "/mo",
      plans: {
        starter: {
          title: "Starter",
          desc: "Perfect for individuals and small startups."
        },
        pro: {
          title: "Pro",
          desc: "Most popular for growing teams."
        },
      },
      features: {
        starterCredits: "500 Credits per month",
        starterAgents: "Basic AI Agents access",
        starterSupport: "Email support (48h)",
        customIntegrations: "Custom integrations",
        proCredits: "2,000 Credits per month",
        proAgents: "All AI Agents access",
        proSupport: "Priority support (12h)",
        proIntegrations: "Up to 5 integrations",
      },
      buttons: {
        downgrade: "Downgrade",
        activePlan: "Active Plan",
        contactSales: "Contact Sales",
        consultSales: "Consult with Sales",
        cancelSubscription: "Cancel Subscription"
      },
      currentBadge: "Current Plan",
      custom: {
        title: "Need a custom plan?",
        desc: "Our sales experts can help you design a tailored solution that fits your specific high-volume business requirements."
      },
      alerts: {
        downgrade: "Downgrading to Starter...",
        contactSales: "Contacting sales team...",
        openChat: "Opening sales chat...",
        cancelConfirm: "Are you sure?",
        cancelRequested: "Subscription cancellation requested."
      }
    },

    // Security Settings
    securitySettings: {
      title: "Security",
      subtitle: "Manage your account protection, authentication methods, and active sessions to keep your data secure.",
      twoFactor: {
        title: "Two-Factor Authentication (2FA)",
        desc: "Secure your account with an extra layer of protection. When enabled, you'll be asked for a verification code when signing in from a new device.",
        comingSoon: "Coming soon",
        available: "Available"
      },
      password: {
        title: "Password Management",
        updated: "Update your password regularly to keep your account secure.",
        reset: "Reset Password"
      },
      sessions: {
        title: "Active Sessions",
        subtitle: "These are the devices that have logged into your account. Revoke any sessions that you do not recognize.",
        currentSession: "Current Session",
        lastLogin: "Last login:",
        current: "Current",
        activeNow: "Active Now",
        signOut: "Sign Out",
        chromeDevice: "Chrome on macOS",
        chromeLocation: "San Francisco, US • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "London, UK • 2 days ago"
      },
      cancel: "Cancel",
      saving: "Saving...",
      saved: "Saved",
      save: "Save Changes",
      signOutConfirm: "Sign out this device?",
      signOutAlert: "Signing out device...",
      footer: "Security Infrastructure"
    },

    // Notifications Center
    notificationsCenter: {
      title: "Notifications",
      subtitle: "View important updates and task results here.",
      tabInbox: "Inbox",
      tabSettings: "Settings",
      unreadOnly: "Unread only",
      refresh: "Refresh",
      markAllRead: "Mark all read",
      loading: "Loading notifications...",
      loadingMore: "Loading...",
      loadMore: "Load more",
      loadFailed: "Failed to load notifications.",
      emptyTitle: "No notifications yet",
      emptyDesc: "When something important happens, it will appear here.",
      delete: "Delete",
      deleteConfirm: "Delete this notification?",
      deleteFailed: "Failed to delete notification.",
      deleteDone: "Deleted",
      collapseDetail: "Collapse",
      markAllDone: "All marked as read",
      markAllFailed: "Failed to mark all as read."
    },
    announcementCenter: {
      title: "Announcements",
      subtitle: "Important platform updates and migration notices are shown here.",
      unreadOnly: "Unread only",
      refresh: "Refresh",
      unreadBadge: "Unread: {count}",
      loading: "Loading announcements...",
      loadFailed: "Failed to load announcements.",
      errorTitle: "Unable to load announcements",
      emptyTitle: "No active announcements",
      emptyDesc: "When major platform updates are available, they will appear here.",
      pinned: "Pinned",
      unread: "Unread",
      publishedAt: "Published",
      endsAt: "Expires",
      acknowledge: "Got it",
      viewAction: "View details",
      severity: {
        info: "Notice",
        warning: "Important",
        critical: "Critical"
      }
    },
    feedbackCenter: {
      title: "My Feedback",
      subtitle: "Track bug fixes and feature request evaluations.",
      loading: "Loading feedback...",
      refresh: "Refresh",
      empty: "No feedback submitted yet.",
      loadFailed: "Failed to load feedback.",
      createdAt: "Created",
      updatedAt: "Updated",
      type: {
        bug: "Bug",
        feature: "Feature request"
      },
      status: {
        pending: "Pending review",
        discussing: "Needs info",
        accepted: "Accepted",
        scheduled: "Scheduled",
        in_progress: "In progress",
        completed: "Completed",
        rejected: "Rejected"
      },
      automation: {
        queued: "Waiting for execution",
        processing: "AI processing",
        reviewed: "Reviewed, awaiting scheduling",
        blocked: "Needs manual review",
        fixed: "Fixed",
        deployed: "Deployed",
        failed: "Processing failed"
      }
    },
    feedbackModal: {
      title: "Submit feedback",
      subtitle: "Just describe it plainly. AI will understand and classify it automatically.",
      close: "Close",
      removeImage: "Remove image",
      uploadImage: "Upload image",
      addImage: "Add",
      rewardHint: "Submit and get 2 credits. If accepted, earn 10 more credits.",
      cancel: "Cancel",
      submit: "Submit feedback",
      submitting: "Submitting...",
      emptyStateTitle: "Drag images here or click to upload",
      emptyStateHint: "Supports Ctrl+V screenshots. Max 2MB each.",
      autoProcessTitle: "AI review",
      autoProcessDesc: "After submission, AI verifies it first. Bugs may be fixed automatically; feature requests are evaluated and then wait for scheduling.",
      fields: {
        title: "Title",
        content: "Details",
        images: "Screenshots / Images ({count}/{max})"
      },
      types: {
        bug: "Bug report",
        feature: "Feature request"
      },
      placeholders: {
        titleBug: "For example: the page freezes after uploading images",
        titleFeature: "For example: export all data in one click",
        contentBug: "Describe what happened, for example: after clicking that button the page kept spinning. You can paste screenshots with Ctrl+V.",
        contentFeature: "Describe what you want, for example: it would be great to export all products in one click. You can paste screenshots with Ctrl+V."
      },
      success: {
        title: "Submitted successfully",
        desc: "Thanks for the feedback. The local AI has been notified to verify it.",
        trackingId: "Ticket ID: {id}",
        trackingStatus: "Current status: {status} (keep tracking in My Feedback)",
        rewardTitle: "+{count} credits",
        rewardDesc: "More credits are granted if the feedback is accepted.",
        acknowledge: "Got it",
        viewMyFeedback: "View My Feedback"
      },
      errors: {
        titleRequired: "Please enter a title.",
        contentRequired: "Please enter details.",
        loginRequired: "Please sign in before submitting feedback.",
        submitFailed: "Submission failed. Please try again later.",
        imageTooLarge: "Image {name} exceeds the 2MB limit.",
        fileNotImage: "File {name} is not an image.",
        maxImages: "You can upload up to {count} images.",
        imageReadFailed: "Failed to read image."
      }
    },

    // Notifications Settings
    notificationsSettings: {
      title: "Notifications",
      subtitle: "Manage how and where you receive system updates and alerts.",
      table: {
        type: "Notification Type",
        email: "Email",
        push: "Push"
      },
      loading: "Loading preferences...",
      loadFailed: "Failed to load notification preferences.",
      saveFailed: "Failed to save notification preferences.",
      pushComingSoon: "Push notifications are coming soon.",
      account: {
        title: "Account",
        desc: "Security alerts and account changes"
      },
      payment: {
        title: "Payments",
        desc: "Payment receipts and transaction updates"
      },
      subscription: {
        title: "Subscription",
        desc: "Plan renewals, invoices, and cancellations"
      },
      quota: {
        title: "Credits & Quota",
        desc: "Low balance alerts and usage notifications"
      },
      task: {
        title: "Tasks",
        desc: "Import, generation, and publish progress"
      },
      system: {
        title: "System",
        desc: "Announcements, maintenance, and security notices"
      },
      listing: {
        title: "Listing Success",
        desc: "Alert when products are published"
      },
      credit: {
        title: "Credit Warning",
        desc: "Alert when balance falls below 10%"
      },
      updates: {
        title: "Updates & Releases",
        desc: "New features and platform improvements"
      },
      mute: {
        title: "Mute all notifications",
        desc: "Temporarily silence all alerts and emails across the platform"
      },
      discard: "Discard Changes",
      saving: "Saving...",
      saved: "Preferences Saved",
      save: "Save Preferences"
    },

    // Integrations
    integrations: {
      title: "Integrations",
      subtitle: "Connect your favorite tools and marketplaces to streamline your workflow.",
      loadFailed: "Failed to load integrations.",
      connected: "Connected",
      connect: "Connect",
      shopify: "Shopify",
      shopifyDesc: "Sync products and inventory.",
      amazon: "Amazon Seller",
      amazonDesc: "Import listings and manage orders.",
      ozon: "Ozon",
      ozonDesc: "Russian marketplace integration.",
      slack: "Slack",
      slackDesc: "Receive notifications in your channels."
    },

    // Platform Preferences
    platformPreferences: {
      title: "Platform Preferences",
      subtitle: "Customize your default marketplace settings and AI agent behavior.",
      targetMarketplace: "Default Target Marketplace",
      advancedHint: "Need finer control? Configure advanced capabilities.",
      openAdvanced: "Advanced Settings",
      loading: "Loading preferences...",
      primaryPlatform: "Primary Platform",
      targetCountry: "Target Country",
      russia: "Russia",
      us: "United States",
      unitedStates: "United States",
      uk: "United Kingdom",
      unitedKingdom: "United Kingdom",
      germany: "Germany",
      france: "France",
      italy: "Italy",
      spain: "Spain",
      japan: "Japan",
      brazil: "Brazil",
      canada: "Canada",
      mexico: "Mexico",
      australia: "Australia",
      singapore: "Singapore",
      india: "India",
      turkey: "Turkey",
      uae: "United Arab Emirates",
      saudi: "Saudi Arabia",
      netherlands: "Netherlands",
      sweden: "Sweden",
      poland: "Poland",
      belgium: "Belgium",
      ireland: "Ireland",
      southAfrica: "South Africa",
      egypt: "Egypt",
      global: "Global",
      aiPersona: "AI Writing Persona",
      poweredBy: "Powered by LexiCopy",
      persuasive: "Persuasive",
      professional: "Professional",
      minimalist: "Minimalist",
      seoFocused: "SEO-Focused",
      personaHint: "This style will be applied by default to all new product descriptions and ad copies.",
      taskCenterDailyGoal: {
        sectionTitle: "Task Center daily goal",
        sectionDesc: "Set a daily publish-success target. When this is off, Task Center removes the widget completely.",
        storeLabel: "Target store",
        storeHint: "Each store keeps its own daily target and progress.",
        storeEmpty: "No active store is available yet.",
        targetLabel: "Daily target",
        targetHint: "Counts successful publishes completed today only.",
        progressLabel: "Today",
        progressEnabled: "Current progress {percent}%. {remaining} more to go.",
        progressDisabled: "Currently disabled. Task Center will not show this widget."
      },
      brandProtection: "Automatic Brand Protection",
      brandGuard: "Brand Guard",
      brandGuardDesc: "Reduce infringement risk by filtering brand terms during generation.",
      brandName: "Default Brand Name",
      brandPlaceholder: "Enter brand name for watermarking...",
      autoWatermark: "Auto-apply AI watermarking",
      autoWatermarkDesc: "Protect all new generations with your brand signature",
      imagePreferences: "Image Preferences",
      deepEnhancement: "Deep Image Enhancement",
      deepEnhancementDesc: "AI upscale and clarity boost",
      autoBgRemoval: "Auto-Background Removal",
      autoBgRemovalDesc: "Clean marketplace-ready assets",
      autoSaveHint: "Changes are saved automatically to your workspace profile.",
      saveHint: "Changes apply to new tasks. Save to sync to your workspace profile.",
      resetDefaults: "Reset to Defaults",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      saveFailed: "Failed to save preferences.",
      footer: "Global Workspace"
    },

    // Assets Management
    assets: {
      nav: "Assets",
      title: "Asset Operations Library",
      subtitle: "Operate completed listings and publish links. For task scheduling, go to Task Center.",
      loading: "Loading assets...",
      loadFailed: "Failed to load assets.",
      loginTitle: "Log in to view assets",
      loginDesc: "Sign in to manage your generated listings and upload records.",
      loginAction: "Log In",
      emptyTitle: "No assets yet",
      emptyHint: "Check Task Center for in-progress jobs, then return here to operate completed listings.",
      emptyAction: "Go to Task Center",
      newOptimization: "Import New Product",
      goTaskCenter: "Task Center",
      focusTitle: "Asset Library only shows operation-ready results",
      focusHint: "Use this page for published links and final assets. Running or failed jobs should be handled in Task Center.",
      platformAll: "Platform: All",
      statusPublished: "Status: Published",
      statusAll: "Status: All",
      progressLabel: "Progress",
      last30Days: "Last 30 Days",
      last7Days: "Last 7 Days",
      last90Days: "Last 90 Days",
      allPeriod: "All Time",
      clearFilters: "Clear Filters",
      score: "Score",
      published: "Published",
      draft: "Draft",
      failed: "Failed",
      retry: "Retry",
      edit: "Edit",
      view: "View",
      viewProgress: "View Progress",
      goToPublish: "Publish",
      fixError: "Fix",
      openListing: "Open Listing",
      noPublishUrl: "No publish link yet.",
      moreActionsComingSoon: "More actions are under development.",
      addNewListing: "Add New Listing",
      addNewDesc: "Upload raw photos for AI enhancement.",
      assetsSelected: "Assets Selected",
      batchDownload: "Batch Download",
      batchSync: "Batch Sync",
      delete: "Delete",
      actions: {
        copyPublishLink: "Copy publish link",
        openPublishLink: "Open publish link",
        deleteRecord: "Delete record",
        deleteTitle: "Delete record",
        deleteConfirmMessage: "Delete this record? This action cannot be undone.",
        deleted: "Deleted.",
        deleteFailed: "Failed to delete.",
        publishLinkCopied: "Publish link copied.",
        publishLinkCopyFailed: "Failed to copy publish link."
      },
      showingAssets: "Showing {from} to {to} of {total} assets",
      searchPlaceholder: "Search assets..."
    },

    // Import Preview
    importPreview: {
      nav: "Product Preview",
      breadcrumb: "Workspace",
      collectStatus: "Collection Status",
      collectQueued: "Waiting",
      collecting: "Collecting",
      collectCompleted: "Completed",
      collectFailed: "Failed",
      collectingHint: "The extension is collecting data in the background.",
      collectProgressTitle: "Collection Progress",
      collectProgressHint: "We are syncing with the extension. You'll see live updates here.",
      progressLabel: "Progress",
      stepQueued: "Waiting for Extension",
      stepAssigned: "Extension Accepted",
      stepCollecting: "Collecting Data",
      stepUploading: "Uploading Data",
      stepCompleted: "Collection Complete",
      currentStage: "Current Stage",
      stage: {
        pending: "Waiting for extension",
        assigned: "Task assigned",
        preparing: "Preparing page",
        scrolling: "Scrolling page",
        collecting: "Collecting content",
        saving: "Saving data",
        uploading: "Uploading assets",
        cached: "Loading cached result",
        completed: "Completed"
      },
      authRequired: "Please log in to view collection progress.",
      pollFailed: "Failed to fetch collection progress.",
      previewFailed: "Failed to load collected data.",
      retryCollect: "Retry collection",
      attributeList: "Additional Attributes",
      detailHighlights: "Detail Highlights",
      noHighlights: "No highlight details detected yet.",
      skuStructure: "SKU Structure",
      skuStock: "Stock",
      skuVariant: "Variant",
      skuSyncing: "Syncing SKU selection...",
      skuAnchorSyncing: "Syncing preferred reference image...",
      skuLockedByUser: "SKU locked",
      skuScopeLockedBadge: "SKU locked",
      skuGuideTitle: "How to choose SKU",
      skuGuideStepSelect: "1. Choose the SKU. This immediately locks the reference-image scope to that SKU.",
      skuGuideStepAnchor: "2. If needed, optionally pick 1 preferred reference image for stricter consistency.",
      skuGuideStepStart: "3. Confirm the locked scope, then start AI optimization.",
      skuSelectionNudgeTitle: "Suggestion: lock SKU before generation",
      skuSelectionNudgeDesc: "For multi-SKU products, manual SKU lock helps keep reference images and final output consistent.",
      skuSelectionNudgeAction: "Go to SKU selection now",
      minOrderRiskTitle: "High minimum-order risk detected",
      minOrderRiskDesc:
        "The source listing indicates a large minimum order quantity. If we proceed using single-piece supplier price, profit can be severely overestimated.",
      minOrderRiskFallbackTitle: "Minimum-order risk warning",
      minOrderRiskDetectedQty: "Detected minimum order quantity: {count} units",
      minOrderRiskAcknowledge:
        "I understand this product may require bulk purchase (>3 units) and confirm the risk before continuing.",
      minOrderRiskConfirmRequired: "Please confirm the minimum-order risk warning before starting optimization.",
      skuRecommendationTitle: "AI SKU suggestion",
      skuRecommendationHint: "Generated by model inference, review before applying.",
      skuRecommendationLoading: "AI is analyzing SKU suggestion...",
      skuRecommendationRefresh: "Refresh suggestion",
      skuRecommendationRefreshing: "Refreshing...",
      skuRecommendationFailed: "Failed to load SKU suggestion.",
      skuRecommendationEmpty: "No valid SKU suggestion returned.",
      skuRecommendationApply: "Apply suggestion",
      skuRecommendationApplied: "Applied",
      skuRecommendationPendingApply: "AI suggestion is ready. You can apply it with one click.",
      skuRecommendationConfidence: "Confidence {confidence}%",
      skuRecommendationSourceLabel: "Suggestion source: {source}",
      skuRecommendationSourceModel: "Model",
      skuRecommendationSourceCache: "Cache",
      skuRecommendationSourceFallback: "Fallback",
      skuRecommendationNoReason: "No model reason returned for this suggestion.",
      stepSelectSkuTitle: "Choose collected SKU",
      stepSelectSkuDesc: "Pick one SKU. The system will automatically match the primary image.",
      skuSelectedBadge: "SKU selected",
      skuOutOfStockBadge: "Out of stock",
      currentSku: "Current SKU",
      lockedSkuTitle: "Locked SKU title",
      skuPriceShort: "Price",
      stepPrimaryReferenceTitle: "Primary reference image locked",
      stepPrimaryReferenceDesc: "The primary image is auto-matched from the selected SKU.",
      primaryLockedBadge: "Auto locked",
      primaryReferenceShortBadge: "Primary",
      primaryReferenceImageTitle: "Primary reference image",
      primaryReferenceImageBadge: "Primary · auto locked",
      primaryDirectionTitle: "Primary image decides direction",
      primaryDirectionDesc: "Generation prioritizes this image for color, size, and style direction.",
      primaryCannotChangeHint: "The primary reference cannot be manually changed, avoiding drift.",
      openLargeImage: "View large",
      stepSupportingReferenceTitle: "Select supporting references (optional)",
      stepSupportingReferenceDesc: "Select up to 4 images to add more product angles and details.",
      supportingSelectedCount: "{count}/{max} selected",
      supportingReferenceRule: "Supporting references only add angles and details. They do not change primary color, size, or style direction.",
      supportingReferenceLimitHint: "Up to {max} supporting images.",
      supportingReferenceLimitReachedHint: "Limit reached. Remove one image before selecting another.",
      supportingReferenceMaxReached: "You can select up to {max} supporting reference images.",
      supportingReferenceImageTitle: "Supporting reference image #{index}",
      supportingReferenceSelectedTitle: "Selected supporting reference #{index}",
      supportingReferenceSelectedBadge: "Selected supporting",
      supportingReferenceUnselectedBadge: "Unselected",
      supportingReferenceSelect: "Select supporting reference",
      supportingReferenceDeselect: "Remove supporting reference",
      supportingReferenceBadge: "Support {index}",
      aiSendSummary: "Will send to AI: {primary} primary reference + {supporting}/{max} supporting references",
      footerSkuSelected: "SKU selected",
      footerPrimaryLocked: "Primary locked",
      footerPrimaryMatched: "Auto matched",
      footerSupportingCount: "Supporting {count}/{max}",
      footerSupportingAdjustable: "Up to 4",
      largeImagePreviewTitle: "View large image",
      previewPrevious: "Previous image",
      previewNext: "Next image",
      largeImageNoSelectionChange: "Viewing large images will not change selection.",
      stockUnits: "{count} Units",
      skuSelectionSyncFailed: "Failed to sync SKU selection. Please retry.",
      skuAnchorSyncFailed: "Failed to sync the preferred reference image. Please retry.",
      referenceScopeTitle: "Reference rule",
      referenceScopeLockedTitle: "Your SKU now defines the reference scope",
      referenceScopeLockedDesc: "AI will stay inside this SKU when choosing reference images.",
      referenceScopeLockedCount: "{count} candidate images are now in scope.",
      referenceScopeLockedNoImage:
        "No direct SKU image was found for this SKU. AI will fall back to text and full-image matching.",
      referenceScopeAutoTitle: "Reference scope is not locked yet",
      referenceScopeAutoDesc: "If you do not manually select SKU, AI will decide the reference scope automatically.",
      referenceScopePreferredOptional: "If you want tighter control, you can also choose 1 preferred reference image.",
      skuAnchorMissingHint: "No preferred reference image chosen yet. The system will pick one automatically inside the current scope.",
      skuAnchorLocked: "Preferred reference image: #{index}",
      skuAnchorOptionalTitle: "Preferred reference image (optional)",
      skuAnchorOptionalDesc: "If you do not choose one, the system will automatically pick 1 image inside the current SKU scope.",
      skuAnchorOptionalNoImageDesc:
        "This SKU has no directly available image yet. AI will supplement the preferred reference during generation.",
      skuAnchorManualTitle: "You chose preferred reference image #{index}",
      skuAnchorManualDesc: "Future generation will directly prioritize this image.",
      skuAnchorManualBadge: "Chosen by you",
      skuAnchorSystemTitle: "System currently prefers reference image #{index}",
      skuAnchorSystemDesc: "This is the default image automatically selected inside the current SKU scope.",
      skuAnchorSystemBadge: "System default",
      skuAnchorSystemSourceLabel: "Current default image comes from: {source}",
      skuAnchorSelectHint: "Choose 1 image below if you want to override the system default:",
      skuAnchorOptionTitle: "Set image #{index} as the preferred reference",
      skuAnchorSourceLabel: "Preferred image source: {source}",
      skuAnchorPreviewTitle: "Preferred reference image #{index}",
      skuAnchorPreviewHint: "Tap image to zoom and confirm details.",
      skuAnchorSelectAction: "Choose preferred image",
      skuAnchorChangeAction: "Change preferred image",
      skuAnchorSetAction: "Set as preferred",
      skuAnchorSelectedAction: "Preferred",
      skuAnchorExpand: "Change preferred image",
      skuAnchorCollapse: "Collapse",
      referenceSource: {
        manual: "chosen by you",
        auto_sku_match: "automatically matched inside the current SKU",
        sku_image_url: "SKU image",
        selected_sku_image_indices: "images inside your locked SKU scope",
        selected_sku: "the SKU you selected",
        selected_anchor: "your preferred image choice",
        sku: "SKU image",
        main: "main image",
        detail: "detail image",
        collection: "collected image",
        extracted: "extracted result",
        legacy: "historical cache",
        none: "not set",
        unknown: "unknown"
      },
      manualSkuReuseDisabledHint:
        "Your SKU now locks the reference scope. Historical reuse is disabled to avoid mismatched references.",
      noSkuData: "No SKU structure data detected yet.",
      productDetails: "Product Details",
      detailSummary: "Detail Summary",
      noDetailSummary: "No detail summary available yet.",
      noImages: "No images yet",
      previewImage: "Preview image",
      dataOverview: "Data Overview",
      overviewImages: "Images",
      overviewSkus: "SKUs",
      overviewAttributes: "Attributes",
      overviewSections: "Raw Sections",
      overviewRawText: "Raw Text Length",
      characters: "chars",
      noOverview: "No overview data yet.",
      flags: "Collection Flags",
      noFlags: "No flags detected yet.",
      flagEnabled: "Yes",
      flagDisabled: "No",
      dimensionsInfo: "Dimensions & Weight",
      noDimensions: "No dimension info available yet.",
      imageTags: "Image Tags",
      noImageTags: "No image tag data yet.",
      rawText: "Raw Text",
      noRawText: "No raw text available yet.",
      rawSections: "Raw Sections",
      validationWarning: "Data Validation Issues",
      issuesDetected: "issues detected that may affect AI optimization",
      manualReview: "Manual Review Required",
      allResolved: "All Issues Resolved",
      source: "Source",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price (1688)",
      priceRange: "Price Range",
      invalid: "Invalid",
      aiSuggest: "AI Suggest",
      marketAvg: "Market Avg. Price",
      suggestedAiPrice: "Suggested AI Price",
      profitMargin: "Profit Margin",
      marginDesc: "Estimated 72.4% net margin after typical shipping.",
      aiStrategy: "AI Optimization Strategy",
      seoEnhancements: "SEO Enhancements",
      seoDesc: "Will add \"orthopedic support\" and \"breathable mesh\" keywords to boost search visibility by 35%.",
      imageRefinement: "Image Refinement",
      imageDesc: "AI will remove 1688 watermarks and generate lifestyle backgrounds for main photos.",
      marketPositioning: "Market Positioning",
      positionDesc: "Targeting \"Affordable Premium\" segment with focus on urban durability.",
      skuAttributes: "SKU Attributes",
      colorVariants: "Color Variants",
      sizeAvailability: "Size Availability",
      currentStock: "Current Stock",
      shippingEst: "Shipping Est.",
      shippingLocal: "3-5 Days (Local)",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "MISSING",
      aiAutofill: "AI Autofill Missing Data",
      dataFilled: "Data Filled by AI",
      viewMetadata: "View All Raw Metadata",
      lowRes: "Low Res",
      dataRequired: "DATA VALIDATION REQUIRED",
      readyOptimize: "READY FOR OPTIMIZATION",
      agentsPaused: "Optimization agents paused",
      aiFillAll: "AI Fill All Missing Data",
      editTitle: "Edit product title",
      saveDraft: "Save Draft",
      saved: "Saved",
      draftSaved: "Saved successfully.",
      localDraftRestored: "Local browser draft restored.",
      localDraftRestoredPartial: "Local draft restored. Images changed, so some reference selections were skipped.",
      localDraftStorageUnavailable: "Browser storage is unavailable, so the draft cannot be saved locally.",
      saveDraftNoContext: "Open this preview from Import or Task Center before saving a draft.",
      saveDraftNoTitle: "Load or enter a product title before saving.",
      saveDraftUnavailable: "Draft cannot be saved yet.",
      saveDraftFailed: "Failed to save draft.",
      startOptimization: "Start AI Optimization",
      optimized: "Optimized",
      fixIssues: "Fix issues to improve AI results",
      exportToStore: "Export to Store",
      exporting: "Exporting...",
      exported: "Exported!",
      exportSuccess: "Product successfully exported to store!"
    },

    // Review (Updated for AI Agent Canvas)
    review: {
      nav: "AI Data Review",
      validating: "AI is suggesting values based on product category.",
      validated: "AI Optimization Successful. All fields validated.",
      pending: "Manual Review Required",
      completed: "Review Completed",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price",
      invalid: "Invalid",
      marketAvg: "Market Avg. Price",
      suggestedPrice: "Suggested AI Price",
      aiGenerated: "AI Generated",
      marginAnalysis: "Profit Margin Analysis",
      marginDesc: "Estimated 72.4% net margin after localized shipping.",
      strategy: "AI Optimization Strategy",
      seoEnhance: "SEO Enhancements",
      seoDesc: "Will add optimized keywords to boost visibility.",
      imgRefine: "Image Refinement",
      imgDesc: "AI will remove watermarks and generate lifestyle backgrounds.",
      marketPos: "Market Positioning",
      marketPosDesc: "Targeting 'Affordable Premium' segment for durability.",
      skuAttrs: "SKU Attributes",
      originalData: "Original Data",
      colorVariants: "Color Variants",
      sizeAvail: "Size Availability",
      stock: "Current Stock",
      shipping: "Shipping Est.",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "Missing",
      confirmBtn: "Confirmed All AI Suggestions",
      autofillBtn: "AI Autofill Missing Data",
      saveDraft: "Save Draft",
      applyAi: "Apply AI Changes",
      startAi: "Start AI Optimization",
      exportBtn: "Export to Store"
    },

    variantFlow: {
      stepperLabel: "Variant creation lifecycle",
      stepReadiness: "Master check",
      stepSelection: "Choose SKUs",
      stepRun: "Generate",
      stepReview: "Review",
      stepPublish: "Publish"
    },

    variantStart: {
      breadcrumbLabel: "Master check",
      title: "Master product readiness",
      desc: "This page only checks whether the master product can continue to variant creation.",
      masterProduct: "Master product",
      masterFallbackTitle: "Master product",
      publishId: "Publish ID",
      skuPool: "Variant SKU pool",
      publishStatus: "Publish status",
      statusUnknown: "Unknown",
      nextAction: "Next action",
      readyTitle: "Ready to choose variants",
      readyDesc: "{count} SKU(s) are available for this variant task.",
      blockedTitle: "Fix the master product first",
      blockedDesc: "The current master product still has blocking issues. Fix them in Ozon, then recheck here.",
      continueToSelection: "Continue to choose variants",
      loadFailed: "Failed to check master product readiness.",
      loadWarning: "Readiness check failed",
      issueListTitle: "Detected issues",
      checkPublished: "Published on Ozon",
      checkPublishedDesc: "The master product must have a successful Ozon publish record.",
      checkModeration: "Not under moderation",
      checkModerationDesc: "Ozon moderation must not block variant creation.",
      checkVisible: "Visible and active",
      checkVisibleDesc: "Archived or invisible products cannot become the active master.",
      checkPriceStock: "Price and stock are valid",
      checkPriceStockDesc: "Price and stock must be usable for downstream publish sync.",
      checkSkuPool: "Variant SKU pool exists",
      checkSkuPoolDesc: "{count} candidate SKU(s) can be selected in the next step."
    },

    // Variant Selection Matrix
    variantSelection: {
      breadcrumbLabel: "Variant Selection",
      pipelineTitle: "1688 to Ozon Pipeline",
      extractedTitle: "Extracted {count} Variants",
      filterAll: "ALL",
      bulkSelectHint: "+ click to bulk select",
      smartSelectBtn: "Apply Smart Selection",
      smartSelectHint: "SKU-only smart strategy picks representative candidates in one click.",
      smartSelectByFacet: "{facet} grouped strategy",
      smartSelectFallback: "SKU composite strategy",
      smartSelectApplied: "Applied smart selection: {count} SKU(s) · {strategy}",
      showingVariants: "Showing {visible} of {total} variants",
      loadMore: "Load more",
      noVariantsTitle: "No variants available",
      noVariantsDesc: "No additional variant SKUs were found in the 1688 source data besides the published master SKU.",
      noFilterResults: "No variants match this filter",
      clearFilter: "Clear filter",
      selectAll: "Select all",
      deselectAll: "Deselect all",
      selectedCount: "Selected {count} variant(s)",
      clearSelection: "Clear selection",
      generateSkus: "Generate Ozon SKUs ({count})",
      creatingTask: "Creating task...",
      taskCreateFailed: "Failed to create variant task. Please retry.",
      maxSelectableExceeded: "You can select up to {max} SKUs per task. Please reduce selection and retry.",
      selectFirst: "Select variants first",
      generate: "Generate ({count})",
      select: "Select",
      missingParams: "Required info missing",
      missingParamsDesc: "This page information has expired. Please return and reopen it.",
      backToTaskCenter: "Back to Task Center",
      back: "Back",
      loadingStatus: "Checking product status and loading SKU data...",
      loadFailed: "Load failed",
      reloadLabel: "Reload",
      issuesTitle: "Fix {count} issue(s) first",
      issuesDesc: "The master product cannot create variants yet. Please fix the issues below.",
      recheckBtn: "Recheck",
      goSellerCenter: "Go to Seller Center",
      issues: {
        notPublished: "Product not yet published",
        notPublishedDesc: "The master product must be successfully published to Ozon before creating variants.",
        notPublishedHint: "Check if the publish task has completed.",
        underModeration: "Product under Ozon review",
        underModerationDesc: "Ozon is reviewing your product. You can create variants after approval.",
        underModerationHint: "Review usually takes a few hours to one day. We will keep checking in the background.",
        archived: "Product archived",
        archivedDesc: "The product is archived in Ozon Seller Center.",
        archivedHint: "Restore the product in Ozon Seller Center.",
        notVisible: "Product not visible on Ozon",
        notVisibleDesc: "The product is currently not visible to buyers. It may have been rejected or delisted.",
        notVisibleHint: "Check the product status in Ozon Seller Center and fix any errors.",
        invalidPriceStock: "Price or stock needs attention",
        invalidPriceStockDesc: "The product has invalid price or stock information on Ozon.",
        invalidPriceStockHint: "Update the product price and stock in Ozon Seller Center.",
        statusUnavailable: "Cannot check product status",
        statusUnavailableDesc: "Unable to retrieve the latest product status from Ozon.",
        statusUnavailableHint: "We are retrying status checks in the background. You can continue with other work.",
        noStock: "Master product has no stock",
        noStockDesc: "The master product currently has 0 stock, but this won't prevent variant creation.",
        systemNotice: "System notice",
        issueDetected: "Issue detected",
        suggestedAction: "Suggested action"
      }
    },

    // Variant Progress (Transformation Pipeline)
    variantProgress: {
      breadcrumbLabel: "Variant Creation Progress",
      missingTaskEyebrow: "Task missing",
      backToSelection: "Back to Selection",
      processingTitle: "Processing {count} SKUs...",
      completedTitle: "Completed {count} SKUs",
      failedTitle: "Variant generation failed",
      cancelledTitle: "Variant task cancelled",
      processingDesc: "Loading the master product and preparing variant attributes.",
      processingDescAttributeOnly: "Loading the master product and rewriting variant attributes only.",
      overallProgress: "Overall Progress",
      taskEnded: "Task Completed",
      safeToLeave: "The system is processing variants automatically. You can safely leave this page.",
      fetchingProgress: "Fetching task progress...",
      itemSyncWarning: "Progress loaded, but SKU details are temporarily unavailable. Retrying...",
      pipelineItemsTitle: "SKU Transformation Pipeline",
      noItemSnapshots: "No SKU snapshots yet. Details will appear after preparation starts.",
      patchTitle: "AI Attribute Patches",
      patchEmpty: "No changes to display (legacy task or unavailable diff).",
      patchMissingDistinctive: "No editable variant attribute patch was produced.",
      patchAttrLabel: "Attr #{id}",
      patchUnknownAttribute: "Attribute",
      patchSystemDictionarySuffix: "System value",
      patchValueUnknown: "Original value unavailable",
      patchValueMissing: "No value",
      patchMore: "+{count} more patches",
      patchViewAll: "View all {count} patches",
      patchCollapse: "Collapse",
      statusLabel: "Status",
      missingTaskId: "Task info expired",
      missingTaskIdDesc: "This page has expired. Return to Task Center and open the task again.",
      backToTaskCenter: "Back to Task Center",
      viewResultPreview: "View Result Preview",
      cancelTask: "Cancel Task",
      cancelling: "Cancelling...",
      statusPending: "Waiting for execution",
      statusRunning: "Running",
      statusCompleted: "Completed",
      statusBlocked: "Blocked",
      statusFailed: "Error",
      statusCancelled: "Cancelled",
      statusUnknown: "Unknown",
      blockedTaskDesc: "The task was blocked by the master product gate and did not continue.",
      imageStageAutoFailedTitle: "Image generation auto-stopped",
      imageStageAutoFailedDesc: "Image generation was inactive for too long. The task has been auto-terminated, please retry.",
      stageAnalyze: "Load master product",
      stageAnalyzeDesc: "Checking the master product status and latest publish gate",
      stagePrepare: "Generate variant attributes",
      stagePrepareDesc: "Building attribute patches for the selected variant SKUs",
      stageImage: "Generate variant images",
      stageImageDesc: "Creating a main image for each variant",
      stagePublish: "Submit to Ozon",
      stagePublishDesc: "Submitting variants and awaiting Ozon confirmation",
      stageNotExecuted: "Not executed",
      stageDeferredManual: "Manual in next step",
      stageInProgress: "In progress",
      itemProgress: "Progress: {completed} / {total}",
      skuDone: "Done",
      skuDoneAttributeOnly: "Attribute rewrite completed",
      skuGeneratedSuccess: "Generated successfully",
      skuGenerating: "Generating image...",
      skuSubmitting: "Submitting to Ozon...",
      skuTranslating: "Translating...",
      skuPending: "Pending",
      skuFailed: "Generation Failed",
      skuFallbackBlocked: "Fallback blocked (publish blocked)",
      skuTimeoutAutoStopped: "Auto-stopped on timeout",
      skuCancelled: "Cancelled",
      skuSkipped: "Skipped",
      sourceImageLockVariantSku: "Locked SKU source image",
      sourceImageLockMasterInherited: "No SKU image, inherit master image set (publishable)",
      sourceImageLockPending: "Reference image not locked yet",
      skuOzonReady: "OZON READY",
      clickToZoom: "Click to enlarge",
      previewTitle: "Image Preview",
      sourceImagePreviewTitle: "Source Image",
      generatedImagePreviewTitle: "Generated Main Image",
      retryBtn: "Retry",
      retryFailed: "Retry Failed Items",
      warning: "Warning",
      retry: "Retry"
    },

    variantResult: {
      breadcrumbLabel: "Variant Result",
      missingTaskEyebrow: "Task missing",
      missingTaskId: "Task info expired",
      missingTaskIdDesc: "This page has expired. Return to Task Center and open the task again.",
      backToTaskCenter: "Back to Task Center",
      backToProgress: "Back to Progress",
      backToList: "Back to List",
      loadFailed: "Failed to load variant result. Please retry.",
      itemSyncWarning: "Result loaded, but SKU metadata is temporarily unavailable. Retrying...",
      retry: "Retry",
      title: "Variant Results",
      desc: "Review localized SKU outputs and compare source 1688 assets with Ozon-ready previews.",
      titleAttributeOnly: "Variant Attribute Result Preview",
      descAttributeOnly: "Review AI-generated variant attribute patches before the manual publish step.",
      viewGrid: "Grid View",
      viewTable: "Table View",
      loading: "Loading variant result...",
      summary: "Total {total} · Ready {ready} · Review {review} · Published {published}",
      summaryAttributeOnly: "Total {total} · Changed {changed} · No rewrite {unchanged} · Failed {failed}",
      cardTotal: "Total",
      cardReady: "Ready",
      cardReview: "Review",
      cardPublished: "Published",
      cardChanged: "Changed",
      cardUnchanged: "No rewrite",
      cardFailed: "Failed",
      systemMeta: "Task: {status} · Stage: {stage} · ID: {taskId}",
      publishSelectionTitle: "Manual selection before publish",
      selectedPublishPanelTitle: "Selected publish items",
      publishSelectionMeta: "Publishable {publishable} · Selected {selected}",
      selectedPublishCountLabel: "Selected",
      publishableCountLabel: "Publishable",
      blockedCountLabel: "Blocked",
      publishedCountLabel: "Published",
      selectedPublishEmptyHint: "Select publishable SKUs from the list before publishing.",
      selectedPublishNoPublishableHint: "No publishable SKUs are available on this result.",
      selectedPublishAllPublishedHint: "All SKUs in this result have already been published.",
      selectedPublishOverflow: "+{count} more",
      publishSelectionWaitTask: "Current task is still running. Please publish after preview finishes.",
      publishSelectionEmpty: "No publishable items currently available. Please check failed items first.",
      selectAllPublishable: "Select all",
      clearPublishSelection: "Clear",
      publishSelectedDirect: "Publish selected to Ozon ({count})",
      publishingSelectedDirect: "Publishing...",
      continueToPublishConfirm: "Continue to publish confirmation ({count})",
      aggregatedPublishBlocked: "Aggregated task view does not support publish submission. Open a single task result first.",
      publishContextAll: "Published {count} variants from the source task. This page only shows the new publish task.",
      publishContextPartial: "Published {count} variants from the source task. This page only shows the newly published subset.",
      publishContextRemaining: "{count} publishable variants still remain in the source task. Return to continue processing.",
      publishContextBackToSource: "Back to source result",
      publishVariants: "Publish variants ({count})",
      openPublishPreview: "Publish Preview ({count})",
      publishSubmitting: "Publishing...",
      publishFromPreviewFailed: "Failed to publish selected preview variants.",
      publishConflictRedirect: "Detected an active publish task. Redirecting to task {taskId}.",
      publishItemSelectable: "Publish",
      publishItemDisabled: "N/A",
      previewEditItem: "Preview / edit",
      previewUnavailableHint: "Preview is being prepared. It will appear automatically.",
      taskStatusUnknown: "unknown",
      stageUnknown: "unknown",
      publishTip: "Ozon may take about 24 hours to merge model variants automatically.",
      publishFinalizationPending: "Publish pending",
      publishFinalizationCompleted: "Published",
      publishFinalizationReviewing: "Under review",
      publishFinalizationFixRequired: "Fix required",
      publishFinalizationFailed: "Publish failed",
      publishFinalizationSummary: "Completed {completed} · Reviewing {reviewing} · Fix required {fixRequired} · Failed {failed}",
      tabAll: "All",
      tabAllReady: "All Ready",
      tabReviewRequired: "Manual Review Required",
      tabPublished: "Published",
      emptyTitle: "No records in this filter",
      emptyDesc: "Switch tabs or wait for pipeline updates.",
      priceLabel: "Price",
      mergeTypeLabel: "Merge",
      variantTypeLabel: "Type",
      errorPrefix: "Error:",
      openOzon: "Open on Ozon",
      tableSku: "SKU",
      tableStatus: "Status",
      tablePrice: "Price",
      tableMergeType: "Merge Type",
      tableAction: "Action",
      systemActive: "AI Localizer Active",
      systemActiveAttributeOnly: "AI Variant Attribute Engine Active",
      downloadReport: "Download Bulk Report",
      retryFailedPlain: "Retry Failed Items",
      doneAndBack: "Done and Back",
      compareBefore: "1688",
      compareAfter: "OZON",
      compareSliderAria: "Compare slider for {sku}",
      previewReady: "OZON READY",
      previewGenerating: "Generating...",
      previewReviewing: "Under review...",
      previewPending: "Pending...",
      previewFailed: "Preview unavailable",
      sourceImageLabel: "Source",
      generatedImageLabel: "Generated Main Image",
      generatedImageEmpty: "No generated image",
      imageStrategyLabel: "Image strategy",
      imageStrategyPending: "Pending",
      imageStrategyGenerated: "Generated from variant image",
      imageStrategyVariantSource: "Use variant source image",
      imageStrategyVariantFallback: "Generation failed, fallback to variant source image",
      imageStrategyMasterFallback: "No variant image, fallback to master image",
      imageStrategyMasterInherited: "No SKU image, inherit master image set",
      imageStrategyStandaloneReused: "Standalone product, reuse source image",
      imageStrategyNoImage: "No image available",
      publishModeStandaloneBadge: "Standalone product",
      statusReady: "Ready",
      statusGeneratedSuccess: "Generated successfully",
      statusPreviewReady: "Preview ready",
      statusStandalonePublishable: "Standalone publishable",
      statusFallbackBlocked: "Fallback blocked",
      statusTimeoutAutoStopped: "Auto-stopped on timeout",
      statusManualAttributeRequired: "Needs attribute input",
      statusRunning: "Generating",
      statusReviewing: "Reviewing",
      statusPending: "Pending",
      statusFailed: "Failed",
      statusCancelled: "Cancelled",
      statusSkipped: "Skipped",
      statusDescReady: "Localization completed and ready for publish review.",
      statusDescPublished: "This SKU has been published to Ozon. Use the Ozon link to view the marketplace result.",
      statusDescPublishInFlight: "This SKU has been submitted to Ozon and is waiting for marketplace review or status sync.",
      statusDescPublishFailed: "Publishing failed. Check the returned issue and retry after fixing it.",
      statusDescAttributeOnlyReady: "Attribute patches are ready for manual preview.",
      statusDescAttributeOnlyStandaloneReady: "No safe merge attribute was found, but this SKU is ready to publish as a standalone Ozon product.",
      statusDescAttributeOnlyRunning: "AI is still producing attribute patches.",
      statusDescAttributeOnlyPending: "Waiting for attribute analysis to start.",
      statusDescAttributeOnlyFailed: "No editable variant attribute patch was produced, so there is no field to supplement manually on the result page.",
      statusDescAttributeOnlyManualRequired: "Editable variant fields are available. Click AI Retry Attribute first; if AI still cannot identify it, use Preview/Edit to fill it manually.",
      statusDescAttributeOnlyImageFailed: "Attribute patches were generated, but image generation failed. Retry image generation.",
      statusDescAttributeOnlyImageFailedSuccessSegment: "Attribute patches were generated, ",
      statusDescAttributeOnlyImageFailedFailureSegment: "but image generation failed, ",
      statusDescAttributeOnlyImageFailedActionSegment: "retry image generation.",
      attributeRewriteSuccessHint: "Attribute rewrite succeeded.",
      statusDescAttributeOnlyFallbackBlocked: "Image fell back to source/master and was blocked from publish.",
      statusDescAttributeOnlyTimeout: "Image generation timed out and was auto-terminated. Retry is required.",
      standalonePatchHint: "No editable merge attribute is required for this fallback; the item will be submitted as a standalone product.",
      manualAttributePatchHint: "AI retry is preferred. If retry still cannot identify the difference, open Preview/Edit and fill the attribute manually.",
      retryAttributesHint: "Click AI Retry Attribute first; if it still cannot identify the difference, use manual edit as fallback.",
      retryAttributes: "AI Retry Attribute",
      retryAttributesShort: "AI Retry",
      retryingAttributes: "AI retrying...",
      retryAttributesFailed: "AI attribute retry failed. Please try again.",
      retryAttributesNoChange: "AI retry did not produce an effective attribute. You can manually fill it in Preview/Edit.",
      aggregatedAttributeRetryBlocked: "Aggregated task view does not support item-level AI retry. Open the single task result page first.",
      locateRetryAttributes: "Find AI retry items ({count})",
      fillManualAttributes: "Manual fallback",
      statusDescRunning: "Localized assets are being generated.",
      statusDescReviewing: "Submitted to Ozon and waiting for moderation confirmation.",
      statusDescPending: "Waiting for execution.",
      statusDescFailed: "Generation failed. Please retry failed items.",
      statusDescCancelled: "Task was cancelled before completion.",
      statusDescSkipped: "Skipped by pipeline constraints.",
      sourceImageLockVariantSku: "Locked SKU source image",
      sourceImageLockMasterInherited: "No SKU image, inherit master image set (publishable)",
      sourceImageLockPending: "Reference image not locked yet",
      mergeTypeModel: "Model",
      mergeTypeSimilarProduct: "Similar Product",
      mergeTypeProductInPhoto: "Product in Photo",
      mergeTypeBundleMerge: "Bundle Merge",
      mergeTypeBundleCreate: "Bundle Create",
      mergeTypeUnknown: "Unknown",
      manageVariantGroups: "Variant Task Manager",
      loadWarning: "Result may be incomplete",
      retryFailed: "Retry Failed ({count})",
      retryingFailed: "Retrying...",
      retryFailedError: "Failed to trigger failed-item retry.",
      regenerateImage: "Regenerate Image",
      regenerating: "Regenerating...",
      regenerateFailed: "Failed to regenerate variant image.",
      regenerateBillingMeta:
        "Image billing: {creditsPerImage} credits/image · This regenerate: {estimatedCredits} credits · Available: {availableCredits}",
      regenerateBillingSettlementHint:
        "The current selected model is charged first; after generation, billing settles by the actual successful model and differences are refunded or additionally deducted.",
      goRecharge: "Recharge Credits",
      creditsInsufficientHint: "Insufficient credits (need {needed}, current {current}). Please recharge and retry.",
    },

    variantPublishConfirm: {
      breadcrumbLabel: "Variant publish confirmation",
      eyebrow: "Publish confirmation",
      title: "Confirm variants to publish",
      desc: "This page only confirms the selected publish list and submits the publish task.",
      missingTitle: "Publish info is missing",
      missingDesc: "Return to the review page and choose the variants to publish again.",
      warningTitle: "Publish submission failed",
      retrySubmit: "Retry submission",
      tableSku: "SKU",
      tablePatch: "Attribute changes",
      tableImage: "Image",
      tableStatus: "Status",
      patchCount: "{count} change(s)",
      imageReady: "Ready",
      imageMissing: "Missing",
      readyToPublish: "Ready to publish",
      confirmTitle: "Publish list",
      confirmDesc: "Only these selected variants will be submitted.",
      riskHint: "After submission, Ozon may still review or merge variants asynchronously.",
      submit: "Confirm publish",
      submitting: "Submitting...",
      submitBlocked: "SKU details not recovered",
      submitFailed: "Failed to submit selected variants.",
      recovering: "Recovering details...",
      recoveringDesc: "This direct link only contains item IDs. Recovering SKU names, images, and change counts from the task result before enabling publish.",
      recoverFailed: "Failed to recover selected SKU details.",
      recoverIncomplete: "Some selected SKUs were not found in the task result. Please return to the review page and enter publish confirmation again.",
      recoverLoginRequired: "Please sign in first so the selected SKU details can be recovered safely.",
      recoveryBlockedTitle: "Publish list needs confirmation details",
      retryRecover: "Retry recovery",
      backToReview: "Back to review",
      submittedTitle: "Publish task submitted",
      submittedDesc: "{count} variant(s) have been handed to the publish task.",
      nextTitle: "What happens next",
      nextDesc: "Track publish execution on the progress page, then return to the variant manager after completion.",
      viewProgress: "View publish progress"
    },

    variantPublishPreview: {
      breadcrumbLabel: "Variant Publish Preview",
      title: "Variant Publish Preview",
      desc: "Review and edit variant fields before publish. Pricing can be recalculated per item or in bulk.",
      taskMeta: "Task: {status} · Stage: {stage}",
      errorTitle: "Preview might be incomplete",
      retry: "Retry",
      loadFailed: "Failed to load variant publish preview.",
      loading: "Loading variant publish preview...",
      summaryTotal: "Total Variants",
      summaryPublishable: "Publishable",
      summaryBlocked: "Blocked",
      summaryProfit: "Estimated Profit",
      publishSelectionMeta: "Publishable {publishable} · Selected {selected} · Total {total}",
      emptyTitle: "No variants available",
      emptyDesc: "No publish-preview data found for this task.",
      selectForPublish: "Select for publish",
      publishable: "Publishable",
      blocked: "Blocked",
      sourceImage: "Source Image",
      generatedImage: "Generated Main Image",
      fieldTitle: "Title",
      fieldPrice: "Price",
      fieldOldPrice: "Old Price",
      fieldMinPrice: "Min Price",
      fieldDictionaryIds: "Dictionary IDs (comma separated)",
      variantAttributes: "Variant Attributes (Editable)",
      noVariantAttributes: "No editable variant attributes.",
      masterAttributes: "Master Attributes (Read-only)",
      emptyMasterAttributes: "No inherited attributes.",
      lockedAttributes: "Locked Attributes (Non-editable)",
      emptyLockedAttributes: "No locked attributes.",
      pricingProfit: "Profit",
      pricingMargin: "Margin",
      pricingRoi: "ROI",
      saveItem: "Save Item",
      saving: "Saving...",
      saveFailed: "Failed to save variant edits.",
      recalculateItem: "Recalculate Pricing",
      recalculateAll: "Recalculate All Pricing",
      recalculating: "Recalculating...",
      recalculateFailed: "Failed to recalculate pricing.",
      imageStatus: "Image Status",
      imageStrategy: "Image Strategy",
      pricePreview: "Price Preview",
      backToResult: "Back to Result",
      backToVariantResult: "Back to variant result",
      backToVariantManager: "Back to variant task manager",
      invalidEntryDesc: "Page info expired. We are restoring it automatically in the background.",
      publishing: "Publishing...",
      publishAllVariants: "Publish All Publishable Variants ({count})",
      publishCurrentVariant: "Publish Current Variant Only",
      publishVariants: "Publish Variants ({count})",
      publishFailed: "Failed to publish selected variants.",
      manualReviewTitle: "Reference image confirmation required",
      manualReviewMessageDefault: "No variant reference image is available. Please choose whether to use master SKU image directly or regenerate with SKU text.",
      manualReviewUseMaster: "Use master SKU image",
      manualReviewGenerateWithText: "Regenerate with SKU text",
      manualReviewPendingHint: "This item cannot be published until manual confirmation is completed.",
      manualReviewApplying: "Applying...",
      manualReviewApplyFailed: "Failed to apply reference image decision.",
      manualReviewApplyInheritSuccess: "Confirmed: use master SKU image as reference.",
      manualReviewApplyGenerateQueued: "Regeneration with SKU text has been queued.",
    }
  },
  zh: {
    ".": ".",
    appName: "一键大卖",
    trustAppName: "一键大卖 Trust",

    // 导航
    nav: {
      features: "功能",
      pricing: "定价",
      faq: "常见问题",
      login: "登录",
      startFree: "免费开始"
    },
    common: {
      ok: "知道了",
      all: "全部",
      cancel: "取消",
      confirm: "确认",
      close: "关闭",
      openMenu: "打开菜单",
      closeMenu: "关闭菜单",
      clearSearch: "清空搜索",
      pagination: "分页",
      retry: "重试",
      saving: "保存中...",
      notice: "提示",
      aiThinking: "AI 正在思考",
      ozonLogoAria: "Ozon 标志",
      backToWorkspace: "返回工作区",
      backHome: "返回首页",
      verificationCodeDigit: "验证码第 {index} 位，共 {total} 位"
    },
    highRisk: {
      subscriptionTarget: "订阅：{name}",
      subscriptionCancelIrreversible: "确认后当前订阅将停止续费。",
      notificationTarget: "通知：{name}",
      notificationDeleteIrreversible: "这条通知将从收件箱中删除。",
      avatarDeleteAction: "删除头像",
      avatarTarget: "当前头像",
      avatarDeleteIrreversible: "当前头像将被删除。",
      collectionTarget: "任务：{name}",
      collectionDeleteIrreversible: "该任务将被删除，且无法恢复。",
      channelTarget: "AI 渠道：{name}",
      channelDeleteIrreversible: "该渠道将被删除，并不再参与路由。",
      ozonPublicCredentialsTarget: "Ozon 公共凭据",
      ozonPublicCredentialsClearIrreversible: "已保存的公共凭据将被清空。",
      imageTarget: "第 {index} 张图片",
      candidateRejectIrreversible: "当前待确认的生图候选将被拒绝。",
      imageGenerateCostIrreversible: "会按当前图片模式扣除 {credits} 积分。",
      imageDeleteIrreversible: "这张已生成图片将被删除。",
      imageDeleteFinal: "删除图片后无法撤销。"
    },
    appError: {
      title: "页面出错了",
      subtitle: "你可以刷新页面，或返回任务中心继续操作。",
      backTaskCenter: "返回任务中心",
      refresh: "刷新页面",
      copy: "复制错误信息"
    },

    theme: {
      light: "浅色",
      dark: "深色",
      switchToLight: "切换到浅色模式",
      switchToDark: "切换到深色模式"
    },

    appNav: {
      more: "更多",
      loginHint: "登录后可使用该功能。",
      unlockHint: "完成导入后解锁。",
      comingSoonBadge: "开发中",
      extensionConnected: "插件已连接",
      extensionMissing: "未检测到插件",
      extensionOffline: "插件离线",
      extensionUpgradeRequired: "插件版本过低",
      extensionUpgradeDetail: "当前 {current}，要求 {required}",
      extensionVersionUnknown: "未知版本",
      extensionTakeoverStatusLabel: "自动接管状态",
      extensionAwaitingSync: "等待站点自动接管",
      extensionAwaitingSyncDetail: "插件已安装成功，保持当前页面打开，系统会自动同步登录态。",
      extensionStatusChecking: "正在确认插件状态",
      extensionStatusCheckingDetail: "插件已安装，正在确认站点接管与后台就绪状态。",
      extensionBackendUnreachable: "暂时连不上服务",
      extensionBackendUnreachableDetail: "插件已安装，但当前服务地址暂时访问不了。请稍后重试，或检查本地/线上服务状态。",
      extensionReadyPolling: "自动轮询已就绪",
      extensionReadyPollingDetail: "站点接管已完成，插件正在等待后端任务并自动采集。",
      extensionCollecting: "自动采集中",
      extensionCollectingDetail: "插件正在后台自动采集商品数据。",
      extensionConnecting: "插件连接中",
      extensionConnectingDetail: "站点接管已完成，插件正在建立后台连接。",
      extensionDownload: "安装插件",
      extensionUpdate: "立即更新",
      extensionDownloadGuide: "正在打开插件安装包...",
      assetsComingSoon: "资产库正在开发中：用于统一管理生成/上传的图片、文案、上架记录等素材，支持检索、复用与跨平台同步。",
      copyrightComingSoon: "版权资产正在开发中：用于对 PSD/图片进行水印与指纹保护、生成侵权证据包，并提供申诉/维权模板，降低侵权风险。"
    },
    extensionGuide: {
      badge: "看图安装向导",
      title: "别猜，按图把 Chrome 插件装好",
      subtitle: "下载后保留一个文件夹，Chrome 里加载这个文件夹，然后回到本页刷新一次。",
      resumeHint: "已恢复你上次的步骤，直接继续即可。",
      progress: "第 {current} / {total} 步",
      sources: {
        header: "来自顶部导航入口",
        workspace: "来自工作区入口",
        import: "来自导入页入口"
      },
      labels: {
        expected: "你下一步应该看到",
        warning: "千万别这样做"
      },
      steps: {
        intro: {
          title: "一次安装，后面长期可用",
          desc: "按图一步一步来就行。最关键两点：保留完整文件夹，安装后必须刷新当前页面。",
          tip: "不要凭感觉乱点子目录，更不要跳过刷新这一步。"
        },
        download: {
          title: "第 1 步：下载 ZIP 安装包",
          desc: "把 ZIP 安装包下载到桌面或下载目录。",
          tip: "这一步结束后，你应该只看到 1 个 ZIP 文件，不是一堆散文件。"
        },
        unzip: {
          title: "第 2 步：解压成一个文件夹",
          desc: "解压后，你应该只得到 1 个文件夹：{folderName}。",
          tip: "不要把里面的文件拖出来乱放，整个文件夹要保持完整。"
        },
        openExtensions: {
          title: "第 3 步：打开 chrome://extensions/",
          desc: "打开 Chrome 扩展管理页，并保留当前引导页。",
          tip: "如果按钮没反应，就手动把 chrome://extensions/ 粘贴到地址栏。"
        },
        loadUnpacked: {
          title: "第 4 步：加载整个文件夹",
          desc: "打开“开发者模式”，点击“加载已解压的扩展程序”，选择整个 {folderName} 文件夹。",
          tip: "不要点进子目录，只能选最外层那个文件夹。"
        },
        refreshPage: {
          title: "第 5 步：回到本页刷新一次",
          desc: "Chrome 接受文件夹后，回到当前业务页面，立刻刷新一次。",
          tip: "不刷新当前页，这个标签页通常检测不到刚安装的插件。"
        },
        verify: {
          title: "第 6 步：刷新后自动检测",
          desc: "刷新后留在本页，我们会自动连续检测插件状态。",
          tip: "如果还检测不到，最常见原因就是选错文件夹，或刚才没有刷新页面。"
        },
        done: {
          title: "完成：本页已经接入插件",
          desc: "安装、刷新、检测都已经成功。",
          tip: "现在可以继续导入商品了。"
        }
      },
      expected: {
        intro: "全程只有一条 Chrome 路径，看图操作即可。",
        download: "你会在桌面或下载目录看到 1 个 ZIP 文件。",
        unzip: "你现在应该只看到 1 个文件夹：{folderName}。",
        openExtensions: "Chrome 会打开扩展管理页。",
        loadUnpacked: "扩展列表里会出现本插件卡片。",
        refreshPage: "当前业务页面会重新加载，并把插件注入到这个标签页。",
        verify: "这里的状态会从“检测中”变成“已连接/已就绪”。",
        done: "当前页面已经刷新过，插件也已经生效。"
      },
      warnings: {
        intro: "安装完一定要刷新当前页面，这一步不能省。",
        download: "不要直接在当前目录原地解压，避免文件全部散开。",
        unzip: "错误做法：选 ZIP、选父目录、或者把文件散得到处都是。",
        openExtensions: "不要进搜索结果页，地址必须是 chrome://extensions/。",
        loadUnpacked: "只能选整个 {folderName} 文件夹，不能点进去选子目录。",
        refreshPage: "不刷新当前页，插件可能其实装好了，但这个标签页还是检测不到。",
        verify: "如果还显示未找到，最大概率就是文件夹选错了，或者没刷新页面。",
        done: "以后如果升级插件，当前页面也要再刷新一次。"
      },
      help: {
        title: "我卡住了怎么办",
        show: "展开提示",
        hide: "收起提示",
        intro1: "按顺序做，不要跳过“刷新当前页”这一步。",
        intro2: "如果你把引导关了，随时可以从 Header、工作区、导入页重新打开。",
        download1: "如果没有开始下载，检查 Chrome 是否拦截了弹窗或下载。",
        download2: "ZIP 文件先别动，等到下一步再解压。",
        unzip1: "正确结果一定是 1 个文件夹：{folderName}。",
        unzip2: "错误结果是一堆散文件直接躺在桌面或下载目录里。",
        openExtensions1: "地址必须完整写成 chrome://extensions/。",
        openExtensions2: "最好一个标签页看引导，另一个标签页操作扩展页。",
        loadUnpacked1: "“开发者模式”一般在扩展页右上角。",
        loadUnpacked2: "只能选整个 {folderName} 文件夹，不能选子目录，也不能选 ZIP。",
        refreshPage1: "刷新是必须的，因为当前标签页要重新注入插件脚本。",
        refreshPage2: "刷新后我们会自动重新打开引导，并继续检测。",
        verify1: "如果检测超时，先再刷新一次，再检查是不是选错了文件夹。",
        verify2: "如果提示服务暂时连不上或等待登录同步，说明安装已经成功，问题不在安装。",
        done1: "以后如果当前页又不响应，可以点顶部插件状态重新检测。",
        done2: "这个引导你随时都可以重新打开。"
      },
      actions: {
        close: "关闭",
        prev: "上一步",
        start: "开始安装",
        next: "下一步",
        downloadZip: "下载安装包",
        downloadDone: "ZIP 已下载",
        confirmFolderReady: "我已经看到 {folderName}",
        openChromeExtensions: "打开 chrome://extensions/",
        backToExtensionsPage: "返回扩展管理页",
        confirmLoadUnpacked: "我已经选了 {folderName} 文件夹",
        refreshNow: "立即刷新当前页面",
        retryDetect: "重新检测",
        downloadUpdate: "下载最新版"
      },
      status: {
        ready: "插件已连接成功",
        needUpgrade: "当前版本 {current}，请升级到 {required} 或更高版本。",
        unknownVersion: "未知版本",
        authRequired: "插件已安装，正在等待站点同步登录态。",
        backendUnreachable: "插件已安装，但当前服务暂时连不上。",
        readyPolling: "插件已就绪，正在等待后端任务。",
        collecting: "插件正在自动采集中。",
        connecting: "插件正在连接后端...",
        connectedWaiting: "已检测到插件，正在完成最后校验...",
        offline: "插件可能离线了，请刷新 Chrome 后重试。",
        verifyTimeout: "检测超时，请先刷新页面或重新检查文件夹。",
        detecting: "正在检测插件状态..."
      },
      verify: {
        successHint: "一切正常，马上进入完成页。",
        upgradeHint: "插件已经装上了，但当前版本太旧。",
        authRequiredHint: "安装已经成功，当前站点正在把登录态同步给插件。",
        backendUnreachableHint: "插件已经装好了，请确认当前后端服务可以访问。",
        readyPollingHint: "刷新已经生效，插件正在等待后端任务。",
        collectingHint: "很好，插件已经在后台自动采集。",
        connectingHint: "插件已拿到站点凭证，正在建立后台连接。",
        timeoutHint: "先刷新当前页；如果还不行，再重新选择 {folderName} 文件夹。",
        detectingHint: "自动检测中（第 {attempts} 次）..."
      },
      doneDesc: "当前插件版本：{version} · 最低要求版本：{required}。当前页面已经刷新完成，现在可以继续操作。",
      doneActions: {
        workspace: "去导入商品",
        import: "立即开始导入",
        header: "继续当前操作"
      }
    },

    admin: {
      title: "管理后台",
      subtitle: "运营与配置中心",
      backToWorkspace: "返回工作台",
      login: {
        title: "管理员登录",
        subtitle: "请输入管理员账号密码",
        checking: "正在验证登录状态...",
        username: "管理员账号",
        usernamePlaceholder: "请输入管理员账号",
        password: "管理员密码",
        passwordPlaceholder: "请输入管理员密码",
        action: "登录后台",
        loggingIn: "登录中...",
        required: "请输入账号和密码",
        failed: "登录失败",
        denied: "无管理员权限",
        logout: "退出登录"
      },
      nav: {
        overview: "概览",
        aiRuntime: "AI 运行配置",
        aiChannels: "AI 渠道",
        aiMonitor: "渠道监控",
        aiUsage: "AI 用量统计",
        marketing: "营销分析",
        billing: "计费与积分",
        consumption: "积分流水",
        users: "用户管理",
        feedback: "用户反馈",
        logs: "操作日志",
        announcements: "公告管理",
        imagePrompts: "生图提示词",
        settings: "系统设置"
      },
      imagePrompts: {
        title: "生图提示词管理",
        desc: "独立管理生图提示词模板与统一约束，保存后立即生效。",
        loading: "正在加载生图提示词配置...",
        error: "加载生图提示词配置失败。",
        empty: "暂无生图提示词配置。",
        searchPlaceholder: "按配置键 / 分类 / 说明搜索",
        onlyChanged: "仅显示已修改",
        save: "保存",
        saving: "保存中...",
        saved: "生图提示词配置已保存。",
        saveConfirm: "确认保存当前生图提示词配置？",
        saveAll: "保存全部改动（{count}）",
        saveAllConfirm: "确认保存全部 {count} 项改动？",
        reset: "恢复当前值",
        resetAll: "恢复全部改动",
        updatedAt: "更新时间 {time}",
        realtimeHint: "保存后会实时应用到生图规划、提示词预览、重新生图与变体生图。",
        validationEmpty: "提示词内容不能为空。",
        unsavedChanges: {
          leaveTitle: "存在未保存修改",
          leaveConfirm: "你有未保存的生图提示词修改，离开后将丢失，是否继续离开？",
          leaveNow: "确认离开",
          stay: "留在当前页"
        },
        categories: {
          constraint: "统一约束",
          common: "公共模板",
          carousel: "主图/轮播模板",
          detail: "详情图模板",
          variant: "变体图模板"
        }
      },
      announcements: {
        title: "公告管理",
        desc: "发布全站重大通知，管理生效窗口、上下线和置顶优先级。",
        refresh: "刷新",
        loading: "正在加载公告...",
        loadFailed: "加载公告失败。",
        createTitle: "发布新公告",
        createDesc: "用于发布关键迁移、维护窗口和重大变更通知。",
        submit: "立即发布",
        submitting: "发布中...",
        createSuccess: "公告发布成功。",
        createFailed: "公告发布失败。",
        status: "状态",
        listTitle: "公告历史",
        empty: "当前筛选下暂无公告。",
        actionPublish: "重新上线",
        actionOffline: "下线",
        actionPin: "置顶",
        actionUnpin: "取消置顶",
        confirmPublish: "确认重新上线这条公告吗？",
        confirmOffline: "确认下线这条公告吗？",
        statusUpdated: "公告状态已更新。",
        statusUpdateFailed: "更新公告状态失败。",
        pinUpdated: "置顶顺序已更新。",
        pinUpdateFailed: "更新置顶顺序失败。",
        moveUp: "上移",
        moveDown: "下移",
        pinnedOrder: "置顶第 {order} 位",
        publishedAt: "发布时间",
        endsAt: "失效时间",
        actionText: "按钮文案",
        actionUrl: "按钮链接",
        filters: {
          all: "全部",
          active: "生效中",
          scheduled: "待生效",
          expired: "已过期",
          offline: "已下线"
        },
        severity: {
          info: "通知",
          warning: "重要",
          critical: "紧急"
        },
        form: {
          title: "公告标题",
          titlePlaceholder: "例如：图片积分计费迁移通知",
          content: "公告正文",
          contentPlaceholder: "请说明影响范围、生效时间、用户需执行的动作。",
          severity: "公告级别",
          pinned: "发布后置顶",
          startsAt: "开始时间",
          endsAt: "结束时间",
          actionText: "按钮文案（可选）",
          actionTextPlaceholder: "例如：查看详情",
          actionUrl: "按钮链接（可选）",
          actionUrlPlaceholder: "例如：/settings/notifications 或 https://...",
          required: "标题和正文不能为空。",
          invalidStartAt: "开始时间格式无效。",
          invalidEndAt: "结束时间格式无效。",
          invalidWindow: "结束时间必须晚于开始时间。"
        }
      },
      marketing: {
        title: "营销埋点分析",
        desc: "查看 PV、停留时长、CTA 漏斗与 UTM 归因效果。",
        loading: "正在加载营销分析数据...",
        error: "营销分析数据加载失败。",
        empty: "当前筛选下暂无营销埋点数据。",
        apply: "应用筛选",
        reset: "重置筛选",
        updatedAt: "更新时间：{time}",
        cards: {
          pv: "总 PV",
          visitors: "独立访客",
          engagement: "平均停留",
          ctaClicks: "CTA 点击",
          conversions: "转化次数",
          conversionRate: "转化率"
        },
        filters: {
          days: "时间范围",
          utmSource: "UTM 来源",
          utmMedium: "UTM 媒介",
          utmCampaign: "UTM 活动",
          experimentId: "实验 ID",
          experimentVariant: "实验分组",
          pagePath: "页面路径"
        },
        business: {
          cards: {
            paidOrders: "支付订单数",
            revenue: "归因收入",
            adCost: "广告成本",
            grossProfit: "毛利润",
            roas: "ROAS",
            roi: "ROI",
            aov: "客单价",
            cac: "单支付获客成本",
            cpa: "单转化成本"
          }
        },
        trend: {
          title: "按天 PV/UV/转化趋势",
          engagementTitle: "按天平均停留时长（秒）",
          series: {
            pv: "PV",
            uv: "UV",
            conversions: "转化",
            engagement: "平均停留（秒）"
          }
        },
        funnel: {
          title: "CTA 转化漏斗",
          view: "浏览",
          ctaClick: "CTA 点击",
          conversion: "转化"
        },
        utm: {
          title: "UTM 归因"
        },
        experiment: {
          title: "实验归因",
          empty: "当前筛选下暂无实验归因数据。",
          columns: {
            id: "实验",
            variant: "分组",
            pv: "PV",
            uv: "UV",
            conversions: "转化",
            paidOrders: "支付订单",
            revenue: "收入",
            adCost: "成本",
            roas: "ROAS",
            roi: "ROI",
            cvr: "CVR"
          }
        },
        pages: {
          title: "页面维度明细",
          empty: "当前筛选下暂无页面明细数据。",
          summary: {
            highPriorityPages: "高优先级页面",
            mediumPriorityPages: "中优先级页面",
            lowPriorityPages: "低优先级页面",
            avgScore: "平均优化评分"
          },
          priority: {
            high: "高优先级",
            medium: "中优先级",
            low: "低优先级"
          },
          signals: {
            HIGH_BOUNCE_RATE: "跳出率偏高",
            LOW_CTA_RATE: "CTA 点击率偏低",
            LOW_CTA_TO_CONVERSION: "CTA 后转化率偏低",
            HIGH_ENGAGEMENT_LOW_CONVERSION: "停留较高但转化偏低",
            NO_ENGAGEMENT_EVENTS: "缺少停留事件数据"
          },
          actions: {
            OPTIMIZE_ABOVE_THE_FOLD: "优先优化首屏价值表达与首屏信息结构",
            IMPROVE_CTA_VISIBILITY: "提高 CTA 位置可见性并强化按钮文案",
            REDUCE_FORM_FRICTION: "减少表单与下单流程阻塞，缩短转化路径",
            ALIGN_CONTENT_AND_OFFER: "让页面内容与活动权益、用户意图更一致",
            VERIFY_ENGAGEMENT_TRACKING: "补齐停留类埋点，避免分析盲区",
            ENRICH_TRAFFIC_QUALITY: "优化流量来源与投放定向质量",
            SCALE_WINNING_PAGE: "可加大该页面流量投放并扩量验证"
          },
          columns: {
            rank: "优先级排名",
            priority: "优先级",
            score: "优化评分",
            page: "页面",
            events: "事件数",
            pv: "PV",
            uv: "UV",
            uvAll: "总 UV",
            uvPageView: "PV UV",
            entry: "入口",
            exit: "出口",
            bounce: "跳出",
            engagement: "平均停留",
            engagementEvents: "停留事件",
            ctaClicks: "CTA 点击",
            ctaRate: "CTA 率",
            conversions: "转化",
            cvr: "CVR",
            cvrPv: "PV 转化率",
            cvrFromCta: "CTA 转化率",
            paidOrders: "支付订单",
            revenue: "收入",
            adCost: "成本",
            roas: "ROAS",
            roi: "ROI",
            aov: "客单价",
            cac: "单支付成本",
            cpa: "单转化成本",
            signals: "问题信号",
            actions: "优化动作",
            topEvents: "事件构成"
          }
        },
        table: {
          source: "来源",
          medium: "媒介",
          campaign: "活动",
          pv: "PV",
          uv: "UV",
          conversions: "转化",
          paidOrders: "支付订单",
          revenue: "收入",
          adCost: "成本",
          roas: "ROAS",
          cvr: "CVR"
        }
      },
      aiUsage: {
        title: "AI 用量统计",
        desc: "按供应商、调用类型、模型维度分析 Token 消耗",
        loading: "正在加载 AI 用量数据...",
        error: "AI 用量数据加载失败。",
        empty: "当前筛选下暂无用量记录。",
        emptyWithLatest: "当前时间窗口暂无记录，最近一条记录时间：{latest}",
        apply: "应用",
        reset: "重置",
        summary: "时间范围：{start} ~ {end}",
        latestRecordAt: "最近记录：{value}",
        cards: {
          totalCalls: "总调用次数",
          totalTokens: "总 Token",
          totalInputTokens: "输入 Token",
          totalOutputTokens: "输出 Token",
          imageCalls: "图片调用",
          nonImageCalls: "非图片调用",
          avgTokens: "单次均值",
          quantity: "数量"
        },
        distribution: {
          provider: "AI 提供商分布",
          supplier: "供应商ID分布",
          callType: "调用类型分布",
          model: "模型分布"
        },
        coverage: {
          title: "Token 口径覆盖",
          exact: "精确拆分：{value}",
          estimated: "估算拆分：{value}",
          missing: "缺失拆分：{value}"
        },
        legend: {
          imageTokens: "图片 Token"
        },
        trend: {
          title: "趋势图"
        },
        cost: {
          title: "成本估算",
          description: "可按当前渠道定价口径手工调整，便于估算真实支出。",
          reset: "恢复默认值",
          inputPrice: "输入 Token 单价（USD / 100万）",
          outputPrice: "输出 Token 单价（USD / 100万）",
          imageCost: "图片成本（RMB/张）",
          exchangeRate: "美元兑人民币",
          totalTokenCost: "Token 成本",
          imageCostTitle: "图片成本",
          totalCost: "总计成本",
          avgPerTask: "单次均摊成本",
          tokenPerNonImageCall: "非图片单次均摊"
        },
        filters: {
          periodStart: "开始时间",
          periodEnd: "结束时间",
          interval: "趋势粒度",
          all: "全部",
          image: "仅图片调用",
          yes: "是",
          no: "否",
          userId: "用户ID",
          supplierId: "供应商ID",
          provider: "AI 提供商",
          model: "模型",
          callType: "调用类型",
          action: "Action",
          pipelineStage: "Pipeline Stage",
          stage: "Stage",
          runId: "Run ID",
          callId: "Call ID"
        },
        intervals: {
          hour: "小时",
          day: "天",
          week: "周",
          month: "月"
        },
        table: {
          time: "时间",
          user: "用户",
          supplierId: "供应商ID",
          runId: "Run ID",
          callId: "Call ID",
          action: "动作",
          provider: "供应商",
          model: "模型",
          callType: "调用类型",
          tokens: "Token",
          splitMethod: "拆分口径",
          estimatedCost: "预估成本",
          image: "是否图片",
          metadata: "元数据"
        },
        records: "明细记录",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页"
      },
      navGroups: {
        overview: "概览",
        ai: "AI 控制台",
        operations: "运营管理",
        system: "系统管理"
      },
      access: {
        checking: "正在验证管理员权限...",
        loginTitle: "请先登录",
        loginDesc: "管理后台需要登录后访问。",
        loginAction: "立即登录",
        deniedTitle: "无权限访问",
        deniedDesc: "当前账号不是管理员。"
      },
      listStates: {
        loading: {
          title: "正在同步列表数据",
          description: "请稍候，系统正在拉取最新记录。"
        },
        error: {
          title: "列表加载失败",
          description: "请重试以获取最新数据。",
          detailPrefix: "错误详情："
        },
        empty: {
          title: "暂无可展示数据",
          description: "可尝试调整筛选条件或刷新后重试。"
        },
        actions: {
          retry: "重试",
          retrying: "重试中...",
          refresh: "刷新数据",
          backToOverview: "返回概览"
        }
      },
      overview: {
        title: "数据概览",
        desc: "核心指标与最近趋势",
        loading: "正在加载概览数据...",
        error: "加载概览数据失败。",
        empty: "暂无数据。",
        lastUpdated: "最近更新 {time}",
        ready: "已就绪",
        refreshing: "刷新中...",
        refresh: "刷新",
        trendLoading: "正在加载趋势...",
        userTrend: "最近 7 天新增用户",
        revenueTrend: "最近 7 天收入",
        cards: {
          totalUsers: "累计用户",
          activeToday: "今日活跃",
          newToday: "今日新增",
          totalRevenue: "累计收入",
          revenueMonth: "本月收入",
          consumptionToday: "当日消费",
          totalConsumption: "累计消费"
        },
        units: {
          users: "用户",
          usd: "USD",
          credits: "积分"
        }
      },
      aiChannels: {
        title: "AI 接入与调度",
        desc: "统一管理渠道资产、模型调度与健康状态",
        routingGuide: {
          title: "渠道资产、模型调度、用户扣费已拆分管理",
          desc: "本页统一管理 Key / Base URL / 健康检查 / 协议能力，以及各 AI 模式背后的高级调度策略；用户侧生图价格仍在「系统设置」里按普通模式 / 官方高速模式统一设置。",
          badge: "渠道资产",
          cards: {
            keys: {
              title: "1. 渠道资产",
              desc: "创建官方或第三方中转渠道，维护协议能力、优先级、有效期与启停状态。"
            },
            routing: {
              title: "2. 后台调度",
              desc: "在下方「高级调度策略」里绑定模型、fallback 顺序和具体渠道策略。"
            },
            price: {
              title: "3. 用户扣费",
              desc: "普通 / 官方高速的积分单价在系统设置里修改，不在本页按单个渠道定价。"
            }
          }
        },
        runtimePanel: {
          title: "高级调度策略",
          hint: "模型、fallback 与各模块覆盖配置",
          notice: "这是高级配置区。修改这里会影响实际走哪个模型/渠道，但不会改变用户侧生图扣费单价。"
        },
        error: "加载渠道失败。",
        testResult: {
          success: "测试通过",
          failed: "测试失败",
          successMessage: "渠道测试成功。",
          failureUnknown: "测试失败，请查看原因：",
          failureReason: "失败原因",
          checkedAt: "检测时间：{time}"
        },
        validationName: "请输入渠道名称。",
        validationKey: "请输入 API Key。",
        validationBaseUrl: "第三方中转渠道与非常规官方协议都必须填写 Base URL。",
        validationSupportedProviders: "第三方中转渠道至少要勾选一个支持协议。",
        validationDefaultProvider: "默认协议必须在已勾选的支持协议中。",
        validationExpiresAtRequired: "请设置有效期时间，或勾选永久有效。",
        validationExpiresAtInvalid: "有效期时间格式不正确。",
        validationVertexProjectLocation: "Vertex AI 渠道必须填写 GCP Project 和 Location。",
        validationVertexProjectLocationPair: "GCP Project 和 Location 需同时填写或同时留空。",
        enableConfirm: "确认启用渠道 {name}？",
        disableConfirm: "确认停用渠道 {name}？",
        testConfirm: "确认测试渠道 {name}？",
        updated: "渠道已更新。",
        created: "渠道已创建。",
        deleted: "渠道已删除。",
        tested: "测试已完成。",
        deleteConfirm: "确认删除渠道 {name}？",
        officialBaseUrl: "官方 Base URL：{url}",
        vertexBaseUrlHint: "Vertex AI 渠道无需配置 Base URL。",
        vertexAuthHint: "Vertex AI 可直接使用 API Key；Project/Location 为可选项，填写时需成对提供。",
        hints: {
          neverExpires: "当前渠道为永久有效。"
        },
        refresh: "刷新",
        updatedAt: "更新时间 {time}",
        edit: "编辑渠道",
        create: "新增渠道",
        newChannel: "新建渠道",
        cancel: "取消编辑",
        formHint: "编辑时留空 API Key 可保持原值。",
        channelTypeHint: "官方渠道按厂商管理；第三方中转渠道可在同一个 Key 下暴露多个协议。",
        searchPlaceholder: "按名称 / Base URL 搜索",
        filters: {
          all: "全部渠道",
          enabled: "仅启用",
          disabled: "仅停用",
          official: "仅官方",
          custom: "仅中转",
          degraded: "仅异常"
        },
        summary: {
          total: "渠道总数",
          enabled: "启用渠道",
          degraded: "异常渠道",
          requests: "24小时请求",
          failureRate: "24小时失败率"
        },
        providers: {
          developerAPI: "Gemini Developer API",
          vertexAI: "Vertex AI",
          openaiCompatible: "OpenAI 兼容协议",
          googleOfficial: "Google 官方",
          customProxy: "自定义代理"
        },
        vendors: {
          gemini: "Google Gemini",
          qwen: "Qwen",
          openai: "OpenAI",
          claude: "Claude / Anthropic",
          custom: "自定义"
        },
        fields: {
          name: "渠道名称",
          apiKey: "API Key",  // pragma: allowlist secret
          provider: "Provider",
          channelType: "渠道类型",
          vendor: "厂商",
          protocol: "协议",
          supportedProviders: "支持协议",
          defaultProvider: "默认协议",
          baseUrl: "Base URL",
          gcpProject: "GCP Project",
          gcpLocation: "GCP Location",
          tier: "等级",
          priority: "优先级",
          creditsPerImage: "图片单价(积分/张)",
          expiresAt: "有效期截止时间",
          maxFail: "最大失败次数",
          recovery: "恢复时间（秒）",
          description: "描述",
          enabled: "启用",
          isOfficial: "官方渠道",
          neverExpires: "永久有效"
        },
        placeholders: {
          name: "例如：Gemini-Primary",
          apiKey: "粘贴 API Key",  // pragma: allowlist secret
          apiKeyEdit: "留空则保持不变",
          baseUrl: "https://api.example.com",
          gcpProject: "例如：my-gcp-project",
          gcpLocation: "例如：us-central1",
          creditsPerImage: "留空则使用系统定价",
          description: "用途说明"
        },
        save: "保存",
        saving: "保存中...",
        listTitle: "渠道列表",
        vendorProtocolHint: "统一以“渠道类型 + 协议能力”管理。第三方中转渠道可用一个 Key 暴露多种协议。",
        protocolHint: "协议决定当前官方渠道使用的接入方式。",
        supportedProvidersHint: "第三方中转渠道可同时暴露多个协议，调度策略会按配置切换。",
        defaultProviderHint: "当路由未显式指定协议时，将使用默认协议进行测试与回退。",
        loading: "正在加载渠道...",
        empty: "暂无渠道配置。",
        statusUnknown: "未知",
        table: {
          name: "渠道",
          status: "状态",
          channelType: "类型",
          supportedProviders: "协议能力",
          defaultProvider: "默认协议",
          vendor: "厂商",
          protocol: "协议",
          priority: "优先级",
          creditsPerImage: "积分/张",
          baseUrl: "Base URL",
          requests: "24小时请求",
          failRate: "24小时失败率",
          updatedAt: "更新时间",
          avgLatency: "平均耗时",
          validUntil: "有效期",
          permanent: "永久有效",
          expired: "已过期",
          actions: "操作",
          scrollHint: "可横向滑动查看完整列。"
        },
        types: {
          official: "官方",
          proxy: "第三方中转"
        },
        actions: {
          edit: "编辑",
          disable: "停用",
          enable: "启用",
          enabling: "启用中...",
          disabling: "停用中...",
          test: "测试",
          testing: "测试中...",
          delete: "删除",
          deleting: "删除中..."
        }
      },
      aiRuntime: {
        title: "AI 调度配置",
        desc: "按模块配置模型、思考强度和渠道策略",
        quickPolicyTitle: "快捷路由策略",
        quickPolicyDesc: "一键强制所有分析模块走官方渠道；生图仍由用户模式决定。",
        manageChannels: "管理渠道",
        analysisOfficialOnlyLabel: "分析模块强制官方渠道",
        analysisOfficialOnlyHintOn: "已开启：所有分析模块固定走官方渠道，仅图片生成可使用普通模式。",
        analysisOfficialOnlyHintOff: "已关闭：分析模块将按下方全局/模块渠道策略执行。",
        enabledOfficialChannels: "已启用官方渠道：{count}",
        enabledThirdPartyChannels: "已启用第三方渠道：{count}",
        loading: "正在加载调度配置...",
        error: "加载调度配置失败。",
        saved: "调度配置已更新。",
        save: "保存配置",
        saving: "保存中...",
        refresh: "重新拉取配置",
        refreshing: "刷新中...",
        refreshed: "已重新拉取调度配置。",
        refreshConfirm: "是否放弃未保存修改并重新拉取当前调度配置？",
        saveConfirm: "即将保存调度配置并立即生效，确认继续？",
        reset: "重置未保存改动",
        summaryTitle: "运行态总览",
        statusDirty: "有未保存改动",
        statusSaved: "已保存",
        global: "全局默认",
        globalHint: "全局配置会被所有模块继承，可在模块中单独覆盖。",
        modules: "模块覆盖",
        channelsHint: "当选择指定渠道时，故障切换仅在所选渠道中进行。",
        emptyChannels: "暂无渠道，请先配置 AI 渠道。",
        effectiveConfig: "最终生效配置",
        copyGlobal: "复制全局配置",
        searchResultCount: "当前显示 {count} 个模块",
        groups: {
          all: "全部",
          extract: "提取",
          content: "内容",
          image: "图片"
        },
        summary: {
          total: "模块总数",
          enabled: "启用中",
          override: "有覆盖配置",
          specific: "指定渠道"
        },
        fields: {
          provider: "Provider",
          model: "模型",
          thinking: "思考强度",
          strategy: "渠道策略",
          channels: "渠道",
          fallbackModels: "回退模型",
          enabled: "启用"
        },
        hints: {
          globalProvider: "模块未单独设置 Provider 时，默认继承这里的配置。",
          globalModel: "模块未设置模型时，继承该模型。",
          globalThinking: "模块未设置思考强度时，使用该默认值。",
          globalStrategy: "模块未设置策略时，继承该策略。",
          globalChannels: "仅当渠道策略为指定渠道时生效。",
          moduleProvider: "覆盖模块 Provider；留空则继承全局默认。",
          moduleModel: "覆盖模块模型；留空则继承全局默认。",
          moduleThinking: "覆盖模块思考强度；留空则继承全局默认。",
          moduleStrategy: "覆盖模块路由策略。",
          moduleChannels: "选择指定渠道后，仅在勾选渠道中路由请求。",
          moduleEnabled: "控制该模块是否参与运行。"
        },
        providers: {
          gemini_developer_api: "Gemini Developer API",
          vertex_ai: "Vertex AI",
          openai_compatible: "OpenAI 兼容协议"
        },
        strategy: {
          auto: "自动",
          official_only: "仅官方渠道",
          specific_channels: "指定渠道"
        },
        inherit: "继承全局",
        moduleSearch: "搜索模块",
        moduleCount: "{count} 个模块",
        selectedModule: "当前模块"
      },
      aiMonitor: {
        title: "渠道运行监控",
        desc: "查看渠道实时状态与健康度",
        loading: "正在加载运行状态...",
        error: "加载渠道状态失败。",
        refreshing: "刷新中...",
        refresh: "刷新",
        autoRefreshOn: "自动刷新：开启",
        autoRefreshOff: "自动刷新：关闭",
        autoRefresh: "自动刷新",
        updatedAt: "更新时间 {time}",
        current: "当前渠道",
        total: "渠道数量",
        healthy: "健康渠道",
        requests: "24小时请求",
        failureRate: "失败率",
        imageSuccessRate: "生图成功率",
        imageFailureRate: "生图失败率",
        officialTextSuccessRate: "官方文字成功率",
        officialTextFailureRate: "官方文字失败率",
        avgLatency: "平均耗时",
        requestUnit: "次",
        percentUnit: "%",
        msUnit: "ms",
        trend: "调用趋势",
        last24h: "最近 24 小时",
        noTrend: "暂无趋势数据",
        errors: "近期错误",
        errorsDesc: "最近 24 小时失败记录",
        noErrors: "暂无错误",
        noErrorMessage: "暂无信息",
        expand: "展开",
        collapse: "收起",
        none: "暂无",
        empty: "暂无运行数据。",
        statusUnknown: "未知",
        yes: "是",
        no: "否",
        table: {
          name: "渠道",
          status: "状态",
          priority: "优先级",
          enabled: "启用",
          available: "可用",
          successRate: "成功率",
          failCount: "失败次数",
          avgLatency: "平均耗时"
        },
        errorsTable: {
          time: "时间",
          summary: "摘要",
          action: "操作"
        }
      },
      billing: {
        title: "积分与计费",
        desc: "调整用户积分与手动充值",
        error: "加载积分用户失败。",
        creditsBreakdown: "订阅 {subscription} / 充值 {recharge} / 旧 {legacy}",
        creditUnit: "积分",
        actionTitle: "积分操作",
        validationUser: "请填写用户 ID 或手机号。",
        defaultReason: "管理员调整",
        rechargeReason: "管理员充值",
        actionSuccess: "操作完成。",
        submitting: "提交中...",
        submit: "提交",
        listTitle: "用户积分列表",
        search: "查询",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        reset: "重置筛选",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        fields: {
          userId: "用户 ID / 手机号",
          actionType: "操作类型",
          reason: "原因",
          credits: "积分",
          amount: "金额",
          bonus: "赠送"
        },
        placeholders: {
          userId: "从列表选择或输入用户 ID / 手机号",
          reason: "可选原因",
          search: "按用户名/手机号/邮箱/管理员备注搜索"
        },
        actions: {
          adjust: "调整积分",
          recharge: "充值",
          select: "选择"
        },
        chart: {
          title: "计费订单趋势",
          desc: "按天对比已支付/待支付/异常订单",
          loading: "正在加载计费趋势...",
          error: "加载计费趋势失败。",
          empty: "暂无计费趋势数据。",
          orderUnit: "单",
          series: {
            paid: "已支付",
            pending: "待支付",
            issue: "异常"
          }
        },
        table: {
          user: "用户",
          plan: "套餐",
          credits: "积分",
          trial: "试用",
          actions: "操作"
        }
      },
      consumption: {
        title: "积分流水",
        desc: "审计充值、消费、赠送、退款与管理员调整",
        loading: "正在加载积分流水...",
        error: "加载积分流水失败。",
        empty: "暂无记录。",
        creditUnit: "积分",
        search: "查询",
        reset: "重置筛选",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          userId: "按用户 ID 筛选"
        },
        chart: {
          title: "积分消耗趋势",
          desc: "按天对比积分消耗与补充",
          loading: "正在加载消耗趋势...",
          error: "加载消耗趋势失败。",
          empty: "暂无消耗趋势数据。",
          series: {
            consumed: "消耗积分",
            recharged: "补充积分"
          }
        },
        table: {
          user: "用户",
          createdAt: "时间"
        }
      },
      ledger: {
        placeholders: {
          search: "按手机号/邮箱/用户名/管理员备注/描述搜索"
        },
        filters: {
          allTypes: "全部类型",
          allBuckets: "全部来源"
        },
        types: {
          recharge: "充值",
          consumption: "消费",
          subscription: "订阅赠送",
          adjustment: "管理员调整",
          promo: "活动赠送",
          refund: "退款返还",
          trialGrant: "试用赠送"
        },
        buckets: {
          subscription: "订阅积分",
          recharge: "充值积分",
          legacy: "旧主账户"
        },
        imageBillingMeta:
          "图片计费：模型 {model} · 单价 {unit} 积分/张 · 数量 {count} · 阶段 {phase} · 批次 {runToken}",
        table: {
          type: "类型/说明",
          bucket: "积分池",
          change: "变动",
          balance: "余额",
          reference: "关联",
          money: "金额/成本"
        }
      },
      users: {
        title: "用户管理",
        desc: "管理用户状态、套餐与管理员权限",
        error: "加载用户失败。",
        filters: "筛选条件",
        search: "搜索",
        filtersAllStatus: "全部状态",
        filtersAllAdmin: "全部用户",
        filtersAdmin: "仅管理员",
        filtersNonAdmin: "非管理员",
        sort: {
          createdAtDesc: "注册时间（最新优先）",
          createdAtAsc: "注册时间（最早优先）",
          lastLoginDesc: "最近登录（最新优先）",
          lastLoginAsc: "最近登录（最早优先）",
          creditsDesc: "积分（从高到低）",
          creditsAsc: "积分（从低到高）"
        },
        editorTitle: "编辑用户",
        detailTitle: "用户详情",
        closeEditor: "关闭",
        adminToggle: "管理员权限",
        saved: "已保存",
        saving: "保存中...",
        save: "保存修改",
        discardChanges: "放弃修改",
        discardSuccess: "已放弃未保存的修改。",
        listTitle: "用户列表",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        reset: "重置筛选",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholderEmpty: "-",
        compareTitle: "待保存变更",
        noChanges: "暂无待保存变更。",
        riskConfirmTitle: "检测到高风险变更",
        riskConfirm: "以下高风险变更需要确认：",
        saveError: "保存失败：{error}",
        unknownError: "未知错误",
        adminStateEnabled: "已开启",
        adminStateDisabled: "未开启",
        fields: {
          status: "状态",
          plan: "套餐",
          admin: "管理员",
          notes: "备注",
          rechargeBonusPercent: "充值赠送比例"
        },
        detailLabels: {
          userId: "用户 ID",
          phone: "手机号",
          email: "邮箱",
          plan: "套餐",
          status: "状态",
          credits: "积分",
          creditsLegacy: "旧主账户积分",
          creditsSubscription: "订阅积分",
          creditsRecharge: "充值积分余额",
          trialCredits: "试用积分余额",
          totalRechargeCredits: "累计充值积分",
          rechargeBonusPercent: "管理员充值赠送比例",
          rechargeTotalAmount: "累计充值金额",
          rechargeRefundAmount: "累计退款金额",
          rechargeNetAmount: "净充值金额",
          paidOrderCount: "支付订单数",
          lastPaidAt: "最近支付时间",
          trial: "试用",
          createdAt: "创建时间",
          lastLogin: "最近登录"
        },
        placeholders: {
          search: "按用户名/手机号/邮箱搜索",
          phone: "按手机号搜索",
          notes: "管理员备注"
        },
        status: {
          active: "正常",
          inactive: "未激活",
          banned: "封禁"
        },
        plan: {
          free: "免费版",
          earlyBird: "早鸟版",
          standard: "标准版",
          enterprise: "企业版"
        },
        table: {
          user: "用户",
          notes: "备注",
          plan: "套餐",
          status: "状态",
          createdAt: "注册时间",
          lastLogin: "最近登录",
          credits: "积分",
          rechargeTotal: "累计充值",
          rechargeOrders: "{count} 笔支付",
          actions: "操作"
        },
        actions: {
          manage: "管理"
        }
      },
      feedback: {
        title: "用户反馈",
        desc: "查看和管理用户提交的 Bug 报告与需求建议，评估可行性并发放积分奖励。",
        stats: {
          total: "总计",
          pending: "待审核",
          accepted: "已采纳",
          inProgress: "开发中",
          completed: "已完成",
          rewarded: "已发积分"
        },
        filters: {
          label: "筛选",
          typeAll: "所有类型",
          statusAll: "所有状态"
        },
        empty: "暂无反馈数据",
        imageCount: "{count} 张图片",
        pagination: {
          prev: "上一页",
          next: "下一页"
        },
        priorityTag: "{label}优先",
        rewardBadge: "+{count} 积分",
        screenshots: "截图",
        meta: {
          userId: "用户 ID",
          submittedAt: "提交时间",
          pageUrl: "页面 URL"
        },
        form: {
          priority: "优先级",
          priorityUnset: "未设置",
          evaluation: "评估结论",
          evaluationUnset: "未评估",
          reply: "回复用户",
          replyVisibleTag: "用户可见",
          replyPlaceholder: "给用户的反馈回复，用户可以在反馈列表中看到...",
          internalNote: "内部备注",
          internalOnlyTag: "仅管理员可见",
          internalNotePlaceholder: "内部备注...",
          reward: "积分奖励（完成时自动发放）",
          rewardPlaceholder: "默认 10",
          rewardHint: "已发: {awarded} 积分，额外奖励最多 10 分",
          statusChange: "变更状态",
          saveOnly: "仅保存评估"
        },
        type: {
          bug: "Bug",
          feature: "需求"
        },
        status: {
          pending: "待审核",
          accepted: "已采纳",
          rejected: "已驳回",
          discussing: "待讨论",
          scheduled: "已排期",
          in_progress: "开发中",
          completed: "已完成"
        },
        priority: {
          low: "低",
          medium: "中",
          high: "高",
          urgent: "紧急"
        },
        evaluation: {
          feasible: "可行",
          needs_discussion: "需讨论",
          not_feasible: "不可行",
          duplicate: "重复"
        },
        imagePreviewAlt: "预览"
      },
      logs: {
        title: "操作日志",
        desc: "管理员操作审计",
        loading: "正在加载日志...",
        error: "加载日志失败。",
        empty: "暂无日志。",
        search: "查询",
        reset: "重置",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          action: "按操作筛选",
          admin: "按管理员 ID 筛选",
          filter: "按关键字筛选",
          module: "按模块筛选",
          startDate: "开始日期",
          endDate: "结束日期"
        },
        labels: {
          level: "日志级别",
          module: "模块",
          keyword: "关键字",
          startDate: "开始日期",
          endDate: "结束日期"
        },
        table: {
          status: "状态",
          level: "级别",
          action: "操作",
          target: "目标",
          admin: "管理员",
          details: "详情",
          ip: "IP",
          createdAt: "时间"
        },
        levels: {
          all: "全部",
          error: "错误",
          warn: "警告",
          info: "信息"
        },
        copy: "复制详情",
        copying: "复制中...",
        copied: "已复制"
      },
      pagination: {
        summary: "显示 {start}-{end} / 共 {total} 条",
        jumpTo: "跳转到第",
        jump: "跳转",
        page: "页码"
      },
      statusBadge: {
        healthy: "健康",
        degraded: "降级",
        failed: "失败",
        disabled: "停用",
        info: "信息",
        unknown: "未知状态",
        ariaLabel: "状态：{label}"
      },
      settings: {
        title: "系统设置",
        desc: "调整平台运行参数",
        error: "加载设置失败。",
        loading: "正在加载设置...",
        empty: "暂无设置项。",
        search: "搜索",
        searchPlaceholder: "按配置名 / 分组 / 说明搜索",
        onlyChanged: "仅显示已修改",
        expandAll: "全部展开",
        collapseAll: "全部收起",
        saved: "设置已保存。",
        saving: "保存中...",
        saveConfirm: "保存该配置将覆盖当前值，是否继续？",
        save: "保存",
        boolean: "启用",
        items: "项",
        noMatch: "没有匹配到配置项。",
        unsavedChanges: {
          leaveTitle: "存在未保存修改",
          leaveConfirm: "你有未保存的系统设置修改，离开当前页面后这些更改将丢失，是否继续离开？",
          leaveNow: "确认离开",
          stay: "留在当前页"
        },
        updatedAt: "更新时间 {time}",
        typeHints: {
          secret: "敏感凭证，变更后会被系统加密保存，请勿外泄。",
          number: "数字类型配置，避免填写非数字内容。",
          boolean: "通过开关控制该能力是否启用。",
          text: "文本内容会直接影响当前功能行为，请谨慎修改。"
        },
        groups: {
          billing: "计费",
          pricing: "定价",
          ai_channel: "AI 渠道",
          ai_runtime: "AI 调度配置",
          image: "图片",
          platform: "平台",
          business: "业务",
          feature: "功能",
          other: "其他"
        },
        showMore: "展开剩余 {count} 项",
        showLess: "收起",
        imageBilling: {
          title: "图片生成计费模式",
          desc: "用户侧生图价格只在这里统一控制；高级调度策略可以调整模型、fallback 和渠道，但不会改变用户被扣的积分单价。",
          badge: "普通 / 官方高速",
          creditUnit: "积分",
          modes: {
            normal: "普通模式",
            official: "官方高速模式"
          },
          fields: {
            defaultMode: "新用户默认模式",
            normalPrice: "普通模式单价",
            officialPrice: "官方高速模式单价",
            officialAllowSubscription: "官方高速可用订阅积分",
            officialAllowLegacy: "官方高速可用旧账户积分"
          },
          hints: {
            defaultMode: "建议默认普通模式；官方高速模式保持用户主动选择后才启用。",
            normalPrice: "普通模式每张图扣除的积分，当前设计通常对应第三方/中转通道。",
            officialPrice: "官方高速模式每张图扣除的积分，当前设计通常对应官方通道。",
            officialAllowSubscription: "关闭后，官方高速优先且主要消耗充值积分，避免订阅赠送积分被高成本通道快速消耗。",
            officialAllowLegacy: "兼容历史主账户积分的开关。"
          }
        },
        ozonPublic: {
          title: "Ozon 公共查询 Key",
          desc: "用于类目/属性等公共只读查询。新用户即使未绑定店铺也能完成生成；真正发布仍需绑定自己的店铺。",
          clientId: "Client ID",
          apiKey: "API Key",  // pragma: allowlist secret
          configured: "已配置",
          notConfigured: "未配置",
          clearTitle: "清除 Ozon 公共查询 Key",
          saved: "Ozon 公共查询 Key 已保存。",
          saveConfirm: "你即将保存新的 Ozon 公共查询配置，确认后新用户将使用该配置进行公共查询，是否继续？",
          fillRequired: "测试前请先填写完整的 Client ID 与 API Key。",
          clientIdHint: "请填写 Ozon 控制台中的 public client ID。",
          apiKeyHint: "请填写 Ozon 控制台中的 public API Key，提交后该值不会回显。",  // pragma: allowlist secret
          test: "测试连接",
          testing: "测试中...",
          testSuccess: "Ozon 连接正常。",
          clear: "清除",
          clearConfirm: "确定清除 Ozon 公共查询 Key？这会影响新用户类目/属性查询。",
          cleared: "Ozon 公共查询 Key 已清除。",
          secretHint: "API Key 仅展示末 4 位，保存后不会回显。",  // pragma: allowlist secret
        }
      }
    },

    // 首页横幅
    hero: {
      badge: "AI 驱动的电商平台",
      title: "一键转化商品",
      titleHighlight: "上架全球市场",
      subtitle:
        "粘贴链接，AI 自动生成图片、文案、SEO 与平台适配，一键发布到 Ozon 等平台。把 30–60 分钟/条的 Listing 制作压缩到分钟级。",
      placeholder: "粘贴 1688 或速卖通链接...",
      button: "开始 AI 优化"
    },

    // 功能展示 1
    feature1: {
      title: "见证 AI 魔法",
      subtitle: "看我们如何将供应商原始数据转化为专业的电商 Listing。",
      tab1: "智能手表",
      tab2: "耳机",
      tab3: "运动鞋",
      imgBtn1: "AI 优化后",
      imgBtn2: "原图",
      imgBtn3: "对比",
      aiBadge: "AI 优化",
      marketingTitle: "营销素材",
      viewAll: "查看全部",
      listingTitleTag: "商品标题",
      translated: "已翻译",
      bulletTitle: "核心卖点",
      seoTitle: "SEO 关键词",
      pushBtn: "发布到平台",
      syncText: "已同步到 Ozon",
      // 手表产品
      prodTitle_watch: "高端智能手表 Pro Max",
      bullet1Title_watch: "健康监测",
      bullet1Desc_watch: "24小时心率、血氧、睡眠追踪",
      bullet2Title_watch: "超长续航",
      bullet2Desc_watch: "单次充电可用 14 天",
      key1_watch: "智能手表",
      key2_watch: "健身追踪器",
      key3_watch: "健康监测",
      hotspot1: "AMOLED 屏幕",
      hotspot2: "钛金属表壳",
      // 耳机产品
      prodTitle_headphones: "无线降噪耳机 Studio",
      bullet1Title_headphones: "主动降噪",
      bullet1Desc_headphones: "隔绝 99% 环境噪音",
      bullet2Title_headphones: "Hi-Res 音质",
      bullet2Desc_headphones: "40mm 驱动单元，支持 LDAC",
      key1_headphones: "无线耳机",
      key2_headphones: "降噪耳机",
      key3_headphones: "蓝牙耳机",
      hotspot_headphones_1: "记忆海绵耳垫",
      hotspot_headphones_2: "触控操作",
      // 运动鞋产品
      prodTitle_sneakers: "超轻跑步鞋 X9",
      bullet1Title_sneakers: "云端缓震",
      bullet1Desc_sneakers: "革命性泡棉科技",
      bullet2Title_sneakers: "透气网面",
      bullet2Desc_sneakers: "360° 通风系统",
      key1_sneakers: "跑步鞋",
      key2_sneakers: "运动鞋",
      key3_sneakers: "休闲运动鞋",
      hotspot_sneakers_1: "碳板支撑",
      hotspot_sneakers_2: "抓地大底"
    },

    // 功能展示 2 - 使用流程
    feature2: {
      title: "使用流程",
      subtitle: "三步完成一条 Listing：导入 → AI 优化 → 一键发布。",
      card1Title: "导入分析",
      card1Desc: "粘贴任意供应商链接，AI 自动提取商品数据、图片和规格。",
      card2Title: "AI 优化",
      card2Desc: "生成 SEO 标题、翻译描述、优化图片、创建 A+ 内容，分钟级完成。"
    },

    // 功能展示 3 - 安全
    feature3: {
      badge: "企业级安全",
      title: "保护您的品牌资产",
      subtitle: "军事级别的图片和 Listing 保护。",
      verified: "已验证",
      watermark: "神经水印",
      hash: "SHA-256 哈希",
      cert: "已认证",
      card1Title: "隐形水印",
      card1Desc: "AI 嵌入的标记，经过压缩和裁剪仍然有效。",
      card2Title: "法律证据包",
      card2Desc: "自动生成 DMCA 删除文件和区块链证明。",
      card3Title: "即时检测",
      card3Desc: "当您的图片出现在未授权的 Listing 时收到提醒。",
      downloadBtn: "下载白皮书",
      downloadSub: "安全架构概述",
      learnMore: "了解更多保护方案"
    },

    // 定价
    pricing: {
      title: "简单透明的定价",
      subtitle: "只对图片生成收费（普通模式 {creditsPerImage} 积分/张）。按每条 listing≈{imagesPerListing} 张图估算，直观看到可生成多少条 listing。",
      roiTitle: "算一笔人力账",
      roiDesc:
        "人工做 1 条 listing 通常要 {minutesMin}–{minutesMax} 分钟；要稳定批量，往往需要专职人员（约 {laborMin}–{laborMax} 元/月）。用 AI 把图片、文案、SEO、属性适配交给系统，你只需审核与一键发布。",
      timeSaveHint: "预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      timeSaveHintMonthly: "每月预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      monthly: "月付",
      yearly: "年付",
      save: "送 2 个月",
      bestForGuests: "游客首选",
      onDemand: "按需付费",
      credits: "积分",
      creditsLabel: "积分",
      imagesLabel: "张图片",
      perMonth: "/月",
      perYear: "/年",
      packBasic: "基础包",
      packGrowth: "成长包",
      packScale: "扩容包",
      bestValue: "最划算",
      featExpires: "充值积分永久有效",
      featAi: "AI 图片生成",
      featPsd: "高清素材下载",
      featPriority: "优先支持",
      purchase: "立即购买",
      planStarter: "入门版",
      planPro: "专业版",
      planBusiness: "商务版",
      featStore: "每月赠送积分",
      featBatch: "批量图片生成",
      featSupport: "优先支持",
      featBatchStarter: "基础批量生成",
      featBatchPro: "高级批量生成",
      featBatchBusiness: "高并发批量生成",
      featSupportStarter: "标准支持（48 小时）",
      featSupportPro: "优先支持（12 小时）",
      featSupportBusiness: "优先支持 + 成功经理",
      featExportsStarter: "高清素材下载",
      featExportsPro: "高清 + 4K 素材下载",
      featExportsBusiness: "品牌素材包导出",
      loadFailed: "计费信息加载失败，请重试或联系支持。",
      retryAction: "重新加载"
    },

    // 常见问题
    faq: {
      title: "常见问题",
      subtitle: "关于平台的一切你需要知道的。",
      contact: "联系客服",
      q1: "AI 优化会改坏我的商品信息吗？",
      a1: "不会。系统以原始数据为基准生成优化方案，所有字段在发布前可预览、可编辑，确保不会改变关键属性。",
      q2: "需要安装插件或绑定店铺吗？",
      a2: "无需安装插件或绑定店铺即可体验，粘贴链接即可开始。仅在发布或同步到平台时才需要授权。",
      q3: "我的数据和图片安全吗？",
      a3: "我们采用企业级加密与权限隔离，图片生成自带隐形水印与取证信息，防止被盗用。",
      q4: "试用与收费如何计算？",
      a4: "新用户可免费体验 1 次任务，体验仅可生成 1 张主图；后续按订阅或积分计费，透明无隐藏费用。"
    },

    // CTA
    cta: {
      title: "准备好扩展",
      titleBreak: "您的电商业务了吗？",
      subtitle: "加入数千名已在使用 AI 征战全球市场的卖家。",
      button: "免费开始"
    },

    // 页脚
    footer: {
      privacy: "隐私政策",
      terms: "服务条款",
      support: "联系我",
      submitFeedback: "提交反馈",
      rights: "© {year} 一键大卖 版权所有"
    },

    // Trust 页面
    trust: {
      forensicData: "取证元数据",
      badge: "企业级安全",
      title: "保护您的",
      titleHighlight: "数字资产",
      subtitle: "军事级别的产品图片和品牌保护。",
      dmcaBadge: "DMCA 保护",
      forensicBadge: "取证就绪",
      encryptedBadge: "256位加密",
      nav: {
        technology: "技术方案",
        legal: "法律框架",
        whitepaper: "资源"
      },
      resources: {
        copyrightTitle: "版权白皮书",
        copyrightDesc: "水印与取证技术的完整说明。",
        legalTitle: "法律证明材料",
        legalDesc: "用于平台验真的官方模板与证书。"
      },
      tech1Title: "神经水印",
      tech1Desc: "隐形标记，经过任何转换仍然有效。",
      tech2Title: "区块链时间戳",
      tech2Desc: "分布式账本上不可篡改的创作证明。",
      tech3Title: "AI 检测",
      tech3Desc: "全平台实时监控。",
      compareTitle: "看看区别",
      compareSub: "移动滑块查看隐藏水印。",
      rawOutput: "原始输出",
      status: "状态",
      ctaBtn: "开始保护",
      protectedLayer: "防护层",
      frameworkTitle: "法律框架",
      frameworkSub: "合规文档与可验证证据链。",
      licenseTitle: "授权协议",
      licenseDesc: "自动生成授权文件并提供不可篡改证明。",
      dmcaPackageTitle: "DMCA 取证包",
      dmcaPackageDesc: "一键生成平台下架所需证据材料。",
      complianceTitle: "合规就绪",
      complianceDesc: "满足平台执法要求的结构化证据。",
      platformLabel: "平台",
      priority: "优先级",
      refLabel: "参考编号",
      evidenceLabel: "证据",
      evidence1: "原始商品截图",
      evidence2: "水印验证哈希",
      generatorTitle: "自动 DMCA 生成器",
      generatorSub: "几分钟内生成完整执法文件。",
      generatorNote: "兼容 Amazon、Shopify、Etsy 等 30+ 平台。",
      letterAutomation: "自动生成 DMCA 通知",
      letterBody1: "本通知确认版权所有及侵权证据。",
      letterBody2: "所有元数据与时间戳已链上验证。",
      letterBody3: "相关证据已随案件材料附上。",
      letterBody4: "请立即移除侵权内容。",
      letterNotice: "本通知已准备好提交。",
      letterSign: "授权代表",
      ctaTitle: "准备好保护品牌了吗？",
      ctaTitleBreak: "马上启动执法",
      ctaSub: "5 分钟内完成合规保护部署。",
      downloadBtn: "下载证据包",
      verifiedHash: "已验证哈希",
      urlLabel: "侵权链接",
      selectPlatform: "选择平台",
      toLabel: "致",
      letterDateLabel: "日期：",
      letterSalutation: "致相关负责人：",
      rights: "版权所有"
    },

    welcome: "欢迎回来",
    loginSubtitle: "请输入您的详细信息以登录您的专业帐户。",
    smsMethod: "短信验证码",
    passMethod: "密码",
    phoneLabel: "手机号码",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "手机号码",
    phoneRequired: "请先填写手机号。",
    phoneInvalid: "手机号格式不正确。",
    sendCode: "发送验证码",
    sendCodeFailed: "验证码发送失败，请稍后重试。",
    debugCodeHint: "开发环境验证码：{code}",
    verifyLabel: "验证码",
    resendIn: "{time}秒后重发",
    resendNow: "重新发送",
    invalidCode: "验证码无效。请尝试 123456。",  // pragma: allowlist secret
    loginFailed: "登录失败，请稍后重试。",
    authErrorTermsRequired: "请先同意《用户协议》和《隐私政策》，再继续操作。",
    authErrorInvalidToken: "登录凭证无效，请重新登录后再试。",
    authErrorTokenExpired: "登录已过期，请重新登录后继续。",
    authErrorTokenRevoked: "当前登录状态已失效，请重新登录。",
    authErrorUnauthorized: "当前未登录，请先登录后重试。",
    authErrorForbidden: "当前账号没有此操作权限，如需帮助请联系支持。",
    authErrorUserNotFound: "未找到该账号，请先注册。",
    authErrorUserDisabled: "该账号已被禁用，请联系支持恢复。",
    authErrorInvalidCredentials: "手机号或密码错误，请重新输入。",
    authErrorPhoneInvalid: "手机号格式不正确，请输入 11 位手机号。",
    authErrorSmsRateLimited: "验证码请求过于频繁，请等待 1 分钟后重试。",
    authErrorSmsDailyLimit: "今日验证码发送次数已达上限，请明日再试。",
    authErrorSmsServiceUnavailable: "短信服务暂时不可用，请稍后再试。",
    passwordLabel: "密码",
    forgotPass: "忘记密码？",
    passPlaceholder: "输入您的密码",
    passwordRequired: "请输入密码。",
    passwordTooShort: "密码至少 8 位。",
    signIn: "登录",
    newToApp: "新用户？",
    signUp: "注册",
    createAccount: "创建您的账户",
    signupSubtitle: "加入 5,000+ 优化列表的卖家。",
    codeVerified: "验证码已验证",
    setPassPlaceholder: "设置您的密码",
    agree: "我同意",
    userAgreement: "用户协议",
    and: "和",
    privacyPolicy: "隐私政策",
    termsRequired: "请先阅读并同意用户协议与隐私政策。",
    autoRegisterHint: "若无账号，请先注册。",
    getStarted: "免费开始",
    haveAccount: "已有账户？",
    login: "登录",
    resetTitle: "重置密码",
    resetSubtitle: "输入您的手机号码以接收验证码。",
    sendReset: "发送重置码",
    resetStep1: "验证手机号",
    resetStep2: "输入验证码",
    resetStep3: "设置新密码",
    resetCodeSent: "验证码已发送，请注意查收。",
    resetCodeHint: "请输入 6 位验证码",
    resetVerifyAction: "验证并继续",
    resetBackStep: "返回上一步",
    resetMismatch: "两次密码不一致。",
    resetRequirement: "至少 8 位，包含字母和数字。",
    resetSuccess: "密码已更新。",
    resetSuccessHint: "正在返回登录...",
    resetContextMissing: "重置会话已失效，请重新获取验证码。",
    resetContextExpiredTitle: "验证会话已失效",
    resetContextExpiredDesc: "为了保护账号安全，请返回忘记密码页重新验证手机号后再设置新密码。",
    resetRequestNewCode: "重新获取验证码",
    resetFailed: "密码重置失败，请稍后重试。",
    resetStrengthLabel: "强度：{level}",
    resetStrengthWeak: "弱",
    resetStrengthMedium: "中",
    resetStrengthStrong: "强",
    backLogin: "返回登录",
    phoneVerified: "手机已验证",
    phoneVerificationRequired: "需要重新验证手机号",
    setNewPass: "设置新密码",
    newPassSubtitle: "请创建一个强密码以保护您的账户。",
    newPassLabel: "新密码",
    newPassPlaceholder: "创建新密码",
    strength: "强度：强",
    confirmPassLabel: "确认新密码",
    confirmPassPlaceholder: "重复新密码",
    updatePass: "更新密码",
    backSignIn: "返回登录",
    captchaLabel: "人机验证",
    captchaHint: "请先完成验证以继续。",
    captchaLoading: "验证加载中...",
    captchaRequired: "请先完成人机验证。",
    captchaUnavailable: "人机验证暂不可用，请稍后再试。",
    captchaDisabledLabel: "人机验证暂不可用，已自动降级。",
    captchaBypassedHint: "当前可直接发送验证码继续。",
    captchaLoadFailed: "验证加载失败，请刷新重试。",
    captchaError: "验证失败，请重试。",

    profile: {
      proPlan: "专业版",
      freePlan: "免费版",
      guest: "游客",
      coreNav: "核心导航",
      workflowNav: "工作流",
      workspace: "工作区",
      variantGroups: "变体管理",
      assetMgmt: "资产管理",
      copyright: "版权资产",
      pricing: "价格策略",
      profitProtection: "Ozon 利润保护",
      storeAuth: "店铺授权",
      marketIntel: "市场情报",
      beta: "Beta",
      marketIntelComingSoon: "市场情报功能即将上线。如需优先体验，请联系支持。",
      account: "账户",
      settings: "账户设置",
      billing: "账单与积分",
      help: "帮助中心",
      signOut: "退出登录"
    },

    settings: {
      general: "基本信息",
      security: "安全",
      preferences: "平台偏好",
      billing: "账单与积分",
      notifications: "通知",
      feedback: "我的反馈",
      integrations: "集成",
      storeAuth: "店铺授权"
    },

    // 店铺绑定
    storeBind: {
      title: "店铺绑定",
      subtitle: "绑定 Ozon 店铺后即可启用同步与发布。",
      guideTitle: "配置指南",
      step1Title: "登录 Ozon 卖家后台",
      step1Desc: "先进入 Ozon 卖家后台，并确认你打开的是正确的卖家账号。",
      step1Btn: "打开 Ozon 卖家后台",
      step2Title: "打开 API 密钥页面",
      step2Desc: "在顶部菜单进入「设置」→「API 密钥」。",
      step2Btn: "打开 API 密钥页面",
      step3Title: "生成 Admin API 密钥",
      step3Desc: "创建 Admin 类型密钥，并复制同一条记录里的 Client ID 和 API Key。",
      setupBadge: "新手分步引导",
      manageBadge: "店铺管理",
      firstSetupTitle: "用最不容易出错的方式绑定 Ozon 店铺",
      firstSetupSubtitle: "你只需要 Ozon API 页面里的两项：API Client ID 和 API Key。不要填店铺 ID。",
      addAnotherTitle: "再绑定一个 Ozon 店铺",
      addAnotherSubtitle: "继续走同样的傻瓜式流程：先验证，再绑定。",
      manageTitle: "管理已绑定的 Ozon 店铺",
      manageSubtitle: "当前有 {count} 个可用店铺。你可以继续新增，也可以管理已绑定店铺。",
      backToManage: "返回店铺管理",
      ozonOnly: "当前仅支持 Ozon",
      progressPrepare: "先记住填什么",
      progressFetch: "去 Ozon 找到入口",
      progressPaste: "把两项值粘进来",
      progressVerify: "先验证再绑定",
      stepLabel: "步骤 {step}",
      prepareTitle: "开始前先记住这一句",
      prepareDesc: "这个页面只需要 Ozon API 页面里的 API Client ID 和 API Key，绝对不需要店铺 ID。",
      needLabel: "你要准备",
      needClientId: "API 密钥页面里的 API Client ID",
      needApiKey: "同一条 API 记录里的 API Key",
      doNotUseLabel: "不要填",
      doNotUseStoreId: "店铺 ID、卖家编号、店铺主页里的任何编号",
      doNotUseLoginPassword: "你的 Ozon 登录密码",  // pragma: allowlist secret
      fetchTitle: "先去 Ozon 把这两个值复制出来",
      fetchDesc: "如果你现在不在 Ozon 的 API 页面，就先点进去，复制好以后再回来粘贴。",
      showGuide: "找不到入口？展开图文引导",
      hideGuide: "收起图文引导",
      pasteTitle: "按 Ozon 页面原样粘贴这两个值",
      pasteDesc: "一定要粘贴同一条 API 记录里的 Client ID 和 API Key，不要混着复制。",
      clientIdLabel: "API Client ID（不是店铺ID）",
      clientIdHelp: "它和 API Key 出现在同一个 API 密钥页面里，不是店铺主页上的编号。",
      apiKeyLabel: "API Key",
      apiKeyHelp: "请填写和上面 Client ID 配套的那一条 API Key。",
      nicknameReady: "验证通过后，我们会自动带出店铺备注，你仍然可以手动修改。",
      nicknamePending: "在凭证验证通过前，店铺备注先锁住，避免用户还没填对就先乱写。",
      nicknameLabel: "店铺备注（验证通过后可修改）",
      nicknamePlaceholder: "系统会自动带出识别到的卖家名",
      nicknameHelp: "这只是你在系统里的备注名，不是店铺 ID，也不要求和 Ozon 原名完全一致。",
      verifyTitle: "先验证，再绑定",
      verifyDesc: "我们先检查这两个值是不是一对。只有验证通过的凭证，才允许真正绑定。",
      successTitle: "店铺已绑定，可以继续用了",
      successDesc: "已识别卖家 {sellerName}。店铺已经绑定完成；如果后面需要商品、库存或订单数据，再手动同步就可以。",
      bindCompleteBadge: "绑定完成",
      unknownSeller: "已识别卖家",
      manageAllStores: "管理已绑定店铺",
      confirmBind: "确认绑定这个店铺",
      singlePageGuideTitle: "在一个页面填完，系统逐项引导你",
      singlePageGuideDesc: "高亮到哪个字段，就填哪个。引导可随时跳过，也能随时重新打开。",
      guideRestart: "重新引导",
      guideSkip: "跳过引导",
      guidePrev: "上一步",
      guideNext: "下一步",
      guideDone: "我知道了",
      guideProgress: "引导 {current}/{total}",
      guideClientIdTitle: "先填 API Client ID",
      guideClientIdDesc: "从 Ozon API 密钥页面复制，不能填店铺 ID 或卖家主页编号。",
      guideApiKeyTitle: "再填配套 API Key",
      guideApiKeyDesc: "必须和上面的 Client ID 来自同一条 API 记录。",
      guideTestTitle: "然后先验证",
      guideTestDesc: "先验证这两个值是一对，避免绑定失败。",
      guideStoreNameTitle: "确认店铺备注",
      guideStoreNameDesc: "验证通过后会自动带出备注，你也可以手动改。",
      guideBindTitle: "最后完成绑定",
      guideKeywordClientId: "API Client ID",
      guideKeywordApiKey: "API Key",
      guideKeywordStoreId: "店铺 ID",
      guideKeywordVerifyAction: "先验证",
      guideKeywordApiPair: "配套一对",
      guideKeywordStoreName: "店铺备注",
      guideKeywordSellerName: "卖家名",
      guideKeywordBindAction: "绑定店铺",
      wizardPrev: "上一步",
      wizardNext: "下一步",
      wizardNextVerify: "进入验证与绑定",
      wizardFinalStepHint: "已到最后一步",
      wizardStepFocus: "当前步骤指引",
      commonMistakeLabel: "最容易犯的错",
      commonMistakeTitle: "很多人会把店铺 ID 填到这里",
      commonMistakeDesc: "如果用户开始迷糊，就只让他记一句：Client ID 来自 API 密钥页面，不来自店铺主页。",
      compareTitle: "一分钟分清楚",
      compareClientId: "正确：API 密钥页面里，和 API Key 成对出现的 API Client ID。",
      compareStoreId: "错误：店铺 ID、卖家编号、店铺主页里复制出来的任何编号。",
      currentDraftLabel: "当前状态",
      validationStatus: "验证状态",
      validationPassed: "已通过",
      validationPending: "还没验证",
      detectedSeller: "识别到的卖家",
      detectedSellerPending: "验证通过后显示",
      securityInline: "这里只会使用加密后的 API 凭证，不会索要你的 Ozon 登录密码。",  // pragma: allowlist secret
      manageSummaryBadge: "已可管理",
      manageSummaryTitle: "当前已连接 {count} 个可用店铺",
      manageSummaryDesc: "当前默认店铺：{storeName}。你可以切换默认店铺、更新凭证，或者继续新增店铺。",
      manageSummaryDescFallback: "你的店铺已经绑定完成，随时可以更新设置或再绑定一个店铺。",

      // 店铺切换器
      activeSwitcher: "当前激活店铺",
      manageAll: "管理全部店铺",
      currentlyActive: "当前激活",
      synced: "已同步",
      connected: "已连接",
      disabled: "已停用",
      syncingShort: "同步中",
      syncFailedShort: "同步失败",
      uploads7d: "近 7 日上架",
      companyNameLabel: "店铺主体",
      legalNameLabel: "法人实体",
      taxIdLabel: "税号",
      regionLabel: "国家 / 币种",
      subscriptionTypeLabel: "订阅类型",
      warehouseCountLabel: "仓库数量",
      warehouseCountSummary: "{count} 个仓库",
      subscriptionSummary: "{plan}",
      subscriptionPremiumSummary: "{plan} Premium",
      lastVerifiedLabel: "最近验证",
      brandLabel: "发布品牌",
      brandNone: "无品牌",
      brandIdFallback: "品牌 #{id}",
      manageCatalogAction: "管理商品链接",
      connectNew: "新增店铺",

      // 平台选择
      fullApiIntegration: "完整 API 集成",
      readyConnect: "可立即接入",
      comingSoon: "即将支持",
      active: "已激活",

      // 连接表单 / 管理
      connectOzon: "连接 Ozon 店铺",
      statusLabel: "连接状态",
      authorized: "已连接，可正常使用",
      noStoresLinked: "还没有连接店铺",
      howToGetKey: "如何获取 API Key？",
      storeName: "店铺备注",
      storeNamePlaceholder: "例如：我的 Ozon 店铺",
      clientId: "API Client ID",
      clientIdPlaceholder: "粘贴 API Client ID",
      apiKey: "API Key",  // pragma: allowlist secret
      addStore: "添加 Ozon 店铺",
      testConnection: "先验证这两个值",
      testing: "验证中...",

      // 同步状态
      syncingData: "正在同步店铺数据...",
      syncingDesc: "正在从 Ozon 导入类目、物流方式和仓储信息，请稍候片刻。",
      syncComplete: "同步完成",
      syncCompleteDesc: "店铺同步已完成，现在可以直接继续发布。",
      success: "成功",

      // 成功状态
      startListing: "开始智能上架",
      connectionSuccess: "连接成功",
      testSuccessWithSeller: "验证通过，已识别卖家：{sellerName}。现在可以继续绑定。",
      testSuccess: "API 凭证验证成功，现在可以继续。",

      // 安全说明
      securityTitle: "API 连接安全保障",
      securityEncryption: "您的凭证采用 AES-256 加密存储。",
      securityNoPassword: "系统绝不会索取或存储您的平台登录密码。",

      // 表格
      authorizedStores: "已授权店铺",
      platform: "平台",
      actions: "操作",
      syncSettings: "同步设置",
      items: "件",

      // 设置弹窗
      settingsTitle: "店铺设置",
      settingsSubtitle: "管理店铺启用状态、自动同步与版权保护。",
      loading: "加载中...",
      settingActive: "启用店铺",
      settingActiveDesc: "关闭后将暂停该店铺的发布与同步。",
      settingAutoSync: "自动同步",
      settingAutoSyncDesc: "自动同步商品、库存与订单数据。",
      settingPsd: "PSD 版权保护",
      settingPsdDesc: "启用图片版权嵌入与证据材料生成。",
      settingSyncPsd: "同步 PSD 处理",
      settingSyncPsdDesc: "上传前自动嵌入 PSD 版权信息。",
      settingRotateKey: "更新 API Key",
      settingRotateKeyHint: "不填写则保持当前 API Key 不变。",
      settingLogisticsTitle: "物流渠道定价设置",
      settingLogisticsDesc: "统一管理 Ozon 渠道同步与定价默认物流商。建议首次先同步一次渠道。",
      settingLogisticsMode: "物流商模式",
      settingLogisticsModeAuto: "自动",
      settingLogisticsModeManual: "手动",
      settingLogisticsProvider: "默认物流商",
      settingLogisticsSyncAction: "同步渠道",
      settingLogisticsSyncing: "渠道同步中...",
      settingLogisticsSaveAction: "保存物流商选择",
      settingLogisticsLastSynced: "最近同步：{date}",
      settingLogisticsNeverSynced: "尚未同步渠道数据，建议先执行一次“同步渠道”。",
      settingLogisticsProvidersEmpty: "暂未识别到可用物流商，可先保存自动模式。",
      settingLogisticsUnknownHint: "还有 {count} 个渠道暂未识别到对应物流商。",
      settingBrandTitle: "发布品牌",
      settingBrandDesc: "从 Ozon 查询并绑定固定品牌。发布时会强制覆盖属性 85 为该品牌。",
      settingBrandCurrent: "当前品牌",
      settingBrandWillApply: "保存后生效",
      settingBrandSearchPlaceholder: "搜索 Ozon 品牌",
      settingBrandSearchAction: "查询",
      settingBrandSearching: "查询中...",
      settingBrandSearchEmpty: "未找到匹配品牌，请换个关键词。",
      settingBrandUseNone: "使用无品牌",
      deleteStore: "删除店铺",
      unbindStore: "删除店铺",
      setDefault: "设为默认店铺",
      openSettingsForStore: "打开 {storeName} 的店铺设置",
      setDefaultConfirmTitle: "确认切换默认店铺？",
      setDefaultConfirmMessage: "后续发布、同步和商品管理将默认使用「{storeName}」。",
      setDefaultConfirmAction: "设为默认店铺",
      saving: "保存中...",
      saveChanges: "保存更改",

      // 同步弹窗
      syncModalTitle: "同步店铺数据",
      syncModalSubtitle: "同步 {name} 的数据。",
      syncModalDesc: "请选择要同步的数据类型。",
      syncProducts: "商品",
      syncProductsDesc: "从 Ozon 刷新商品列表。",
      syncStocks: "库存",
      syncStocksDesc: "按仓库刷新库存数量。",
      syncOrders: "订单",
      syncOrdersDesc: "刷新最近订单与状态。",
      syncNow: "开始同步",

      // 删除确认
      deleteTitle: "删除店铺",
      deleteSecondTitle: "二次确认删除",
      confirmDelete: "确定要删除该店铺吗？",
      confirmDeleteSecond: "是否确认删除？",
      deleteConfirm: "删除",
      unbindConfirm: "删除",
      storeTarget: "已授权 Ozon 店铺",
      storeUnbindImpact: "确定要删除该店铺吗？",
      storeUnbindSecondImpact: "是否确认删除？",

      // 底部 / 兼容旧文案
      storageSystem: "存储体系：本地化 AES 加密",
      securityGuaranteed: "安全保障",
      encryptedBridge: "加密 API 通道",
      noPasswordStorage: "零密码存储",
      cancel: "取消",
      saveAuth: "保存授权",

      // 提示
      toastTitle: "Ozon 店铺已绑定",
      toastDesc: "凭证验证通过，店铺已经接入系统；后续需要时再手动同步即可。",
      toasts: {
        defaultUpdated: "默认店铺已更新。",
        noChanges: "没有需要保存的改动。",
        storeUpdated: "店铺设置已更新。",
        storeDeleted: "店铺已删除。",
        storeUnbound: "店铺已删除。",
        syncStarted: "已开始同步，数据将自动刷新。",
        logisticsSynced: "物流渠道已同步。",
        logisticsSaved: "物流商选择已保存。"
      },

      continuePublish: "继续发布",
      noStores: "暂无已授权店铺。",
      hints: {
        fillCredentialsFirst: "先把 API Client ID 和 API Key 粘贴进来。",
        validateFirst: "先点“验证”，只有验证通过后才允许绑定。",
        waitForStoreName: "验证已经通过了，再确认一下自动带出的店铺备注，然后继续绑定。",
        readyToBind: "验证通过，现在可以直接绑定这个店铺。"
      },
      errors: {
        missingCredentials: "请先粘贴 API Client ID 和 API Key。",
        validateFirst: "请先验证 API Client ID 和 API Key，再执行绑定。",
        missingFields: "请完整填写店铺名称、Client ID 与 API Key。",
        testFailed: "连接测试失败，请检查凭证是否正确。",
        clientIdMustBeNumeric: "API Client ID 应为数字。请复制 Ozon Seller 后台 API 密钥页面里的 Client ID，不要填写店铺 ID、店铺名称或其它文字。",
        apiKeyMayBeWrong: "Ozon 拒绝了这个 API Key。请从同一条 Ozon API 密钥记录重新复制 API Key，再验证一次。",
        credentialsRejected: "Ozon 拒绝了这组 API 凭证。请从同一条 API 密钥记录重新复制 API Client ID 和 API Key，再验证一次。",
        bindFailed: "店铺绑定失败（状态码 {status}）。",
        statusFailed: "加载店铺状态失败（状态码 {status}）。",
        syncFailed: "同步失败，可在「同步设置」中重试。",
        defaultFailed: "更新默认店铺失败。",
        storeDetailFailed: "加载店铺详情失败（状态码 {status}）。",
        storeUpdateFailed: "更新店铺失败（状态码 {status}）。",
        logisticsLoadFailed: "加载物流渠道失败（状态码 {status}）。",
        logisticsSyncFailed: "同步物流渠道失败（状态码 {status}）。",
        logisticsSaveFailed: "保存物流商选择失败（状态码 {status}）。",
        logisticsProviderRequired: "手动模式下必须选择物流商。",
        brandSearchFailed: "品牌查询失败（状态码 {status}）。",
        brandCreateFailed: "保存品牌失败（状态码 {status}）。",
        storeNameRequired: "请先确认或修改自动带出的店铺备注，再继续绑定。",
        deleteFailed: "删除店铺失败（状态码 {status}）。",
        unbindFailed: "删除店铺失败（状态码 {status}）。",
        syncTypesRequired: "请至少选择一种同步类型。",
        syncTriggerFailed: "触发同步失败（状态码 {status}）。"
      }
    },

    storeCatalog: {
      nav: "商品管理",
      title: "商品链接管理",
      subtitle: "同步店铺商品（含系统外历史商品）并发起批量更新任务。",
      noStores: "暂未绑定店铺，请先绑定店铺。",
      syncButton: "同步商品",
      syncing: "同步中...",
      syncStarted: "已开始同步任务。",
      syncStatusTitle: "同步状态",
      syncStatus: "状态：{status}",
      syncProgress: "进度：{progress}%（{done}/{total}）",
      syncNoJob: "暂无同步任务。点击同步获取 Ozon 最新商品数据。",
      syncRetry: "重试同步",
      syncFailedTitle: "同步任务存在失败",
      syncStoreErrorItem: "{storeName}：{message}",
      syncUnknownError: "未知同步错误",
      syncColumnsSynced: "已同步",
      syncColumnsFailed: "失败数",
      syncNoStoreJobs: "暂无店铺同步明细。",
      statusRunning: "进行中",
      statusFailed: "失败",
      statusCompleted: "已完成",
      refresh: "刷新",
      storeScope: "店铺范围",
      keywordPlaceholder: "按 offer_id 或商品名搜索",
      sourceAll: "全部来源",
      sourcePipeline: "系统发布",
      sourceExternal: "外部历史",
      inSaleOnly: "仅在售相关",
      query: "查询",
      itemsTotal: "共 {total} 条",
      selectionManual: "已选择 {count} 条",
      selectionFiltered: "按当前筛选条件全选",
      selectionStoreAll: "按店铺范围全选",
      selectPage: "全选当前页",
      selectFiltered: "按筛选全选",
      selectStoreAll: "按店铺全选",
      clearSelection: "清空选择",
      allPageSelected: "已选中当前页全部 {count} 条。",
      selectAllMatching: "选择全部 {total} 条匹配商品",
      selectAllInStores: "选择店铺内全部商品",
      storeSelectAll: "全选",
      storeDeselectAll: "清空",
      columnsStore: "店铺",
      columnsOffer: "Offer ID",
      columnsName: "商品名",
      columnsBrand: "品牌",
      columnsPrice: "价格",
      columnsStock: "库存",
      columnsSource: "来源",
      associationMatched: "已关联",
      associationUnmatched: "未关联",
      openStudio: "打开采集记录",
      openSource: "打开 1688 货源",
      openPublish: "打开 Ozon 商品",
      noAssociation: "暂无关联",
      columnsStatus: "状态",
      columnsError: "错误信息",
      loading: "加载中...",
      noItems: "暂无商品数据。",
      noBrand: "无品牌",
      pageIndicator: "第 {page}/{totalPages} 页",
      prevPage: "上一页",
      nextPage: "下一页",
      batchTitle: "批量更新",
      brandOperation: "更新品牌",
      brandStoreBound: "使用店铺绑定品牌",
      brandCustom: "使用查询品牌",
      brandNone: "使用无品牌",
      brandSearchPlaceholder: "搜索 Ozon 品牌",
      searchBrand: "查询品牌",
      searching: "查询中...",
      priceOperation: "更新价格",
      priceValue: "新价格",
      oldPriceValue: "原价（可选）",
      minPriceValue: "最低价（可选）",
      stockOperation: "更新库存",
      stockValue: "库存值",
      warehouseLoading: "仓库加载中...",
      warehouseSelectPlaceholder: "请选择仓库",
      warehouseDefaultName: "仓库 {id}",
      warehouseEmptyForStore: "该店铺暂无可用仓库。",
      warehouseLoadErrorForStore: "仓库加载失败：{message}",
      warehouseMissingHint: "还有 {count} 个店铺未完成仓库选择。",
      startBatch: "开始批量任务",
      batchSubmitting: "提交中...",
      batchStarted: "已开始批量任务。",
      jobTitle: "任务进度",
      jobStatus: "状态：{status}",
      jobProgress: "进度：{progress}%（{done}/{total}）",
      jobFailedCount: "{count} 条失败",
      retryFailed: "重试失败项",
      retryStarted: "已开始重试任务。",
      noJobItems: "暂无结果明细。",
      errors: {
        loadStoresFailed: "加载店铺失败。",
        loadItemsFailed: "加载商品列表失败。",
        syncFailed: "发起同步失败。",
        syncTaskFailed: "同步任务失败，请重试。",
        brandSearchFailed: "品牌查询失败。",
        brandRequired: "请先选择品牌。",
        priceInvalid: "价格值不合法。",
        oldPriceInvalid: "原价值不合法。",
        minPriceInvalid: "最低价值不合法。",
        warehouseLoadFailed: "加载仓库失败。",
        warehouseLoading: "仓库仍在加载中。",
        warehouseRequiredForStore: "请先为 {storeName} 选择仓库。",
        stockInvalid: "库存值不合法。",
        noOperation: "请至少选择一种更新操作。",
        selectItemsFirst: "请先选择商品。",
        batchCreateFailed: "创建批量任务失败。",
        retryFailed: "重试失败项失败。",
      },
    },

    publish: {
      title: "发布确认",
      subtitle: "当前已针对 Ozon 平台优化发布。",
      healthScore: "上架健康度",
      health: {
        excellent: "优秀可发布",
        good: "表现良好",
        warning: "需要检查",
        poor: "需要优化"
      },
      targetPlatforms: "1. 目标平台",
      storeLabel: "店铺",
      manageStores: "管理店铺",
      storeModeSingle: "单店铺",
      storeModeBatch: "批量",
      batchPrimary: "摘要店铺",
      batchHint: "批量模式会为每个店铺创建独立发布任务，并自动做标题/SKU/主图顺序差异化。",
      batchSelectedCount: "已选择 {count} 个店铺",
      selectAllStores: "全选",
      clearStores: "清空",
      creatingTasks: "正在创建发布任务（{current}/{total}）...",
      modeNoticeUpdate: "检测到已发布商品（offer_id: {offerId}），本次将自动执行“更新商品”，避免重复创建。",
      modeNoticeCreate: "当前没有已发布记录，本次将创建新的 Ozon 商品。",
      platformReady: "Ozon Global",
      platformUnavailable: "暂不可用",
      comingSoon: "即将支持",
      pricingConfirmation: "2. 价格确认",
      aiOptimized: "AI 智能定价",
      tablePlatform: "平台",
      tableSuggested: "建议价格",
      tableAdjusted: "调整价格",
      tableProfit: "预估利润",
      scheduleTitle: "3. 发布计划",
      publishNow: "立即发布",
      publishNowDesc: "立即同步到 Ozon 上架。",
      scheduleLater: "定时发布",
      scheduleLaterDesc: "选择高峰时间发布。",
      releaseDate: "发布日期",
      releaseTime: "发布时间",
      backToEdit: "返回编辑",
      confirmLaunch: "确认发布",
      confirmLaunchBatch: "确认批量发布（{count}）",
      launching: "发布中...",
      loading: "加载中...",
      progressTitle: "Ozon 发布进度",
      progressSubtitle: "OneClickSell · Ozon API 集成",
      publishing: "发布中",
      batchPublishing: "批量发布中",
      batchCompleted: "批量发布已结束",
      batchCompletedWithIssues: "批量发布已结束（有失败项）",
      viewResult: "查看结果",
      estimatedRemaining: "预计剩余",
      progressIndeterminate: "正在等待平台反馈，进度可能会短暂停留。",
      progressRefreshHint: "状态约每 20–30 秒自动刷新，可手动刷新。",
      stepCompleted: "已完成",
      stepInProgress: "进行中...",
      stepFailed: "失败",
      stepPending: "等待中",
      stepLabels: {
        validate: "校验发布数据",
        uploadImages: "上传商品图片",
        createListing: "创建 Ozon 商品",
        syncStock: "同步价格与库存",
        updateListing: "更新商品基础信息",
        updateAttributes: "同步商品属性",
        updatePrices: "同步商品价格",
        updateStock: "同步商品库存",
        updateImages: "同步商品图片",
        verifyUpdate: "验证更新结果"
      },
      doNotClose: "请勿关闭此页面。",
      doNotCloseDesc: "商品正在安全上传至 Ozon API。",
      safeToClose: "可以关闭此页面。",
      safeToCloseDesc: "发布流程已进入终态，可查看结果或继续人工复核。",
      publishFailedFinal: "发布失败。",
      publishFailedFinalDesc: "当前没有继续上传中的任务，请查看错误详情并按需人工处理。",
      successTitle: "商品发布成功",
      successSubtitle: "商品已通过校验并上线到平台。",
      successWarningTitle: "商品已创建（需处理）",
      successWarningSubtitle: "商品卡片已创建，但当前在 Ozon 后台不可见。",
      successFixRequiredTitle: "商品已创建（待整改）",
      successFixRequiredSubtitle: "商品已创建成功，但 Ozon 返回了需要整改的校验项。",
      successFixRequiredHint: "请打开发布详情处理整改项，处理完成后再创建变体。",
      liveNow: "已上线",
      needsAction: "需处理",
      openInOzon: "前往 Ozon 店铺",
      openInOzonDisabledHint: "发布完成后将显示商品链接。",
      detailRouteHint: "这次发布结果沿用了当前详情图路线。",
      returnDashboard: "返回工作台",
      goToAssets: "进入资产库",
      publishAnother: "继续发布新商品",
      resultFooter: "© {year} AI Listing Optimizer. 批次号：{ref}",
      failureTitle: "Ozon 发布失败",
      failureSubtitle: "平台 API 未能处理该商品。",
      errorDetails: "错误详情",
      referenceId: "参考编号：{ref}",
      retryPublishing: "重新发布",
      retrying: "重试中...",
      contactSupport: "联系支持",
      batchFailuresTitle: "{count} 个店铺任务创建失败",
      manualReview: {
        title: "建议人工复核",
        desc: "Ozon 可能返回失败，但商品卡片已部分创建。请登录 Ozon 卖家后台核对真实状态。",
        partialSuccessHint: "如果你已在 Ozon 卖家后台看到该商品，通常表示上传已部分成功，仅需按后台提示修正具体字段。",
        checkOzon: "登录 Ozon 卖家后台查看该商品的具体校验错误。",
        fixInOzon: "若商品已创建，可在 Ozon 后台手工修正字段后再次提交。",
        aiNotPerfect: "AI 生成数据可显著提效，但并非 100% 准确，部分商品仍需要人工复核。"
      },
      recovery: {
        title: "恢复方案",
        loading: "正在分析失败原因...",
        reasonFallback: "Ozon 未返回明确错误原因，可先尝试重发或联系支持。",
        reasonCode: "规则",
        newOfferId: "新 SKU",
        newOfferIdPlaceholder: "请输入新的 SKU（offer_id）",
        newOfferIdHint: "仅支持字母、数字、下划线、连字符，最长 50 位。",
        retryWithNewSku: "使用新 SKU 重新发布",
        retrySamePayload: "使用原参数重发",
        executing: "提交中...",
        goBindStore: "检查店铺授权",
        offerIdRequired: "请先填写新的 SKU。",
        loadFailed: "恢复方案加载失败。",
        executeFailed: "重新发布失败，请稍后重试。",
        actions: {
          retry_with_new_offer_id: "建议：使用新 SKU 重发",
          retry_same_payload: "建议：原参数重试",
          fix_content_then_retry: "建议：先修复内容再发布",
          store_auth_required: "建议：先处理店铺授权",
          wait_and_poll: "建议：继续等待并刷新状态",
          contact_support: "建议：联系支持排查"
        }
      },
      result: {
        untitled: "未命名商品"
      },
      warnings: {
        nonBlockingTitle: "非阻断发布提醒",
        nonBlockingFields: "部分适配字段不完整（{fields}），但不影响继续发布；提交后平台会继续校验。"
      },
      errors: {
        missingCollection: "缺少采集记录，请返回重试。",
        summaryFailed: "加载发布摘要失败（状态码 {status}）。",
        storeStatusFailed: "加载店铺状态失败（状态码 {status}）。",
        missingFields: "仍有必填项未完成，请先补齐。",
        noUpdateFields: "未检测到实际变更（价格/图片/内容均未变化），本次无需提交更新。",
        richContentInvalid: "富文本格式不正确，Ozon 不会接受，请重新生成后再发布。",
        missingFieldsHint: "缺失字段：{fields}",
        storeRequired: "请选择至少一个店铺。",
        missingSchedule: "请选择发布日期和时间。",
        invalidAdjustedPrice: "调整价格必须是大于 0 的有效数字。",
        publishFailed: "发布失败（状态码 {status}）。",
        statusFailed: "获取发布状态失败（状态码 {status}）。",
        retryFailed: "重试失败，请稍后再试。",
        batchPartialFailed: "{count} 个店铺创建任务失败，其余店铺已开始发布。",
        socksProxyDependencyMissing: "发布请求失败：系统代理组件缺失，已自动切换直连重试，请稍后再次提交。",
        viewTechnicalDetails: "查看技术详情",
        hideTechnicalDetails: "收起技术详情"
      }
    },

    capability: {
      title: "能力设置",
      subtitle: "设置默认的 6 Agent 工作流。Ozon 支持全量自动化，其他平台仅导出列表。",
      targetPlatform: "目标平台选择",
      supported: "全量支持",
      exportOnly: "仅导出列表",
      currentPlatform: "当前：",
      systemNotice: "系统通知",
      systemNoticeBody: "6 Agent 流程与平台无关，仅 Ozon 支持直连发布，其他平台会输出列表包供手动上传。",
      platformActive: "全量自动化",
      platformOzonDesc: "支持全流程与一键发布",
      platformAmazonDesc: "全球市场，导出列表后手动上传",
      platformShopifyDesc: "店铺型平台，导出列表包",
      platformAliDesc: "跨境平台，导出列表包",
      platformEbayDesc: "海外平台，导出列表包",
      languageLockedHint: "Ozon 语言已锁定为俄语",
      targetMarket: "目标市场",
      russiaDefault: "俄罗斯（Ozon 默认）",
      outputLanguage: "输出语言",
      russianDefault: "俄语（标准西里尔文）",
      contentStyleTitle: "内容风格",
      seoLevelTitle: "SEO 深度",
      customPromptTitle: "自定义提示词",
      customPromptPlaceholder: "请输入自定义提示词...",
      customPromptHint: "仅在选择「自定义风格」时生效。",
      brandGuardTitle: "品牌与合规保护",
      brandGuardDesc: "为内容与图片启用品牌保护和合规保障。",
      brandGuardHint: "平台侧将继续提供高级合规防护。",
      summaryTitle: "执行摘要",
      summaryHint: "本次将执行",
      summaryImages: "生成 {count} 张轮播图（主图 + 场景 + 卖点）。",
      summaryDetailImages: "生成 {count} 张详情图。",
      summaryDetailDisabled: "详情图：关闭。",
      summaryDetailCompose: "详情图：开启，复用轮播拼接（固定 {count} 张）。",
      summaryDetailStandalone: "详情图：开启，独立生成（固定 {count} 张）。",
      summarySeo: "SEO 深度：{level}。",
      summaryRich: "富文本主题：{theme}。",
      summaryBrandOn: "品牌保护已启用。",
      summaryBrandOff: "品牌保护已关闭。",
      summaryExportOnly: "{platform} 当前仅支持导出列表。",
      imageSetStandardShort: "套图",
      imageSetDetailOnlyShort: "详情图",
      carouselImagesTitle: "轮播图",
      detailImagesTitle: "详情图",
      detailImagesHint: "可选开启详情图，固定 {count} 张，不影响轮播图张数。",
      detailImagesDisabledShort: "已关闭",
      detailGenerationModeCompose: "复用轮播拼接",
      detailGenerationModeComposeDesc: "沿用已生成的轮播图和文案拼接详情图。",
      detailGenerationModeComposeShort: "拼接",
      detailGenerationModeStandalone: "独立生成",
      detailGenerationModeStandaloneDesc: "额外独立生成 3 张详情图。",
      detailGenerationModeStandaloneShort: "独立生成",
      quickImagesSummary: "详情图路径：{detail}",
      saveDefault: "保存为默认",
      useDefault: "恢复默认",
      saving: "保存中...",
      saved: "已保存",
      skipAutoOpen: "保存为默认后不再自动弹出",
      skipAutoOpenHint: "需要修改时可从导航栏进入",
      aiContentGen: "AI 内容生成",
      aiContentDesc: "图片、标题和本地化规格",
      active: "活跃",
      directPublish: "一键直发",
      directPublishDesc: "跳过手动 CSV 导出/导入",
      ozonExclusive: "OZON 专属",
      launchBtn: "启动 AI 优化",
      status: "系统状态：准备好 Ozon 高速同步",
      secure: "安全协议",
      neural: "神经引擎 v4.2",
      apiSync: "API 同步活跃",
      quickModeSubtitle: "快速编辑现有配置",
      expertModeSubtitle: "完全控制所有设置",
      guidedModeSubtitle: "完成设置以开始使用",
      currentConfig: "当前配置",
      quickModeDesc: "编辑关键设置或使用当前配置启动 AI",
      expertMode: "专家模式",
      quickMode: "快速模式",
      showAdvanced: "显示高级设置",
      saveChanges: "保存更改",
      launchAI: "启动 AI",
      perProduct: "每件产品",
      platformLabel: "平台",
      marketLabel: "市场",
      styleLabel: "风格",
      imagesLabel: "图片",
      languageLabel: "语言",
      marketRuShort: "俄罗斯",
      marketUsShort: "美国",
      marketCnShort: "中国",
      marketGlobalShort: "全球",
      styleProfessionalDesc: "商务正式语气",
      styleCreativeDesc: "情感化故事叙述",
      styleConciseDesc: "简洁有力的表达",
      styleCustomDesc: "自定义提示词配置",
      stepPlatformHint: "先确认要执行流程的平台。",
      stepMarketHint: "确认本次上架内容面向的核心市场。",
      stepLanguageHint: "选择生成文案时使用的目标语言。",
      stepStyleHint: "选择最符合品牌调性的文案风格。",
      stepCustomPromptHint: "填写你希望 AI 严格遵循的自定义写作指令。",
      stepImageCountHint: "设置每个商品需要生成的图片数量。",
      stepSkipAutoOpenHint: "选择下次是否自动打开此配置流程。",
      stepSaved: "当前步骤已保存。",
      loginRequired: "请先登录后再保存设置",
      saveSuccess: "设置已保存",
      saveFailed: "保存设置失败",
      remoteLoadFallbackHint: "云端配置加载失败，已使用本地配置，可点击重试。",
      strategyTierTitle: "策略档位",
      strategyTierHint: "选择自动化深度与质量级别。",
      tierEconomy: "经济档",
      tierEconomyDesc: "图片数量更少、输出更快，适合快速起量。",
      tierBalanced: "平衡档",
      tierBalancedDesc: "质量与成本平衡，推荐默认使用。",
      tierPremium: "高阶档",
      tierPremiumDesc: "图片数量更多，模型质量更高。",
      guidedStepLabel: "第 {current} / {total} 步",
      guidedBack: "上一步",
      guidedNext: "下一步",
      guidedSaveNext: "保存并下一步",
      guidedLaunch: "启动优化",
      guidedReviewLabel: "复核",
      guidedReviewHint: "确认后启动",
      guidedPlatformTitle: "选择目标平台",
      guidedPlatformDesc: "确认这次要把商品卖到哪个平台。",
      guidedMarketTitle: "目标市场",
      guidedMarketDesc: "确认买家所在地区和生成语言。",
      guidedCountryRegion: "国家 / 地区",
      guidedLanguageLocked: "已按平台锁定语言",
      guidedStyleTitle: "内容风格",
      guidedStyleDesc: "选择商品文案要呈现的语气。",
      guidedImagesTitle: "AI 图片生成",
      guidedImagesDesc: "设置轮播图数量和详情图生成方式。",
      guidedCarouselCountHint: "选择固定 5 / 7 / 10 张轮播图",
      guidedReviewTitle: "复核并启动",
      guidedReviewDesc: "AI 流程已准备好，可以开始执行。",
      guidedDetailStandaloneCount: "{count} 张独立生成",
      guidedDetailComposedCount: "{count} 张拼接生成"
    },

    aiConfig: {
      badge: "AI 配置",
      pageTitle: "AI 优化设置",
      pageSubtitle: "在启动优化任务前，确认并微调 AI 参数。",
      statusHint: "AI 建议已准备好复核",
      targetPlatform: "目标平台",
      platformActive: "Ozon 已启用",
      platformOzonDesc: "面向独联体市场与本地物流优化",
      platformAmazonDesc: "全球市场优化流程",
      platformShopifyDesc: "Shopify 店铺上架准备",
      platformAliDesc: "跨境上架准备",
      comingSoon: "即将上线",
      targetLanguage: "目标语言",
      languageRu: "俄语",
      languageEn: "英语",
      languageDe: "德语",
      languageFr: "法语",
      languageIt: "意大利语",
      languageEs: "西班牙语",
      languageJa: "日语",
      languagePtBr: "葡萄牙语（巴西）",
      languagePtPt: "葡萄牙语（葡萄牙）",
      languageNl: "荷兰语",
      languageSv: "瑞典语",
      languagePl: "波兰语",
      languageTr: "土耳其语",
      languageAr: "阿拉伯语",
      languageZhHans: "简体中文",
      languageZhHant: "繁体中文",
      languageCs: "捷克语",
      languageDa: "丹麦语",
      languageFi: "芬兰语",
      languageKo: "韩语",
      languageNb: "挪威语（博克马尔）",
      languageTh: "泰语",
      contentStyle: "内容风格",
      styleProfessional: "专业",
      styleCreative: "创意",
      styleConcise: "简洁",
      styleCustom: "自定义",
      stylePrompt: "风格提示逻辑",
      currentStyle: "当前风格：",
      customActive: "自定义逻辑生效",
      promptProfessional: "提示词：突出技术规格、耐用性与商务语气。",
      promptCreative: "提示词：强调情绪价值、故事化表达与生活方式收益。",
      promptConcise: "提示词：用简洁有力的句子聚焦核心卖点。",
      promptCustom: "已加载自定义提示词，请在高级设置中精细调整。",
      customPrompt: "自定义提示词编辑器",
      customPromptPlaceholder: "在此输入自定义提示词...",
      customPromptHint: "可用变量：{product_name}、{category}、{specs}",
      customHint: "前往高级编辑器完善自定义提示词",
      imageGeneration: "开启 AI 图片生成",
      imageGenerationDesc: "去除水印并生成生活方式场景图。",
      imageGenerationOnce: "仅本次生效。",
      imageCredits: "图片生成将消耗图片积分。",
      imageCount: "生成图片张数",
      imageCountHint: "轮播图张数固定为 5、7、10 张。",
      trialImageBadge: "体验仅生成 1 张主图",
      trialImageHint: "体验模式仅生成 1 张主图，升级后解锁完整图片组。",
      trialUpgradeCta: "查看套餐",
      seoLevel: "SEO 优化级别",
      seoStandard: "标准",
      seoStandardDesc: "基础关键词注入与元标签优化",
      seoDeep: "深度分析",
      seoDeepDesc: "语义聚类、竞品分析与长尾关键词整合",
      seoLockedHint: "SEO 深度已固定为最优（系统默认）。",
      review: "审查并启动",
      recommended: "推荐",
      launch: "启动 AI 优化",

      modalTitle: "AI 优化配置",
      modalSubtitle: "启动 AI 任务前设置最终偏好。",
      reuseTitle: "检测到历史结果",
      reuseSubtitle: "该商品存在历史优化产出。",
      reuseDesc: "可复用最近一次生成的下游结果，或重新生成。",
      reuseNotice: "复用将直接展示结果，不会重新生成。",
      reuseTimeLabel: "最近生成时间：{time}",
      reuseStageLabel: "可复用阶段：{stages}",
      reuseSourceLabel: "来源任务：{id}",
      reuseViewHistory: "查看历史结果",
      reuseRegenerate: "重新生成",
      reuseUse: "复用历史结果",
      reuseApplying: "复用中...",
      useDefault: "使用默认配置",
      advanced: "高级设置",
      cancel: "取消",
      saveDraft: "保存草稿",
      saving: "保存中...",
      saved: "已保存",
      footerHint: "AI 将按照当前策略与积分消耗执行任务。"
    },

    ai: {
      workspaceTitle: "活跃列表优化",
      syncProgress: "同步进度",
      sourceRef: "来源参考",
      rawMedia: "原始媒体资产",
      brandProt: "品牌保护",
      agentOrch: "代理编排",
      neuralCenter: "神经洞察中心",
      sourceAnalysis: "来源分析与结构",
      byImageAnalyzer: "通过图像分析器",
      dataStructurer: "数据结构化工具",
      rawImages: "原始供应商图片",
      scanned: "已扫描",
      unstructured: "非结构化文本 → 结构化属性",
      processed: "已处理",
      seoArch: "SEO 关键词图谱",
      bySeo: "通过 SEO 架构师",
      optTitle: "优化标题",
      score: "评分",
      bullets: "要点",
      keywordStrat: "关键词策略",
      core: "核心",
      hot: "热门",
      longTail: "长尾",
      visualPipeline: "视觉资产管道",
      byImgSel: "通过图像选择器",
      visualPlanner: "视觉规划师",
      refSel: "参考选择",
      sourcesLocked: "来源已锁定",
      visualPlan: "视觉计划（生成提示）",
      readyArtist: "艺术家准备就绪",
      optGallery: "Listing 文案",
      byArtist: "通过视觉艺术家",
      marketInt: "平台适配",
      byRichBuilder: "通过丰富内容构建器",
      contextMaster: "上下文大师",
      aplusPreview: "富文本预览",
      fieldMap: "平台字段映射",
      motionProd: "导出交付",
      byVideoCreator: "通过视频创作者",
      rendering: "VEO 3.1 渲染引擎",
      scriptGen: "脚本已生成",
      analyzer: "商品理解",
      structurer: "商品理解",
      architect: "SEO 关键词代理",
      planner: "图片槽位",
      selector: "参考图筛选",
      richContent: "平台适配",
      videoCreator: "导出代理",
      visualArtist: "Listing 文案",
      complete: "完成",
      thinking: "思考中",
      queued: "等待执行",
      waiting: "等待中",
      idle: "空闲",
      active: "活跃（渲染中）",
      enableVideo: "启用视频生成",
      reviewChanges: "审查更改",
      reviewAfterGenerate: "生成后审查",
      oneClick: "一键发布",
      neuralActive: "神经洞察中心活跃。管道针对 {platform}{market}（{language}）市场。"
    },
    markets: {
      ru: "俄罗斯",
      us: "美国",
      cn: "中国",
      global: "全球"
    },

    studio: {
      nav: "Studio",
      control: {
        title: "生成与调试控制",
        simpleTitle: "AI 生成控制",
        collection: "Collection:",
        collectionEmpty: "未选择",
        setupRequiredTitle: "请先完成 AI 设置",
        setupRequiredDesc: "请先确认目标市场/语言与图片生成设置，再开始生成流程。",
        setupRequiredAction: "去 AI 设置",
        store: "店铺",
        manageStores: "管理店铺",
        storeLockedHint: "当前任务绑定店铺为 {store}。如需切换店铺，请先返回任务中心在目标店铺下重新进入或新建任务。",
        skipImages: "跳过图片",
        onceOnly: "仅本次生效",
        costHint: "预计扣费：{count} 张 / {credits} 积分",
        confirmCostTitle: "确认扣费",
        confirmCostMessage: "本次预计生成 {count} 张图片，将扣除 {credits} 积分。\n是否继续？",
        confirmCostConfirm: "继续生成",
        start: "开始生成",
        regenerate: "重新生成",
        resume: "继续生成",
        starting: "启动中...",
        cancelGenerate: "取消任务",
        cancelling: "取消中...",
        runningStages: "执行中阶段",
        parallelRunningHint: "多 Agent 并行执行中",
        stageDebug: "单阶段调试",
        runStage: "执行单阶段",
        executing: "执行中...",
        infoStructureHint: "提示：执行商品理解只更新本阶段数据。"
      },
      credits: {
        balanceHint: "余额：{credits} 积分",
        totalBalanceHint: "总计：{credits} 积分",
        bucketSubscription: "订阅赠送积分",
        bucketRecharge: "充值积分",
        bucketLegacy: "旧积分",
        availableTooltipPrefix: "当前模式下，可用积分 = ",
        availableTooltipSuffixThirdParty: "（普通模式）",
        availableTooltipSuffixOfficial: "（官方高速模式；可用账户按后台设置）",
        channelPlanLabel: "图片模式",
        channelPlanThirdParty: "普通模式",
        channelPlanOfficial: "官方高速模式",
        perImageHint: "当前模式 {credits} 积分/张",
        settlementShortHint: "后台自动调度模型，价格按当前模式",
        settlementConfirmHint: "平台可能在后台调度不同生图模型，但扣费始终按当前图片模式；失败图片会自动退款。",
        channelPlanUnavailable: "该图片模式暂不可用，请切换模式或联系管理员处理。",
        channelPlanUnavailableInline: "暂不可用",
        channelPlanCompareHint: "普通 {thirdParty} 积分/张 · 官方高速 {official} 积分/张",
        channelSwitchChecking: "正在检测任务重试状态...",
        channelSwitchBlockedDefault: "仅当当前任务自动重试全部耗尽后，才允许切换渠道。",
        switchOfficialTitle: "切换到官方高速模式？",
        switchOfficialConfirm: "官方高速模式会按官方渠道价格扣费，是否继续切换？",
        switchOfficialConfirmWithDiff:
          "官方高速模式 {official} 积分/张（当前 {current} 积分/张，每张多 {diff} 积分），确认切换？",
        switchOfficialConfirmLabel: "确认切换",
        switchOfficialCancelLabel: "取消",
        insufficientTitle: "积分不足",
        insufficientMessage: "本次需要 {needed} 积分，你当前仅 {current} 积分。\n充值后即可继续生成图片。",
        insufficientMessageGeneric: "当前积分不足，充值后可继续生成图片。",
        goRecharge: "去充值",
        later: "稍后",
        skipImages: "先跳过图片",
        stageHint: "你可以先勾选“跳过图片”继续生成文案与适配，稍后充值后再补图。",
        generateHint: "本次预计生成 {count} 张图片。也可以先跳过图片继续流程。",
        affordableHint: "按当前余额，最多可生成 {count} 张图片。可先去 AI 设置调整图片张数。",
        singleHint: "图片生成会消耗积分（按张计费）。充值后再试一次。"
      },
      errors: {
        missingCollection: "缺少任务信息，无法加载 Studio 数据。",
        previewFailed: "预览数据加载失败。",
        stageStatusFailed: "阶段状态加载失败。",
        generateFailed: "生成启动失败。",
        stageExecuteFailed: "单阶段执行失败。",
        agentLogFailed: "处理记录加载失败。",
        previewRequestFailed: "预览数据加载失败（状态 {status}）。",
        imageSlotRequestFailed: "图片方案加载失败（状态 {status}）。",
        imageGenerationStatusRequestFailed: "图片生成状态加载失败（状态 {status}）。",
        stageStatusRequestFailed: "阶段状态加载失败（状态 {status}）。",
        generateRequestFailed: "启动生成失败（状态 {status}）。",
        stageExecuteRequestFailed: "执行当前阶段失败（状态 {status}）。",
        agentLogRequestFailed: "处理记录加载失败（状态 {status}）。",
        promptUpdateFailed: "更新提示词失败。",
        imageGenerateFailed: "生成图片失败。",
        imageRegenerateFailed: "重生成图片失败。",
        imageRegenerateNotAccepted: "重生成请求未被后端受理，请重试。",
        imageRegenerateSubmittedSyncDelayed: "已提交重生成任务，但状态刷新延迟，请稍后查看。",
        imageDecisionFailed: "处理图片选择失败。",
        imageDeleteFailed: "删除图片失败。",
        cancelGenerateFailed: "取消生成失败。",
        queueBusy: "队列繁忙，请稍后再试。",
        internalStageFailed: "图片任务遇到内部服务错误，请刷新后重试。",
        refundHint: "积分已退款。",
        publishBlocked: "当前流程在「{stage}」阶段失败，请先继续生成修复后再发布。",
        publishNotReady: "当前内容尚未生成完成，请先完成生成后再发布。",
        publishImageOpsPending: "图片仍在处理中（保存/生成/重生成），请稍后再发布。",
        publishMissingImages: "当前缺少可上传的图片，请先生成/补充图片后再发布。",
        publishPartialImagesWarning: "当前必需图片仅完成 {generated}/{required}（还差 {missing} 张）。你可以继续发布，也可先补齐图片。",
        publishMissingFields: "仍有必填属性缺失，请先完成平台适配后再发布。",
        publishAttributesUnavailable: "当前属性数据尚未就绪，请重新执行平台适配后再发布。",
        updateChannelFailed: "更新图片模式失败。",
        stageFailed: "阶段失败：{stage}"
      },
      publishGuide: {
        title: "发布前检查",
        subtitle: "请先处理以下缺失项后再发布。",
        close: "我知道了",
        itemImages: "缺少可上传的图片",
        itemPendingImages: "图片仍在处理中",
        itemCategory: "类目/商品类型缺失",
        itemTitle: "标题缺失",
        itemAttributes: "必填属性缺失",
        itemPrice: "价格缺失",
        itemAdapter: "平台适配未完成",
        itemStageFailed: "阶段失败：{stage}",
        itemStageIncomplete: "阶段未完成：{stage}",
        goFixImages: "去补图",
        goToStage: "去 {stage}",
        partialDecisionTitle: "必需图片未补齐",
        generateMissingOnly: "仅补失败图片",
        continuePublish: "直接继续发布"
      },
      action: {
        generateStarted: "已启动生成流程。",
        generateAdjusted: "积分不足，已自动降级为 {count} 张图片并继续生成。",
        generateSkipImagesAdjusted: "积分不足，已自动切换为跳过图片（可先生成文案/适配，稍后充值再补图）。",
        generateQueued: "同一生成任务正在执行，已为你恢复当前进度。",
        cancelRequested: "已提交取消请求。",
        stageTriggered: "已触发阶段执行：{stage}",
        stageQueued: "同一阶段任务正在执行：{stage}",
        promptUpdated: "已更新图片 {index} 的提示词。",
        imageGenerateTriggered: "已触发图片生成：{index}",
        imageGenerateQueued: "图片生成已在队列中：{index}",
        missingImagesGenerateTriggered: "已触发缺失图片补生成，共 {count} 张。",
        missingImagesGenerateQueued: "缺失图片补生成已在队列中，共 {count} 张。",
        trackImageGenerateTriggered: "已触发{track}批量生成，共 {count} 条方案。",
        trackImageGenerateQueued: "{track}批量生成已在队列中，共 {count} 条方案。",
        trackImageGenerateNothingPending: "{track}没有待生成的图片了。",
        imageRegenerateTriggered: "已触发图片重生成：{index}",
        imageRegenerateQueued: "图片重生成已在队列中：{index}",
        imageCandidateAccepted: "已采用方案 {index} 的新图。",
        imageCandidateRejected: "已保留方案 {index} 的旧图。",
        imageDeleted: "已删除图片：{index}",
        queueRetrying: "队列繁忙，正在重试（{attempt}/{max}）..."
      },
      imageGeneration: {
        title: "图片生成任务",
        statusQueued: "等待资源",
        statusRunning: "执行中",
        statusRunningBackground: "后台执行中",
        statusCompleted: "已完成",
        statusFailed: "失败",
        statusCancelled: "已取消",
        progress: "已完成 {done}/{total}",
        progressWithFailed: "已完成 {done}/{total} · 失败 {failed}",
        queuePosition: "排队第 {position} 位",
        estimatedWait: "预计约 {duration} 后开始",
        backgroundEstimatedWait: "预计约 {duration} 后同步",
        nextAttemptAt: "下次尝试：{time}",
        defaultQueued: "任务已提交，正在等待资源。",
        defaultRunning: "图片生成进行中。",
        defaultRunningBackground: "任务仍在后台执行，完成后会自动同步结果。",
        defaultCompleted: "图片生成已完成，结果已同步。",
        defaultFailed: "图片生成失败，积分会自动退款。",
        defaultCancelled: "图片生成任务已取消。",
        backgroundHint: "你可以继续其他操作，任务仍在后台执行。",
        lastUpdated: "最近更新：{time}"
      },
      module: {
        expand: "展开",
        collapse: "收起",
        collapsedHint: "模块已收起，点击“展开”查看详情。",
        hiddenQueueTitle: "待执行模块",
        hiddenQueueDesc: "未开始且无结果的模块已自动隐藏，阶段开始后会自动显示。",
        hiddenQueueAction: "显示"
      },
      stage: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词图谱",
        listing: "Listing 文案",
        selector: "参考图筛选",
        visual: "图片生成",
        imageGeneration: "图片生成",
        normalized: "通用结构",
        category: "类目匹配",
        adapter: "平台适配",
        export: "导出交付"
      },
      source: {
        title: "商品理解",
        verified: "数据已确认",
        pending: "等待数据",
        visualDiagnostics: "资产诊断",
        analyzing: "分析中",
        ready: "就绪",
        summary: "摘要",
        expand: "展开详情",
        collapse: "收起详情",
        thumbnailAlt: "缩略图",
        mainImageAlt: "主图",
        emptyImages: "暂无来源图片",
        emptyTitle: "等待来源图片",
        emptyDesc: "连接插件或导入产品后将自动完成商品理解。",
        imageCount: "图片数量：{count}",
        noImages: "暂无图片",
        scanning: "分析中...",
        sectionsTitle: "采集区块",
        sectionsEmpty: "暂无区块数据"
      },
      rawText: {
        title: "原始文本",
        length: "{count} 字符",
        emptyTitle: "暂无原始文本",
        emptyDesc: "运行商品理解后将解析来源文本。"
      },
      productCore: {
        title: "产品核心",
        materials: "材质",
        specs: "规格",
        useCases: "使用场景",
        audience: "适用人群",
        facts: "关键事实",
        emptyTitle: "产品核心待生成",
        emptyDesc: "运行商品理解后将生成核心事实。"
      },
      productSchema: {
        title: "产品数据结构",
        identity: "基础信息",
        titleLabel: "标题",
        brandLabel: "品牌",
        categoryLabel: "类目",
        attributes: "关键属性",
        categoryKeywords: "类目候选关键词",
        noCategoryKeywords: "暂无类目候选关键词",
        empty: "暂无属性数据",
        emptyTitle: "属性待生成",
        emptyDesc: "完成结构化阶段后将自动生成关键属性。",
        logistics: "物流信息",
        price: "价格 (CNY)",
        weight: "重量",
        dimensions: "尺寸",
        emptyValue: "—",
        sku: "SKU 概览",
        skuCount: "SKU 数量",
        skuUnit: "条",
        noColors: "暂无颜色",
        sizes: "尺码",
        noSizes: "暂无尺码",
        skuReadOnlyHint: "SKU 与优先参考图请在商品预览页设置，这里只展示最终生图引用结果。",
        skuReadOnlyAction: "前往商品预览设置",
        stockZeroWarning: "当前选择库存为 0，系统会保留该 SKU，请发布前确认库存状态"
      },
      metrics: {
        images: "图片",
        skus: "SKU",
        attributes: "属性",
        sections: "分段",
        rawText: "原文"
      },
      imageText: {
        title: "图片文字提取",
        count: "图片 {count}",
        empty: "暂无图片文字结果",
        emptyTitle: "暂无识别结果",
        emptyDesc: "运行图片文字提取后将在此显示结果。"
      },
      seo: {
        strength: "搜索强度",
        failed: "SEO 生成失败",
        failedDesc: "暂无 SEO 结果。",
        optimizedKeywords: "优化关键词",
        emptyKeywords: "暂无关键词数据",
        emptyTitle: "SEO 关键词待生成",
        emptyDesc: "完成内容生成后将输出关键词与搜索词。",
        searchVolume: "搜索量区间",
        competition: "意图",
        unknown: "未知",
        clusterLabel: "聚类",
        volumeTier: "热度",
        volumeHigh: "高",
        volumeMedium: "中",
        volumeLow: "低",
        volumeUnknown: "未知",
        searchPipeline: "关键词图谱概览",
        pipelinePending: "关键词图谱生成中...",
        graphTitle: "关键词图谱可视化",
        graphEmpty: "暂无图谱节点。",
        meaningLabel: "中文含义",
        coverageTitle: "覆盖报告",
        missingAspects: "缺失维度",
        duplicates: "重复关键词",
        languageIssues: "语言问题",
        coverageEmpty: "暂无问题"
      },
      keyword: {
        primary: "核心",
        secondary: "次要",
        longTail: "长尾",
        conversion: "转化",
        transactional: "交易",
        information: "信息",
        other: "其他"
      },
      visual: {
        title: "图片资产",
        subtitle: "图片槽位 • {count} 组",
        collapseAll: "全部收起",
        regeneratePrompts: "重跑提示词",
        regeneratePromptsTitle: "重跑图片提示词",
        regeneratePromptsDesc:
          "将按当前轮播图与详情图配置重建完整提示词。当前自动生图会提交轮播图 {carouselCount} 张 + 详情图 {detailCount} 张，共 {totalCount} 条方案。",
        regeneratePromptsRunning: "执行中...",
        regeneratePromptsOnly: "仅重跑提示词",
        regeneratePromptsAndGenerate: "重跑并自动生图",
        regeneratePromptsAutoTitle: "重跑提示词并自动生图",
        regeneratePromptsAutoAction: "确认执行",
        regeneratePromptsAutoConfirm:
          "将重跑全部提示词并自动生成前 {count} 张图。自动生图按当前规则扣费（{credits} 积分/张），是否继续？",
        regeneratePromptsSuccess: "提示词重跑完成（共 {count} 条），剩余免费次数：{remaining}。",
        regeneratePromptsCharged: "本次提示词重跑扣费 {credits} 积分。",
        regeneratePromptsAutoQueued: "已提交前 {count} 张自动生图任务。",
        regeneratePromptsAutoFailed: "提示词已重跑，但自动生图任务未提交成功。",
        empty: "暂无图片槽位，请先运行图片阶段。",
        emptyTitle: "图片阶段待启动",
        emptyDesc: "运行图片阶段后，系统会在这里输出可编辑的提示词与生成结果。",
        emptyStep1: "步骤 1：运行图片阶段",
        emptyStep2: "步骤 2：审核提示词",
        emptyStep3: "步骤 3：生成图片",
        constraintsTitle: "约束条件",
        constraintsEmpty: "暂无约束",
        keywordsTitle: "关键词聚焦",
        keywordsEmpty: "暂无关键词",
        referenceTitle: "参考图",
        referenceEmpty: "暂无参考图",
        referencePreviewTitle: "参考图 #{index}",
        referenceSwitchTitle: "切换参考图",
        referenceSwitchHint: "可通过缩略图或左右箭头快速切换。",
        referenceSource: {
          sku: "SKU 图片",
          main: "主图",
          detail: "详情图",
          collection: "采集图",
          extracted: "抽取结果",
          legacy: "历史缓存",
          manual: "你手动指定的",
          auto_sku_match: "系统按当前 SKU 自动匹配",
          sku_image_url: "SKU 图片",
          selected_sku_image_indices: "你锁定 SKU 后的候选图",
          selected_sku: "你选择的 SKU",
          selected_anchor: "你指定的优先图",
          none: "无",
          unknown: "未知"
        },
        anchorModeImage: "优先图模式",
        anchorModeText: "文字匹配 · 阈值 {threshold}%",
        anchorSourceLabel: "当前默认图来自：{source}",
        selectorSelectedCount: "已选 {count} 张",
        referenceControlTitle: "参考图控制",
        referenceControlScopeTitle: "参考图范围",
        referenceControlScopeUserSku: "已锁定到你选择的 SKU",
        referenceControlScopeUserSkuCount: "当前有 {count} 张候选图在你的 SKU 范围内。",
        referenceControlScopeUserSkuNoImage: "该 SKU 暂无直接匹配图，AI 会回退到文字与全图匹配。",
        referenceControlScopeAiAuto: "当前参考图范围仍由 AI 自动判断。",
        referenceControlSelectionTitle: "当前选择方式",
        referenceControlSelectionManualLocked: "你已手动锁定最终参考图，后续不再执行 AI 参考图筛选。",
        referenceControlSelectionAiWithinUserSku: "你已锁定 SKU，系统会在该 SKU 范围内自动确定 1 张最终参考图。",
        referenceControlSelectionAiAuto: "系统会自动确定最终参考图。",
        referenceControlSelectionPendingUserSku: "SKU 已锁定；如果你不再指定优先图，系统会在该 SKU 范围内自动确定 1 张参考图。",
        referenceControlSelectionPendingAi: "后续将由系统自动决定参考图范围和最终参考图。",
        referenceControlPreferredTitle: "优先参考图",
        referenceControlPreferredManual: "你指定了优先参考图 #{index}。",
        referenceControlPreferredAuto: "系统当前优先使用参考图 #{index}。",
        referenceControlPreferredNone: "当前还没有固定优先图，系统会在当前范围内自动选 1 张。",
        referenceOwnershipManual: "你指定的",
        referenceOwnershipAiWithinUserSku: "AI 选择（受你的 SKU 约束）",
        referenceOwnershipAiAuto: "AI 自动选择",
        referencePreferredManualBadge: "你的优先图",
        referencePreferredAutoBadge: "系统优先图",
        referenceLockedTitle: "参考图已锁定",
        referenceLockedSubtitle: "后续将直接使用你指定的这 1 张图",
        referenceLockedBannerTitle: "你已经手动指定了参考图",
        referenceLockedBannerBody: "下一次生图会直接使用这张图，不再执行 AI 参考图筛选。",
        referenceLockedPendingHint: "这张图已经锁定，后续生成时会直接使用。",
        referenceLockedCardTitle: "已锁定参考图",
        referenceLockedChangeAction: "更换优先参考图",
        selectorSummaryTitle: "筛选摘要",
        selectorSummaryEmpty: "暂无筛选摘要",
        selectorRationaleEmpty: "暂无筛选理由",
        selectorPromptTitle: "选图提示词",
        selectorPromptEmpty: "暂无提示词快照",
        selectorDecisionManualLocked: "手动锁定",
        selectorDecisionPrimary: "主组优先",
        selectorDecisionSupplement: "置信度补选",
        selectorDecisionFallback: "兜底补齐",
        selectorReasonTitle: "入选理由",
        selectorReasonEmpty: "暂无详细理由",
        selectorConfidence: "置信度 {value}%",
        selectorSkuConfidence: "SKU 置信度 {value}%",
        singleSkuDecisionTitle: "单 SKU 决策",
        singleSkuDecisionEmpty: "暂无单 SKU 决策结果",
        singleSkuUnknown: "未知 SKU",
        singleSkuBadge: "SKU · {sku}",
        singleSkuReasonsTitle: "选择原因",
        singleSkuReasonsEmpty: "暂无单 SKU 理由",
        singleSkuExcludedTitle: "排除 SKU 说明",
        singleSkuExcludedEmpty: "暂无排除 SKU 说明",
        singleSkuRiskPrefix: "风险提示：{risk}",
        singleSkuCandidatesTitle: "SKU 候选评分",
        singleSkuCandidatesEmpty: "暂无 SKU 候选评分数据",
        singleSkuCandidateScore: "评分 {value}",
        singleSkuCandidateImageCount: "关联图片 {count} 张",
        singleSkuCandidateReasonEmpty: "暂无候选评分理由",
        manualAnchorAction: "前往商品预览页并指定优先参考图",
        referenceSkuTitle: "图像归属 SKU",
        referenceSkuEmpty: "暂无 SKU 归属",
        referenceSkuReasonTitle: "归属理由",
        referenceSkuReasonEmpty: "暂无 SKU 归属理由",
        overlayLabel: "图中文字",
        promptCopied: "提示词已复制",
        sceneLabel: "场景",
        placeholderMainTitle: "主图方案",
        placeholderMainDesc: "核心展示与主视觉",
        placeholderSceneTitle: "场景方案",
        placeholderSceneDesc: "使用场景与氛围",
        placeholderDetailTitle: "细节方案",
        placeholderDetailDesc: "材质与卖点特写",
        detailRouteLabel: "详情图路线",
        detailRouteComposeTitle: "轮播拼接版",
        detailRouteComposeDesc: "复用已生成的轮播图和文案，在后续详情页阶段继续拼接。",
        detailRouteStandaloneTitle: "AI 独立版",
        detailRouteStandaloneDesc: "额外连续生成 3 张独立详情图，只展示页目标与最终结果。",
        detailRouteChanged: "已切换详情图路线：{route}。",
        detailRouteChangeFailed: "切换详情图路线失败。",
        detailRouteComposeEmpty: "这条路线会直接复用轮播图结果作为详情页拼接素材，请先生成上方轮播图。",
        trackSecondaryEmpty: "独立详情图会与轮播图分开生成，当前还没有可展示的专属详情图方案。",
        morePlansHint: "还有 {count} 个方案已准备好提示词，可随时补生成。",
        expandExtraPlans: "展开另外 {count} 个方案",
        collapseExtraPlans: "收起额外方案",
        viewMore: "查看 {count} 条",
        statusWaiting: "状态：等待中",
        statusGenerated: "状态：已生成 {current}/{total}",
        statusPrompted: "状态：已规划 {count}",
        statusQueueing: "状态：等待执行 {count}",
        statusRendering: "状态：生成中 {count}",
        galleryTitle: "主图/轮播图预览",
        detailGalleryTitle: "详情图预览",
        editorCarouselSectionTitle: "主图/轮播图生图方案",
        editorDetailSectionTitle: "详情图生图方案",
        detailReuseFromCarouselHint: "当前详情图路线为轮播拼接版：详情图会复用主图/轮播图结果，不支持在这里独立生图。",
        detailSectionIntroAdvantagesTitle: "第一部分：产品介绍与核心优势",
        detailSectionIntroAdvantagesGoal: "先用一张总览页建立产品身份，并集中表达 2-4 个最强卖点。",
        detailSectionUsageEvidenceTitle: "第二部分：使用场景与功能证据",
        detailSectionUsageEvidenceGoal: "进入真实使用场景或结构细节，用可见证据支撑主要卖点。",
        detailSectionSpecsGuaranteeTitle: "第三部分：规格参数与保障信息",
        detailSectionSpecsGuaranteeGoal: "最后收口规格、颜色/包装、材质与保障，补齐决策信息。",
        detailSectionFallbackTitle: "第 {order} 部分",
        planSectionLabel: "详情部分",
        planGoalLabel: "编排目标",
        generated: "已生成",
        openImage: "查看大图",
        selectingReference: "正在筛选参考图...",
        savePrompt: "保存提示词",
        savePromptSaving: "保存中...",
        generateImage: "生成图片",
        generating: "生成中...",
        regenerate: "重新生成",
        regenerating: "重生成中...",
        regenerateModalTitle: "重新生成图片方案 #{index}",
        regenerateModalDesc: "请选择使用原始提示词，或在原始提示词基础上追加新的提示词约束。",
        regenerateModeOriginal: "使用原始提示词",
        regenerateModeAppend: "追加提示词约束",
        regenerateBasePromptLabel: "完整提示词预览",
        regenerateBasePromptEmpty: "暂无原始提示词",
        regeneratePromptPreviewLoading: "正在加载完整提示词预览...",
        regeneratePromptPreviewEmpty: "完整提示词为空，已回退展示基础提示词。",
        regenerateConstraintLabel: "新增约束",
        regenerateConstraintPlaceholder: "请输入本次重生图希望额外满足的约束，例如构图、背景、卖点表达等。",
        regenerateConstraintRequired: "请选择追加约束模式后，需要填写约束内容才能确认。",
        regenerateModalCancel: "取消",
        regenerateModalConfirm: "确认重生图",
        pendingCandidateLabel: "新图待确认",
        pendingCandidateAt: "生成时间",
        pendingCandidateExists: "该方案已有待确认新图，请先选择保留旧图或使用新图。",
        compareOpen: "对比",
        compareUseNew: "使用新图",
        compareKeepOld: "保留旧图",
        compareKeepOldTitle: "保留当前图片",
        compareKeepOldConfirm: "确认保留方案 #{index} 的旧图并丢弃新候选图吗？",
        compareOld: "当前图片",
        compareNew: "新候选图",
        compareModeSlider: "滑块对比",
        compareModeSideBySide: "并排对比",
        compareModalTitle: "图片方案 #{index} 对比",
        compareModalDesc: "请对比新旧两张图，并选择最终保留版本。",
        promptSlotLabel: "槽位 {slot}：",
        promptGenerating: "生成中...",
        promptWaiting: "等待上下文...",
        deleteImage: "删除图片",
        deleteConfirmTitle: "删除已生成图片",
        deleteConfirmMessage: "确定删除图片方案 #{index} 的已生成图片吗？可稍后重新生成。",
        deleteConfirm: "删除",
        costHint: "预计扣费：{count} 张 / {credits} 积分",
        planBadge: "{index} {type}",
        planDefaultType: "主图",
        planTypeTitle: "{type} 方案",
        planTitle: "图片方案 {index}",
        planAspect: "比例 {ratio}",
        planSubtitle: "图片生成方案",
        historyTitle: "历史版本",
        historyEmpty: "暂无历史记录",
        historyAccepted: "已采纳",
        historyRejected: "已拒绝",
        historyViewImage: "查看",
        historyRestore: "切换使用",
        historyRestored: "已切换到该版本",
        historyReplaced: "已替换",
        historyCompare: "对比",
        historyAt: "{time}",
        stylePreset: {
          dramatic: "戏剧感",
          minimal: "极简",
          lifestyle: "生活方式",
          tech: "科技感",
          warm: "暖调",
          industrial: "工业风",
          nature: "自然风",
          luxury: "高奢"
        }
      },
      listing: {
        title: "Listing 文案",
        writing: "生成中...",
        keywordInput: "SEO 关键词输入",
        keywordEmpty: "暂无关键词提示。",
        emptyTitle: "Listing 待生成",
        emptyDesc: "运行 Listing 阶段后将输出文案结果。",
        fieldTitle: "标题",
        shortDesc: "短描述",
        longDesc: "长描述",
        bullets: "要点",
        emptyBullets: "暂无要点。",
        faq: "FAQ",
        emptyFaq: "暂无 FAQ。"
      },
      normalized: {
        title: "通用结构",
        platformUnknown: "平台",
        summary: "结构概览",
        platform: "平台",
        market: "市场",
        language: "语言",
        keywordCount: "关键词",
        imageCount: "图片",
        skuCount: "SKU",
        factCount: "事实",
        payload: "结构输出",
        emptyPayload: "暂无通用结构输出。",
        emptyTitle: "通用结构待生成",
        emptyDesc: "请先运行通用结构阶段。"
      },
      adapter: {
        title: "平台适配",
        rerun: "重新执行平台适配",
        platformUnknown: "平台",
        summary: "适配概览",
        platform: "平台",
        format: "格式",
        fieldCount: "字段数",
        category: "类目",
        typeId: "类型 ID",
        price: "价格 (CNY)",
        images: "图片",
        attributesCount: "属性数",
        richContent: "富文本",
        richContentMissingHint: "富文本缺失，建议重新执行平台适配生成。",
        missingFields: "缺失字段",
        missingFieldsEmpty: "暂无缺失字段",
        payload: "适配输出",
        emptyPayload: "暂无适配输出。",
        fieldMap: "字段映射",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "请先运行平台适配阶段。",
        mappingField: "字段",
        mappingSource: "通用结构来源",
        mappingStatus: "状态",
        mappingSuggestion: "建议",
        mappingStatusMapped: "已映射",
        mappingStatusInferred: "可推断",
        mappingStatusMissing: "缺失",
        mappingSuggestUseSource: "可从通用结构填充",
        mappingSuggestInfer: "需要推断或补全",
        mappingSuggestCategory: "需要类目匹配",
        mappingPlatformHint: "当前为通用结构输出，请运行 Ozon 适配后查看字段映射。",
        schemaTitle: "上传参数",
        schemaFetchedAt: "参数结构拉取时间 {time}",
        schemaEmpty: "暂无参数数据。",
        schemaField: "字段",
        schemaFieldId: "ID",
        schemaRequired: "必填",
        schemaType: "类型",
        schemaValue: "值",
        schemaDictionary: "字典值（{count}）",
        schemaStatus: "状态",
        schemaStatusFilled: "已填写",
        schemaStatusMissing: "缺失",
        schemaRequiredYes: "必填",
        schemaRequiredNo: "可选",
        overviewTitle: "发布就绪概览",
        pendingTitle: "待处理项",
        pendingDesc: "请先补齐缺失字段，避免发布被平台拒绝。",
        viewMissingAction: "查看缺失项",
        blockingTitle: "必填缺失项",
        blockingDesc: "发布前仍有 {count} 个必填属性需要补齐",
        coreTitle: "核心电商属性",
        coreDesc: "影响转化与合规的关键属性共 {count} 项",
        contentTitle: "内容属性",
        contentDesc: "描述 / 标签 / 富文本共 {count} 项",
        optionalTitle: "其他可选属性",
        optionalDesc: "筛选后共 {count} 项",
        groupOverflowHint: "另有 +{count} 项已隐藏",
        keyAttributesTitle: "关键属性",
        keyAttributesDesc: "按优先级展示 {count} 项",
        showAllAttributes: "查看全部属性",
        hideAllAttributes: "收起全部属性",
        allAttributesTitle: "全部属性",
        allAttributesDesc: "筛选后共 {count} 项",
        searchPlaceholder: "按字段名 / ID / 值搜索",
        filterAll: "全部",
        filterMissing: "缺失",
        filterRequired: "必填",
        filterFilled: "已填",
        expandValueAction: "展开",
        collapseValueAction: "收起",
        emptyFilterResult: "当前筛选条件下暂无属性。",
        pageIndicator: "第 {current}/{total} 页",
        pagePrev: "上一页",
        pageNext: "下一页",
        fieldIdFallback: "属性 ID {id}",
        payloadAttributeCount: "提交属性 {count} 项",
        mappingHiddenWithoutSchema: "当前暂无可读字段名，已临时显示字段 ID。"
      },
      export: {
        title: "导出交付",
        exporting: "导出中...",
        open: "导出详情",
        reexport: "重新导出",
        platform: "平台",
        category: "类目",
        columns: "字段数",
        rows: "行数",
        templateKey: "模板标识",
        format: "格式",
        version: "模板版本",
        validation: "校验",
        validationPass: "通过",
        validationPending: "待校验 / 有问题",
        fileName: "文件名",
        delivery: "交付",
        ready: "下载就绪。",
        pending: "等待导出数据。",
        download: "下载",
        waiting: "暂无下载链接。",
        validationErrors: "校验问题"
      },
      preview: {
        title: "生成结果预览",
        back: "返回",
        retry: "重试加载",
        images: "预览图片",
        adapter: "平台适配输出",
        upload: "上传参数",
        export: "导出内容",
        noImages: "暂无预览图片",
        imageCountSuffix: "张",
        mainImageAlt: "主图预览",
        listingTitle: "标题",
        shortDesc: "短描述",
        bulletPoints: "卖点",
        longDesc: "长描述",
        faq: "FAQ",
        attributes: "商品属性",
        highlights: "核心卖点",
        seoKeywords: "SEO 关键词",
        metricsTitle: "内容概览",
        metricImages: "图片",
        metricSkus: "SKU",
        metricAttributes: "属性",
        metricSections: "模块",
        metricRawText: "原始文本",
        priceSummary: "商品要点",
        price: "价格 (CNY)",
        purchaseCostCny: "进货成本 (CNY)",
        skuCount: "SKU 数量",
        weight: "重量 (g)",
        size: "尺寸 (mm)",
        empty: "暂无数据",
        statusReady: "可审查",
        statusUploaded: "已上传，可更新",
        reviewBtn: "审查更改",
        publishBtn: "一键发布",
        updateBtn: "更新到 Ozon",
        updateUnavailable: "当前任务尚未上传成功，暂不可更新。",
        fixRequiredTitle: "来自 Ozon 的整改提示",
        fixRequiredDesc: "你可以先在本页继续修改字段，也可以直接跳转 Ozon 后台完成整改。",
        fixRequiredFallback: "商品已创建，但仍存在待整改项。请整改后刷新状态。",
        fixRequiredOpenOzon: "打开 Ozon 整改页",
        fixRequiredOpenOzonDisabled: "暂时无法生成 Ozon 整改链接。",
        updateTriggered: "更新请求已提交。",
        saveDraft: "保存草稿",
        savingDraft: "保存中...",
        draftSaved: "草稿已保存。",
        draftNoChanges: "没有需要保存的改动。",
        draftSaveFailed: "草稿保存失败。",
        draftUnsaved: "草稿有改动，未保存",
        openPricingEngine: "打开定价引擎",
        setPrimaryImage: "设为主图",
        primaryImageCurrent: "当前主图",
        imageOrderHint: "拖拽缩略图可调整上传顺序，主图/轮播与详情图分组隔离，不能跨组混排。",
        imageOrderCarouselSectionTitle: "主图 / 轮播图",
        imageOrderDetailSectionTitle: "详情图",
        imageOrderSectionCount: "{count} 张",
        imageOrderCarouselEmpty: "暂无主图/轮播图",
        imageOrderTitle: "图片顺序 {index}",
        imageOrderCarouselBadge: "轮播",
        imageOrderDetailBadge: "详情",
        imageOrderCrossGroupBlocked: "仅支持在同一分组内拖拽排序（轮播/详情不可跨组）。",
        detailRouteHint: "当前预览中的富文本与导出结果，都会沿用这条详情图路线。",
        readonlyTitle: "只读字段说明",
        tagsHint: "标签使用空格或逗号分隔",
        oldPrice: "原价 (CNY)",
        invalidPrice: "售价必须是大于 0 的数字。",
        invalidOldPrice: "原价必须是大于 0 的数字。",
        invalidPriceRelation: "原价不能小于售价。",
        invalidWidth: "宽度必须是大于 0 的数字。",
        invalidHeight: "高度必须是大于 0 的数字。",
        invalidDepth: "深度必须是大于 0 的数字。",
        invalidWeight: "重量必须是大于 0 的数字。",
        invalidImages: "至少保留一张图片。",
        invalidPrimaryImage: "主图必须来自当前图片列表。",
        ozonOnly: "当前仅支持 Ozon 平台预览。",
        ozonPayloadMissing: "未找到可用的 Ozon 预览数据，请先补齐平台适配与商品字段。",
        ozonListing: "Ozon 商品信息",
        targetStoreLabel: "要发布的店铺",
        targetStoreBinding: "绑定中...",
        targetStoreMissing: "请先选择要发布的店铺，再生成图片或发布商品。",
        targetStoreMissingShort: "未选择店铺",
        targetStoreBindFailed: "绑定目标店铺失败。",
        ozonOfferId: "Ozon SKU",
        ozonCurrency: "币种",
        ozonSpecs: "规格信息 (mm)",
        selectedSkuTitle: "已选产品快照",
        selectedSkuLocked: "已锁定",
        selectedSkuId: "SKU 编号",
        selectedSkuPrice: "货源价格",
        selectedSkuSource: "选择来源",
        selectedSkuSourceUser: "手动选择",
        selectedSkuSourceAuto: "系统自动",
        selectedSkuNoDetail: "暂无可展示的规格快照",
        selectedSkuDetailShape: "类型",
        selectedSkuDetailDimensions: "尺寸",
        selectedSkuDetailPackaging: "包装",
        selectedSkuDetailSize: "规格",
        selectedSkuDetailColor: "颜色",
        selectedSkuDetailStock: "库存",
        dimensionReviewTitle: "尺寸重量复核",
        dimensionReviewManualHint: "建议人工复核",
        dimensionReviewOriginal: "原始值",
        dimensionReviewResolved: "当前采用值",
        packagingReserveBadge: "估算预留值",
        packagingReserveDimensions: "当前 {current}；原始 {original}；包装/物流预留上浮 {percent}。",
        packagingReserveWeight: "当前 {current}；原始 {original}；包装/物流预留上浮 {increase} / {percent}。",
        packagingReserveLength: "长",
        packagingReserveWidth: "宽",
        packagingReserveHeight: "高",
        dimensionReviewStatus: {
          passed: "正常放行",
          corrected: "AI 联网修正",
          needs_manual_review: "需人工复核",
          failed: "复核失败"
        },
        ozonDescription: "Ozon 简介",
        ozonTags: "Ozon 标签",
        ozonAttributes: "Ozon 属性",
        pricingFinalized: "定价已锁定",
        pricingEngineTitle: "定价引擎逻辑",
        pricingPurchaseCost: "进货成本",
        pricingTotalCost: "前期总投入",
        pricingPostSaleDeductions: "后扣费用合计",
        pricingProfitPerOrder: "每单利润",
        pricingRoi: "投入回报率",
        pricingProfitability: "盈利能力",
        pricingMargin: "利润率",
        pricingFinalPrice: "建议上架价 (CNY)",
        pricingBuyerPriceLabel: "客户实付价",
        pricingSnapshotOutdatedHint: "当前上架价已调整，利润与成本指标仍来自最近一次定价快照。建议打开定价引擎重新计算。",
        pricingLogisticsWarningTitle: "物流匹配提醒",
        pricingLogisticsWarningHint: "当前定价未完全匹配可用物流渠道（物流商：{provider}），已降级为提醒。你可以继续发布，但建议先复核物流设置。",
        pricingViewBreakdown: "展开明细",
        pricingHideBreakdown: "收起明细",
        pricingDiscountExplainTitle: "固定40%折扣口径",
        pricingDiscountExplainLine: "上架价会按折扣策略上浮。折扣 {discount}，实付系数 {pay_factor}。在 Ozon 后台填写 {listing_price}，买家实付约为 {target_price}。",
        pricingBreakdownMissing: "暂无成本拆解数据",
        pricingBreakdown: {
          purchaseCost: "进货成本",
          upfrontInvestment: "前期总投入",
          postSaleLogistics: "后扣国际物流",
          postSalePlatform: "后扣平台费用",
          postSaleOther: "后扣其他费用"
        }
      },
      gallery: {
        main: "主图",
        empty: "暂无生成图片",
        slot: "空位 {index}",
        diffusing: "生成中...",
        loading: "加载中...",
        mainPlaceholder: "主图位置",
        emptySlot: "图片位 {index}",
        emptyHint: "等待生成",
        waiting: "生成中..."
      },
      marketplace: {
        emptyRich: "暂无富文本素材",
        widgets: "模块 {count}",
        images: "图片 {count}",
        theme: "主题 {theme}",
        emptyMapping: "暂无属性映射"
      },
      richContent: {
        title: "富文本结构",
        summary: "{count} 个模块",
        empty: "暂无富文本模块",
        emptyTitle: "富文本待生成",
        emptyDesc: "富文本阶段完成后，模块与图片将在此展示。",
        emptyPreview: "暂无富文本预览",
        images: "富文本图片",
        emptyImages: "暂无富文本图片",
        version: "版本",
        generatedAt: "生成时间",
        blocks: "{count} 个区块",
        rows: "{count} 行",
        columns: "{count} 列",
        untitled: "未命名模块",
        noDescription: "暂无文字内容",
        moreWidgets: "更多模块：{count}",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "完成属性填充后会自动生成映射。",
        detailHint: "结构已就绪",
        viewDetail: "查看详情",
        collapse: "收起",
        detailTitle: "模块详情",
        detailDesc: "来自富文本代理的结构化输出。",
        jsonTitle: "模块 JSON",
        blocksTitle: "区块预览",
        emptyBlocks: "暂无区块内容",
        galleryTitle: "富文本图片瀑布流",
        emptyGallery: "暂无富文本图片",
        emptyGalleryDesc: "生成富文本后将在此展示图片流。",
        widget: {
          showcase: "图文展示",
          text: "文本模块",
          table: "表格模块",
          video: "视频模块",
          list: "列表模块",
          unknown: "模块"
        }
      },
      video: {
        empty: "视频生成尚未启用。",
        hint: "开启视频阶段后将生成动效素材。",
        recording: "录制中..."
      },
      status: {
        complete: "完成",
        thinking: "思考中",
        processing: "处理中",
        backgroundProcessing: "后台处理中",
        error: "错误",
        waiting: "等待中"
      },
      agent: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词代理",
        listing: "Listing 文案",
        visual: "图片生成",
        images: "图片生成",
        selector: "参考图筛选",
        normalized: "通用结构",
        category: "类目匹配",
        adapter: "平台适配",
        export: "导出交付",
        error: "阶段失败，请查看日志。"
      },
      agentLogs: {
        title: "Agent 运行日志",
        subtitle: "代理实时控制台",
        copyDiagnostics: "复制诊断信息",
        copied: "已复制",
        downloadLogs: "下载日志",
        chainTitle: "处理日志流",
        streaming: "实时更新",
        emptyTitle: "暂无日志",
        emptyDesc: "执行阶段后会在此显示代理日志。",
        logCount: "{count} 条日志",
        lastUpdated: "更新于 {time}",
        duration: "耗时 {duration}",
        expandLog: "展开",
        collapseLog: "收起",
        status: {
          running: "运行中",
          completed: "已完成",
          failed: "失败",
          idle: "空闲"
        },
        event: {
          start: "启动",
          thinking: "思考",
          result: "完成",
          error: "错误",
          cacheHit: "缓存",
          skip: "跳过"
        }
      }
    },

    pricingEngine: {
      title: "一键大卖动态定价引擎",
      subtitle: "多平台利润计算器，内置各平台费率与费用逻辑。",
      returnToPreview: "返回预览继续编辑",
      calcMode: "计算模式",
      manualEntry: "手动输入",
      myListings: "我的列表",
      destPlatform: "目标平台",
      globalChannels: "{count} 个主流平台可用",
      ready: "就绪",
      soon: "即将推出",
      listingsOzonOnly: "当前仅支持 Ozon 列表同步。",
      inputMode: "输入模式",
      compactMode: "简化",
      fullMode: "完整",
      singlePlatformHint: "大多数卖家专注单一平台。",
      rateSource: "实时汇率来源",
      rateLoading: "更新中...",
      calculationLoadingTitle: "正在重新计算定价...",
      calculationLoadingDesc: "系统正在更新 Ozon 费率与利润模拟，请稍候后再重试。",
      calculationFailed: "定价计算失败",
      retryCalculation: "重试计算",
      rateUpdated: "更新于 {date}",
      rateError: "汇率异常",
      rateErrorDesc: "获取最新汇率失败，请稍后重试。",
      networkErrorDesc: "网络连接失败，请重试。",
      requestTimeoutDesc: "处理时间有点久，请稍后重试。",
      autoRateOn: "自动汇率",
      autoRateOff: "手动输入",
      refreshRate: "刷新汇率",
      autoRateHint: "已启用自动汇率（Frankfurter API）。",
      manualRateHint: "可手动覆盖汇率。",
      tiktokRegion: "销售地区",
      regionUs: "美国",
      regionUk: "英国",
      regionSea: "东南亚",
      tiktokRegionHint: "切换地区会自动更新佣金/交易费率，可按实际合同调整。",
      selectProduct: "从工作区选择产品",
      viewDetails: "查看详情",
      dataSynced: "数据已同步",
      pricingStrategy: "定价策略",
      targetMargin: "目标利润率 (%)",
      targetProfit: "目标利润 (¥)",
      tieredProfit: "分段利润",
      tieredProfitHint: "按采购成本区间配置利润目标，系统会取固定利润和成本×利润率中的较高值。",
      currentTier: "当前命中",
      tierFixedProfit: "固定利润",
      tierProfitRate: "利润率",
      tieredTargetHint: "当前区间目标利润：{value}",
      tierLabel: "区间",
      targetValue: "目标值",
      quickAdjustTag: "实时快调",
      quickAdjustTitle: "在这里直接调整定价策略",
      quickAdjustHint: "可拖动滑杆或直接输入",
      quickAdjustClickHint: "点击切换到利润率编辑",
      autoCalculated: "基于当前输入自动计算",
      breakEven: "盈亏平衡",
      suggestedPrice: "建议上架价格",
      ozonListingPrice: "建议后台上架价",
      ozonListingPriceShort: "后台上架价",
      ozonTargetPriceLabel: "客户实付价",
      rate: "汇率：1 CNY = {rate} {currency}（输入）",
      breakEvenPrice: "盈亏平衡价格：{price}",
      optimizedMsg: "优化为 {value}% 利润率",
      optimizedProfit: "优化为 {value} 利润",
      netProfit: "净利润",
      profitMargin: "利润率",
      targetMet: "目标达成",
      targetNotReached: "目标未达到（已应用上限）",
      roi: "投资回报率",
      highMargin: "高利润",
      updateListing: "更新列表价格",
      syncDesc: "立即将此价格同步到您工作区的 Ozon 列表。",
      syncSuccess: "价格同步完成",
      syncSuccessDesc: "已将 {price} 同步至 {platform} 列表。",
      feeBreakdown: "平台费用明细",
      catLogic: "类目逻辑已应用",
      commission: "市场佣金",
      lastMile: "最后一公里配送",
      handling: "处理与仓储",
      totalOpCost: "总运营成本",
      costBreakdown: "成本拆解",
      coreCalculationFailed: "定价计算失败，请检查输入后重试。",
      costBreakdownTotalHintOzon: "总成本已包含前期投入、国际物流、平台后扣、提现、广告与退货预留。",
      exportReport: "导出报告",
      shareImage: "生成分享图",
      shareReady: "分享图已生成",
      shareReadyDesc: "{platform} 分享图已保存到本地。",
      shareError: "分享图生成失败",
      shareErrorDesc: "无法渲染分享图，请稍后重试。",
      saveOzonConfig: "保存 Ozon 策略",
      savingConfig: "保存中...",
      applyOzonConfig: "应用到 Ozon 流程",
      saveAndApplyOzonConfig: "保存并应用（同步预览）",
      applyingConfig: "应用中...",
      saveConfigSuccess: "Ozon 策略已保存",
      saveConfigSuccessDesc: "已成功保存你的 Ozon 自定义定价策略。",
      saveConfig: "保存配置",
      saveConfigFailed: "保存失败",
      saveConfigFailedDesc: "保存 Ozon 定价策略失败，请稍后重试。",
      applyConfig: "应用配置",
      heroBadge: "定价指挥舱",
      platformMatrixHint: "点击图表可直接切换平台，支持利润率、ROI 与平台费率视图。",
      strategyWorkbenchTitle: "策略设计台",
      strategyWorkbenchDesc: "简易模式专注关键输入，完整模式支持全部参数和 Ozon 策略闭环。",
      delete: "删除",
      minShort: "最小值",
      maxShort: "最大值",
      noLimitHint: "0=无上限",
      targetProgress: "目标达成进度",
      summaryBadge: "策略驾驶舱",
      profitCurveTitle: "利润敏感曲线",
      costWaterfallTitle: "成本瀑布图",
      profitSignals: {
        strong: {
          label: "盈利信号强",
          desc: "可维持当前策略并加大投放"
        },
        stable: {
          label: "盈利稳定",
          desc: "建议继续观察类目费率波动"
        },
        pressure: {
          label: "盈利承压",
          desc: "建议先调高目标利润率后再投放"
        }
      },
      executionSignals: {
        localOnly: "非 Ozon 场景：仅本地策略模拟",
        loginRequired: "未登录：无法保存并应用 Ozon 配置",
        contextReady: "已接入商品上下文：支持闭环回写预览",
        contextMissing: "未绑定商品上下文：可先在快速选品中选择商品"
      },
      ozonPanel: {
        core: "核心策略",
        cost: "履约成本",
        risk: "风险费率"
      },
      ozonPanelHint: "高密度参数已拆分为分组面板，避免一次性暴露全部字段，降低配置复杂度。",
      applyConfigSuccess: "策略已应用",
      applyConfigSuccessDesc: "后续 Ozon 生成将使用当前策略。",
      applyConfigTriggeredDesc: "已应用策略，并开始回刷当前商品历史定价结果。",
      applyConfigNoCollectionDesc: "已应用到后续生成；当前页面无商品上下文，请前往 Studio 回刷。",
      applyConfigSnapshotSavedDesc: "已基于后端权威引擎重算并保存当前价格快照。",
      applyConfigConflictDesc: "检测到已锁定价格，与当前策略冲突。",
      applyConfigConflictConfirm: "是否用当前策略覆盖已锁定价格？",
      applyConfigAutoSyncSuccessDesc: "策略已应用，并已同步到 Studio 预览草稿（含固定40%折扣口径）。",
      applyConfigAutoSyncFailedDesc: "策略已应用，但同步到 Studio 预览失败。请点击“重试同步预览”。",
      nextStepTitle: "下一步操作",
      nextStepSubtitle: "你可以立即应用当前定价、导出方案快照，或继续进入发布流程。",
      nextStepApplyCurrent: "应用到当前商品",
      nextStepApplyCurrentDesc: "将当前定价策略推送到已选中的商品上下文。",
      nextStepExportPlan: "导出方案",
      nextStepExportPlanDesc: "把当前输入参数与计算结果导出为 JSON 文件。",
      nextStepGoPublish: "进入发布",
      nextStepGoPublishDesc: "基于当前商品上下文，继续后续发布流程。",
      nextStepDisabledCalculating: "系统正在重新计算定价，请稍候再操作。",
      nextStepDisabledInvalidPrice: "当前建议价无效，请先调整输入参数。",
      nextStepApplyDisabledPlatform: "“应用到当前商品”仅支持 Ozon 平台。",
      nextStepApplyDisabledAuth: "请先登录后再应用到当前商品。",
      nextStepApplyDisabledContext: "请先选择商品上下文后再执行应用。",
      nextStepApplyDisabledApplying: "正在应用策略，请稍候。",
      nextStepApplyDisabledSaving: "正在保存策略，请稍候。",
      nextStepGoPublishDisabledAuth: "请先登录后再进入发布流程。",
      nextStepGoPublishDisabledContext: "请先选择商品上下文后再进入发布。",
      nextStepExportSuccess: "定价方案已导出。",
      nextStepExportFailed: "导出定价方案失败，请重试。",
      applyConfigFailed: "应用失败",
      applyConfigFailedDesc: "应用 Ozon 策略失败，请稍后重试。",
      loadConfigFailed: "加载失败",
      loadConfigFailedDesc: "加载已保存的 Ozon 策略失败。",
      loginRequiredTitle: "请先登录",
      loginRequiredDesc: "登录后才可保存并应用 Ozon 策略。",
      loadingOzonConfig: "正在加载 Ozon 策略...",
      updatedAt: "最近更新：{date}",
      ozonOnlyHint: "仅 Ozon 平台支持策略应用，其他平台即将支持。",
      ozonFlowStrategyTitle: "Ozon 流程策略",
      ozonFlowStrategyDesc: "一键应用当前策略到 Ozon 流程，并自动同步 Studio 预览。",
      collectionContextTitle: "当前商品上下文",
      contextLoading: "正在加载当前商品上下文...",
      contextEmpty: "未检测到商品上下文，可在下方快速选品。",
      contextPriceHint: "当前建议 CNY 价格：{price}",
      contextLoadFailedDesc: "加载当前商品定价上下文失败。",
      dimensionReviewTitle: "尺寸重量复核",
      dimensionReviewManualHint: "建议人工复核",
      dimensionReviewOriginal: "原始值",
      dimensionReviewResolved: "当前采用值",
      dimensionReviewStatus: {
        passed: "正常放行",
        corrected: "AI 联网修正",
        needs_manual_review: "需人工复核",
        failed: "复核失败"
      },
      collectionUnnamed: "未命名商品",
      quickSourceTitle: "快速选品",
      sourceAssets: "资产库",
      sourceTasks: "任务列表",
      sourceSearchPlaceholder: "支持 SKU ID / 1688 ID / 标题 / 链接搜索",
      sourceSearchHint: "支持 SKU、1688 编号、商品编号、任务编号、标题与链接模糊搜索。",
      sourcesLoading: "正在加载商品列表...",
      sourcesEmpty: "未找到匹配商品。",
      sourcesLoadFailedDesc: "加载快速选品列表失败。",
      sourceUploaded: "已上传",
      sourcePending: "未上传",
      inputConfirm: "确认输入",
      inputConfirmShort: "确认",
      sourceNotReadyTitle: "暂不可用",
      sourceNotReadyDesc: "该任务尚未生成商品集合，暂不能用于定价。",
      syncToPreview: "同步价格到预览",
      retrySyncToPreview: "重试同步预览",
      syncingPreview: "同步中...",
      syncPreviewHint: "应用策略后会自动同步到 Studio 预览草稿：上架价 = 客户实付价 ÷ 0.6（固定40%折扣）。",
      syncPreviewMissingCollection: "缺少商品上下文，请从 Studio 预览进入或先在上方选择商品。",
      syncPreviewInvalidRate: "当前汇率无效，无法将 RUB 正确换算为 CNY。",
      syncPreviewInvalidPrice: "建议价无效，无法同步。",
      syncPreviewFailed: "同步失败",
      syncPreviewFailedDesc: "无法将最新定价细节同步到 Studio 预览。",
      syncPreviewSuccess: "已同步到预览",
      syncPreviewSuccessDesc: "最新定价明细已写入 Studio 预览草稿。",
      logisticsWarningNonBlocking: "当前物流暂未匹配，仍可继续同步和保存。建议先修正尺寸/重量或切换物流商后重算。",
      logisticsWarningBackendFlag: "后端已标记该结果存在物流风险，可继续操作，但建议先处理物流匹配。",
      ozonMinProfitSyncHint: "当前 Ozon 最低利润：¥{value}",
      ozonTieredProfitSyncHint: "当前 Ozon 区间目标：¥{value}（{range}）",
      ozonStrategyTitle: "Ozon 生成策略",
      ozonStrategyDesc: "以下配置会持久保存，并在 Ozon 生成流程定价阶段真实生效。",
      ozonProfitRate: "目标利润率",
      ozonMinProfit: "最低利润",
      ozonPromotionReserve: "促销预留",
      ozonOperationMode: "运营模式",
      ozonAdvertisingStrategy: "广告策略",
      ozonAdvertisingNone: "不投放",
      ozonAdvertisingCpoCpc: "CPO/CPC",
      ozonAdvertisingAggressive: "激进投放",
      ozonCrossBorder: "卖家类型",
      ozonCrossBorderOn: "跨境卖家",
      ozonCrossBorderOff: "本土卖家",
      ozonDefaultCategory: "默认品类代码",
      ozonExchangeRate: "自定义汇率（可选）",
      ozonDomesticShipping: "国内头程",
      ozonLabelingFee: "贴单费",
      ozonReturnRate: "退货率",
      ozonWithdrawalRate: "提现费率",
      ozonOrderProcessingRate: "订单处理费率",
      ozonAcquiringRate: "收单费率",
      ozonAgentRate: "Ozon 代理费率",
      ozonPartnerDeliveryRate: "Partner Delivery 基础费率",
      logisticsProvider: "物流商",
      logisticsProviderMode: "物流商选择",
      logisticsProviderModeAuto: "自动（推荐）",
      logisticsProviderModeManual: "手动",
      logisticsProviderUni: "UNI（旧版）",
      logisticsProviderUral: "Ural（2026-03 报价）",
      autoModeLabel: "自动",
      logisticsManagedByStore: "物流渠道由店铺设置统一管理。",
      logisticsManagedByStoreDesc: "当前定价使用店铺级物流渠道配置。店铺 ID：{storeId}",
      logisticsManageInStoreSettings: "前往店铺设置",
      logisticsSyncChannelsNow: "立即同步渠道",
      logisticsSyncingChannels: "渠道同步中...",
      logisticsSyncChannelsSuccess: "物流渠道同步成功。",
      logisticsSyncChannelsFailed: "物流渠道同步失败。",
      ozonDiscountExplainTitle: "固定40%折扣口径",
      ozonDiscountExplainLine: "先计算客户实付价，再反推后台上架价。折扣 {discount}，实付系数 {pay_factor}。客户实付 {target_rub}，后台应填写 {listing_rub}。",
      ozonDiscountExplainFormula: "公式：客户实付 = 上架价 × 0.6，因此上架价 = {target_cny} ÷ 0.6 = {listing_cny}。",
      saveScenario: "保存方案",
      prodMetadata: "产品元数据（锁定）",
      prodCategory: "产品类目",
      purchaseCost: "采购成本 (CNY)",
      weight: "重量 (g)",
      dimensions: "尺寸 (长*宽*高 mm)",
      dimensionLengthMm: "长（mm）",
      dimensionWidthMm: "宽（mm）",
      dimensionHeightMm: "高（mm）",
      dimensionDepthMm: "深（mm）",
      logistics: "物流",
      air: "空运",
      sea: "海运",
      saveWorkspace: "将此计算保存到您的工作区",
      saveWorkspaceDesc: "创建免费账户，直接同步到 Ozon 并跟踪您的利润历史。",
      createAccount: "创建免费账户",
      basicCosts: "基础成本",
      costCurrency: "成本币种：CNY → {currency}",
      purchaseCostLabel: "采购成本",
      shippingCostLabel: "头程运费",
      shippingEst: "预计国际物流（估算）",
      estimatedShipping: "预计运费",
      estimatedShippingHint: "基于当前重量、尺寸和配送方式估算。",
      packagingCostLabel: "包装/处理成本",
      otherCostLabel: "其他成本",
      estimatedTag: "估算",
      shippingCharged: "向买家收取的运费",
      exchangeRateLabel: "汇率 (1 CNY → {currency})",
      inputGuardrailInvalid: "请输入有效数字（范围 {range}），系统已自动修正为 {value}。",
      inputGuardrailAdjusted: "可接受范围：{range}，系统已自动修正为 {value}。",
      inputRangeBetween: "{min} - {max}",
      inputRangeMinimum: "≥ {min}",
      platformFees: "平台费用输入",
      marginCapHint: "当前可达利润率上限：{value}%",
      categoryLabel: "类目/细分",
      referralFeeLabel: "平台佣金",
      referralRateLabel: "平台佣金/费率",
      ozonCommissionTierLow: "低价档",
      ozonCommissionTierMid: "中价档",
      ozonCommissionTierHigh: "高价档",
      ozonCommissionTierActive: "当前命中",
      ozonCommissionTierRangeLow: "≤ {upper} RUB",
      ozonCommissionTierRangeMid: "{lower} - {upper} RUB",
      ozonCommissionTierRangeHigh: "> {lower} RUB",
      ozonCommissionTierHint: "佣金按目标成交价分档：≤{low} RUB 用低价档，≤{mid} RUB 用中价档，>{mid} RUB 用高价档。",
      ozonCommissionCurrentHint: "当前目标成交价 {price} 命中{tier}，生效佣金 {rate}%（{mode_adjust}）。",
      ozonCommissionModeAdjustFbo: "FBO 模式额外调整 {delta}%",
      ozonCommissionModeAdjustDefault: "默认费率不参与履约模式调整",
      ozonCommissionModeAdjustNone: "无履约模式额外调整",
      ozonEffectiveCommissionLabel: "当前生效佣金（分档）",
      ozonEffectiveCommissionFeeLabel: "平台佣金（当前生效：{tier}，{rate}%）",
      ozonDefaultCommissionFeeLabel: "平台佣金（默认值：{rate}%，未匹配类目）",
      ozonUnmatchedCategoryTag: "默认值15%（未匹配类目）",
      ozonUnmatchedCategoryOptionRate: "默认 {rate}%",
      ozonUnmatchedCategoryHint: "当前未检索到有效类目，按默认佣金 {rate}% 计算（不属于任何类目，不参与分档）。",
      commissionRate: "佣金费率",
      referralMinFeeLabel: "最低佣金",
      sellingPlan: "销售计划",
      sellingPlanIndividual: "个人",
      sellingPlanProfessional: "专业",
      sellingPlanFee: "按件收费",
      closingFee: "成交费",
      closingFeeLabel: "成交费",
      fulfillmentMode: "履约方式",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "履约费用",
      storageFee: "仓储费",
      inboundShippingFee: "入仓运费",
      fbmHandlingFee: "自发货处理费",
      platformDiscount: "平台补贴/折扣",
      tax: "税费/VAT",
      sellerDiscount: "卖家优惠",
      affiliateRate: "达人分佣费率",
      affiliatePartnerRate: "联盟分佣费率",
      adsRate: "广告投放费率",
      marketingCost: "营销成本",
      shippingType: "配送模式",
      shippingPlatform: "平台配送",
      shippingSeller: "卖家配送",
      shippingFbt: "FBT 平台履约",
      fbtFulfillmentFee: "FBT 履约费",
      fbtStorageFee: "FBT 仓储费",
      transactionFeeRate: "交易手续费率",
      transactionFeeFixed: "交易固定费",
      serviceFeeRate: "平台服务费率",
      serviceFeeFixed: "平台固定服务费",
      orderServiceFee: "订单服务费",
      platformSupportFee: "平台支持费",
      platformSupportFeeHint: "部分站点按单收取固定支持费（如马来西亚）。",
      shippingSubsidy: "运费补贴/项目成本",
      ozonPlacementFee: "Ozon 上架/服务费",
      ebayFinalValueRate: "成交费率",
      ebayPerOrderFee: "每单固定费用",
      ebayInsertionFee: "刊登费",
      ebayInternationalRate: "跨境交易费率",
      etsyListingFee: "上架费",
      etsyTransactionFee: "成交费率",
      etsyPaymentRate: "支付处理费率",
      paymentFeeRate: "支付费率",
      etsyPaymentFixed: "支付固定费",
      etsyOffsiteAdsRate: "站外广告费率",
      shippingSupportRate: "物流支持费率",
      costItemTransactionFee: "交易手续费",
      costItemServiceFee: "平台服务费",
      costItemAds: "广告费",
      costItemAffiliate: "达人分佣",
      costItemFinalValueFee: "成交费",
      costItemOrderFee: "订单固定费",
      costItemInternationalFee: "跨境交易费",
      costItemPaymentProcessing: "支付处理费",
      costItemOffsiteAds: "站外广告费",
      totalCost: "总成本",
      totalBaseCost: "基础成本合计",
      platformFeeTotal: "平台费用",
      baseCostTotal: "基础成本",
      platformInsights: "平台提示",
      marketingCosts: "营销与达人费用",
      temuProgram: "Temu 补贴/激励",
      aliexpressProgram: "AliExpress 活动费用",
      shopeeProgram: "Shopee 项目费用",
      temuHeroTitle: "Temu 低佣金模型",
      temuHeroDesc: "多数品类佣金为 0%，可单独跟踪服务费、履约费与补贴。",
      temuHeroTag1: "0% 佣金",
      temuHeroTag2: "服务费可调",
      aliexpressHeroTitle: "AliExpress 类目佣金",
      aliexpressHeroDesc: "佣金随类目变化，建议同步建模支付/服务费。",
      aliexpressHeroTag1: "5-8% 佣金",
      aliexpressHeroTag2: "无年费",
      shopeeHeroTitle: "Shopee 费用叠加",
      shopeeHeroDesc: "叠加佣金、交易费与项目费，快速估算净利。",
      shopeeHeroTag1: "多费率",
      shopeeHeroTag2: "项目可选",
      shopeeHeroTag3: "支持费",
      amazonHighlightTitle: "Amazon 费用快照",
      amazonHighlightDesc: "同时考虑佣金、销售计划与 FBA/FBM 成本，快速确定价格下限。",
      ebayHighlightTitle: "eBay 成交费提示",
      ebayHighlightDesc: "成交费通常基于商品+运费+税费，叠加每单固定费与跨境费。",
      walmartHighlightTitle: "Walmart 佣金提醒",
      walmartHighlightDesc: "佣金按类目变化，WFS 等履约成本可在此基础上补充。",
      etsyHighlightTitle: "Etsy 列表周期提示",
      etsyHighlightDesc: "上架费、成交费、支付费与站外广告可按单配置。",
      platformNotesAmazon: "包含类目佣金、按件收费（个人计划）以及 FBA/FBM 履约费用。",
      platformNotesTikTok: "包含类目佣金、交易手续费、达人分佣与可选 FBT 履约费用。",
      platformNotesOzon: "佣金与类目/履约模式相关；国际物流、提现与退货预留属于估算项。",
      platformNotesEbay: "成交费基于商品+运费+税费，另加每单固定费用。",
      platformNotesWalmart: "佣金随类目变化，履约成本未在此快速计算中展开。",
      platformNotesEtsy: "包含上架费、成交费、支付处理费及可选站外广告费。",
      platformNotesTemu: "Temu 佣金可能为 0%，但服务费与履约成本需按项目填入。",
      platformNotesAliExpress: "AliExpress 佣金按类目变化，部分地区有支付/服务费。",
      platformNotesShopee: "Shopee 通常包含佣金、交易费与可选项目/支持费。",

      // Strategy presets (new)
      selectStrategy: "选择定价策略",
      strategyHint: "根据您的业务目标选择",
      strategyVolume: "快速起量",
      strategyVolumeDesc: "低利润率 (15-20%)，快速打开市场",
      strategyBalanced: "均衡增长",
      strategyBalancedDesc: "中等利润率 (25%)，平衡销量与利润",
      strategyProfit: "利润优先",
      strategyProfitDesc: "高利润率 (35%)，最大化单笔收益",
      strategyCustom: "自定义策略",
      strategyCustomDesc: "完全手动控制所有参数",
      strategyScenarioLabel: "适用场景",
      strategyRiskLabel: "风险提示",
      strategyVolumeScenario: "适合新品冷启动或活动期，需要先快速拉起订单体量。",
      strategyVolumeRisk: "单笔利润空间较薄，广告波动和退货会更容易吞噬利润。",
      strategyBalancedScenario: "适合稳定增长阶段，兼顾转化效率与利润表现。",
      strategyBalancedRisk: "若促销折扣失控，利润率会被持续侵蚀。",
      strategyProfitScenario: "适合差异化明显、需求稳定且具备一定议价能力的商品。",
      strategyProfitRisk: "定价抬升后，若竞品跟进降价，转化率可能下降。",
      strategyCustomScenario: "适合已掌握成本结构和渠道基准的成熟运营阶段。",
      strategyCustomRisk: "手动调参依赖持续监控，目标值越界会放大计算偏差。",
      strategyImpactTitle: "策略影响项",
      strategyImpactSubtitle: "切换后建议重点关注以下变化",
      strategyVolumeImpact1: "建议价基线下探，优先提升点击率与进店流量。",
      strategyVolumeImpact2: "库存周转速度提升，但单笔净利润贡献会下降。",
      strategyVolumeImpact3: "需要严格控制投放上限，避免利润被进一步压缩。",
      strategyBalancedImpact1: "利润与转化保持在中间区间，周度表现更稳定。",
      strategyBalancedImpact2: "便于持续小步调整，不容易出现价格大幅波动。",
      strategyBalancedImpact3: "保留一定利润缓冲，可承接中等力度促销。",
      strategyProfitImpact1: "提升单笔贡献利润，优先追求净利润最大化。",
      strategyProfitImpact2: "需要内容差异化、评价质量和履约体验共同支撑。",
      strategyProfitImpact3: "更适合精细化流量投放，不建议盲目放量。",
      strategyCustomImpact1: "当前目标参数将直接主导建议价计算结果。",
      strategyCustomImpact2: "可快速配合活动档期或类目试验进行定制调优。",
      strategyCustomImpact3: "请持续校验输入范围，避免异常值影响策略判断。",
      recommended: "推荐",
      estimatedProfit: "预计利润",
      estimatedSales: "预计月销量",
      profitTarget: "利润目标",
      profitTargetHint: "可选择利润率、固定利润或分段利润。",
      enabled: "启用",

      // Slider
      adjusting: "调整中...",

      // Actions
      saving: "保存中...",
      applying: "应用中...",

      // Cost breakdown
      clickForDetails: "点击查看详情",

      // Logistics
      logisticsHint: "根据重量和尺寸估算物流成本影响。",

      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    copyright: {
      nav: "版权资产",
      title: "版权与法律资产",
      subtitle: "一站式证据包，用于即时知识产权执法和版权保护。",
      statusActive: "法律安全状态：活跃",
      protected: "受保护列表",
      certified: "认证包",
      onChain: "链上证明",
      ready: "执法就绪",
      filter: "筛选",
      searchPlaceholder: "按产品名称或 SKU 搜索...",
      table: {
        preview: "资产预览",
        product: "产品 / 平台",
        status: "认证状态",
        bundle: "法律证据包",
        actions: "操作",
        download: "下载完整法律包",
        previewBtn: "预览",
        synced: "同步：",
        certified: "已认证",
        docs: "高清文档"
      },
      bundleNav: {
        back: "返回",
        details: "包详情",
        downloadCurrent: "下载当前",
        downloadAll: "下载完整证据包 (.zip)",
        contents: "包内容",
        letter: "官方授权书",
        psd: "取证 PSD 源文件",
        watermark: "神经水印分析",
        blockchain: "区块链创作证明",
        dispute: "知识产权争议模板",
        securityHash: "安全哈希"
      },
      tabs: {
        docView: "文档视图",
        metaDash: "元数据仪表板",
        xray: "X 射线分析"
      },
      letter: {
        title: "平台授权书",
        seal: "已加盖官方印章",
        certTitle: "真实性证书",
        certSub: "官方法律证据记录",
        docId: "文档 ID",
        date: "签发日期",
        subject: "主题识别",
        prodName: "产品名称",
        sku: "资产 SKU",
        jurisdiction: "平台管辖权",
        grade: "证据等级",
        gradeValue: "取证认证",
        body1: "本文件构成正式法律证据，证明上述资产已在一键大卖知识产权保护库中验证和注册。",
        body2: "验证包括 PSD 源文件的取证分析、在转换过程中持久存在的神经水印嵌入，以及公共区块链账本上的时间戳。",
        officer: "法律合规官",
        signature: "数字签名：",
        footer1: "安全取证链：#991-AUTH-Z",
        footer2: "专有证据格式 v4.2"
      },
      psd: {
        reportTitle: "取证元数据报告",
        verified: "元数据已验证",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 个图层",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "技术属性",
        colValue: "取证值",
        created: "创建时间戳",
        createdDesc: "文件系统起源日期",
        fingerprint: "神经指纹 (SHA-256)",
        fingerprintDesc: "唯一数学标识",
        author: "作者 ID",
        authorDesc: "已验证的创作者签名",
        software: "软件签名",
        softwareDesc: "引擎和构建版本",
        seed: "嵌入水印种子",
        seedDesc: "持久神经追踪器",
        engine: "分析引擎",
        integrity: "完整性检查",
        secure: "100% 安全"
      },
      watermark: {
        title: "神经水印 X 射线",
        scanning: "正在扫描神经层...",
        status: "状态",
        detected: "检测到神经水印",
        score: "弹性评分",
        stable: "加密稳定",
        encodingTitle: "神经编码",
        encodingDesc: "此水印在生成扩散过程中嵌入神经层。与传统像素叠加不同，此签名是图像权重的一部分，使其能够抵抗基于 AI 的擦除、裁剪或噪声注入。",
        latentSeed: "潜在种子",
        engineSig: "引擎签名",
        inference: "推理时间",
        confidence: "扫描置信度"
      },
      blockchain: {
        title: "区块链验证证书",
        subtitle: "资产来源的不可变证明",
        verified: "已链上验证",
        network: "网络：",
        networkVal: "Arbitrum One / 以太坊主网",
        txHash: "交易哈希 (TxID)",
        signature: "数字签名 (EdDSA)",
        block: "区块高度",
        timestamp: "时间戳 (UTC)",
        legalNoteTitle: "法律可采性说明",
        legalNoteDesc: "此证书提供加密安全的时间戳和作者记录。记录永久锚定在 Arbitrum L2 网络上，继承以太坊结算层的完整安全性。",
        scan: "扫描验证",
        verifStatus: "验证状态",
        protocol: "OneClick-ChainLink 预言机 v2",
        confirmed: "已确认",
        confirmations: "节点确认数",
        integrity: "数据完整性",
        secure: "100% 安全"
      },
      dispute: {
        preview: "预览文档",
        settings: "模板设置",
        language: "语言：",
        title: "知识产权侵权删除通知",
        subtitle: "自动法律合规文档",
        to: "致：法律合规部门",
        ref: "参考：",
        date: "日期：",
        s1Title: "1. 所有权声明",
        s1Body: "本人，签署人，特此在伪证处罚下声明，本人是本文所述知识产权版权所有者的授权代表。一键大卖公司对与此声明相关的专有数字资产拥有专有权利。",
        s2Title: "2. 侵权描述",
        s2Body: "我们已在以下位置发现未经授权使用我们的版权视觉资产：",
        s2Url: "URL：",
        s2Desc: "此列表使用的专有产品图像和创意布局与我们的原创作品相同或实质相似。",
        s3Title: "3. 原创性证据",
        s3Body: "争议资产的原创性和创建日期通过附件证据包中包含的以下加密方法进行验证：",
        s3Item1: "神经水印：",
        s3Item1Desc: "嵌入图像数据中与我们主文件匹配的隐形取证标记。",
        s3Item2: "区块链证明：",
        s3Item2Desc: "记录在 Arbitrum 主网上的不可变创作证明。",
        s4Title: "4. 法律声明",
        s4Body: "本人真诚相信，以被投诉的方式使用该材料未经版权所有者、其代理人或法律授权。本通知中的信息准确无误。我们要求立即删除侵权材料。",
        signature: "授权签名",
        division: "一键大卖公司知识产权保护部",

        sidebar: {
          verifStatus: "验证状态",
          link: "侵权链接",
          linkDesc: "已提供 URL",
          psd: "取证 PSD",
          psdDesc: "图层数据已验证",
          evidence: "其他证据",
          missing: "缺少 2 个模块",
          prob: "执法概率",
          lowConf: "低置信度",
          guide: "证据完成指南",
          scan: "神经水印扫描",
          boost30: "缺失：+30% 提升",
          complete: "立即完成",
          proof: "区块链证明",
          boost22: "缺失：+22% 提升",
          autoFiling: "自动提交",
          filingNote: "置信度高于 80% 前不建议提交。",
          autoSubmit: "自动提交声明",
          processing: "处理中...",
          submitted: "声明已提交"
        }
      }
    },

    workspace: {
      nav: "工作区",
      newUserDesc: "先导入一个商品，即可解锁完整工作流。",
      heroTitle: "创建您的第一个 AI 列表",
      heroSub: "粘贴链接 → AI 优化 → 一键发布。在几秒钟内转换产品数据。",
      returningLabel: "欢迎回来",
      returningTitle: "继续优化你的最新列表",
      returningSub: "你的最近导入已准备就绪。继续完善内容或进入任务中心查看进度。",
      startBtn: "开始导入产品",
      taskCenterEntry: "进入任务中心",
      pasteLink: "粘贴链接",
      pasteDesc: "从速卖通、1688、亚马逊等平台导入。",
      uploadMedia: "上传媒体",
      uploadDesc: "拖放图片、视频或 ZIP 文件。",
      batchImport: "批量导入",
      batchDesc: "通过 CSV 或多个 URL 批量导入产品。",
      recentImports: "最近导入",
      recentImportsDesc: "你的最新导入及其当前状态",
      viewHistory: "查看历史",
      extRequired: "需要 Chrome 扩展",
      extNotFound: "未找到",
      extOutdated: "版本过低",
      extDesc: "从主要平台安全收集数据所必需。",
      extDownload: "下载扩展",
      extUpdate: "更新扩展",
      extRecheck: "我已更新，重新检测",
      extUpgradeRequired: "插件版本需要更新",
      extUpgradeDesc: "当前版本 {current}，最低要求 {required}。请先更新后再继续采集。",
      extVersionUnknown: "未知版本",
      watchTutorial: "观看视频教程",
      readGuide: "阅读用户指南",
      status: {
        processing: "处理中",
        ready: "待发布",
        uploaded: "已上传",
        failed: "失败"
      },
      loadFailed: "加载最近导入失败。",
      loadHint: "请重试以获取最新任务进度。",
      retry: "重试",
      emptyTitle: "暂无导入记录",
      emptyDesc: "先导入一个产品链接，就能在这里查看进度。",
      loginTitle: "登录后查看导入记录",
      loginDesc: "登录即可同步你的导入历史与流程进度。",
      loginAction: "立即登录",
      unknownSource: "未知来源",
      totalImports: "总导入数",
      completed: "已完成",
      processing: "处理中"
    },

    // 任务中心
    taskCenter: {
      nav: "统一工作台",
      breadcrumb: {
        workspace: "工作区",
        taskCenter: "任务中心"
      },
      title: "统一工作台",
      subtitle: "在一个页面完成任务调度与已完成资产运营，减少来回跳转。",
      topBarSearchPlaceholder: "搜 1688 链接 / 商品名 / SKU / Ozon 编号 / 资产编号",
      target: {
        label: "目标：",
        platform: "Amazon",
        region: "美国",
        language: "英语"
      },
      sync: {
        label: "同步进度",
        value: "85%"
      },
      reviewChanges: "查看变更",
      noReviewableTasks: "暂无可查看的变更，请先完成采集或生成。",
      createTask: "创建新任务",
      dropdown: {
        singleTitle: "单品任务",
        singleDesc: "通过单个链接导入",
        batchTitle: "批量导入任务",
        batchDesc: "通过多链接或 CSV 导入"
      },
      table: {
        source: "来源",
        task: "任务名称 / ID",
        status: "状态",
        progress: "AI 进度",
        createdAt: "创建时间",
        action: "操作"
      },
      sourceUnavailable: "暂无来源链接",
      dailyGoal: {
        title: "今日上品目标",
        subtitle: "按今日发布成功数实时统计",
        currentStore: "当前店铺：{store}",
        currentStoreShort: "当前店铺",
        workingStoreLabel: "工作店铺",
        switchStore: "快捷切换店铺",
        quickEdit: "快捷设置",
        progressLabel: "进度",
        openSettings: "设置页",
        badges: {
          completed: "已达成",
          behind: "稍落后",
          error: "加载异常",
          disabled: "已关闭",
          active: "进行中"
        },
        summary: {
          disabled: "已关闭今日目标展示，需要时可随时开启。",
          completed: "今天已完成 {count} 个，目标达成。",
          behind: "当前进度略慢，还差 {remaining} 个达到目标。",
          start: "今天还没开始，目标是 {target} 个。",
          progress: "今天已完成 {completed}/{target}，还差 {remaining} 个。"
        },
        disabledValue: "未开启",
        targetLabel: "目标",
        remainingLabel: "剩余",
        updatedLabel: "更新",
        todayCount: "今日已发布 {count} 个",
        quickEnableHint: "开启后会在任务中心实时显示。",
        updatedAt: "更新于 {time}",
        collapseToIcon: "最小化目标卡片",
        restoreCard: "展开今日目标",
        hideCard: "前往设置关闭",
        displaySwitchLabel: "显示目标",
        enableCard: "开启目标",
        quickEditHint: "这里可以调整今日目标和展示偏好。",
        targetInputLabel: "今日目标数",
        saveAction: "保存"
      },
      filters: {
        failed: "失败"
      },
      status: {
        active: "进行中",
        completed: "已完成",
        waiting: "等待中",
        idle: "空闲",
        collect_pending: "等待采集",
        assigned: "已分配采集",
        collecting: "采集中",
        collect_uploading: "上传中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "已取消",
        collect_expired: "已超时",
        pending_generate: "等待生成",
        generating: "生成中",
        publishing: "发布中",
        publish_reviewing: "审核中",
        publish_fix_required: "待整改",
        ready: "待发布",
        uploaded: "可售",
        publish_failed: "发布失败",
        generation_failed: "生成失败",
        variant_pending: "等待执行",
        variant_running: "变体创建中",
        variant_completed: "变体已完成",
        variant_preview_ready: "预览待发布",
        variant_partial: "部分可发布",
        variant_blocked: "变体门禁阻断",
        variant_failed: "变体失败",
        variant_cancelled: "变体已取消"
      },
      batchLabel: "批量",
      itemsLabel: "件",
      progress: {
        processing: "处理中",
        copywriting: "AI 文案",
        inQueue: "等待执行",
        summary: {
          queueing: "任务已提交，正在等待执行资源。",
          collecting: "正在采集商品数据并同步资源。",
          prepareGenerate: "采集已完成，等待你手动开始生成。",
          generating: "AI 正在按阶段生成内容。",
          publishing: "正在发布到店铺，等待平台反馈。",
          variantRunning: "变体任务生成中。",
          variantPreviewReady: "变体预览已生成，请继续勾选并发布。",
          completed: "任务已成功完成。",
          failed: "任务失败，请查看报错后重试。",
          stuckRecoverable: "长时间无新进展，可刷新或重试。",
          stuckUnrecoverable: "任务状态异常，暂时无法自动恢复。"
        },
        detail: {
          currentStageAndLastUpdated: "{detail}"
        }
      },
      stage: {
        collect_pending: "等待采集",
        collect_assigned: "插件已接单",
        collect_preparing: "准备采集",
        collect_scrolling: "页面滚动中",
        collect_collecting: "采集数据中",
        collect_saving: "保存数据中",
        collect_uploading: "上传素材中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "任务已取消",
        collect_expired: "任务已超时",
        generate_pending: "等待生成",
        generate_info_structure: "商品理解中",
        generate_seo: "SEO 关键词图谱生成中",
        generate_listing: "Listing 文案生成中",
        generate_visual: "图片阶段生成中",
        generate_adapter: "平台适配中",
        generate_export: "导出交付中",
        publish_pending: "准备发布",
        publish_processing: "发布中",
        publish_reviewing: "审核中",
        publish_scheduled: "已排期",
        publish_failed: "发布失败",
        publish_completed: "发布完成",
        generate_ready: "生成完成待发布",
        generate_uploaded: "已发布",
        generate_failed: "生成失败"
      },
      actions: {
        workspace: "工作区",
        preview: "预览",
        generate: "开始生成",
        publish: "去发布",
        viewPublish: "查看发布",
        goEdit: "去编辑",
        goOzonBackend: "去 Ozon 后台",
        fixChoiceTitle: "选择整改入口",
        fixChoiceDesc: "该商品已创建，但存在待整改项。请选择接下来的处理入口。",
        fixChoiceOzonHint: "已打开发布详情，你可以在结果页进入 Ozon 后台编辑并刷新状态。",
        refreshPublishStatus: "刷新状态",
        refreshPublishStatusSuccess: "已刷新发布状态。",
        refreshPublishStatusFailed: "刷新发布状态失败。",
        createVariantPending: "待审核通过",
        createVariantPendingHint: "主 SKU 审核通过后即可创建变体。",
        createVariantFixRequired: "先整改后创建",
        createVariantFixRequiredHint: "请先处理发布整改项，并刷新状态后再创建变体。",
        update: "更新商品",
        batchOverview: "批次总览",
        assets: "进入资产视图",
        viewProgress: "查看进度",
        retry: "重试",
        retryTask: "重试任务",
        retryCollect: "重新采集",
        retryGenerate: "重试生成",
        createVariant: "创建变体",
        viewVariantProgress: "查看变体进度",
        viewVariantResult: "查看变体结果",
        reviewAndPublishVariant: "复核并发布",
        continueVariantTask: "继续处理",
        handleVariantException: "处理异常",
        viewVariantBlockReason: "查看阻断原因",
        viewVariantRecord: "查看变体记录",
        retryVariant: "重试变体任务",
        cancelCollect: "取消本地采集",
        forceStopRetry: "终止并重试",
        pending: "待处理",
        managementDisabledFallback: "当前暂不可操作，请稍后重试。",
        managementMissingTarget: "任务信息不完整，暂不可执行该操作。",
        archive: "归档",
        delete: "删除"
      },
      tooltips: {
        previewLocked: "采集完成后可预览",
        previewUnavailable: "采集完成后才可预览",
        previewCollecting: "采集中，稍后可预览。",
        previewFailed: "采集失败，暂无可预览内容。",
        expand: "展开批量任务",
        collapse: "收起批量任务",
        errorFallback: "任务执行失败，请重试或联系客服。",
        publishFixRequiredFallback: "已发布但存在整改项，请打开发布详情处理。",
        workspaceLocked: "采集与 AI 处理完成后可进入工作区",
        actionLocked: "任务处理中，请稍后再试"
      },
      confirmArchive: "确定要归档该任务吗？",
      confirmDelete: "确定要删除该任务吗？此操作无法撤销。",
      actionFailed: "操作失败，请稍后重试。",
      generateNotReady: "采集结果正在准备中，系统会在后台自动更新。",
      retryUnavailable: "该任务缺少可重试的采集ID，请重新导入发起新任务。",
      errorSummaryDefault: "任务执行异常，请重试。",
      manualReview: {
        shortHint: "请到 Ozon 后台核对，该商品可能已部分创建。",
        detailHint:
          "Ozon 可能返回发布失败，但部分数据已创建成功。请登录 Ozon 卖家后台查看具体报错并手工修正字段。AI 生成数据并非 100% 准确，仍需人工复核。"
      },
      pagination: {
        showing: "显示 {count}/{total} 个任务",
        showingRange: "显示 {start}-{end} / {total}（第 {page}/{totalPages} 页）",
        first: "首页",
        last: "末页",
        perPage: "每页"
      },
      stats: {
        total: "总数",
        active: "进行中",
        completed: "已完成",
        failed: "失败",
        waiting: "等待中",
        successRate: "成功率",
        avgTime: "平均完成时间",
        performance: "性能"
      },
      bulk: {
        selected: "已选择 {count} 项",
        selectedAllFiltered: "已选择全部匹配的 {count} 项",
        retry: "重试",
        refreshStatus: "刷新状态",
        archive: "归档",
        delete: "删除",
        deleting: "删除中",
        archiving: "归档中...",
        retrying: "重试中...",
        refreshingStatus: "正在刷新状态...",
        deleteImpactCalculating: "正在计算删除影响范围...",
        deleteWaitingBackend: "等待后端确认删除...",
        deleteImpactFailed: "删除影响范围计算失败。请先停止操作，重试计算后再删除。",
        deleteRefreshing: "正在刷新卡片...",
        selectAll: "全选",
        selectCurrentPage: "选择当前页",
        selectAllFiltered: "选择全部匹配项（{count}）",
        deselectAll: "取消全选",
        confirmArchive: "确定要归档选中的 {count} 个任务吗？",
        confirmDelete: "确定要删除选中的 {count} 个任务吗？此操作无法撤销。",
        confirmDeleteTitle1: "确认删除",
        confirmDeleteTitle2: "二次确认删除",
        confirmDeleteTitle3: "最终确认删除",
        confirmDeleteStep1: "删除选中的 {count} 个任务？",
        confirmDeleteStep2: "确认删除 {count} 个任务？",
        confirmDeleteStep3: "最终确认：删除 {count} 个任务？"
      },
      view: {
        cards: "卡片",
        list: "列表",
        compact: "紧凑"
      },
      batch: "批量",
      refresh: "刷新",
      autoRefresh: {
        activeTitle: "自动刷新已开启",
        pausedTitle: "自动刷新已暂停",
        runningSummary: "运行中任务：{count} 个",
        waitingSummary: "等待中任务：{count} 个",
        noAutoRefreshTasks: "当前没有需要自动刷新的任务。",
        activeCadence: "运行中任务每 {seconds} 秒自动同步一次",
        waitingCadence: "等待中任务每 {seconds} 秒自动同步一次",
        manualOnlyCadence: "自动更新暂不可用",
        lastRefresh: "最近刷新：{time}",
        neverRefreshed: "尚未刷新",
        backgroundHint: "页面切到后台时会暂停自动刷新，回到页面后会自动补刷。",
        pausedDetail: "当前页面处于后台，自动刷新已暂停，返回页面后会立即恢复。",
        refreshCadenceLabel: "每 {seconds} 秒",
        pausedBadge: "后台已暂停"
      },
      exportCSV: "导出 CSV",
      clearFilters: "清除筛选",
      storeFilter: {
        label: "店铺",
        allStores: "全部店铺",
        defaultTag: "默认",
        inactiveTag: "已停用",
        unboundTag: "已解绑",
        unboundStore: "已解绑店铺 #{id}"
      },
      importComposerToggleExpand: "展开快速导入",
      importComposerToggleCollapse: "收起快速导入",
      surfaceTasks: "待处理任务",
      surfaceAssets: "已完成资产",
      focusTitle: "这里用于任务调度与异常恢复",
      focusHint: "进行中/失败任务在此处理，已完成商品请切换到资产视图继续运营。",
      focusShowAll: "查看全部任务",
      focusGoAssets: "切换到资产视图",
      assetsFocusTitle: "这里用于已完成资产运营",
      assetsFocusHint: "在此处理待发布/已发布结果；重试与异常请切回任务视图。",
      assetsFilterAll: "全部结果",
      assetsFilterReady: "待发布",
      assetsFilterUploaded: "已发布",
      assetsFilterFailed: "需修复",
      filterPanelExpand: "展开筛选",
      filterPanelCollapse: "收起筛选",
      shortcuts: {
        title: "快捷键",
        refresh: "刷新任务",
        selectAll: "全选",
        bulkArchive: "批量归档",
        bulkDelete: "批量删除",
        bulkRetry: "批量重试",
        gridView: "网格视图",
        listView: "列表视图",
        compactView: "紧凑视图",
        search: "搜索",
        newTask: "新建任务"
      },
      banner: {
        template: "{pipeline}，{brand}。目标：{target}。任务完成后可进入工作区。",
        pipelineMulti: "多代理流水线已启用",
        pipelineLite: "轻量流水线已启用",
        brandOn: "品牌保护已启用",
        brandOff: "品牌保护未启用"
      },
      loading: "任务加载中...",
      emptyTitle: "这里暂时还没有任务",
      emptyHint: "导入单个商品或创建批量任务后，将在这里查看进度。",
      emptyAction: "创建新任务",
      emptyImportAction: "导入商品",
      emptySampleAction: "查看示例",
      emptyHelpAction: "帮助文档",
      emptyNoActiveTitle: "当前没有进行中的任务",
      emptyNoActiveHint: "你可以查看历史任务，或切换到资产视图管理已完成商品。",
      emptyNoActivePrimaryAction: "查看全部任务",
      emptyNoActiveSecondaryAction: "打开资产视图",
      assetsEmptyTitle: "当前还没有可运营资产",
      assetsEmptyHint: "请先创建并运行任务，完成后会自动出现在资产视图。",
      assetsEmptyPrimaryAction: "创建任务",
      assetsEmptySecondaryAction: "返回任务视图",
      loginHint: "登录后即可查看任务中心",
      loginDesc: "登录账户以追踪采集、生成与上传的完整进度。",
      loginAction: "去登录",
      loadFailed: "任务加载失败",
      retryHint: "请刷新页面或稍后重试。",
      retryAction: "重新加载",
      modal: {
        title: "批量导入任务",
        subtitle: "一次性向目录中添加多个商品。",
        productUrls: "商品链接",
        totalLinks: "唯一链接总数：",
        onePerLine: "每行一个链接",
        runModeTitle: "运行模式",
        runMode: {
          collect: {
            title: "只采集",
            desc: "只抓取商品信息，不生成、不发布（适合先收集素材）"
          },
          generate: {
            title: "采集 + 生成",
            desc: "采集完成后自动开始 AI 生成，停在“待发布”"
          },
          publish: {
            title: "一键上新",
            desc: "采集 + 生成 + 自动发布到你选择的店铺"
          }
        },
        recommended: "推荐",
        storeTitle: "选择要发布的店铺",
        storeNotBoundTitle: "还没有绑定 Ozon 店铺",
        storeNotBoundDesc: "先绑定店铺的 API 信息，才能发布到 Ozon。",
        storeBindAction: "去绑定",
        storeSelected: "已选择 {count} 个店铺",
        storeSelectAll: "全选",
        storeClear: "清空",
        defaultStore: "默认",
        scheduleTitle: "发布时间",
        scheduleNow: "立即发布",
        scheduleLater: "定时发布",
        scheduleHint: "时间以系统时区为准。",
        advancedTitle: "高级设置",
        skipImagesTitle: "不生成图片",
        skipImagesDesc: "更快，只生成文案。后续可再补图片。",
        useCacheTitle: "复用历史结果",
        useCacheDesc: "如果有历史缓存，将复用以节省时间和消耗。",
        emptyWarning: "请至少输入一个商品链接。",
        storeNotBound: "你还没有绑定 Ozon 店铺。现在去绑定吗？",
        storeEmptyWarning: "请选择至少 1 个店铺用于发布。",
        scheduleEmptyWarning: "请选择定时发布的日期和时间。",
        fileEmptyWarning: "文件中未找到有效链接。",
        duplicateWarning: "检测到 {count} 个重复链接，已自动移除。",
        uploadTitle: "上传 TXT 或 CSV 文件",
        uploadSubtitle: "每批最多 500 条链接，每行一个 URL。",
        uploadHint: "需要模板？下载示例文件",
        browseFiles: "浏览文件",
        cancel: "取消",
        startBatch: "开始批量导入",
        startCollect: "开始采集",
        startGenerate: "采集并生成",
        startPublish: "一键上新",
        batchConfirmTitle: "确认批量导入",
        batchConfirmMessage: "即将创建 {count} 个采集任务。请确认这些是真实商品链接，不是示例链接。",
        batchConfirmAction: "确认导入",
        batchPartial: "批量导入部分成功",
        batchSuccess: "批量导入已提交。"
      },
      batchOverview: {
        title: "批次总览",
        subtitle: "查看该批次每个商品在各店铺的发布结果。",
        back: "返回任务中心",
        batchIdLabel: "批次ID：",
        publishBatch: "发布到店铺",
        publishMore: "补发布到更多店铺",
        manageSchedule: "管理排期",
        refresh: "刷新",
        exportFailedCsv: "导出失败 CSV",
        exportingCsv: "导出中...",
        exportFailedCsvMessage: "导出失败，请稍后重试。",
        loading: "批次加载中...",
        loadFailed: "批次加载失败",
        retryLoad: "重新加载",
        empty: "该批次暂时还没有可展示的记录。",
        hint: "提示：点击某个店铺状态，可以查看更详细的发布进度。",
        searchPlaceholder: "搜索标题 / ID / 链接",
        search: "搜索",
        clear: "清除",
        viewLabel: "查看",
        view: {
          all: "全部",
          failed: "失败",
          publishing: "发布中",
          completed: "已发布",
          scheduled: "已排期"
        },
        storeColumns: "店铺列",
        storeColumnsHint: "已显示 {shown}/{total}",
        storeColumnsManage: "管理",
        pagination: "显示 {start}-{end} / {total}",
        prev: "上一页",
        next: "下一页",
        retryAllFailed: "重试全部失败",
        retryStoreFailed: "重试 {count} 个失败",
        retryItemFailed: "重试失败店铺",
        retrying: "重试中...",
        retrySubmitted: "已提交重试。",
        retryFailed: "重试失败，请稍后再试。",
        retryConfirm: "确定要重试该批次所有失败的发布吗？",
        retryStoreConfirm: "确定要重试该店铺的失败发布吗？",
        relaunchModal: {
          title: "智能恢复重发",
          subtitle: "范围：{scope}。系统已按失败类型生成重发方案。",
          scopeBatch: "整个批次",
          scopeStore: "当前店铺",
          scopeSelection: "选中失败项",
          planFailed: "恢复方案生成失败。",
          noActionable: "当前范围内没有可自动重发的失败项。",
          reasonFallback: "未返回可解释的错误详情。",
          originalSku: "原 SKU",
          newSkuLabel: "新 SKU",
          newSkuPlaceholder: "请输入新的 SKU",
          newSkuHint: "仅支持字母、数字、下划线、连字符，最长 50 位。",
          manualHint: "该失败需先人工处理（店铺授权 / 内容修复 / 联系支持）。",
          skippedTitle: "跳过记录（{count}）",
          selectedCount: "已选择 {count} 条可重发记录",
          selectAtLeastOne: "请至少选择一条可重发记录。",
          offerIdRequired: "请先补全新 SKU。",
          confirmExecute: "确认提交 {count} 条重发任务？",
          submit: "确认重发",
          submitting: "提交中...",
          executeFailed: "批量重发失败，请稍后再试。",
          executeSuccess: "重发已提交：成功 {created} 条，跳过 {skipped} 条。",
          actions: {
            retry_with_new_offer_id: "新 SKU 重发",
            retry_same_payload: "原参数重试",
            fix_content_then_retry: "先修复内容",
            store_auth_required: "先处理店铺授权",
            wait_and_poll: "继续等待",
            contact_support: "联系支持"
          },
          summary: {
            total: "已分析",
            newSku: "新 SKU",
            retry: "原参数重试",
            manual: "需人工处理"
          },
          skipped: {
            NOT_IN_BATCH: "不属于当前批次",
            NOT_FOUND: "发布记录不存在",
            NOT_RETRYABLE_STATUS: "当前状态不可重试",
            STORE_FILTERED: "被店铺筛选条件过滤",
            COLLECTION_ID_MISSING: "缺少采集记录引用",
            STORE_NOT_BOUND: "店铺未绑定或已失效",
            STRATEGY_NOT_ALLOWED: "所选策略与失败类型不匹配",
            INVALID_STRATEGY: "重发策略无效",
            EMPTY_OFFER_ID: "新 SKU 为空",
            INVALID_OFFER_ID_FORMAT: "新 SKU 格式不合法",
            DUPLICATE_IN_REQUEST: "本次请求中 SKU 重复",
            EXISTS_IN_LOCAL: "新 SKU 在本系统已存在",
            EXISTS_IN_OZON: "新 SKU 在 Ozon 店铺已存在",
            CHECK_OZON_FAILED: "校验 Ozon SKU 可用性失败",
            OFFER_ID_UNAVAILABLE: "新 SKU 不可用",
            RECOVERY_PLAN_BUILD_FAILED: "生成 SKU 建议失败",
            DUPLICATE_PUBLISH_ID: "请求中存在重复 publish_id",
            UNKNOWN: "未知原因"
          }
        },
        missingBatch: "缺少批次ID。",
        notStarted: "未开始",
        notSelected: "未选择",
        storeInactive: "已失效",
        openLink: "打开链接",
        failedCount: "{count} 个失败",
        storeFilter: {
          title: "选择店铺列",
          subtitle: "选择要在表格中显示的店铺列。",
          selected: "已选 {count} 个",
          selectAll: "全选",
          activeOnly: "仅启用",
          clear: "清空",
          save: "保存",
          empty: "至少选择 1 个店铺列。"
        },
        publishModal: {
          title: "发布该批次",
          subtitle: "选择要发布的店铺，并设置发布时间。",
          storeTitle: "选择店铺",
          selectAll: "全选",
          clear: "清空",
          noActiveStores: "还没有可用店铺，请先绑定或启用店铺。",
          storeEmpty: "请选择至少 1 个店铺用于发布。",
          scheduleTitle: "发布时间",
          scheduleNow: "立即发布",
          scheduleLater: "定时发布",
          scheduleHint: "时间以系统时区为准。",
          scheduleEmpty: "请选择定时发布的日期和时间。",
          scheduleInvalid: "定时发布时间无效。",
          submit: "提交",
          submitting: "提交中...",
          submitted: "已提交发布。",
          failed: "提交失败，请稍后重试。"
        },
        scheduleModal: {
          title: "管理排期",
          subtitle: "可取消或修改该店铺的定时发布。",
          storeTitle: "选择店铺",
          storePlaceholder: "请选择店铺",
          storeEmpty: "请选择店铺。",
          actionTitle: "操作",
          cancel: "取消排期",
          reschedule: "修改时间",
          timeTitle: "新时间",
          scheduleHint: "时间以系统时区为准。",
          scheduleEmpty: "请选择新的日期和时间。",
          scheduleInvalid: "新的发布时间无效。",
          submit: "提交",
          submitting: "提交中...",
          submitted: "排期已更新。",
          failed: "排期更新失败，请稍后再试。"
        },
        summary: {
          items: "商品数",
          completed: "已发布",
          failed: "发布失败",
          processing: "发布中",
          scheduled: "已排期"
        },
        table: {
          product: "商品",
          actions: "操作"
        }
      }
    },

    orders: {
      nav: "订单中心",
      title: "订单与采购中心",
      subtitle: "集中查看 Ozon 订单、站内履约进度，并快速回到 1688 原链接完成采购。",
      refresh: "刷新订单",
      retry: "重试",
      loading: "加载订单中...",
      loadFailed: "订单加载失败",
      loadFailedDetail: "当前无法获取最新订单列表，请稍后重试。",
      detailLoadFailed: "订单详情加载失败",
      detailLoadFailedDetail: "当前无法获取最新订单详情，请稍后重试。",
      loginTitle: "请先登录查看订单",
      loginDesc: "登录后即可加载 Ozon 订单并更新履约进度。",
      loginAction: "立即登录",
      emptyTitle: "暂无订单",
      emptyDesc: "待真实 Ozon 订单同步后，可在此处理采购与履约。",
      filteredEmptyTitle: "当前筛选下暂无订单",
      filteredEmptyDesc: "可清空部分筛选条件后查看其他订单。",
      detailEmptyTitle: "请选择一个订单",
      detailEmptyDesc: "从订单列表打开详情后，可查看采购回源链接与履约操作。",
      save: "保存履约信息",
      saving: "保存中...",
      saveSuccess: "履约信息已保存。",
      saveFailed: "履约信息保存失败。",
      orderSaveConfirm: "确认保存订单采购信息",
      orderSaveSuccess: "采购信息已保存。",
      orderSaveFailed: "采购信息保存失败。",
      itemSave: "保存商品进度",
      itemSaving: "保存商品中...",
      itemSaveSuccess: "商品履约进度已更新。",
      itemSaveFailed: "商品履约进度更新失败。",
      syncSuccess: "已从 Ozon 同步 {count} 条订单。",
      syncFailed: "同步 Ozon 订单失败。",
      shipToOzon: "回写 Ozon 发货",
      shippingToOzon: "回写发货中...",
      shipToOzonSuccess: "物流号与发货状态已同步到 Ozon。",
      shipToOzonFailed: "同步 Ozon 发货失败。",
      prepareToOzon: "Ozon 备货",
      preparingToOzon: "备货中...",
      prepareToOzonSuccess: "已完成 Ozon 备货，可下载贴单标签。",
      prepareToOzonFailed: "Ozon 备货失败。",
      batchPreparing: "批量备货中...",
      batchPrepareSuccess: "批量备货完成：成功 {count} 单。",
      batchPrepareFailed: "批量备货失败。",
      batchPreparePartial: "批量备货完成：成功 {succeeded} 单，失败 {failed} 单。",
      batchPrepareSelectRequired: "请先勾选至少一个货件后再批量备货。",
      batchPrepareFilteredEmpty: "当前筛选下没有可批量备货的订单。",
      batchPrepareConfirmSelected: "确认立即批量备货已勾选的 {count} 个货件？",
      batchPrepareConfirmFiltered: "确认立即批量备货当前筛选下的全部 {count} 个货件？",
      prepareSplitConfirm: "该货件包含 {productCount} 种商品 / {quantity} 件商品。是否按 Ozon 拆包规则继续备货？",
      batchPrepareSplitConfirmSelected: "已勾选货件中有 {count} 个可能需要 Ozon 拆包。确认按拆包备货继续？",
      batchPrepareSplitConfirmFiltered: "当前筛选结果中可能包含需要 Ozon 拆包的货件。确认按拆包备货继续？",
      prepareTrackingRequired: "Ozon 备货不需要国内物流单号。",
      prepareTrackingRequiredHint: "Ozon 备货只发送包裹商品信息；国内物流单号用于后续货代导出。",
      prepareRequiresPurchased: "请先填写国内订单号或采购信息，至少更新为已采购后再执行 Ozon 备货。",
      prepareRequiresPurchasedHint: "防呆提醒：先填国内订单号，状态至少应变为已采购。",
      prepareNotAvailableByOzonStatus: "当前 Ozon 状态不是待备货，禁止重复执行 Ozon 备货。",
      prepareBlockedByBuyerCancel: "该订单已被买家取消，禁止执行 Ozon 备货。",
      buyerCancelledTag: "买家已取消",
      buyerCancelledNote: "买家已取消订单，勿备货，请仓库拦截处理。",
      qualityInspection: {
        badge: "质检单",
        noticeTitle: "质检单提醒",
        noticeDescription: "该 Ozon 订单号以 0213 开头，属于质检单，请处理时特别注意。",
        groupNotice: "本组有 {count} 个 0213 开头的质检单，请处理时特别注意。"
      },
      shipMissingStore: "当前订单缺少店铺上下文，暂时无法回写发货。",
      productIdCopied: "1688 商品 ID 已复制。",
      productIdCopyFailed: "复制 1688 商品 ID 失败。",
      labelDownloading: "标签下载中...",
      labelDownloadSuccess: "已下载发货标签。",
      labelDownloadFailed: "下载发货标签失败。",
      exportSuccess: "已导出货代表格。",
      exportPartialWithSkipped: "货代表格已导出：成功 {exported} 单，自动跳过 {skipped} 单（已在运输中）。",
      exportFailed: "导出货代表格失败。",
      exportingForwarderSheet: "导出中...",
      exportSelectRequired: "请先选择至少一个货件后再导出。",
      exportIncomplete: "仍有订单缺少导出所需字段，请先补全。",
      exportIncompleteMore: "以及另外 {count} 条记录。",
      exportIncompleteItemFallback: "请补全导出必填字段。",
      matchedSource: "已匹配货源",
      unmatchedSource: "未匹配货源",
      unknown: "未知",
      creatorBadge: "{creator} 创建",
      lastEditedBadge: "最后编辑：{editor}",
      results: "显示 {visible} / {total} 条订单",
      syncBackgroundStarted: "已发起后台增量同步：{count} 个店铺正在更新。",
      syncBackgroundPending: "后台同步状态更新中，请稍候…",
      syncBackgroundRunning: "后台同步进行中：{count} 个店铺仍在同步。",
      syncBackgroundCompleted: "后台同步完成，订单已更新。",
      syncBackgroundFinishedWithFailures: "后台同步结束，但有 {count} 个店铺同步失败，请检查店铺状态。",
      hasMoreHint: "下一页还有更多订单。",
      itemsTitle: "订单商品",
      itemsHint: "查看当前订单的商品快照，以及匹配到的货号/SKU 信息。",
      itemsCount: "{count} 件商品",
      moreItems: "还有 {count} 件商品",
      noItems: "当前暂无商品快照。",
      quickActionsTitle: "快速采购操作",
      quickActionsHint: "可直接打开 1688 原链接、复制供应商商品 ID，或查看 Ozon 发布页。",
      workbench: {
        sidebarTitle: "订单中心",
        sidebarSubtitle: "履约工作台",
        skuHandoffDesk: "SKU履约交接台",
        skuOverview: "SKU总览",
        skuOverviewDescription: "全部活跃与历史 SKU 组",
        skuOverviewFocus: "先选择一个 SKU 组，再补齐最早缺失的履约步骤。",
        skuListByStatus: "SKU列表（按状态分组）",
        issueBlocked: "异常阻塞",
        issueBlockedDescription: "需要人工处理的订单",
        issueBlockedFocus: "优先处理缺字段、物流异常或 Ozon 侧阻塞。",
        historyCompleted: "历史完成订单",
        relatedOrdersByMissing: "关联订单（按缺失字段分组）",
        missingPurchaseInfo: "缺采购信息",
        missingDomesticOrder: "缺国内订单号",
        missingDomesticTracking: "缺快递单号",
        ozonActionNeeded: "Ozon 待处理",
        forwarderReady: "已就绪（可导出）",
        inProgressOrders: "其他处理中订单",
        stepPurchaseInfo: "采购信息",
        stepPurchaseInfoHint: "填写采购平台、批量采购总价、采购 SKU 与国内订单号。",
        stepDomesticLogistics: "国内物流",
        stepOzonPrepare: "Ozon备货",
        stepForwarderExport: "货代导出",
        goodMorningTitle: "早上好，今天有 {count} 个货件需要采购",
        dataUpdatedAt: "数据更新于 {time}",
        neverSynced: "暂未同步",
        recentSync: "最近同步 {minutes} 分钟前",
        recentSyncUnknown: "暂无同步记录",
        syncNow: "立即同步",
        metricPendingPurchase: "待采购货件",
        metricReadyPrepare: "可备货",
        metricReadyExport: "可导出",
        metricIssue: "异常处理",
        metricShippingToday: "今日发货中",
        metricAwaitingAction: "待处理",
        metricReadyAction: "可操作",
        metricAttentionNeeded: "需关注",
        metricRealtime: "实时",
        allOrders: "全部订单",
        selectedShipment: "选中货件",
        detailTab: "详情",
        ordersTab: "订单（{count}）",
        timelineTab: "时间线",
        markOrdered: "标记下单",
        badge: "卡片式履约流",
        cardFlowTitle: "卡片式订单履约流",
        cardFlowSubtitle: "把订单拆成待采购、已下单、国内在途、Ozon 备货、货代导出五个清晰阶段，用户只看下一步要做什么。",
        commandCenter: "履约指挥台",
        loadedLines: "已加载订单行",
        issueLines: "异常订单行",
        flowGroups: "履约卡片",
        totalQuantity: "商品总件数",
        searchPlaceholder: "搜索订单号、SKU、1688 ID、采购备注或商品名",
        advancedFilters: "高级筛选",
        queueMeta: "{groups} 张卡片 · {lines} 条订单行 · {quantity} 件商品",
        emptyTitle: "当前履约流暂无卡片",
        emptyDesc: "可清空筛选或刷新订单，继续处理采购与履约。",
        lineItems: "订单行（{count}）",
        contextDrawer: "行动抽屉",
        flowRail: "履约流程",
        cardFlow: "卡片流看板",
        linesUnit: "条",
        stageEmpty: "暂无卡片",
        hasIssue: "有异常",
        quantity: "数量",
        orderCount: "订单数",
        nextStep: "下一步",
        exportChecklist: "导出准备度",
        decisionBadge: "单品大图采购决策台",
        decisionTitle: "采购决策中心",
        decisionSubtitle: "用一张足够大的商品图、克制的 SKU 事实、店铺筛选和历史采购记录，帮助用户更快判断去哪下单，不假装展示拿不到的平台价格。",
        currentTask: "当前任务",
        storeSnapshot: "店铺信息",
        allStoreSnapshot: "全部店铺视图",
        readyActions: "可操作",
        activeFilters: "当前筛选",
        noActiveFilters: "暂无额外筛选",
        batchMode: "批量模式",
        batchBasket: "合并采购篮",
        filteredQueueHint: "基于当前筛选结果",
        batchGroupSummary: "{orders} 个订单 / {quantity} 件",
        expectedOnePurchase: "预计 1 次下单",
        batchPrepareReady: "Ozon 备货范围",
        batchExportReady: "可导出订单",
        sameSkuOrders: "{orders} 单 / {quantity} 件",
        storesCovered: "覆盖 {count} 个店铺",
        groupOrderList: "组内订单",
        groupOrderListHint: "本次采购记录会应用到当前商品组的 {orders} 个订单、{quantity} 件商品。",
        groupOrderListMore: "还有 {count} 条订单行在本组内",
        batchApplyScope: "应用到本组 {count} 个订单",
        batchProcurementRecord: "批量采购记录",
        batchProcurementHint: "一次填写采购结果，批量写回本组 {count} 个订单。",
        batchOperationQueue: "批量操作队列",
        batchSelection: "批量勾选",
        batchReady: "已就绪",
        batchMissing: "待补充",
        batchReadyCount: "{count} 单可执行",
        batchReadyChecklist: "批量准备度",
        domesticTrackingOzonNumberIgnored: "Ozon 货件号不是国内物流单号；这里需要填写供应商/国内快递实际单号。",
        batchSaveAndSync: "应用到本组 {count} 个订单",
        batchRecordPurchaseResult: "批量记录采购结果",
        batchPrepareToOzon: "批量 Ozon 备货",
        batchPrepareToOzonWithCount: "批量 Ozon 备货（{count}）",
        batchExportForwarder: "导出货代表格",
        batchExportForwarderWithCount: "导出货代表格（{count}）",
        currentBatchProgress: "当前商品组 {current} / {total} · {orders} 单 · {quantity} 件",
        selectCurrentBatch: "选择当前商品组订单",
        selectedCurrentBatch: "已选当前商品组",
        groupSelectionCount: "已选 {selected}/{total}",
        productQueue: "待采购列表",
        phoneScanHint: "可用手机 App 直接扫屏幕大图",
        viewAllImages: "查看全部大图",
        noImageHint: "暂无商品图",
        dataFromSnapshot: "来自商品快照",
        expandMoreInfo: "展开更多信息",
        platformStatus: "平台状态",
        nextAction: "下一步",
        procurementDecision: "采购决策",
        procurementDecisionHint: "先判断在哪里买，再记录采购结果。",
        recommendedMethod: "建议方法",
        recommendReuseHistory: "建议沿用：上次 {channel} 采购",
        recommendUse1688: "建议优先核对 1688 原链接",
        recommendIdentifyFirst: "建议先用大图识别同款",
        historyHelpsDecision: "已有历史采购记录，优先复用上次路径，减少决策时间。",
        noHistoryDecisionHint: "这里不展示拿不到的淘宝/拼多多价格；用户可直接用手机 App 扫屏幕大图找同款。",
        purchaseMethodSelection: "采购方式选择",
        open1688Source: "打开 1688 原链接",
        noSourceUrl: "暂无货源链接",
        taobaoScreenSearch: "可直接用手机淘宝扫屏幕图片搜索",
        pddScreenSearch: "可直接用手机拼多多扫屏幕图片搜索",
        usePhoneSearchOnly: "在手机 App 内打开相机/以图搜图，对准中间大图即可；本页不展示平台价格。",
        procurementRecord: "采购记录",
        readyChecklist: "操作检查",
        checkSourceImage: "已核对货源图片与商品身份",
        checkPurchaseMethod: "已记录采购方式或凭证",
        recordPurchaseResult: "记录采购结果",
        selectCurrentProduct: "选择当前商品订单",
        selectedCurrentProduct: "已选当前商品",
        currentProductProgress: "当前商品 {current} / {total}",
        moreExport: "更多导出",
        nextPurchaseFirst: "先完成采购",
        nextExportForwarder: "导出货代表格",
        nextPrepareOzon: "备货到 Ozon",
        nextCompleteProcurement: "继续完成下一步履约",
        tabs: {
          purchase: { title: "待采购", description: "打开货源链接并完成供应商下单。" },
          ordered: { title: "已下单", description: "补采购成本与国内订单号。" },
          domestic: { title: "国内在途", description: "已有国内物流单号，跟进包裹到货与异常。" },
          ozon: { title: "Ozon备货", description: "执行备货并下载 posting-label。" },
          warehouse: { title: "货代导出", description: "导出可交接仓库的包裹与标签。" }
        },
        focus: {
          purchase: "打开货源链接，核对 SKU 与数量，保存采购信息。",
          ordered: "填写国内订单号、采购成本、运费和采购凭证。",
          domestic: "确认实际国内物流单号和轨迹，不把 Ozon 货件号当作国内物流。",
          ozon: "执行 Ozon 备货，下载发货标签，并保持物流数据一致。",
          warehouse: "检查导出准备度，再把表格和标签交接给货代。"
        }
      },
      purchaseReimbursement: {
        badge: "采购日报",
        title: "采购报销日报",
        hint: "按采购保存时间和操作人统计；合并采购只按整批总额统计一次，不按订单行重复累加。",
        date: "日期",
        totalCost: "合计费用",
        unreimbursedCost: "未报销金额",
        reimbursedCost: "已报销金额",
        orderCount: "订单数",
        recordCount: "采购记录数",
        itemUnit: "订单行",
        loadFailed: "采购报销日报加载失败。",
        recordsLoadFailed: "采购记录明细加载失败。",
        saveFailed: "报销状态更新失败。",
        empty: "当天暂无采购记录。",
        emptyHint: "店员保存采购详情后，这里会自动按账号汇总报销金额。",
        recordsEmpty: "暂无采购记录明细。",
        markReimbursed: "标记已报销",
        markUnreimbursed: "标记未报销",
        markRecordReimbursedSuccess: "已标记该采购记录为已报销。",
        markRecordUnreimbursedSuccess: "已标记该采购记录为未报销。",
        markDayReimbursedSuccess: "已标记该员工当天采购记录为已报销。",
        markDayUnreimbursedSuccess: "已标记该员工当天采购记录为未报销。",
        mergedRecord: "合并采购",
        singleRecord: "单独采购",
        mergedCostHint: "合并采购按整批总额只统计一次。",
        status: {
          unreimbursed: "未报销",
          partial: "部分报销",
          reimbursed: "已报销"
        }
      },
      preview: {
        domesticTitle: "站内履约概览",
        ozonTitle: "Ozon 状态概览",
        scopeCurrentPage: "统计口径：当前页数据",
        scopeStoreLevel: "统计口径：当前店铺范围（不受状态、搜索、分页影响）"
      },
      procurementTitle: "采购信息",
      logisticsTitle: "国内物流与货代信息",
      mergeInfoTitle: "合单与包裹映射",
      statusCodesTitle: "Ozon 状态码",
      fulfillmentPanelTitle: "站内履约台",
      fulfillmentPanelHint: "在当前页面维护采购、到货与发货状态，无需离开工作台。",
      timelineTitle: "履约时间线",
      detailTitle: "订单详情",
      trackingPlaceholder: "填写内部物流号；回写 Ozon 发货时会同步该值",
      itemNotePlaceholder: "记录该商品的采购备注或异常说明",
      purchaseSkuPlaceholder: "例如：红色 / L / 220V",
      purchaseReferencePlaceholder: "可填写链接、口令、下单备注或手机下单说明",
      purchaseReferenceCopied: "采购信息已复制。",
      purchaseReferenceCopyFailed: "复制采购信息失败。",
      sourceUrlPlaceholder: "粘贴 1688 链接或直接填写 1688 商品 ID；留空可清除当前货源。",
      sourceUrlHint: "未匹配订单可在这里补充货源，已匹配订单也可改成新的 1688 链接或商品 ID。",
      notePlaceholder: "记录采购备注、供应商反馈或异常说明",
      mergeSuggestionsTitle: "智能合并建议",
      mergeSuggestionsHint: "{count} 条订单行 · {quantity} 件",
      mergeApplyAction: "应用合并",
      mergeApplySuccess: "已应用合并建议。",
      mergeApplyFailed: "应用合并建议失败。",
      mergeSaveConfirm: "确认保存合单采购信息",
      mergeSaveSuccess: "已保存合并采购信息。",
      mergeSaveFailed: "保存合并采购信息失败。",
      mergeMissingGroupKey: "请填写合并分组键。",
      mergeGroupsHint: "{lines} 条订单行 · {quantity} 件 · {orders} 个订单",
      mergeFieldConflictHint: "合并子订单中这些字段值不一致，请确认后再保存：{fields}。",
      singleProcurementHint: "当前分组中有 {count} 条子订单设置为单独采购处理。",
      singleProcurementEnabled: "已切换为单独采购处理。",
      singleProcurementDisabled: "已恢复为合并采购。",
      mergeToggleFailed: "切换合并状态失败。",
      mergedSectionTitle: "合并采购（{count}）",
      mergedSectionEmpty: "当前分组暂无“合并采购”子订单。",
      singleSectionTitle: "单独采购处理（{count}）",
      singleSectionEmpty: "当前分组暂无“单独采购处理”子订单。",
      procurementMode: {
        merged: "合并采购",
        single: "单独采购处理"
      },
      freeShippingStatus: {
        unset: "未设置",
        free: "包邮",
        paid: "不包邮"
      },
      filters: {
        store: "店铺",
        allStores: "全部店铺",
        ozonStatus: "Ozon 状态",
        allStatuses: "全部 Ozon 状态",
        fulfillmentStatus: "履约状态",
        allFulfillmentStatuses: "全部履约状态",
        domesticTrackingStatus: "国内物流状态",
        allDomesticTrackingStatuses: "全部国内物流状态",
        warehouseExportStatus: "仓库导出状态",
        allWarehouseExportStatuses: "全部导出状态",
        warehouseExportEligible: "导出建议",
        allWarehouseExportEligibility: "全部建议",
        search: "搜索",
        searchPlaceholder: "搜索包裹号、货号、1688 编号、SKU 或商品名",
        showPanel: "展开筛选",
        hidePanel: "收起筛选",
        clear: "清空筛选"
      },
      actions: {
        open1688: "打开 1688 原链接",
        copyProductId: "复制 1688 ID",
        viewPublish: "查看 Ozon 页面",
        openPurchase: "打开采购链接",
        copyPurchaseReference: "复制采购信息",
        useOriginal1688Link: "使用原 1688 链接",
        queryDomesticTracking: "查询国内物流",
        openDomesticTracking: "外部查询",
        refreshDomesticTracking: "刷新状态",
        downloadLabel: "下载发货标签",
        openOrderCenter: "查看订单中心",
        exportForwarderCsv: "导出货代表格",
        selectPageVisible: "勾选本页货件",
        clearPageSelection: "取消本页勾选",
        selectAllFiltered: "勾选当前筛选全部（跨分页）",
        selectCurrentQueue: "勾选当前队列",
        clearCurrentQueue: "取消当前队列",
        clearSelection: "清空勾选",
        forceExportForwarder: "强制导出（允许日期/备注为空）",
        selectGroup: "勾选该分组",
        goPurchase: "去 1688 采购",
        batchPrepareSelected: "快速备货（已勾选）",
        batchPrepareFiltered: "快速备货（筛选全部）",
        moveToSingleProcurement: "单独采购处理",
        restoreMergedProcurement: "恢复合并采购",
        previousImage: "上一张图片",
        nextImage: "下一张图片"
      },
      selection: {
        count: "已勾选 {count} 个货件",
        queueCount: "已勾选 {selected} / {total} 个货件",
        filteredAllActive: "当前将导出筛选结果内全部货件"
      },
      unsavedChanges: {
        leaveTitle: "未保存的采购修改",
        leaveConfirm: "当前有未保存的采购修改，仍要离开这个订单吗？",
        refreshConfirm: "当前有未保存的采购修改，刷新将丢弃这些修改，确定继续吗？"
      },
      labels: {
        postingNumber: "货件号",
        orderNumber: "订单号",
        store: "店铺",
        totalPrice: "订单金额",
        inProcessAt: "创建时间",
        shipmentDate: "最晚发货时间",
        substatus: "子状态",
        noSubstatus: "暂无子状态",
        offerId: "Offer ID",
        noOfferId: "暂无匹配 Offer ID",
        productId: "1688 ID",
        noProductId: "暂无匹配 1688 ID",
        collectionId: "Collection ID",
        matchSource: "匹配结果",
        sourceUrl: "货源链接 / 1688 ID",
        platformStatus: "平台状态",
        domesticStatus: "国内状态",
        warehouseExportStatus: "仓库导出",
        warehouseExportEligible: "导出建议",
        purchaseChannel: "采购渠道",
        selectPurchaseChannel: "请选择采购渠道",
        purchaseReference: "采购信息",
        purchaseUrl: "采购链接",
        purchaseSkuInfo: "采购 SKU 信息",
        purchaseCostCny: "采购成本（CNY）",
        batchPurchaseTotalCostCny: "批量采购总价（CNY）",
        purchaseTotalCostCny: "合计费用（CNY）",
        purchaseOperator: "采购操作人",
        purchaseRecordedAt: "采购保存时间",
        reimbursementStatus: "报销状态",
        shippingFeeCny: "运费（CNY）",
        isFreeShipping: "是否包邮",
        domesticOrderNumber: "国内订单号",
        domesticLogisticsChannel: "国内物流渠道",
        domesticTrackingNumber: "国内物流单号",
        domesticTrackingPhoneTail: "手机号后四位",
        domesticTrackingLatestTime: "最新轨迹时间",
        domesticTrackingSignedAt: "签收时间",
        forwarderName: "货代名称",
        forwarderWarehouse: "货代仓库",
        labelingFeeCny: "贴单费（CNY）",
        dimensions: "尺寸",
        weight: "重量",
        russianTitle: "生成俄语标题",
        chineseTitle: "采集中文标题",
        lockedSkuKey: "锁定 SKU Key",
        lockedSkuSource: "锁定来源",
        lockedSkuState: "锁定 SKU 原始 JSON",
        mergeGroupKey: "合采批次",
        packageCode: "包裹编码",
        procurementMode: "采购模式",
        fulfillmentStatus: "履约状态",
        ozonTrackingNumber: "Ozon 物流号",
        ozonLogistics: "Ozon 物流",
        deliveryMethod: "物流方式",
        trackingNumber: "内部物流号",
        trackingNumberUnit: "个物流号",
        note: "备注"
      },
      warehouseExport: {
        status: {
          not_exported: "未导出",
          exported: "已导出",
          needs_reexport: "需重导"
        },
        eligibility: {
          eligible: "建议导出",
          ineligible: "暂不建议"
        },
        blockReasons: {
          ALREADY_EXPORTED: "该订单已导出过，默认不建议重复导出。",
          INVALID_OZON_STATUS: "当前 Ozon 状态不适合继续导出给仓库。",
          BUYER_CANCELLED_OR_TERMINAL: "订单已取消或已进入终态，不建议走正常仓库导出。",
          FULFILLMENT_NOT_SHIPPED: "站内履约尚未到已发货，暂不建议导出给仓库。",
          MISSING_STORE_ID: "缺少店铺信息，无法导出仓库表格。",
          MISSING_SHIPPED_AT: "缺少货物发出日期。",
          MISSING_PURCHASE_RECORD: "缺少采购方式或采购凭证。",
          MISSING_DOMESTIC_ORDER_NUMBER: "缺少国内订单号。",
          MISSING_DOMESTIC_TRACKING_NUMBER: "缺少国内物流单号。"
        },
        lastExportedAt: "最近导出时间",
        exportCount: "导出 {count} 次"
      },
      orderIssue: {
        quickTitle: "异常订单",
        filterLabel: "异常",
        warningPrefix: "预警：",
        overduePrefix: "已逾期：",
        responsibility: "责任判断",
        referenceAt: "开始时间",
        filters: {
          problem_orders: "全部异常订单",
          domestic_signed_ozon_pending: "已签收但 Ozon 待发货"
        },
        types: {
          not_exported_after_domestic_signed: "用户未导出给货代",
          needs_reexport_after_domestic_signed: "用户需要重导货代表格",
          warehouse_not_processed_after_export: "货代/仓库未处理",
          domestic_tracking_verification_blocked: "国内物流需补校验信息"
        },
        descriptions: {
          not_exported_after_domestic_signed: "国内快递已到合作仓库，但还没有导出货代表格给仓库。",
          needs_reexport_after_domestic_signed: "国内快递已到合作仓库，但导出后关键信息发生变化，需要重新导出给仓库。",
          warehouse_not_processed_after_export: "国内快递已签收且货代表格已导出，但 Ozon 仍待发货，疑似货代/仓库未及时处理。",
          domestic_tracking_verification_blocked: "快递100暂时无法确认国内物流状态，需要先补充手机号后四位等校验信息后再刷新。"
        },
        responsibilities: {
          user: "用户需要处理",
          forwarder_warehouse: "货代/仓库需要跟进"
        }
      },
      domesticTrackingStatus: {
        pending: "待订阅",
        subscribed: "已订阅",
        in_transit: "运输中",
        warehouse_signed: "国内头程合作仓库已签收",
        exception: "物流异常",
        needs_courier: "需补充物流公司"
      },
      domesticTrackingNoTrace: "暂无物流轨迹。",
      domesticTrackingRequiresPhoneTail: "需补手机号后四位",
      domesticTrackingRequiresPhoneTailHint: "该快递公司需要收/寄件手机号后四位校验。补充后保存，再刷新国内物流状态。",
      domesticTrackingRefreshSuccess: "国内物流状态已刷新。",
      domesticTrackingRefreshFailed: "国内物流状态刷新失败。",
      ozonStatusLabels: {
        awaiting_packaging: "待打包",
        awaiting_deliver: "待发货",
        delivering: "配送中",
        delivered: "已送达",
        cancelled: "已取消",
        acceptance_in_progress: "受理中",
        sent_by_seller: "卖家已发货",
        not_accepted: "未接收",
        returned: "已退回"
      },
      ozonSubstatusLabels: {
        posting_created: "货件已创建",
        awaiting_registration: "待登记",
        acceptance_in_progress: "受理中",
        ready_to_ship: "待出库",
        shipped: "已发货",
        delivered: "已送达",
        cancelled: "已取消",
        product_cancel: "商品取消",
        product_cancel_awaiting_resolve: "取消处理中",
        package_created: "包裹已创建"
      },
      ozonActionLabels: {
        ship: "发货",
        ship_async: "异步发货",
        ship_async_retry: "重试异步发货",
        ship_with_additional_info: "附加信息发货",
        cancel: "取消订单",
        product_cancel: "取消商品",
        has_barcode_for_printing: "可打印条码",
        label_download: "下载标签",
        label_download_big: "下载 A4 标签",
        label_download_small: "下载热敏标签",
        set_cutoff: "设置发运时间",
        tracking_number_set: "设置物流号"
      },
      matchSourceLabels: {
        manualSource: "手动更新货源",
        offerId: "按 Offer ID 匹配"
      },
      purchaseChannels: {
        1688: "1688 原链接",
        taobao: "淘宝",
        pinduoduo: "拼多多",
        other: "其他"
      },
      timeline: {
        purchasedAt: "已采购时间",
        receivedAt: "已到货时间",
        readyToShipAt: "待发货时间",
        shippedAt: "已发货时间",
        ozonShipmentDate: "Ozon 发货截止"
      },
      fulfillmentStatus: {
        pending_purchase: "待采购",
        purchased: "已采购",
        received: "已到货",
        ready_to_ship: "待发货",
        shipped: "已发货",
        issue: "异常"
      }
    },

    import: {
      nav: "导入产品",
      title: "导入产品来源",
      subtitle: "连接您的供应商链接以开始 AI 优化。",
      extActive: "浏览器扩展活跃",
      connected: "已连接",
      extActiveDesc: "伴侣扩展活跃。您可以直接从供应商导入。",
      extSettings: "扩展设置",
      extMissing: "需要扩展",
      extMissingDesc: "请安装伴侣扩展以在本地抓取产品数据。",
      extUpgradeRequiredDesc: "当前插件版本 {current}，请升级到 {required} 或更高版本后继续。",
      extVersionUnknown: "未知版本",
      extUpdateBtn: "更新扩展",
      extRecheckBtn: "我已更新",
      extRecheckGuide: "正在重新检测插件状态...",
      downloadBtn: "下载扩展",
      downloadGuide: "正在打开扩展下载页...",
      urlLabel: "产品 URL",
      urlSub: "支持速卖通、1688、亚马逊、Shopify 等。",
      urlPlaceholder: "在此粘贴您的产品链接...",
      mainProductAlt: "商品主图",
      urlRequired: "请先填写产品链接。",
      authRequired: "请先登录后再开始采集。",
      guestFailed: "无法创建游客体验，请稍后重试。",
      consent: {
        title: "继续前请确认",
        subtitle: "为创建游客会话并继续导入，请先阅读并同意服务条款与隐私政策。",
        note: "你可以先以游客身份体验；我们将仅为提供导入与 AI 优化服务处理相关数据。",
        cancel: "取消",
        continue: "同意并继续"
      },
      importBtn: "导入",
      submitting: "正在提交...",
      collectFailed: "提交采集任务失败。",
      uploadTitle: "上传媒体资产",
      uploadSub: "将图片、视频或 ZIP 文件夹拖放到此处",
      chooseFiles: "选择文件",
      alternative: "替代方式",
      targetGlobal: "目标：全球市场",
      taskCenterEntry: "任务中心",
      taskCenterHint: "登录后可在任务中心查看进度。",
      recent: {
        title: "最近任务",
        subtitle: "最新导入任务：{status}",
        taskId: "任务ID",
        source: "来源链接",
        createdAt: "创建时间",
        statusHint: "采集完成后会在任务中心通知。",
        continue: "继续查看",
        hint: {
          processing: "采集中，请保持页面打开或前往任务中心查看进度。",
          queued: "任务已提交，正在等待插件或执行资源。",
          completed: "采集已完成，可继续预览并配置生成设置。",
          failed: "采集失败，可在任务中心查看原因并重试。",
          generating: "AI 生成中，可前往任务中心查看进度。",
          publishing: "发布中，可查看发布进度。",
          publishFailed: "发布失败，请查看发布结果并按人工复核建议处理。",
          ready: "生成完成，可去发布。",
          uploaded: "已发布，可在资产库查看。"
        },
        viewTaskCenter: "查看任务中心",
        loginToView: "登录查看",
        status: {
          processing: "处理中",
          queued: "等待中",
          completed: "已完成",
          failed: "失败"
        }
      },
      success: {
        title: "任务已创建",
        subtitle: "我们已开始采集商品数据。您可以继续预览或前往任务中心跟踪进度。",
        taskId: "任务ID",
        source: "来源链接",
        status: "状态",
        statusProcessing: "处理中",
        statusHint: "采集正在后台执行。",
        continue: "继续导入",
        goTaskCenter: "前往任务中心",
        loginTaskCenter: "登录查看任务",
        loginHint: "登录后可在任务中心查看进度。"
      }
    },

    // 基本信息设置
    generalInfo: {
      title: "基本信息",
      subtitle: "管理您的账户详情和个人资料。",
      proPlan: "专业版",
      upgradePlan: "升级套餐",
      uploadNew: "上传新头像",
      remove: "移除",
      photoHint: "建议尺寸不小于 400x400 像素，支持 JPG 或 PNG 格式。",
      fullName: "姓名",
      fullNamePlaceholder: "例如：张三",
      professionalEmail: "工作邮箱",
      emailPlaceholder: "name@company.com",
      emailReadonlyHint: "邮箱暂不可修改。",
      companyName: "公司名称",
      companyPlaceholder: "请输入公司名称",
      cancel: "取消",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      saveFailed: "保存失败。",
      avatarUploadFailed: "头像上传失败。",
      avatarUpdated: "头像已更新。",
      avatarRemoved: "头像已移除。",
      footer: "平台设置"
    },

    // 账单与积分
    billing: {
      title: "账单与积分",
      subtitle: "查看积分余额、图片扣费规则与完整账单记录。",
      currentBalance: "当前余额",
      credits: "积分",
      balanceHint: "订阅积分每个计费周期清零，充值积分永久有效。",
      topUp: "充值",
      subscriptionPlan: "订阅套餐",
      proPlanPrice: "专业版 · 每月560积分（$49）",
      nextRenewal: "下次续费：",
      noPlan: "未订阅",
      noPlanHint: "暂无订阅",
      loading: "正在加载账单信息...",
      loadFailed: "账单信息加载失败。",
      emptyHistory: "暂无账单记录。",
      paymentSuccess: "订阅支付完成",
      paymentCanceled: "订阅支付已取消",
      rechargeSuccess: "充值支付完成",
      rechargeCanceled: "充值支付已取消",
      refresh: "刷新",
      syncing: "同步中...",
      summaryTotal: "总积分",
      summarySubscription: "订阅积分",
      summaryRecharge: "充值积分",
      summaryTrial: "试用积分",
      statusActive: "已生效",
      statusInactive: "未生效",
      badgeSubscription: "订阅",
      badgeRecharge: "充值",
      badgeAdjustment: "调整",
      badgeRefund: "退款",
      badgePromo: "赠送",
      badgeUsage: "用量",
      badgeOther: "其他",
      usageImageGenerate: "图片生成 · {count} 张",
      usageImageRefund: "图片生成退款 · {count} 张",
      usageImageGenerateDetailed: "图片生成 · {count} 张 · {credits} 积分/张 · {model}",
      usageImageRefundDetailed: "图片生成退款 · +{credits} 积分 · {model}",
      usageImageAdjustmentDetailed: "历史图片扣费调整 · -{credits} 积分 · {model}",
      managePlan: "管理套餐",
      billingHistory: "账单记录",
      date: "日期",
      description: "描述",
      amount: "金额",
      invoice: "发票",
      proSubscription: "专业版订阅（560 积分）",
      creditTopUp: "积分充值 (300 积分)",
      downloadAll: "导出账单记录",
      exportRow: "导出此行",
      exportSuccess: "账单记录已导出。",
      exporting: "导出中...",
      footer: "所有价格以美元计价，订阅积分每个计费周期清零，充值积分永久有效。",
      footerCopyright: "财务运营中心",
      pricingTitle: "计费规则",
      ruleImagePrice: "图片生成 {creditsPerImage} 积分/张（${usdPerImage}）。",
      ruleImageSettlement: "图片生成按当前图片模式扣费；后台自动调度模型，模型变化不影响单价，失败图片自动退款。",
      ruleExchange: "1 积分 = ${usdPerCredit}。",
      ruleRollover: "订阅积分每个计费周期清零，充值积分永久有效。",
      ruleGift: "注册赠送 {registerGiftCredits} 积分，游客赠送 {guestTrialCredits} 积分。",
      imageUsageTitle: "图片模式消耗（近 {days} 天）",
      imageUsageHint: "仅统计扣费的图片生成记录；退款会在明细中单独展示。",
      imageUsageTotal: "总计",
      imageUsageUnknownHint: "其他：{images} 张 · {credits} 积分。",
      faqTitle: "常见问题",
      faq: {
        q1: "为什么只对图片收费？",
        a1: "图片生成是主要成本项，其它模块免费以保持定价透明。",
        q2: "订阅积分会结转吗？",
        a2: "不会，订阅积分每个计费周期清零；充值积分永久有效。",
        q3: "生成失败会退积分吗？",
        a3: "会，系统会根据失败记录自动退还积分。",
        q4: "订阅期间还能充值吗？",
        a4: "可以，充值积分叠加到订阅积分之上。",
      }
    },

    purchaseCredits: {
      title: "充值积分",
      subtitle: "选择充值包用于图片生成（{plan} · {creditsPerImage} 积分/张）。",
      priceHint: "1 积分 = ${usdPerCredit}，充值积分永久有效。",
      planSectionTitle: "图片生成模式",
      planThirdParty: "普通模式",
      planThirdPartyDesc: "平台自动调度 · 适合日常批量生成。",
      planOfficial: "官方高速模式",
      planOfficialDesc: "官方渠道优先，适合重点商品和急单。",
      planRecommended: "推荐",
      planUnavailable: "该模式暂不可用，请联系管理员处理。",
      planUnavailableTag: "暂不可用",
      noChannelsHint: "当前未配置可用的 AI 渠道，请联系管理员在后台启用。",
      creditsUnitPerImage: "积分/张",
      planEstimateHint: "估算按每条 listing 约 {imagesPerListing} 张图（主图+详情图+场景/细节）计算，实际以你的配置为准。",
      bucketSubscription: "订阅赠送积分",
      bucketRecharge: "充值积分",
      bucketLegacy: "旧积分",
      creditsScopePrefix: "扣费范围：",
      creditsScopeSuffixThirdParty: "（扣费顺序：订阅 → 充值 → 旧积分）",
      creditsScopeSuffixOfficial: "（扣费顺序：充值优先；是否使用订阅/旧积分取决于系统设置）",
      summaryPlanLine: "当前模式：{plan} · {creditsPerImage} 积分/张",
      adminBonusLine: "管理员设置赠送 {percent}%：+{credits} 积分",
      creditBreakdownLine:
        "基础到账 {baseCredits} 积分 + 管理员赠送 {adminPercent}%（{adminCredits}）= {totalCredits} 积分",
      listingsLabel: "Listing",
      listingsUnit: "条 Listing",
      timeSaveHint: "预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      laborHint: "人工制作一条 listing 通常需要 30–60 分钟；稳定批量一般需要专职人员（约 4,000–8,000 元/月）。",
      planCompareHint:
        "同样积分：普通模式≈{thirdPartyImages}张/{thirdPartyListings}条，官方高速模式≈{officialImages}张/{officialListings}条（按每条{imagesPerListing}张估算）。",
      planUpdateFailed: "更新图片生成模式失败。",
      stepPack: "选择套餐",
      stepPay: "选择支付",
      stepConfirm: "完成支付",
      creditsLabel: "积分",
      imagesLabel: "可生成",
      imagesUnit: "张",
      topUpLabel: "充值",
      bestValue: "最划算",
      saveLabel: "立省",
      summaryTitle: "当前选择",
      summaryPay: "支付方式",
      bonusLabel: "赠送",
      featureNeverExpire: "充值积分永久有效",
      featureBatch: "支持批量生成",
      featureNoBatch: "不支持批量生成",
      featurePriority: "优先支持",
      paymentTitle: "支付方式",
      purchaseButton: "完成支付",
      buttonProcessing: "支付处理中...",
      processing: "正在处理支付...",
      redirecting: "正在跳转到支付页面...",
      success: "支付成功，积分已到账。",
      paymentFailed: "发起支付失败，请稍后重试",
      payHint: "充值支持微信或支付宝，支付完成后积分即时到账。",
      acceptedMethods: "支持方式",
      visaLabel: "Visa 尾号 4242",
      visaExpire: "到期时间 12/26",
      wechat: "微信支付",
      wechatDesc: "快捷安全的移动支付",
      wechatPendingBadge: "待批准",
      wechatPendingTitle: "微信支付待 Stripe 审核",
      wechatPendingDesc: "Stripe 生产环境正在审核微信支付，暂不可用。请先使用支付宝完成充值。",
      wechatPendingToast: "微信支付暂不可用（待 Stripe 审核），请先使用支付宝。",
      alipay: "支付宝",
      alipayDesc: "全球支付解决方案",
      addNew: "添加新的支付方式",
      addNewDesc: "信用卡、微信支付或支付宝",
      security: "Stripe SSL 加密保障",
      total: "支付总额",
      payHintAlipayOnly: "当前仅支持支付宝充值；微信支付待 Stripe 审核，通过后将开放。",
      footer: "© {year} 一键大卖财务运营中心"
    },

    subscription: {
      title: "管理订阅套餐",
      subtitle: "选择符合图片生成需求的套餐。",
      currentPlanLabel: "当前套餐",
      currentPlanBadge: "当前套餐",
      activeTag: "生效中",
      inactiveTag: "未激活",
      freeTag: "免费版",
      noPlan: "未订阅",
      noPlanHint: "暂无订阅",
      freePlanBillingHint: "免费使用中，无订阅扣费",
      nextBilling: "下次扣费日期：",
      nextBillingDate: "更新中...",
      uploadQuotaTitle: "图片上传额度",
      uploadQuotaSubtitle: "开始新任务前，先查看今日原图上传额度和总存储占用。",
      uploadQuotaBadge: "今日",
      uploadQuotaRemaining: "已用 {used}/{total} MB",
      uploadQuotaDailyLimit: "每日上传上限",
      uploadQuotaDailyLimitHint: "按原图上传体积统计，每日自动刷新。",
      uploadQuotaUsedToday: "今日已上传",
      uploadQuotaUsedHint: "仅统计已确认成功的原图上传。",
      uploadQuotaRemainingLabel: "今日剩余",
      uploadQuotaRemainingHint: "下次请求上传前会再次校验。",
      uploadQuotaStorageLabel: "已用存储",
      uploadQuotaMaxImageHint: "单张图片最大 {size}MB。",
      uploadQuotaFootnote: "该额度仅限制原图上传体积，不影响 AI 生成所使用的账户积分。",
      uploadQuotaDynamicHint: "原图日额度取套餐上限 {planCap}MB 与积分折算 {creditCap}MB 中较小值。",
      uploadQuotaBlockedHint: "原图上传至少需要 {minCredits} 积分，当前可用 {availableCredits}。",
      uploadQuotaLoadFailed: "上传额度加载失败。",
      activeCycle: "当前周期",
      monthly: "月付",
      yearly: "年付",
      yearlySave: "送 2 个月",
      billedMonthly: "按月计费",
      billedYearly: "按年计费（10个月价格）",
      rolloverHint: "订阅积分每个计费周期清零，充值积分永久有效。",
      creditsScopeHint: "当前图片模式：{plan} · {creditsPerImage} 积分/张。扣费范围：{scope}。",
      officialTopUpOnly: "默认不消耗订阅积分（需使用充值积分）",
      perMonth: "/月",
      perYear: "/年",
      plansEmpty: "暂无可用套餐。",
      planImagesPerMonth: "每月 {count} 张",
      planListingsPerMonth: "每月 {count} 条 Listing",
      planCreditsPerMonth: "每月 {count} 积分",
      planUnlimited: "不限量",
      planStarterDesc: "适合个人或刚起步团队。",
      planProDesc: "成长团队首选。",
      planBusinessDesc: "适合高频卖家与工作室。",
      featureStarter1: "基础批量生成",
      featureStarter2: "高清素材下载",
      featureStarter3: "标准支持（48 小时）",
      featurePro1: "高级批量生成",
      featurePro2: "高清 + 4K 素材下载",
      featurePro3: "优先支持（12 小时）",
      featurePro4: "专属增长经理",
      featureBusiness1: "高并发批量生成",
      featureBusiness2: "品牌素材包导出",
      featureBusiness3: "优先支持 + 成功经理",
      featureBusiness4: "流程自动化指导",
      downgrade: "降级套餐",
      downgradeSuccess: "已完成降级",
      upgrade: "升级套餐",
      manageInStripe: "前往 Stripe 管理",
      activePlan: "当前套餐",
      subscribe: "立即订阅",
      processing: "正在跳转到 Stripe 支付页...",
      paymentFailed: "创建支付失败",
      loading: "正在同步订阅状态...",
      buttonProcessing: "处理中...",
      cardHint: "订阅仅支持信用卡（Stripe）。",
      contactSales: "联系销售",
      customTitle: "需要定制方案？",
      customDesc: "我们可以根据你的产出与流程定制方案。",
      consultSales: "咨询销售",
      cancel: "取消订阅",
      cancelConfirm: "确定要取消订阅吗？",
      footer: "© {year} 一键大卖财务运营中心"
    },

    // 订阅方案设置
    subscriptionSettings: {
      title: "管理订阅方案",
      subtitle: "选择最适合你业务的套餐。",
      currentPlanLabel: "当前套餐：",
      currentPlan: "专业版",
      active: "已启用",
      nextBillingLabel: "下次扣费日期：",
      nextBillingDate: "更新中...",
      activeCycleLabel: "当前周期：",
      monthly: "月度",
      yearly: "年度",
      save20: "节省 20%",
      billedAnnually: "按年计费，享受最大优惠",
      billed: "按{cycle}计费",
      perMonth: "/月",
      plans: {
        starter: {
          title: "入门版",
          desc: "适合个人与小型创业团队。"
        },
        pro: {
          title: "专业版",
          desc: "最受成长型团队欢迎。"
        },
      },
      features: {
        starterCredits: "每月 500 积分",
        starterAgents: "基础 AI 代理权限",
        starterSupport: "邮件支持（48 小时内）",
        customIntegrations: "自定义集成",
        proCredits: "每月 2,000 积分",
        proAgents: "全部 AI 代理权限",
        proSupport: "优先支持（12 小时内）",
        proIntegrations: "最多 5 个集成",
      },
      buttons: {
        downgrade: "降级",
        activePlan: "当前方案",
        contactSales: "联系销售",
        consultSales: "咨询销售",
        cancelSubscription: "取消订阅"
      },
      currentBadge: "当前方案",
      custom: {
        title: "需要定制方案？",
        desc: "我们的销售专家可为高频业务量身定制解决方案。"
      },
      alerts: {
        downgrade: "正在降级到入门版...",
        contactSales: "正在联系销售团队...",
        openChat: "正在打开销售聊天...",
        cancelConfirm: "确定要取消吗？",
        cancelRequested: "已提交取消订阅请求。"
      }
    },

    // 安全设置
    securitySettings: {
      title: "安全",
      subtitle: "管理账号保护、认证方式与活跃会话，确保数据安全。",
      twoFactor: {
        title: "双重验证 (2FA)",
        desc: "启用后，新的设备登录需要验证码，提升账号安全。",
        comingSoon: "即将上线",
        available: "已开放"
      },
      password: {
        title: "密码管理",
        updated: "建议定期更新密码以确保账号安全。",
        reset: "重置密码"
      },
      sessions: {
        title: "活跃会话",
        subtitle: "以下是已登录设备。如不认识请撤销。",
        currentSession: "当前会话",
        lastLogin: "最近登录：",
        current: "当前",
        activeNow: "在线中",
        signOut: "退出登录",
        chromeDevice: "macOS 上的 Chrome",
        chromeLocation: "美国旧金山 • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "英国伦敦 • 2 天前"
      },
      cancel: "取消",
      saving: "保存中...",
      saved: "已保存",
      save: "保存更改",
      signOutConfirm: "确定要退出该设备吗？",
      signOutAlert: "正在退出该设备...",
      footer: "安全基础设施"
    },

    // 通知中心
    notificationsCenter: {
      title: "通知",
      subtitle: "这里会展示重要消息与任务结果。",
      tabInbox: "通知",
      tabSettings: "设置",
      unreadOnly: "只看未读",
      refresh: "刷新",
      markAllRead: "全部已读",
      loading: "正在加载通知...",
      loadingMore: "加载中...",
      loadMore: "加载更多",
      loadFailed: "加载通知失败。",
      emptyTitle: "暂无通知",
      emptyDesc: "当有重要进展（如发布成功/失败）时，会在这里提示。",
      delete: "删除",
      deleteConfirm: "确定要删除这条通知吗？",
      deleteFailed: "删除通知失败。",
      deleteDone: "已删除",
      collapseDetail: "收起",
      markAllDone: "已全部标记为已读",
      markAllFailed: "标记已读失败。"
    },
    announcementCenter: {
      title: "公告中心",
      subtitle: "平台重大更新与迁移通知会在这里统一展示。",
      unreadOnly: "只看未读",
      refresh: "刷新",
      unreadBadge: "未读：{count}",
      loading: "正在加载公告...",
      loadFailed: "加载公告失败。",
      errorTitle: "公告加载失败",
      emptyTitle: "当前没有生效公告",
      emptyDesc: "有重要平台通知时，会在这里第一时间展示。",
      pinned: "置顶",
      unread: "未读",
      publishedAt: "发布时间",
      endsAt: "失效时间",
      acknowledge: "我知道了",
      viewAction: "查看详情",
      severity: {
        info: "通知",
        warning: "重要",
        critical: "紧急"
      }
    },
    feedbackCenter: {
      title: "我的反馈",
      subtitle: "跟踪 Bug 修复进展和需求评估结果。",
      loading: "正在加载反馈...",
      refresh: "刷新",
      empty: "你还没有提交过反馈。",
      loadFailed: "加载反馈失败。",
      createdAt: "创建时间",
      updatedAt: "最近更新",
      type: {
        bug: "Bug",
        feature: "需求"
      },
      status: {
        pending: "待审核",
        discussing: "待补充",
        accepted: "已采纳",
        scheduled: "已排期",
        in_progress: "开发中",
        completed: "已完成",
        rejected: "已驳回"
      },
      automation: {
        queued: "等待执行",
        processing: "AI处理中",
        reviewed: "已评估，待排期",
        blocked: "需人工处理",
        fixed: "已修复",
        deployed: "已发布",
        failed: "处理失败"
      }
    },
    feedbackModal: {
      title: "提交反馈",
      subtitle: "说人话就行，AI 会自动理解并分类处理",
      close: "关闭",
      removeImage: "删除图片",
      uploadImage: "上传图片",
      addImage: "添加",
      rewardHint: "提交即奖 2 积分，被采纳再奖 10 积分",
      cancel: "取消",
      submit: "提交反馈",
      submitting: "提交中...",
      emptyStateTitle: "拖拽图片到这里，或点击上传",
      emptyStateHint: "支持 Ctrl+V 粘贴截图，单张不超过 2MB",
      autoProcessTitle: "AI 核实处理",
      autoProcessDesc: "提交后会立即通知本地 AI 先核实分类。Bug 会继续自动修复；需求会先评估，之后等待管理员排期。",
      fields: {
        title: "标题",
        content: "详细描述",
        images: "截图/图片 ({count}/{max})"
      },
      types: {
        bug: "Bug 报告",
        feature: "需求建议"
      },
      placeholders: {
        titleBug: "比如：上传图片后页面卡住了",
        titleFeature: "比如：希望能批量导出数据",
        contentBug: "说说怎么出的问题就行，比如“点了那个按钮之后页面一直转圈”。可以 Ctrl+V 粘贴截图",
        contentFeature: "随便说说你想要什么，比如“要是能一键把所有商品都导出来就好了”。可以 Ctrl+V 粘贴截图"
      },
      success: {
        title: "提交成功",
        desc: "感谢你的反馈！已通知本地 AI 开始核实。",
        trackingId: "反馈单号：{id}",
        trackingStatus: "当前状态：{status}（可在“我的反馈”里持续查看）",
        rewardTitle: "+{count} 积分",
        rewardDesc: "反馈被采纳后可获得更多积分奖励",
        acknowledge: "知道了",
        viewMyFeedback: "查看我的反馈"
      },
      errors: {
        titleRequired: "请填写标题",
        contentRequired: "请填写详细描述",
        loginRequired: "请先登录后再提交反馈",
        submitFailed: "提交失败，请稍后重试",
        imageTooLarge: "图片 {name} 超过 2MB 限制",
        fileNotImage: "文件 {name} 不是图片格式",
        maxImages: "最多只能上传 {count} 张图片",
        imageReadFailed: "图片读取失败"
      }
    },

    // 通知设置
    notificationsSettings: {
      title: "通知设置",
      subtitle: "管理系统更新和提醒的接收方式。",
      table: {
        type: "通知类型",
        email: "邮箱",
        push: "推送"
      },
      loading: "加载中...",
      loadFailed: "加载通知偏好失败。",
      saveFailed: "保存通知偏好失败。",
      pushComingSoon: "推送通知即将上线。",
      account: {
        title: "账号",
        desc: "账号变更与安全提醒"
      },
      payment: {
        title: "支付",
        desc: "支付回执与交易更新"
      },
      subscription: {
        title: "订阅",
        desc: "续费、账单与取消订阅通知"
      },
      quota: {
        title: "积分与额度",
        desc: "余额预警与用量提醒"
      },
      task: {
        title: "任务",
        desc: "导入、生成与发布进度提醒"
      },
      system: {
        title: "系统",
        desc: "平台公告、维护与安全通知"
      },
      listing: {
        title: "上架成功",
        desc: "商品发布时提醒"
      },
      credit: {
        title: "积分预警",
        desc: "余额低于 10% 时提醒"
      },
      updates: {
        title: "更新与发布",
        desc: "新功能与平台改进"
      },
      mute: {
        title: "静默全部通知",
        desc: "暂时关闭平台内所有提醒与邮件"
      },
      discard: "放弃更改",
      saving: "保存中...",
      saved: "偏好已保存",
      save: "保存偏好"
    },

    // 集成
    integrations: {
      title: "平台集成",
      subtitle: "连接您常用的工具和电商平台，优化工作流程。",
      loadFailed: "加载集成信息失败。",
      connected: "已连接",
      connect: "连接",
      shopify: "Shopify",
      shopifyDesc: "同步商品和库存数据。",
      amazon: "亚马逊卖家",
      amazonDesc: "导入商品列表并管理订单。",
      ozon: "Ozon",
      ozonDesc: "俄罗斯电商平台集成。",
      slack: "Slack",
      slackDesc: "在频道中接收实时通知。"
    },

    // 平台偏好
    platformPreferences: {
      title: "平台偏好设置",
      subtitle: "自定义默认的目标市场和 AI 智能体行为。",
      targetMarketplace: "默认目标市场",
      advancedHint: "需要更细的控制？请前往高级能力设置。",
      openAdvanced: "高级设置",
      loading: "加载中...",
      primaryPlatform: "主要平台",
      targetCountry: "目标国家",
      russia: "俄罗斯",
      us: "美国",
      unitedStates: "美国",
      uk: "英国",
      unitedKingdom: "英国",
      germany: "德国",
      france: "法国",
      italy: "意大利",
      spain: "西班牙",
      japan: "日本",
      brazil: "巴西",
      canada: "加拿大",
      mexico: "墨西哥",
      australia: "澳大利亚",
      singapore: "新加坡",
      india: "印度",
      turkey: "土耳其",
      uae: "阿联酋",
      saudi: "沙特阿拉伯",
      netherlands: "荷兰",
      sweden: "瑞典",
      poland: "波兰",
      belgium: "比利时",
      ireland: "爱尔兰",
      southAfrica: "南非",
      egypt: "埃及",
      global: "全球",
      aiPersona: "AI 写作风格",
      poweredBy: "由 LexiCopy 驱动",
      persuasive: "营销说服型",
      professional: "专业严谨型",
      minimalist: "简约精炼型",
      seoFocused: "SEO 优先型",
      personaHint: "此风格将默认应用于所有新的产品描述和广告文案。",
      taskCenterDailyGoal: {
        sectionTitle: "任务中心每日目标",
        sectionDesc: "设置每天发布成功的目标数；关闭后任务中心会彻底移除这个组件。",
        storeLabel: "目标店铺",
        storeHint: "每个店铺分别保存自己的每日目标和进度。",
        storeEmpty: "当前还没有可用店铺。",
        targetLabel: "今日目标数",
        targetHint: "只统计今天发布成功的数量。",
        progressLabel: "今日进度",
        progressEnabled: "当前完成度 {percent}%，还差 {remaining} 个。",
        progressDisabled: "当前已关闭，任务中心不会显示这个组件。"
      },
      brandProtection: "自动品牌保护",
      brandGuard: "品牌守护",
      brandGuardDesc: "生成时自动过滤品牌词，降低侵权风险",
      brandName: "默认品牌名称",
      brandPlaceholder: "输入用于水印的品牌名称...",
      autoWatermark: "自动添加 AI 水印",
      autoWatermarkDesc: "为所有新生成的内容添加品牌标识保护",
      imagePreferences: "图片处理偏好",
      deepEnhancement: "深度图像增强",
      deepEnhancementDesc: "AI 超分辨率和清晰度提升",
      autoBgRemoval: "自动去除背景",
      autoBgRemovalDesc: "生成电商平台规范的干净素材",
      autoSaveHint: "更改将自动保存到您的工作区配置。",
      saveHint: "更改将应用于新任务，保存后同步到工作区配置。",
      resetDefaults: "恢复默认设置",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      saveFailed: "保存偏好失败。",
      footer: "全球工作区"
    },

    // 资产管理
    assets: {
      nav: "资产库",
      title: "资产运营库",
      subtitle: "专注已完成商品与发布链接运营。任务调度请前往任务中心。",
      loading: "加载资产中...",
      loadFailed: "加载资产失败。",
      loginTitle: "请先登录查看资产库",
      loginDesc: "登录后可管理生成的商品信息与上传记录。",
      loginAction: "登录",
      emptyTitle: "暂无资产",
      emptyHint: "请先到任务中心查看进行中任务，完成后会进入资产库。",
      emptyAction: "前往任务中心",
      newOptimization: "导入新商品",
      goTaskCenter: "任务中心",
      focusTitle: "资产库仅展示可运营结果",
      focusHint: "发布链接、成品素材在此管理；进行中或失败任务请回到任务中心处理。",
      platformAll: "平台：全部",
      statusPublished: "状态：已发布",
      statusAll: "状态：全部",
      progressLabel: "进度",
      last30Days: "近 30 天",
      last7Days: "近 7 天",
      last90Days: "近 90 天",
      allPeriod: "全部时间",
      clearFilters: "清除筛选",
      score: "评分",
      published: "已发布",
      draft: "草稿",
      failed: "失败",
      retry: "重试",
      edit: "编辑",
      view: "查看",
      viewProgress: "查看进度",
      goToPublish: "去发布",
      fixError: "去修复",
      openListing: "打开商品",
      noPublishUrl: "暂无商品链接",
      moreActionsComingSoon: "更多操作正在开发中。",
      addNewListing: "添加新商品",
      addNewDesc: "上传原始图片进行 AI 增强。",
      assetsSelected: "已选资产",
      batchDownload: "批量下载",
      batchSync: "批量同步",
      delete: "删除",
      actions: {
        copyPublishLink: "复制发布链接",
        openPublishLink: "打开发布链接",
        deleteRecord: "删除记录",
        deleteTitle: "删除记录",
        deleteConfirmMessage: "确定要删除这条记录吗？此操作不可撤销。",
        deleted: "已删除。",
        deleteFailed: "删除失败。",
        publishLinkCopied: "发布链接已复制。",
        publishLinkCopyFailed: "复制失败，请重试。"
      },
      showingAssets: "显示第 {from} 至 {to} 项，共 {total} 项资产",
      searchPlaceholder: "搜索资产..."
    },

    // 导入预览
    importPreview: {
      nav: "商品预览",
      breadcrumb: "工作区",
      collectStatus: "采集状态",
      collectQueued: "等待插件接收",
      collecting: "采集中",
      collectCompleted: "已完成",
      collectFailed: "采集失败",
      collectingHint: "插件正在后台采集数据。",
      collectProgressTitle: "采集进度",
      collectProgressHint: "正在与插件同步，请在此查看实时进度。",
      progressLabel: "进度",
      stepQueued: "等待插件接收",
      stepAssigned: "插件已接收",
      stepCollecting: "采集中",
      stepUploading: "上传中",
      stepCompleted: "采集完成",
      currentStage: "当前阶段",
      stage: {
        pending: "等待插件接收",
        assigned: "任务已分配",
        preparing: "准备页面",
        scrolling: "滚动页面",
        collecting: "采集中",
        saving: "保存数据",
        uploading: "上传资源",
        cached: "加载缓存结果",
        completed: "已完成"
      },
      authRequired: "请先登录后查看采集进度。",
      pollFailed: "获取采集进度失败。",
      previewFailed: "加载采集数据失败。",
      retryCollect: "重新采集",
      attributeList: "其他属性",
      detailHighlights: "详情要点",
      noHighlights: "暂无可用的详情要点。",
      skuStructure: "SKU 结构",
      skuStock: "库存",
      skuVariant: "规格",
      skuSyncing: "正在同步 SKU 选择...",
      skuAnchorSyncing: "正在同步优先参考图...",
      skuLockedByUser: "SKU 已锁定",
      skuScopeLockedBadge: "SKU 已锁定",
      skuGuideTitle: "SKU 选择指引",
      skuGuideStepSelect: "1. 选中 SKU 后，参考图范围会立即锁定到这个 SKU。",
      skuGuideStepAnchor: "2. 如需更稳，可再指定 1 张优先参考图（可选）。",
      skuGuideStepStart: "3. 确认锁定范围后，再开始 AI 优化。",
      skuSelectionNudgeTitle: "建议先锁定 SKU 再生成",
      skuSelectionNudgeDesc: "多 SKU 商品建议手动锁定 SKU，可显著降低参考图与成图不一致风险。",
      skuSelectionNudgeAction: "现在去选择 SKU",
      minOrderRiskTitle: "检测到高起批量风险",
      minOrderRiskDesc: "该商品来源页显示存在较高起批量。若继续按单件采购价测算并上架，利润可能被严重高估。",
      minOrderRiskFallbackTitle: "起批量风险预警",
      minOrderRiskDetectedQty: "检测到最小起批量：{count} 件",
      minOrderRiskAcknowledge: "我已知晓该商品可能需要批量采购（>3 件），并确认继续承担该风险。",
      minOrderRiskConfirmRequired: "开始优化前，请先勾选并确认起批量风险预警。",
      skuRecommendationTitle: "AI 推荐 SKU",
      skuRecommendationHint: "基于模型识别结果生成，请确认后再应用。",
      skuRecommendationLoading: "AI 正在分析 SKU 推荐...",
      skuRecommendationRefresh: "刷新推荐",
      skuRecommendationRefreshing: "刷新中...",
      skuRecommendationFailed: "SKU 推荐加载失败。",
      skuRecommendationEmpty: "模型未返回有效 SKU 推荐。",
      skuRecommendationApply: "应用推荐",
      skuRecommendationApplied: "已应用",
      skuRecommendationPendingApply: "AI 推荐已就绪，可一键应用。",
      skuRecommendationConfidence: "置信度 {confidence}%",
      skuRecommendationSourceLabel: "推荐来源：{source}",
      skuRecommendationSourceModel: "模型",
      skuRecommendationSourceCache: "缓存",
      skuRecommendationSourceFallback: "回退",
      skuRecommendationNoReason: "该推荐暂无模型理由说明。",
      stepSelectSkuTitle: "选择采集 SKU",
      stepSelectSkuDesc: "点一个 SKU，系统会自动匹配主图。",
      skuSelectedBadge: "SKU 已选",
      skuOutOfStockBadge: "缺货",
      currentSku: "当前 SKU",
      lockedSkuTitle: "锁定 SKU 标题",
      skuPriceShort: "价格",
      stepPrimaryReferenceTitle: "主参考图已自动锁定",
      stepPrimaryReferenceDesc: "主图由所选 SKU 自动匹配，无需手动选择。",
      primaryLockedBadge: "系统自动锁定",
      primaryReferenceShortBadge: "主图",
      primaryReferenceImageTitle: "主参考图",
      primaryReferenceImageBadge: "主参考图 · 系统自动锁定",
      primaryDirectionTitle: "主图决定方向",
      primaryDirectionDesc: "生图优先保持这张图的颜色、尺寸和款式方向。",
      primaryCannotChangeHint: "主参考图不能手动改动，避免串图。",
      openLargeImage: "查看大图",
      stepSupportingReferenceTitle: "勾选辅助参考图（可选）",
      stepSupportingReferenceDesc: "最多选择 4 张，帮助 AI 理解更多角度和细节。",
      supportingSelectedCount: "已选 {count}/{max} 张",
      supportingReferenceRule: "辅助参考图只补充角度和细节，不改变主图颜色、尺寸和款式。",
      supportingReferenceLimitHint: "最多 {max} 张辅助图。",
      supportingReferenceLimitReachedHint: "已达上限，取消一张后再选择新的辅助图。",
      supportingReferenceMaxReached: "最多只能选择 {max} 张辅助参考图。",
      supportingReferenceImageTitle: "辅助参考图 #{index}",
      supportingReferenceSelectedTitle: "已选辅助参考图 #{index}",
      supportingReferenceSelectedBadge: "已选辅助图",
      supportingReferenceUnselectedBadge: "未选辅助图",
      supportingReferenceSelect: "选择辅助参考图",
      supportingReferenceDeselect: "取消辅助参考图",
      supportingReferenceBadge: "辅助 {index}",
      aiSendSummary: "将发送给 AI：{primary} 张主参考图 + {supporting}/{max} 张辅助图",
      footerSkuSelected: "SKU 已选",
      footerPrimaryLocked: "主图已锁",
      footerPrimaryMatched: "自动匹配完成",
      footerSupportingCount: "辅助图 {count}/{max} 张",
      footerSupportingAdjustable: "最多 4 张",
      largeImagePreviewTitle: "查看大图",
      previewPrevious: "上一张图片",
      previewNext: "下一张图片",
      largeImageNoSelectionChange: "查看大图不会改变选择状态。",
      stockUnits: "{count} 件",
      skuSelectionSyncFailed: "SKU 选择同步失败，请重试。",
      skuAnchorSyncFailed: "优先参考图同步失败，请重试。",
      referenceScopeTitle: "参考图规则",
      referenceScopeLockedTitle: "你选择的 SKU 已直接锁定参考图范围",
      referenceScopeLockedDesc: "后续 AI 只会在这个 SKU 范围内选择参考图。",
      referenceScopeLockedCount: "当前已锁定 {count} 张候选参考图。",
      referenceScopeLockedNoImage: "该 SKU 暂无直接匹配图片，AI 会在生成时结合文字与全图自动补选。",
      referenceScopeAutoTitle: "当前还没有锁定参考图范围",
      referenceScopeAutoDesc: "如果不手动选择 SKU，系统会自动判断参考图范围。",
      referenceScopePreferredOptional: "如需更稳，你还可以再指定 1 张优先参考图。",
      skuAnchorMissingHint: "当前还没有指定优先参考图，系统会在当前范围内自动选 1 张。",
      skuAnchorLocked: "已指定优先参考图：#{index}",
      skuAnchorOptionalTitle: "优先参考图（可选）",
      skuAnchorOptionalDesc: "如果你不指定，系统会在当前 SKU 范围内自动选 1 张。",
      skuAnchorOptionalNoImageDesc: "当前 SKU 暂无可直接使用的图片，AI 会在生成时自动补选优先参考图。",
      skuAnchorManualTitle: "你已指定优先参考图 #{index}",
      skuAnchorManualDesc: "后续生成会直接优先使用这张图。",
      skuAnchorManualBadge: "你指定的",
      skuAnchorSystemTitle: "系统当前优先使用参考图 #{index}",
      skuAnchorSystemDesc: "这是系统在当前 SKU 范围内自动确定的默认图。",
      skuAnchorSystemBadge: "系统默认",
      skuAnchorSystemSourceLabel: "当前默认图来自：{source}",
      skuAnchorSelectHint: "如果你想覆盖系统默认图，可在下方选择 1 张：",
      skuAnchorOptionTitle: "设为优先参考图 #{index}",
      skuAnchorSourceLabel: "优先图来源：{source}",
      skuAnchorPreviewTitle: "优先参考图 #{index}",
      skuAnchorPreviewHint: "点击图片可查看大图并确认细节。",
      skuAnchorSelectAction: "选择优先图",
      skuAnchorChangeAction: "更换优先图",
      skuAnchorSetAction: "设为优先图",
      skuAnchorSelectedAction: "已设为优先",
      skuAnchorExpand: "更改优先图",
      skuAnchorCollapse: "收起",
      referenceSource: {
        manual: "你手动指定的",
        auto_sku_match: "系统按当前 SKU 自动匹配",
        sku_image_url: "SKU 图片",
        selected_sku_image_indices: "你锁定 SKU 后的候选图",
        selected_sku: "你选择的 SKU",
        selected_anchor: "你指定的优先图",
        sku: "SKU 图片",
        main: "主图",
        detail: "详情图",
        collection: "采集图",
        extracted: "抽取结果",
        legacy: "历史缓存",
        none: "未设置",
        unknown: "未知"
      },
      manualSkuReuseDisabledHint: "你选择的 SKU 已锁定参考图范围，为避免串图会跳过历史复用。",
      noSkuData: "暂无 SKU 结构数据。",
      productDetails: "商品详情",
      detailSummary: "详情摘要",
      noDetailSummary: "暂无详情摘要。",
      noImages: "暂无图片",
      previewImage: "预览图片",
      dataOverview: "采集概览",
      overviewImages: "图片数量",
      overviewSkus: "SKU 数量",
      overviewAttributes: "属性数量",
      overviewSections: "区块数量",
      overviewRawText: "原始文本长度",
      characters: "字符",
      noOverview: "暂无采集概览数据。",
      flags: "采集标记",
      noFlags: "暂无采集标记。",
      flagEnabled: "是",
      flagDisabled: "否",
      dimensionsInfo: "尺寸与重量",
      noDimensions: "暂无尺寸信息。",
      imageTags: "图片标签",
      noImageTags: "暂无图片标签信息。",
      rawText: "原始文本",
      noRawText: "暂无原始文本。",
      rawSections: "原始区块",
      validationWarning: "数据校验问题",
      issuesDetected: "个问题可能影响 AI 优化效果",
      manualReview: "需要人工审核",
      allResolved: "所有问题已解决",
      source: "来源",
      marketPricing: "市场定价分析",
      supplierPrice: "供应商价格 (1688)",
      priceRange: "价格区间",
      invalid: "无效",
      aiSuggest: "AI 建议",
      marketAvg: "市场均价",
      suggestedAiPrice: "AI 建议价格",
      profitMargin: "利润率",
      marginDesc: "扣除常规运费后预估净利润率 72.4%。",
      aiStrategy: "AI 优化策略",
      seoEnhancements: "SEO 增强",
      seoDesc: "将添加「矫正支撑」和「透气网面」等关键词，预计提升搜索曝光 35%。",
      imageRefinement: "图片优化",
      imageDesc: "AI 将去除 1688 水印，并为主图生成场景化背景。",
      marketPositioning: "市场定位",
      positionDesc: "定位「轻奢品质」细分市场，主打城市耐用性。",
      skuAttributes: "SKU 属性",
      colorVariants: "颜色款式",
      sizeAvailability: "尺码可选",
      currentStock: "当前库存",
      shippingEst: "预计发货",
      shippingLocal: "3-5 天 (本地)",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      aiAutofill: "AI 自动补全缺失数据",
      dataFilled: "AI 已补全数据",
      viewMetadata: "查看完整原始数据",
      lowRes: "低分辨率",
      dataRequired: "需要数据校验",
      readyOptimize: "已就绪，可优化",
      agentsPaused: "优化智能体已暂停",
      aiFillAll: "AI 补全所有缺失数据",
      editTitle: "编辑商品标题",
      saveDraft: "保存草稿",
      saved: "已保存",
      draftSaved: "保存成功。",
      localDraftRestored: "已恢复本机浏览器草稿。",
      localDraftRestoredPartial: "已恢复本机草稿；图片列表已变化，部分参考图选择已跳过。",
      localDraftStorageUnavailable: "浏览器本地存储不可用，无法保存草稿。",
      saveDraftNoContext: "请先从导入页或任务中心进入该预览，再保存草稿。",
      saveDraftNoTitle: "请先加载或填写商品标题，再保存草稿。",
      saveDraftUnavailable: "当前还不能保存草稿。",
      saveDraftFailed: "保存草稿失败。",
      startOptimization: "开始 AI 优化",
      optimized: "已优化",
      fixIssues: "修复问题以提升 AI 效果",
      exportToStore: "导出到店铺",
      exporting: "导出中...",
      exported: "已导出",
      exportSuccess: "商品已成功导出到店铺！"
    },

    review: {
      nav: "AI 数据审核",
      validating: "AI 正在根据产品类目建议值。",
      validated: "AI 优化成功。所有字段已验证。",
      pending: "需要人工审核",
      completed: "审核完成",
      marketPricing: "市场定价比较",
      supplierPrice: "供应商价格",
      invalid: "无效",
      marketAvg: "市场均价",
      suggestedPrice: "AI 建议价格",
      aiGenerated: "AI 生成",
      marginAnalysis: "利润率分析",
      marginDesc: "本地化运费后估计净利润率为 72.4%。",
      strategy: "AI 优化策略",
      seoEnhance: "SEO 增强",
      seoDesc: "将添加优化关键词以提高可见性。",
      imgRefine: "图像优化",
      imgDesc: "AI 将去除水印并生成生活场景背景。",
      marketPos: "市场定位",
      marketPosDesc: "针对耐用性定位于'经济高端'细分市场。",
      skuAttrs: "SKU 属性",
      originalData: "原始数据",
      colorVariants: "颜色变体",
      sizeAvail: "尺寸可用性",
      stock: "当前库存",
      shipping: "运费估算",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      confirmBtn: "确认所有 AI 建议",
      autofillBtn: "AI 自动填充缺失数据",
      saveDraft: "保存草稿",
      applyAi: "应用 AI 更改",
      startAi: "开始 AI 优化",
      exportBtn: "导出到店铺"
    },

    variantFlow: {
      stepperLabel: "变体创建生命周期",
      stepReadiness: "确认主商品",
      stepSelection: "选择变体",
      stepRun: "生成任务",
      stepReview: "复核结果",
      stepPublish: "发布完成"
    },

    variantStart: {
      breadcrumbLabel: "主商品确认",
      title: "主商品状态确认",
      desc: "这个页面只判断当前主商品是否可以继续创建变体。",
      masterProduct: "主商品",
      masterFallbackTitle: "主商品",
      publishId: "发布 ID",
      skuPool: "可选变体池",
      publishStatus: "发布状态",
      statusUnknown: "未知",
      nextAction: "下一步",
      readyTitle: "可以继续选择变体",
      readyDesc: "当前有 {count} 个 SKU 可以进入本次变体任务。",
      blockedTitle: "需要先处理主商品",
      blockedDesc: "当前主商品还有阻断问题。请先去 Ozon 处理，回来后重新检查。",
      continueToSelection: "继续选择变体",
      loadFailed: "检查主商品状态失败。",
      loadWarning: "主商品检查失败",
      issueListTitle: "检测到的问题",
      checkPublished: "已发布到 Ozon",
      checkPublishedDesc: "主商品必须已经有成功的 Ozon 发布记录。",
      checkModeration: "不在审核中",
      checkModerationDesc: "Ozon 审核状态不能阻断变体创建。",
      checkVisible: "商品可见且有效",
      checkVisibleDesc: "归档或不可见商品不能作为当前主商品。",
      checkPriceStock: "价格和库存有效",
      checkPriceStockDesc: "价格和库存需要满足后续发布同步条件。",
      checkSkuPool: "存在可选变体",
      checkSkuPoolDesc: "下一步可选择 {count} 个候选 SKU。"
    },

    // 变体选择矩阵
    variantSelection: {
      breadcrumbLabel: "变体选择",
      pipelineTitle: "1688 to Ozon Pipeline",
      extractedTitle: "已提取 {count} 个变体",
      filterAll: "全部",
      bulkSelectHint: "+ 点击批量选择",
      smartSelectBtn: "一键应用智能选择",
      smartSelectHint: "仅按 SKU 做智能推荐，可一键选出代表款。",
      smartSelectByFacet: "{facet} 分类策略",
      smartSelectFallback: "SKU 综合策略",
      smartSelectApplied: "已应用智能选择：{count} 个 SKU · {strategy}",
      showingVariants: "显示 {visible} / {total} 个变体",
      loadMore: "加载更多",
      noVariantsTitle: "没有可创建的变体",
      noVariantsDesc: "除了已发布的主商品外，我们在 1688 数据中未找到其他可用的变体款式。",
      noFilterResults: "此筛选条件下无变体",
      clearFilter: "清除筛选",
      selectAll: "全选当前",
      deselectAll: "取消全选",
      selectedCount: "已选择 {count} 个变体",
      clearSelection: "清空选择",
      generateSkus: "生成 Ozon SKUs（{count}）",
      creatingTask: "正在创建任务...",
      taskCreateFailed: "创建变体任务失败，请稍后重试。",
      maxSelectableExceeded: "单个任务最多可选择 {max} 个 SKU，请减少后重试。",
      selectFirst: "请先选择变体",
      generate: "生成（{count}）",
      select: "选择",
      missingParams: "缺少必要信息",
      missingParamsDesc: "当前页面信息已过期，请返回上一步重新进入。",
      backToTaskCenter: "返回任务中心",
      back: "返回",
      loadingStatus: "正在检查商品状态并读取 SKU 数据...",
      loadFailed: "加载失败",
      reloadLabel: "重新加载",
      issuesTitle: "需要先解决 {count} 个问题",
      issuesDesc: "目前的主商品无法直接创建变体，请先根据下方提示进行修复。",
      recheckBtn: "重新检查",
      goSellerCenter: "去卖家中心处理",
      issues: {
        notPublished: "商品尚未发布成功",
        notPublishedDesc: "必须先将主商品成功发布到 Ozon 后，才能创建变体。",
        notPublishedHint: "请检查发布任务是否已完成。",
        underModeration: "商品正在 Ozon 审核中",
        underModerationDesc: "Ozon 正在审核您的商品，审核通过后即可创建变体。",
        underModerationHint: "审核通常需要几个小时到一天，系统会在后台持续检测并自动更新。",
        archived: "商品已被归档",
        archivedDesc: "商品在 Ozon 卖家中心已处于归档状态。",
        archivedHint: "请前往 Ozon 卖家中心将商品恢复上架。",
        notVisible: "商品在 Ozon 上不可见",
        notVisibleDesc: "商品当前在 Ozon 前台不可见，可能审核被拒或因其他原因下架。",
        notVisibleHint: "请检查 Ozon 卖家后台的商品状态并修复错误。",
        invalidPriceStock: "价格或库存需要完善",
        invalidPriceStockDesc: "商品在 Ozon 端的价格或库存信息有误。",
        invalidPriceStockHint: "请前往 Ozon 卖家中心更新商品的价格和库存。",
        statusUnavailable: "暂时无法检查商品状态",
        statusUnavailableDesc: "我们目前无法从 Ozon 获取您商品的最新状态。",
        statusUnavailableHint: "系统正在后台重试获取最新状态，你可以先继续其他操作。",
        noStock: "主商品当前无库存",
        noStockDesc: "主商品当前库存为 0，但这不会阻止您创建变体。",
        systemNotice: "系统提示",
        issueDetected: "检测到一个问题",
        suggestedAction: "建议操作"
      }
    },

    // Variant Progress (Transformation Pipeline)
    variantProgress: {
      breadcrumbLabel: "变体创建进度",
      missingTaskEyebrow: "任务缺失",
      backToSelection: "返回重选",
      processingTitle: "正在处理 {count} 个 SKU...",
      completedTitle: "已完成 {count} 个 SKU",
      failedTitle: "变体生成失败",
      cancelledTitle: "变体任务已取消",
      processingDesc: "正在读取主商品并准备变体属性。",
      processingDescAttributeOnly: "仅进行主商品读取与变体属性改写。",
      overallProgress: "整体进度",
      taskEnded: "任务已结束",
      safeToLeave: "系统正在自动处理变体，您可以安全离开此页面。",
      fetchingProgress: "正在获取任务进度...",
      itemSyncWarning: "主进度已获取，但 SKU 明细暂时不可用，系统将自动重试。",
      pipelineItemsTitle: "SKU 处理流水",
      noItemSnapshots: "暂时还没有可展示的 SKU 处理快照。",
      patchTitle: "AI 变体属性改写",
      patchEmpty: "暂无可展示改写（历史任务或差异不可用）",
      patchMissingDistinctive: "未生成可编辑的变体属性改写",
      patchAttrLabel: "属性 #{id}",
      patchUnknownAttribute: "属性",
      patchSystemDictionarySuffix: "系统值",
      patchValueUnknown: "原始值暂无",
      patchValueMissing: "未返回值",
      patchMore: "还有 {count} 条改写",
      patchViewAll: "查看全部 {count} 条改写",
      patchCollapse: "收起",
      statusLabel: "状态",
      missingTaskId: "任务信息已过期",
      missingTaskIdDesc: "当前页面已过期，请返回任务中心重新打开该任务。",
      backToTaskCenter: "返回任务中心",
      viewResultPreview: "查看结果预览",
      cancelTask: "取消任务",
      cancelling: "正在取消...",
      statusPending: "等待执行",
      statusRunning: "执行中",
      statusCompleted: "已完成",
      statusBlocked: "已阻断",
      statusFailed: "发生错误",
      statusCancelled: "已取消",
      statusUnknown: "未知状态",
      blockedTaskDesc: "主商品门禁阻断了当前任务，流程未继续执行。",
      imageStageAutoFailedTitle: "图片阶段已自动终止",
      imageStageAutoFailedDesc: "图片生成长时间无进度，任务已自动终止，请重试。",
      stageAnalyze: "读取主商品",
      stageAnalyzeDesc: "检查主商品状态与最新发布门禁",
      stagePrepare: "生成变体属性",
      stagePrepareDesc: "为选中的变体 SKU 生成属性改写结果",
      stageImage: "生成变体图片",
      stageImageDesc: "为每个变体生成主图",
      stagePublish: "提交到 Ozon",
      stagePublishDesc: "提交变体并等待 Ozon 确认",
      stageNotExecuted: "未执行",
      stageDeferredManual: "后续手动",
      stageInProgress: "进行中",
      itemProgress: "进度: {completed} / {total}",
      skuDone: "完成",
      skuDoneAttributeOnly: "属性改写完成",
      skuGeneratedSuccess: "生成成功",
      skuGenerating: "生成图片中...",
      skuSubmitting: "提交到 Ozon 中...",
      skuTranslating: "翻译中...",
      skuPending: "等待中",
      skuFailed: "生成失败",
      skuFallbackBlocked: "回退失败（已阻断发布）",
      skuTimeoutAutoStopped: "超时自动终止",
      skuCancelled: "已取消",
      skuSkipped: "已跳过",
      sourceImageLockVariantSku: "已锁定 SKU 原图（生图）",
      sourceImageLockMasterInherited: "无 SKU 图，继承主 SKU 图集（可发布）",
      sourceImageLockPending: "参考图尚未锁定",
      skuOzonReady: "OZON 就绪",
      clickToZoom: "点击查看大图",
      previewTitle: "图片预览",
      sourceImagePreviewTitle: "SKU 原图",
      generatedImagePreviewTitle: "生成主图",
      retryBtn: "重试",
      retryFailed: "重试失败项",
      warning: "警告",
      retry: "重试"
    },

    variantResult: {
      breadcrumbLabel: "变体结果",
      missingTaskEyebrow: "任务缺失",
      missingTaskId: "任务信息已过期",
      missingTaskIdDesc: "当前页面已过期，请从任务中心重新打开该任务。",
      backToTaskCenter: "返回任务中心",
      backToProgress: "返回进度",
      backToList: "返回列表",
      loadFailed: "读取变体结果失败，请稍后重试。",
      itemSyncWarning: "结果已获取，但 SKU 元数据暂时不可用，系统将自动重试。",
      retry: "重试",
      title: "变体结果",
      desc: "查看本地化后的 SKU 效果，拖动滑块对比 1688 原图与 Ozon 预览。",
      titleAttributeOnly: "变体属性结果预览",
      descAttributeOnly: "查看 AI 生成的变体属性改写结果，供下一步手动发布前确认。",
      viewGrid: "网格视图",
      viewTable: "表格视图",
      loading: "正在加载变体结果...",
      summary: "总计 {total} · 已就绪 {ready} · 待复核 {review} · 已发布 {published}",
      summaryAttributeOnly: "总计 {total} · 已改写 {changed} · 无需改写 {unchanged} · 失败 {failed}",
      cardTotal: "总数",
      cardReady: "已就绪",
      cardReview: "需复核",
      cardPublished: "已发布",
      cardChanged: "已改写",
      cardUnchanged: "无需改写",
      cardFailed: "失败",
      systemMeta: "任务: {status} · 阶段: {stage} · ID: {taskId}",
      publishSelectionTitle: "发布前手动勾选",
      selectedPublishPanelTitle: "已选发布项",
      publishSelectionMeta: "可发布 {publishable} · 已勾选 {selected}",
      selectedPublishCountLabel: "已选",
      publishableCountLabel: "可发布",
      blockedCountLabel: "阻塞项",
      publishedCountLabel: "已发布",
      selectedPublishEmptyHint: "先在左侧列表勾选可发布 SKU，再批量发布。",
      selectedPublishNoPublishableHint: "当前结果没有可发布 SKU。",
      selectedPublishAllPublishedHint: "当前结果内的 SKU 已全部发布，无需再次发布。",
      selectedPublishOverflow: "另有 {count} 个",
      publishSelectionWaitTask: "当前任务仍在执行，请等待预览完成后再发布。",
      publishSelectionEmpty: "当前没有可发布项，请先处理失败条目。",
      selectAllPublishable: "全选可发布",
      clearPublishSelection: "清空勾选",
      publishSelectedDirect: "发布已选到 Ozon（{count}）",
      publishingSelectedDirect: "发布中...",
      continueToPublishConfirm: "进入发布确认（{count}）",
      aggregatedPublishBlocked: "多任务聚合视图暂不支持提交发布，请先进入单任务结果页处理。",
      publishContextAll: "已从源任务发布 {count} 个变体，当前结果页仅展示本次新发布任务。",
      publishContextPartial: "本次仅从源任务发布了 {count} 个变体，当前结果页仅展示本次发布的子集。",
      publishContextRemaining: "源任务中仍有 {count} 个可发布项，可返回源结果页继续处理。",
      publishContextBackToSource: "返回源结果页",
      publishVariants: "发布变体（{count}）",
      openPublishPreview: "发布前预览（{count}）",
      publishSubmitting: "发布中...",
      publishFromPreviewFailed: "发布预览勾选变体失败，请稍后重试。",
      publishConflictRedirect: "检测到已有进行中的发布任务，正在跳转任务 {taskId}。",
      publishItemSelectable: "发布",
      publishItemDisabled: "不可发布",
      previewEditItem: "预览编辑",
      previewUnavailableHint: "该条目的预览正在准备中，系统会在后台自动更新。",
      taskStatusUnknown: "未知",
      stageUnknown: "未知",
      publishTip: "型号变体通常会在约 24 小时内由 Ozon 自动合并。",
      publishFinalizationPending: "发布处理中",
      publishFinalizationCompleted: "已发布",
      publishFinalizationReviewing: "审核中",
      publishFinalizationFixRequired: "需整改",
      publishFinalizationFailed: "发布失败",
      publishFinalizationSummary: "完成 {completed} · 审核中 {reviewing} · 需整改 {fixRequired} · 失败 {failed}",
      tabAll: "全部",
      tabAllReady: "全部就绪",
      tabReviewRequired: "待人工决策",
      tabPublished: "已发布",
      emptyTitle: "当前筛选暂无记录",
      emptyDesc: "请切换筛选标签或等待任务继续产出。",
      priceLabel: "价格",
      mergeTypeLabel: "合并类型",
      variantTypeLabel: "变体类型",
      errorPrefix: "错误：",
      openOzon: "在 Ozon 打开",
      tableSku: "SKU",
      tableStatus: "状态",
      tablePrice: "价格",
      tableMergeType: "合并类型",
      tableAction: "操作",
      systemActive: "AI 本地化引擎运行中",
      systemActiveAttributeOnly: "AI 变体属性引擎运行中",
      downloadReport: "下载批量报告",
      retryFailedPlain: "重试失败项",
      doneAndBack: "完成并返回",
      compareBefore: "1688",
      compareAfter: "OZON",
      compareSliderAria: "{sku} 对比滑块",
      previewReady: "OZON 就绪",
      previewGenerating: "生成中...",
      previewReviewing: "审核中...",
      previewPending: "等待中...",
      previewFailed: "预览不可用",
      sourceImageLabel: "参考图",
      generatedImageLabel: "生成主图",
      generatedImageEmpty: "暂无主图",
      imageStrategyLabel: "图片策略",
      imageStrategyPending: "待处理",
      imageStrategyGenerated: "已基于变体图生成主图",
      imageStrategyVariantSource: "直接使用变体原图",
      imageStrategyVariantFallback: "生图失败，回退变体原图",
      imageStrategyMasterFallback: "变体无图，回退主 SKU 主图",
      imageStrategyMasterInherited: "无 SKU 图，继承主 SKU 图集",
      imageStrategyStandaloneReused: "独立商品发布，复用参考图",
      imageStrategyNoImage: "无可用图片",
      publishModeStandaloneBadge: "独立商品可发布",
      statusReady: "已就绪",
      statusGeneratedSuccess: "生成成功",
      statusPreviewReady: "待发布",
      statusStandalonePublishable: "独立商品可发布",
      statusFallbackBlocked: "回退失败（已阻断发布）",
      statusTimeoutAutoStopped: "超时自动终止",
      statusManualAttributeRequired: "待补充属性",
      statusRunning: "生成中",
      statusReviewing: "审核中",
      statusPending: "等待中",
      statusFailed: "失败",
      statusCancelled: "已取消",
      statusSkipped: "已跳过",
      statusDescReady: "本地化处理完成，可直接进入发布复核。",
      statusDescPublished: "该 SKU 已发布到 Ozon，可直接打开 Ozon 链接查看平台结果。",
      statusDescPublishInFlight: "该 SKU 已提交到 Ozon，正在等待平台审核或状态同步。",
      statusDescPublishFailed: "该 SKU 发布失败，请根据平台返回原因修正后重试。",
      statusDescAttributeOnlyReady: "变体属性改写已完成，可进入手动发布前预览。",
      statusDescAttributeOnlyStandaloneReady: "未找到稳定可合并的差异属性，但该 SKU 已可作为独立 Ozon 商品发布。",
      statusDescAttributeOnlyRunning: "AI 正在生成变体属性改写。",
      statusDescAttributeOnlyPending: "等待进入属性分析阶段。",
      statusDescAttributeOnlyFailed: "未生成可编辑的变体属性改写，因此结果页里没有可手动补充的差异字段。",
      statusDescAttributeOnlyManualRequired: "后端已识别到可编辑差异字段，请先点击 AI 重试属性；AI 仍识别不到时，再打开预览编辑手动补充。",
      statusDescAttributeOnlyImageFailed: "属性改写已生成，但主图生成失败，请重试生图。",
      statusDescAttributeOnlyImageFailedSuccessSegment: "属性改写已生成，",
      statusDescAttributeOnlyImageFailedFailureSegment: "但主图生成失败，",
      statusDescAttributeOnlyImageFailedActionSegment: "请重试生图。",
      attributeRewriteSuccessHint: "属性改写成功。",
      statusDescAttributeOnlyFallbackBlocked: "主图回退到原图/主图，已按规则阻断发布。",
      statusDescAttributeOnlyTimeout: "主图生成超时并自动终止，请重试。",
      standalonePatchHint: "当前无需手动补充差异字段，系统会按独立商品提交该 SKU。",
      manualAttributePatchHint: "优先点击 AI 重试属性；AI 仍识别不到时，再打开预览编辑手动补充。",
      retryAttributesHint: "先点击 AI 重试属性；AI 仍识别不到时，再手动补充差异字段。",
      retryAttributes: "AI 重试属性",
      retryAttributesShort: "AI 重试",
      retryingAttributes: "AI 重试中...",
      retryAttributesFailed: "AI 重试属性失败，请稍后再试。",
      retryAttributesNoChange: "AI 重试仍未生成有效属性，可进入预览编辑手动补充。",
      aggregatedAttributeRetryBlocked: "多任务聚合视图暂不支持单条 AI 重试，请进入单任务结果页处理。",
      locateRetryAttributes: "定位 AI 重试项（{count}）",
      fillManualAttributes: "手动补充属性",
      statusDescRunning: "正在生成本地化素材。",
      statusDescReviewing: "已提交 Ozon，仍在审核确认中。",
      statusDescPending: "等待执行。",
      statusDescFailed: "处理失败，请重试失败项。",
      statusDescCancelled: "任务在完成前被取消。",
      statusDescSkipped: "由于流程约束被跳过。",
      sourceImageLockVariantSku: "已锁定 SKU 原图（生图）",
      sourceImageLockMasterInherited: "无 SKU 图，继承主 SKU 图集（可发布）",
      sourceImageLockPending: "参考图尚未锁定",
      mergeTypeModel: "型号",
      mergeTypeSimilarProduct: "相似商品",
      mergeTypeProductInPhoto: "照片上的商品",
      mergeTypeBundleMerge: "套装合并",
      mergeTypeBundleCreate: "商品套装创建",
      mergeTypeUnknown: "未知",
      manageVariantGroups: "变体任务管理",
      loadWarning: "结果可能不完整",
      retryFailed: "重试失败项（{count}）",
      retryingFailed: "重试中...",
      retryFailedError: "触发失败项重试失败，请稍后再试。",
      regenerateImage: "重新生成主图",
      regenerating: "重生成中...",
      regenerateFailed: "重新生成变体主图失败，请稍后重试。",
      regenerateBillingMeta: "生图计费：{creditsPerImage} 积分/张 · 本次重生预计 {estimatedCredits} 积分 · 当前可用 {availableCredits}",
      regenerateBillingSettlementHint: "会按当前图片模式扣费；后台自动调度模型，模型变化不影响单价，失败图片自动退款。",
      goRecharge: "去充值",
      creditsInsufficientHint: "积分不足（需要 {needed}，当前 {current}），请充值后重试。",
    },

    variantPublishConfirm: {
      breadcrumbLabel: "变体发布确认",
      eyebrow: "发布确认",
      title: "确认要发布的变体",
      desc: "这个页面只确认本次发布清单，然后提交发布任务。",
      missingTitle: "缺少发布信息",
      missingDesc: "请回到复核结果页，重新选择要发布的变体。",
      warningTitle: "提交发布失败",
      retrySubmit: "重新提交",
      tableSku: "SKU",
      tablePatch: "属性改写",
      tableImage: "图片",
      tableStatus: "状态",
      patchCount: "{count} 条改写",
      imageReady: "已就绪",
      imageMissing: "缺失",
      readyToPublish: "可发布",
      confirmTitle: "发布清单",
      confirmDesc: "只有这些已选变体会被提交。",
      riskHint: "提交后 Ozon 仍可能异步审核或合并变体。",
      submit: "确认发布",
      submitting: "提交中...",
      submitBlocked: "SKU 明细未恢复",
      submitFailed: "提交所选变体失败。",
      recovering: "正在恢复明细...",
      recoveringDesc: "当前直达链接只带了 itemId，正在从任务结果恢复 SKU 名称、图片和属性改写数量；恢复完成前不会允许直接发布。",
      recoverFailed: "恢复所选 SKU 明细失败。",
      recoverIncomplete: "任务结果中没有找到部分已选 SKU，请返回复核页重新进入发布确认。",
      recoverLoginRequired: "请先登录，以便安全恢复所选 SKU 明细。",
      recoveryBlockedTitle: "发布清单缺少确认明细",
      retryRecover: "重新恢复",
      backToReview: "返回复核",
      submittedTitle: "发布任务已提交",
      submittedDesc: "已将 {count} 个变体交给发布任务处理。",
      nextTitle: "接下来",
      nextDesc: "到进度页查看发布执行情况，完成后再回到变体管理。",
      viewProgress: "查看发布进度"
    },

    variantPublishPreview: {
      breadcrumbLabel: "变体发布前预览",
      title: "变体发布前预览",
      desc: "发布前可编辑关键字段并重算定价，确认可发布条目。",
      taskMeta: "任务: {status} · 阶段: {stage}",
      errorTitle: "预览结果可能不完整",
      retry: "重试",
      loadFailed: "读取变体发布前预览失败。",
      loading: "正在加载变体发布前预览...",
      summaryTotal: "变体总数",
      summaryPublishable: "可发布",
      summaryBlocked: "已阻断",
      summaryProfit: "预计总利润",
      publishSelectionMeta: "可发布 {publishable} · 已勾选 {selected} · 总计 {total}",
      emptyTitle: "暂无可预览变体",
      emptyDesc: "当前任务没有可用的发布前预览数据。",
      selectForPublish: "勾选发布",
      publishable: "可发布",
      blocked: "已阻断",
      sourceImage: "参考图",
      generatedImage: "生成主图",
      fieldTitle: "标题",
      fieldPrice: "售价",
      fieldOldPrice: "划线价",
      fieldMinPrice: "最低价",
      fieldDictionaryIds: "字典 ID（逗号分隔）",
      variantAttributes: "变体属性（可编辑）",
      noVariantAttributes: "暂无可编辑的变体属性。",
      masterAttributes: "主 SKU 继承属性（只读）",
      emptyMasterAttributes: "暂无主 SKU 继承属性。",
      lockedAttributes: "禁改属性（不可编辑）",
      emptyLockedAttributes: "暂无禁改属性。",
      pricingProfit: "利润",
      pricingMargin: "利润率",
      pricingRoi: "ROI",
      saveItem: "保存当前条目",
      saving: "保存中...",
      saveFailed: "保存变体编辑失败。",
      recalculateItem: "重算定价",
      recalculateAll: "批量重算定价",
      recalculating: "重算中...",
      recalculateFailed: "重算定价失败。",
      imageStatus: "主图状态",
      imageStrategy: "图片策略",
      pricePreview: "价格预览",
      backToResult: "返回结果页",
      backToVariantResult: "返回变体结果页",
      backToVariantManager: "返回变体任务管理",
      invalidEntryDesc: "页面信息已过期，系统正在后台自动恢复。",
      publishing: "发布中...",
      publishAllVariants: "发布全部可发布变体（{count}）",
      publishCurrentVariant: "仅发布当前项",
      publishVariants: "发布变体（{count}）",
      publishFailed: "发布勾选变体失败，请稍后重试。",
      manualReviewTitle: "缺少参考图，需人工确认",
      manualReviewMessageDefault: "当前缺少变体参考图，请确认是否直接使用主 SKU 图，或按 SKU 文案重生成主图。",
      manualReviewUseMaster: "使用主 SKU 图",
      manualReviewGenerateWithText: "按 SKU 文案重生成",
      manualReviewPendingHint: "该条目在完成人工确认前不可发布。",
      manualReviewApplying: "提交中...",
      manualReviewApplyFailed: "提交参考图决策失败。",
      manualReviewApplyInheritSuccess: "已确认使用主 SKU 图作为参考。",
      manualReviewApplyGenerateQueued: "已提交按 SKU 文案重生成请求。",
    }
  }
};
